/**
 *  Type consentiti: string, number, boolean, select, slot
*/

import SecurityService from '../services/security';
import MovementInstructionService from '../services/movement-instruction';
import PlanningService from '../services/planning';
import VesselProtocolService from '../services/vessel-protocol';
import MaterialBalanceService from '../services/material-balance';
import CommonService from '../services/common';
import EquipmentOperationService from '@/services/equipment-operation';
import Constants from './Constants';
import Utils from './Utils';
import moment from 'moment';

import { FilterMatchMode } from 'primevue/api';
import TankExceptions from '../views/planning/TankExceptions.vue';
import genericService from '../services/genericService';
import { readonly } from 'vue';
import { maxValue, required } from '@vuelidate/validators';
import FiscalNotificationService from '../services/fiscal-notification';

const configurations = {
    SecurityUser: {
        title: 'users.page_title',
        api: 'security/user',
        enableUpdate: true,
        editButtonName: 'securityUserEditButton',
        enableDelete: true,
        deleteButtonName: 'securityUserDeleteButton',
        enableInsert: true,
        addButtonName: 'securityUserAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'firstName',
                label: 'users.firstName',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'lastName',
                label: 'users.lastName',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'email',
                label: 'users.email',
                type: 'string',
                validations: {
                    maxLength: 100,
                    required: true
                }
            },
            {
                name: 'username',
                label: 'users.username',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                },
                disabledInEdit: true
            },
            {
                name: 'password',
                label: 'users.password',
                type: 'string',
                validations: {
                    maxLength: 100,
                    required: true
                },
                gridHide: true,
                disabledInEdit: true
            },
            {
                name: 'realm',
                label: 'users.realm',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isEnabled',
                label: 'users.isEnabled',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isChangePasswordRequired',
                label: 'users.isChangePasswordRequired',
                type: 'boolean',
                readOnly: true
            },
            {
                name: 'isLoginByActiveDirectory',
                label: 'users.isLoginByActiveDirectory',
                type: 'boolean'
            },
            {
                name: 'isDisabledByAdmin',
                label: 'users.isDisabledByAdmin',
                type: 'boolean',
                readOnly: true
            },
            {
                name: 'isAPIKeyUser',
                label: 'users.isAPIKeyUser',
                type: 'boolean'
            }
        ],
        // toolbarActions: [
        //     {
        //         visible: false,
        //         name: 'reset_password',
        //         icon: 'pi pi-unlock',
        //         title: 'resetPassword',
        //         message: 'messages.reset_password_question',
        //         confirmBtnText: 'btn.save',
        //         data: {},
        //         fields: [
        //             {
        //                 name: 'newPassword',
        //                 label: 'users.newPassword',
        //                 type: 'string'
        //             }
        //         ],
        //         action: function(data, caller){
        //             console.log(data);
        //         }
        //     }
        // ],
        customActions: [
            {
                visible: false,
                type: 'window',
                name: 'securityUserResetPasswordButton',
                icon: 'pi pi-key',
                title: 'resetPassword',
                message: 'messages.reset_password_question',
                confirmBtnText: 'btn.save',
                data: {},
                fields: [
                    {
                        name: 'newPassword',
                        label: 'users.newPassword',
                        type: 'string'
                    }
                ],
                action: function (data, model, caller) {
                    let body = {
                        idSecurityUser: data.id,
                        newPassword: model.newPassword
                    };

                    SecurityService.passwordReset(body, resp => {
                        if (resp.success) {
                            this.visible = false;
                            model.newPassword = null;
                            caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                            caller.loadData();
                        } else {
                            caller.$toast.add({ detail: resp.message, life: 3000, severity: 'error' });
                        }
                    });

                }
            },
            {
                visible: false,
                type: 'window',
                name: 'securityUserCopyProfileButton',
                icon: 'pi pi-clone',
                title: 'copyProfile',
                message: 'messages.copyProfileMessage',
                confirmBtnText: 'btn.save',
                data: {},
                fields: [
                    {
                        name: 'idSecurityUserRef',
                        label: 'user_roles.user',
                        type: 'select',
                        selectApi: 'security/user',
                        selectFormat: {label: 'username', value:'id'}
                    }
                ],
                action: function (data, model, caller) {
                    let body = {
                        idDestinationUser: data.id,
                        idSecurityUserRef: model.idSecurityUserRef
                    };

                    SecurityService.cloneRoles(body, resp => {
                        if (resp.success) {
                            this.visible = false;
                            caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                            caller.loadData();
                        } else {
                            caller.$toast.add({ detail: resp.message, life: 3000, severity: 'error' });
                        }
                    });

                }
            }
        ]
    },
    SecurityRole: {
        title: 'roles.page_title',
        api: 'security/role',
        enableUpdate: true,
        editButtonName: 'securityRoleEditButton',
        enableDelete: true,
        deleteButtonName: 'securityRoleDeleteButton',
        enableInsert: true,
        addButtonName: 'securityRoleAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'name',
                label: 'roles.name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'roles.description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isEnabled',
                label: 'roles.isEnabled',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            // {
            //     name: 'actions',
            //     label: ' ',
            //     type: 'slot'
            // }
        ]
    },
    SecurityRoleAttribute: {
        title: 'attributes.page_title',
        api: 'security/role-attribute',
        enableUpdate: true,
        editButtonName: 'securityRoleAttributeEditButton',
        enableDelete: true,
        deleteButtonName: 'securityRoleAttributeDeleteButton',
        enableInsert: true,
        addButtonName: 'securityRoleAttributeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRole',
                label: 'attributes.role',
                type: 'select',
                selectApi: 'security/role',
                selectFormat: { label: 'name', value: 'id' },
                gridHide: true,
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'role',
                label: 'attributes.role',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'security/role',
                gridFilterSelectFormat: { label: 'name', value: 'id' },
                gridFilterName: 'idRole'
            },
            {
                name: 'attribute',
                label: 'attributes.attribute',
                type: 'string',
                validations: {
                    maxLength: 150
                }
            },
            {
                name: 'attributeType',
                label: 'attributes.type',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'attributeValue',
                label: 'attributes.value',
                type: 'string',
                validations: {
                    maxLength: 255
                }
            },
            // {
            //     name: 'actions',
            //     label: ' ',
            //     type: 'slot'
            // }
        ]
    },
    SecurityUserRole: {
        title: 'user_roles.page_title',
        api: 'security/user-roles',
        enableUpdate: true,
        editButtonName: 'securityUserRoleEditButton',
        enableDelete: true,
        deleteButtonName: 'securityUserRoleDeleteButton',
        enableInsert: true,
        addButtonName: 'securityUserRoleAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idSecurityUser',
                label: 'user_roles.user',
                type: 'select',
                selectApi: 'security/user',
                selectFormat: { label: 'username', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'user',
                label: 'user_roles.user',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'security/user',
                gridFilterSelectFormat: { label: 'username', value: 'id' },
                gridFilterName: 'idSecurityUser'
            },
            {
                name: 'idRole',
                label: 'user_roles.role',
                type: 'select',
                selectApi: 'security/role',
                selectFormat: { label: 'name', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'role',
                label: 'user_roles.role',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'security/role',
                gridFilterSelectFormat: { label: 'name', value: 'id' },
                gridFilterName: 'idRole'
            },
            // {
            //     name: 'actions',
            //     label: ' ',
            //     type: 'slot'
            // }
        ]
    },
    MovementInstructionRole: {
        title: 'movement_instruction_roles.page_title',
        api: 'movement-instruction/role',
        enableUpdate: true,
        editButtonName: 'movementInstructionRoleEditButton',
        enableDelete: true,
        deleteButtonName: 'movementInstructionRoleDeleteButton',
        enableInsert: true,
        addButtonName: 'movementInstructionRoleAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'name',
                label: 'roles.name',
                type: 'string'
            },
            {
                name: 'isEnabled',
                label: 'roles.isEnabled',
                type: 'boolean'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                formHide: true
            },
        ]
    },
    MovementInstructionUserRole: {
        title: 'movement_instruction_user_roles.page_title',
        api: 'movement-instruction/user-role',
        enableUpdate: true,
        editButtonName: 'movementInstructionUserRoleEditButton',
        enableDelete: true,
        deleteButtonName: 'movementInstructionUserRoleDeleteButton',
        enableInsert: true,
        addButtonName: 'movementInstructionUserRoleAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idSecurityUser',
                label: 'user_roles.user',
                type: 'select',
                selectApi: 'security/user',
                selectFormat: { label: 'username', value: 'id' },
                gridHide: true
            },
            {
                name: 'user',
                label: 'user_roles.user',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'security/user',
                gridFilterSelectFormat: { label: 'username', value: 'id' },
                gridFilterName: 'idSecurityUser'
            },
            {
                name: 'idMovementInstructionRole',
                label: 'user_roles.role',
                type: 'select',
                selectApi: 'movement-instruction/role',
                selectFormat: { label: 'name', value: 'id' },
                gridHide: true
            },
            {
                name: 'role',
                label: 'user_roles.role',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'movement-instruction/role',
                gridFilterSelectFormat: { label: 'name', value: 'id' },
                gridFilterName: 'idMovementInstructionRole'
            },
        ]
    },
    MovementInstructionUserRoleOperationType: {
        title: 'movement_instruction_user_role_operation_types.page_title',
        api: 'movement-instruction/user-role-operation-type',
        enableUpdate: true,
        editButtonName: 'movementInstructionRoleOperationTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'movementInstructionRoleOperationTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'movementInstructionRoleOperationTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryOperationType',
                label: 'operationType.fk',
                type: 'select',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'operationType',
                label: 'operationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/operation-type-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryOperationType'
            },
            {
                name: 'idMovementInstructionRole',
                label: 'user_roles.role',
                type: 'select',
                selectApi: 'movement-instruction/role',
                selectFormat: { label: 'name', value: 'id' },
                gridHide: true
            },
            {
                name: 'role',
                label: 'user_roles.role',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'movement-instruction/role',
                gridFilterSelectFormat: { label: 'name', value: 'id' },
                gridFilterName: 'idMovementInstructionRole'
            },
        ]
    },
    AutomaticEMailAddress: {
        title: 'automaticEMailAddress.page_title',
        api: 'masterdata/automatic-email-address',
        enableUpdate: true,
        editButtonName: 'automaticEMailAddressEditButton',
        enableDelete: true,
        deleteButtonName: 'automaticEMailAddressDeleteButton',
        enableInsert: true,
        addButtonName: 'automaticEMailAddressAddButton',
        idColumn: "id",
        skipCache: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryAutomaticMailingList',
                label: 'automaticMailingList.fk',
                type: 'select',
                selectApi: 'masterdata/automatic-mailing-list',
                selectFormat: { label: 'emailType', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryAutomaticMailingList',
                label: 'automaticMailingList.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/automatic-mailing-list',
                gridFilterSelectFormat: { label: 'emailType', value: 'id' },
                gridFilterName: 'idRegistryAutomaticMailingList'
            },
            {
                name: 'mode',
                label: 'automaticEMailAddress.mode',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'address',
                label: 'automaticEMailAddress.address',
                type: 'string',
                validations: {
                    maxLength: 255,
                    required: true
                }
            }
        ]
    },
    AutomaticMailingList: {
        title: 'automaticMailingList.page_title',
        api: 'masterdata/automatic-mailing-list',
        enableUpdate: true,
        editButtonName: 'automaticMailingListEditButton',
        enableDelete: true,
        deleteButtonName: 'automaticMailingListDeleteButton',
        enableInsert: true,
        addButtonName: 'automaticMailingListAddButton',
        idColumn: "id",
        dataTables: [
            {
                label: 'automaticEMailAddress.page_title',
                dataTableUnderForm: 'AutomaticEMailAddress',
                dataTableDefaultKey: 'idRegistryAutomaticMailingList',
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'emailType',
                label: 'automaticMailingList.emailType',
                type: 'string',
                validations: {
                    maxLength: 100,
                    required: true
                }
            }
        ]
    },
    ConversionFactor: {
        title: 'conversionFactor.page_title',
        api: 'masterdata/conversion-factor',
        enableUpdate: true,
        editButtonName: 'conversionFactorEditButton',
        enableDelete: true,
        deleteButtonName: 'conversionFactorDeleteButton',
        enableInsert: true,
        addButtonName: 'conversionFactorAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'conversionFactor.code',
                type: 'string',
                validations: {
                    maxLength: 20,
                    required: true
                }
            },
            {
                name: 'density',
                label: 'conversionFactor.density',
                type: 'decimal',
                digits: 4,
                validations: {
                    required: true
                }
            },
            {
                name: 'factor',
                label: 'conversionFactor.factor',
                type: 'decimal',
                digits: 6,
                validations: {
                    required: true
                }
            },
        ]
    },


    AnalysisSampleReceiver: {
        title: 'analysisSampleReceiver.page_title',
        api: 'masterdata/analysis-sample-receiver',
        enableUpdate: true,
        editButtonName: 'analysisSampleReceiverEditButton',
        enableDelete: true,
        deleteButtonName: 'analysisSampleReceiverDeleteButton',
        enableInsert: true,
        addButtonName: 'analysisSampleReceiverAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'analysisSampleReceiver.description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'englishDescription',
                label: 'analysisSampleReceiver.englishDescription',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'analysisSampleReceiver.isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    ClearanceMovementType: {
        title: 'clearanceMovementType.page_title',
        api: 'masterdata/clearance-movement-type',
        enableUpdate: true,
        editButtonName: 'clearanceMovementTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'clearanceMovementTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'clearanceMovementTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 6,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 100,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    Vessel: {
        title: 'vessel.page_title',
        api: 'masterdata/vessel',
        enableUpdate: true,
        editButtonName: 'vesselEditButton',
        enableDelete: true,
        deleteButtonName: 'vesselDeleteButton',
        enableInsert: true,
        addButtonName: 'vesselAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'imoNumber',
                label: 'vessel.imoNumber',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'name',
                label: 'vessel.name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'deadWeightTonnage',
                label: 'vessel.deadWeightTonnage',
                type: 'decimal',
                digits: 3,
                max: Constants.MAX_10_3,
                validations: {
                    required: true
                }
            },
            {
                name: 'lengthOverall',
                label: 'vessel.lengthOverall',
                type: 'decimal',
                digits: 3,
                max: Constants.MAX_18_3
            },
            {
                name: 'draught',
                label: 'vessel.draught',
                type: 'decimal',
                digits: 3,
                max: Constants.MAX_18_3
            },
            {
                name: 'grossTonnage',
                label: 'vessel.grossTonnage',
                type: 'decimal',
                digits: 0,
                max: Constants.MAX_18
            },
            {
                name: 'netTonnage',
                label: 'vessel.netTonnage',
                type: 'decimal',
                digits: 0,
                max: Constants.MAX_18
            },
            {
                name: 'isBunkerBarge',
                label: 'vessel.isBunkerBarge',
                type: 'boolean'
            },
            {
                name: 'countryAndTerritoryFlagCode',
                label: 'countryAndTerritory.countryAndTerritory',
                type: 'select',
                selectApi: 'masterdata/country-and-territory',
                selectFormat: { label: 'isoCode', value: 'isoCode' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'countryAndTerritoryFlagCode',
                label: 'countryAndTerritory.countryAndTerritory',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/country-and-territory',
                gridFilterSelectFormat: { label: 'isoCode', value: 'isoCode' },
                gridFilterName: 'countryAndTerritoryFlagCode'
            },
            {
                name: 'yearOfBuilt',
                label: 'vessel.yearOfBuilt',
                type: 'number'
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date'
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    CommercialOperationType: {
        title: 'commercialOperationType.page_title',
        api: 'masterdata/commercial-operation-type',
        enableUpdate: true,
        editButtonName: 'commercialOperationTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'commercialOperationTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'commercialOperationTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'commercialOperationType.code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isPhysicalOperation',
                label: 'commercialOperationType.isPhysicalOperation',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },

    Company: {
        title: 'company.page_title',
        api: 'masterdata/company',
        enableUpdate: true,
        editButtonName: 'companyEditButton',
        enableDelete: true,
        deleteButtonName: 'companyDeleteButton',
        enableInsert: true,
        addButtonName: 'companyAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isOwner',
                label: 'owner',
                type: 'boolean'
            },
            {
                name: 'transportDepartmentCode',
                label: 'company.transportDepartmentCode',
                type: 'string',
                validations: {
                    maxLength: 7
                }
            },
            {
                name: 'infoilCode',
                label: 'company.infoilCode',
                type: 'string',
                validations: {
                    maxLength: 4
                }
            },
            {
                name: 'cvtCode',
                label: 'company.cvtCode',
                type: 'string',
                validations: {
                    maxLength: 4
                }
            },
            {
                name: 'shipperDescription',
                label: 'movementInstructions.shipper',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'deliveryDocumentHolderDescription',
                label: 'movementInstructions.deliveryDocumentHolder',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'billOfLadingHolderDescription',
                label: 'movementInstructions.billOfLadingHolder',
                type: 'string',
                validations: {
                    maxLength: 300
                }
            },
            {
                name: 'billOfLadingReportFormat',
                label: 'movementInstructions.BillOfLadingReportFormat',
                type: 'string',
                validations: {
                    maxLength: 5
                }
            },
            {
                name: 'isRefinery',
                label: 'movementInstructions.isRefinery',
                type: 'boolean',
                validations: {
                    required: true
                },
            },
            {
                name: 'isChargeByLandRetailer',
                label: 'company.isChargeByLandRetailer',
                type: 'boolean'
            },
            {
                name: 'compulsoryStockCode',
                label: 'company.compulsoryStockCode',
                type: 'string',
                validations: {
                    maxLength: 40
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'isCompulsoryStockEnabled',
                label: 'company.isCompulsoryStockEnabled',
                type: 'boolean'
            },
            {
                name: 'isStocchinoPlanEnabled',
                label: 'company.isStocchinoPlanEnabled',
                type: 'boolean'
            }
        ]
    },
    CompanyAddress: {
        title: 'companyAddress.page_title',
        api: 'masterdata/company-address',
        enableUpdate: true,
        editButtonName: 'companyAddressEditButton',
        enableDelete: true,
        deleteButtonName: 'companyAddressDeleteButton',
        enableInsert: true,
        addButtonName: 'companyAddressAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },

            {
                name: 'idRegistryCompany',
                label: 'company.fk',
                type: 'select',
                selectApi: 'masterdata/company-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                sortField: 'Description',
                gridHide: true
            },
            {
                name: 'company',
                label: 'company.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryCompany'
            },
            {
                name: 'completeAddress',
                label: 'companyAddress.completeAddress',
                type: 'string',
                validations: {
                    maxLength: 250,
                    required: true
                }
            },
            {
                name: 'city',
                label: 'companyAddress.city',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'country',
                label: 'companyAddress.country',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'zipCode',
                label: 'companyAddress.zipCode',
                type: 'string',
                validations: {
                    maxLength: 20
                }
            },
            {
                name: 'district',
                label: 'companyAddress.district',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'isMainAddress',
                label: 'companyAddress.isMainAddress',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    RecordFileState: {
        title: 'recordFileState.page_title',
        api: 'masterdata/record-file-state',
        enableUpdate: true,
        editButtonName: 'recordFileStateEditButton',
        enableDelete: true,
        deleteButtonName: 'recordFileStateDeleteButton',
        enableInsert: true,
        addButtonName: 'recordFileStateAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 1
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 50
                }
            },
        ]
    },
    ImportFromFileState: {
        title: 'importFromFileState.page_title',
        api: 'masterdata/import-from-file-state',
        enableUpdate: true,
        editButtonName: 'importFromFileStateEditButton',
        enableDelete: true,
        deleteButtonName: 'importFromFileStateDeleteButton',
        enableInsert: true,
        addButtonName: 'importFromFileStateAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 3
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 50
                }
            },
        ]
    },
    AccountType: {
        title: 'accountType.page_title',
        api: 'masterdata/account-type',
        enableUpdate: true,
        editButtonName: 'accountTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'accountTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'accountTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 20
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                }
            },
            {
                name: 'isOutCDep',
                label: 'isOutCDep',
                type: 'boolean',
            },
        ]
    },
    CompanySection: {
        title: 'companySection.page_title',
        api: 'material-balance-company-section',
        enableUpdate: true,
        editButtonName: 'companySectionEditButton',
        enableDelete: true,
        deleteButtonName: 'companySectionDeleteButton',
        enableInsert: true,
        addButtonName: 'companySectionAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            
            {
                name: 'idRegistryPlant',
                label: 'registryPlant.fk',
                type: 'select',
                selectApi: 'masterdata/registry-plant-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                sortField: 'Description',
                gridHide: true
            },
            {
                name: 'registryPlant',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/registry-plant-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryPlant'
            },
            {
                name: 'idRegistryCompany',
                label: 'company.fk',
                type: 'select',
                selectApi: 'masterdata/company-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                sortField: 'Description',
                gridHide: true
            },
            {
                name: 'registryCompany',
                label: 'company.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryCompany'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
            },
            {
                name: 'position',
                label: 'position',
                type: 'number',
                validations: {
                    required: true
                }
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'isRefinery',
                label: 'isRefinery',
                type: 'boolean',
            },
            {
                name: 'isIgcc',
                label: 'isIgcc',
                type: 'boolean',
            },
        ]
    },
    BalanceConfidentialLevel: {
        title: 'balanceConfidentialLevel.page_title',
        api: 'masterdata/balance-confidential-level',
        enableUpdate: true,
        editButtonName: 'balanceConfidentialLevelEditButton',
        enableDelete: true,
        deleteButtonName: 'balanceConfidentialLevelDeleteButton',
        enableInsert: true,
        addButtonName: 'balanceConfidentialLevelAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'level',
                label: 'level',
                type: 'number',
                validations: {
                    required: true,
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true,
                }
            },
            {
                name: 'isDefault',
                label: 'isDefault',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
            },
            {
                name: 'isPublished',
                label: 'isPublished',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    BalancePhase: {
        title: 'balancePhase.page_title',
        api: 'masterdata/balance-phase',
        enableUpdate: true,
        editButtonName: 'balancePhaseEditButton',
        enableDelete: true,
        deleteButtonName: 'balancePhaseDeleteButton',
        enableInsert: true,
        addButtonName: 'balancePhaseAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true,
                }
            },
            {
                name: 'isDefault',
                label: 'isDefault',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
            },
            {
                name: 'isFinal',
                label: 'isFinal',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    BalanceSheetAccountType: {
        title: 'balanceSheetAccountType.page_title',
        api: 'masterdata/balance-sheet-account-type',
        enableUpdate: true,
        editButtonName: 'balanceSheetAccountTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'balanceSheetAccountTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'balanceSheetAccountTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
        ]
    },
    BalanceSheetType: {
        title: 'balanceSheetType.page_title',
        api: 'masterdata/balance-sheet-type',
        enableUpdate: true,
        editButtonName: 'balanceSheetTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'balanceSheetTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'balanceSheetTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
        ]
    },
    OriginStorage: {
        title: 'originStorage.page_title',
        api: 'masterdata/origin-storage',
        enableUpdate: true,
        editButtonName: 'originStorageEditButton',
        enableDelete: true,
        deleteButtonName: 'originStorageDeleteButton',
        enableInsert: true,
        addButtonName: 'originStorageAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true,
                }
            },
            {
                name: 'isManual',
                label: 'isManual',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    CustomsChangeValue: {
        title: 'customsChangeValue.page_title',
        api: 'masterdata/customs-change-value',
        enableUpdate: true,
        editButtonName: 'customsChangeValueEditButton',
        enableDelete: true,
        deleteButtonName: 'customsChangeValueDeleteButton',
        enableInsert: true,
        addButtonName: 'customsChangeValueAddButton',
        defaultSortField: 'validFrom',
        defaultSortOrder: -1,
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true
            },
            {
                name: 'validFrom',
                label: 'validFrom',
                type: 'date',
                format: "dd/mm/yy",
                defaultSort: 'asc',
                validations: {
                    required: true,
                }
            },
            {
                name: 'validTo',
                label: 'validTo',
                type: 'date',
                format: "dd/mm/yy",
                validations: {
                    required: true,
                }
            },
            {
                name: 'value',
                label: 'value',
                type: 'decimal',
                digits: 5,
                max: Constants.MAX_18_5,
                validations: {
                    required: true,
                }
            },
        ]
    },
    VAT: {
        title: 'vat.page_title',
        api: 'masterdata/vat',
        enableUpdate: true,
        editButtonName: 'vatEditButton',
        enableDelete: true,
        deleteButtonName: 'vatDeleteButton',
        enableInsert: true,
        addButtonName: 'vatAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'validFrom',
                label: 'validFrom',
                type: 'date',
                format: "dd/mm/yy",
                validations: {
                    required: true,
                }
            },
            {
                name: 'validTo',
                label: 'validTo',
                type: 'date',
                format: "dd/mm/yy",
                validations: {
                    required: true,
                }
            },
            {
                name: 'value',
                label: 'value',
                type: 'decimal',
                digits: 0,
                max: Constants.MAX_18,
                validations: {
                    required: true,
                }
            },
        ]
    },
    PortualTax: {
        title: 'portualTax.page_title',
        api: 'masterdata/portual-tax',
        enableUpdate: true,
        editButtonName: 'portualTaxEditButton',
        enableDelete: true,
        deleteButtonName: 'portualTaxDeleteButton',
        enableInsert: true,
        addButtonName: 'portualTaxAddButton',
        defaultSortField: 'startDateValidity',
        defaultSortOrder: -1,
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'startDateValidity',
                label: 'validityStartDate',
                type: 'date',
                format: "dd/mm/yy",
            },
            {
                name: 'endDateValidity',
                label: 'validityEndDate',
                type: 'date',
                format: "dd/mm/yy",
            },
            {
                name: 'idFiscalDestination',
                label: 'fiscalDestination.fk',
                type: 'select',
                selectApi: 'masterdata/fiscal-destination-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'fiscalDestination',
                label: 'fiscalDestination.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-destination-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idFiscalDestination'
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'taxRate',
                label: 'taxRate',
                type: 'decimal',
                digits: 4,
                max: Constants.MAX_18_4,
                validations: {
                    required: true,
                }
            },
            {
                name: 'destination',
                label: 'destination',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true,
                }
            },
        ]
    },
    VesselProtocolPaymentType: {
        title: 'vesselProtocolPaymentType.page_title',
        api: 'masterdata/vessel-protocol-payment-type',
        enableUpdate: true,
        editButtonName: 'vesselProtocolPaymentTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'vesselProtocolPaymentTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'vesselProtocolPaymentTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'englishDescription',
                label: 'englishDescription',
                type: 'string',
                validations: {
                    maxLength: 250,
                }
            },
            {
                name: 'italianDescription',
                label: 'italianDescription',
                type: 'string',
                validations: {
                    maxLength: 250,
                }
            },
        ]
    },
    CountryAndTerritory: {
        title: 'countryAndTerritory.page_title',
        api: 'masterdata/country-and-territory',
        enableUpdate: true,
        editButtonName: 'countryAndTerritoryEditButton',
        enableDelete: true,
        deleteButtonName: 'countryAndTerritoryDeleteButton',
        enableInsert: true,
        addButtonName: 'countryAndTerritoryAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'isoCode',
                label: 'countryAndTerritory.isoCode',
                type: 'string',
                disabledInEdit: true,
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'shortName',
                label: 'countryAndTerritory.shortName',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'idRegistryFiscalDestination',
                label: 'fiscalDestination.fk',
                type: 'select',
                selectApi: 'masterdata/fiscal-destination-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryFiscalDestination',
                label: 'fiscalDestination.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-destination-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryFiscalDestination'
            },
            {
                name: 'currencyCode',
                label: 'currencyCode',
                type: 'select',
                selectApi: 'masterdata/currency',
                selectFormat: { label: 'isoCode', value: 'isoCode' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'currencyCode',
                label: 'currencyCode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/currency',
                gridFilterSelectFormat: { label: 'isoCode', value: 'isoCode' },
                gridFilterName: 'currencyCode'
            },
            // {
            //     name: 'currencyCode',
            //     label: 'currencyCode',
            //     type: 'string',
            //     validations: {
            //         maxLength: 3
            //     }
            // },
            {
                name: 'transportDepartmentCode',
                label: 'countryAndTerritory.transportDepartmentCode',
                type: 'string',
                validations: {
                    maxLength: 7
                }
            },
            {
                name: 'transportDepartmentDescription',
                label: 'countryAndTerritory.transportDepartmentDescription',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'isCEE',
                label: 'isCEE',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations:{
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    Currency: {
        title: 'currency.page_title',
        api: 'masterdata/currency',
        enableUpdate: true,
        editButtonName: 'currencyEditButton',
        enableDelete: true,
        deleteButtonName: 'currencyDeleteButton',
        enableInsert: true,
        addButtonName: 'currencyAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'isoCode',
                label: 'isoCode',
                type: 'string',
                disabledInEdit: true,
                validations: {
                    maxLength: 3,
                    required: true
                }
            },
            {
                name: 'isoCodeNumber',
                label: 'isoCodeNumber',
                type: 'string',
                validations: {
                    maxLength: 3,
                    required: true
                }
            },
            {
                name: 'name',
                label: 'name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    DocumentType: {
        title: 'documentType.page_title',
        api: 'masterdata/document-type',
        enableUpdate: true,
        editButtonName: 'documentTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'documentTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'documentTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'aliasVesselNominationAI',
                label: 'aliasVesselNominationAI',
                type: 'string',
                validations: {
                    maxLength: 300
                }
            },
            {
                name: 'languageCode',
                label: 'languageCode',
                type: 'string',
                validations: {
                    maxLength: 2,
                    required: true
                }
            },
            {
                name: 'isIssued',
                label: 'isIssued',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isFiscal',
                label: 'isFiscal',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    DocumentReceiver: {
        title: 'documentReceiver.page_title',
        api: 'masterdata/document-receiver',
        enableUpdate: true,
        editButtonName: 'documentReceiverEditButton',
        enableDelete: true,
        deleteButtonName: 'documentReceiverDeleteButton',
        enableInsert: true,
        addButtonName: 'documentReceiverAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'englishDescription',
                label: 'englishDescription',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'isInternalUse',
                label: 'isInternalUse',
                type: 'boolean'
            },
            {
                name: 'isForTheMaster',
                label: 'isForTheMaster',
                type: 'boolean'
            },
            {
                name: 'isForTheConsignee',
                label: 'isForTheConsignee',
                type: 'boolean'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    FiscalPosition: {
        title: 'fiscalPosition.page_title',
        api: 'masterdata/fiscal-position',
        enableUpdate: true,
        editButtonName: 'fiscalPositionEditButton',
        enableDelete: true,
        deleteButtonName: 'fiscalPositionDeleteButton',
        enableInsert: true,
        addButtonName: 'fiscalPositionAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'shortDescription',
                label: 'shortDescription',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'infoilCode',
                label: 'infoilCode',
                type: 'string',
                validations: {
                    maxLength: 4,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    FiscalUse: {
        title: 'fiscalUse.page_title',
        api: 'masterdata/fiscal-use',
        enableUpdate: true,
        editButtonName: 'fiscalUseEditButton',
        enableDelete: true,
        deleteButtonName: 'fiscalUseDeleteButton',
        enableInsert: true,
        addButtonName: 'fiscalUseAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'infoilCode',
                label: 'infoilCode',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'isMovementInstructionSelected',
                label: 'isMovementInstructionSelected',
                type: 'boolean'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    Incoterm: {
        title: 'incoterm.page_title',
        api: 'masterdata/incoterm',
        enableUpdate: true,
        editButtonName: 'incotermEditButton',
        enableDelete: true,
        deleteButtonName: 'incotermDeleteButton',
        enableInsert: true,
        addButtonName: 'incotermAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 3,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    InspectionCost: {
        title: 'inspectionCost.page_title',
        api: 'masterdata/inspection-cost',
        enableUpdate: true,
        editButtonName: 'inspectionCostEditButton',
        enableDelete: true,
        deleteButtonName: 'inspectionCostDeleteButton',
        enableInsert: true,
        addButtonName: 'inspectionCostAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 4,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    Inspector: {
        title: 'inspector.page_title',
        api: 'masterdata/inspector',
        enableUpdate: true,
        editButtonName: 'inspectorEditButton',
        enableDelete: true,
        deleteButtonName: 'inspectorDeleteButton',
        enableInsert: true,
        addButtonName: 'inspectorAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'name',
                label: 'name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'aliasVesselNominationAI',
                label: 'aliasVesselNominationAI',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'emailAddress',
                label: 'inspector.email',
                type: 'string'
            },
            {
                name: 'isVoyageReportEmailEnabled',
                label: 'inspector.isVoyageReportEmailEnabled',
                type: 'boolean',
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    Language: {
        title: 'language.page_title',
        api: 'masterdata/language',
        enableUpdate: true,
        editButtonName: 'languageEditButton',
        enableDelete: true,
        deleteButtonName: 'languageDeleteButton',
        enableInsert: true,
        addButtonName: 'languageAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'isoCode',
                label: 'isoCode',
                disabledInEdit: true,
                type: 'string',
                validations: {
                    maxLength: 2,
                    required: true
                }
            },
            {
                name: 'name',
                label: 'name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    MacroMovementType: {
        title: 'macroMovementType.page_title',
        api: 'masterdata/macro-movement-type',
        enableUpdate: true,
        editButtonName: 'macroMovementTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'macroMovementTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'macroMovementTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    SamplingPoint: {
        title: 'samplingPoint.page_title',
        api: 'masterdata/sampling-point',
        enableUpdate: true,
        editButtonName: 'samplingPointEditButton',
        enableDelete: true,
        deleteButtonName: 'samplingPointDeleteButton',
        enableInsert: true,
        addButtonName: 'samplingPointAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'englishDescription',
                label: 'englishDescription',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    ShippingAgency: {
        title: 'shippingAgency.page_title',
        api: 'masterdata/shipping-agency',
        enableUpdate: true,
        editButtonName: 'shippingAgencyEditButton',
        enableDelete: true,
        deleteButtonName: 'shippingAgencyDeleteButton',
        enableInsert: true,
        addButtonName: 'shippingAgencyAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'name',
                label: 'name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    TransportType: {
        title: 'transportType.page_title',
        api: 'masterdata/transport-type',
        enableUpdate: true,
        editButtonName: 'transportTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'transportTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'transportTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'documentDescription',
                label: 'documentDescription',
                type: 'string',
                validations: {
                    maxLength: 200,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    CounterFieldType: {
        title: 'counterFieldType.page_title',
        api: 'masterdata/counter-field-type',
        enableUpdate: true,
        editButtonName: 'counterFieldTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'counterFieldTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'counterFieldTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'counterTypeName',
                label: 'name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'prefix',
                label: 'prefix',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'suffix',
                label: 'suffix',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'format',
                label: 'format',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    Counter: {
        title: 'counter.page_title',
        api: 'masterdata/counter',
        enableUpdate: true,
        editButtonName: 'counterEditButton',
        enableDelete: true,
        deleteButtonName: 'counterDeleteButton',
        enableInsert: true,
        addButtonName: 'counterAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idCommonCounterFieldType',
                label: 'counterFieldType.fk',
                type: 'select',
                selectApi: 'masterdata/counter-field-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'commonCounterFieldType',
                label: 'counterFieldType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/counter-field-type-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idCommonCounterFieldType'
            },
            {
                name: 'year',
                label: 'year',
                type: 'number',
                validations: {
                    maxLength: 5
                }
            },
            {
                name: 'month',
                label: 'month',
                type: 'number',
                validations: {
                    maxLength: 5
                }
            },
            {
                name: 'day',
                label: 'day',
                type: 'number',
                validations: {
                    maxLength: 5
                }
            },
            {
                name: 'progressive',
                label: 'progressive',
                type: 'number',
                validations: {
                    maxLength: 5,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    Plant: {
        title: 'registryPlant.page_title',
        api: 'masterdata/registry-plant',
        enableUpdate: true,
        editButtonName: 'registryPlantEditButton',
        enableDelete: true,
        deleteButtonName: 'registryPlantDeleteButton',
        enableInsert: true,
        addButtonName: 'registryPlantAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'registryPortCode',
                label: 'registryPlant.registryPortCode',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'infoilPortCode',
                label: 'registryPlant.infoilPortCode',
                type: 'string',
                validations: {
                    maxLength: 4,
                    required: true
                }
            },
            {
                name: 'header',
                label: 'header',
                type: 'string',
                validations: {
                    maxLength: 200
                }
            },
            {
                name: 'abbreviation',
                label: 'abbreviation',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
            {
                name: 'customAgencyOfficeReference',
                label: 'registryPlant.customAgencyOfficeReference',
                type: 'string',
                validations: {
                    maxLength: 200
                }
            },
            {
                name: 'italianDocumentHeader',
                label: 'italianDocumentHeader',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'englishDocumentHeader',
                label: 'englishDocumentHeader',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'transportDepartmentCode',
                label: 'registryPlant.transportDepartmentCode',
                type: 'string',
                validations: {
                    maxLength: 7
                }
            },
            {
                name: 'idOriginDestinationCountry',
                label: 'countryAndTerritory.originDestination',
                type: 'select',
                selectApi: 'masterdata/country-and-territory-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'originDestinationCountry',
                label: 'countryAndTerritory.originDestination',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/country-and-territory-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idOriginDestinationCountry'
            },

            {
                name: 'idPlaceOfLoadingDischarge',
                label: 'countryAndTerritory.placeOfLoadingDischarge',
                type: 'select',
                selectApi: 'masterdata/country-and-territory-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'placeOfLoadingDischarge',
                label: 'countryAndTerritory.placeOfLoadingDischarge',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/country-and-territory-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idPlaceOfLoadingDischarge'
            },


            {
                name: 'idTaxWarehouse',
                label: 'taxWarehouse.fk',
                type: 'select',
                selectApi: 'masterdata/tax-warehouse-combo',
                selectFormat: { label: 'label', value: 'value' },
                // selectFormat: { label: function(e){
                //     return e.consigneeExciseCode + ' - ' + e.consigneeName;
                // }, value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'taxWarehouse',
                label: 'taxWarehouse.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/tax-warehouse-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                // gridFilterSelectFormat: { label: function(e){
                //     return e.consigneeExciseCode + ' - ' + e.consigneeName;
                // }, value: 'id' },
                gridFilterName: 'idTaxWarehouse'
            },

            {
                name: 'infoSipCode',
                label: 'registryPlant.infoSipCode',
                type: 'string',
                validations: {
                    maxLength: 3
                }
            },

            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    AuthorizationType: {
        title: 'authorizationType.page_title',
        api: 'masterdata/authorization-type',
        enableUpdate: true,
        editButtonName: 'authorizationTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'authorizationTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'authorizationTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    ContractType: {
        title: 'contractType.page_title',
        api: 'masterdata/contract-type',
        enableUpdate: true,
        editButtonName: 'contractTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'contractTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'contractTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    FiscalDestination: {
        title: 'fiscalDestination.page_title',
        api: 'masterdata/fiscal-destination',
        enableUpdate: true,
        editButtonName: 'fiscalDestinationEditButton',
        enableDelete: true,
        deleteButtonName: 'fiscalDestinationDeleteButton',
        enableInsert: true,
        addButtonName: 'fiscalDestinationAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'shortDescription',
                label: 'shortDescription',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'infoilCode',
                label: 'infoilCode',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    InfoilFamilyProduct: {
        title: 'infoilFamilyProduct.page_title',
        api: 'masterdata/infoil-family-product',
        enableUpdate: true,
        editButtonName: 'infoilFamilyProductEditButton',
        enableDelete: true,
        deleteButtonName: 'infoilFamilyProductDeleteButton',
        enableInsert: true,
        addButtonName: 'infoilFamilyProductAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 2,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isIncludedIntoHter9',
                label: 'infoilFamilyProduct.isIncludedIntoHter9Flag',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isGas',
                label: 'infoilFamilyProduct.isGasFlag',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'uoMFlag',
                label: 'infoilFamilyProduct.uoMFlag',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
            {
                name: 'denaturationLabel',
                label: 'infoilFamilyProduct.denaturationLabel',
                type: 'string',
                validations: {
                    maxLength: 2
                }
            },
            {
                name: 'genericDescription',
                label: 'genericDescription',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'order',
                label: 'order',
                type: 'number',
                validations: {
                    maxLength: 2,
                    required: true
                }
            },
            {
                name: 'note',
                label: 'note',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    InfoilOperationType: {
        title: 'infoilOperationType.page_title',
        api: 'masterdata/infoil-operation-type',
        enableUpdate: true,
        editButtonName: 'infoilOperationTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'infoilOperationTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'infoilOperationTypeAddButton',
        idColumn: "idRegistryOperationType",
        columns: [
            // {
            //     name: 'idRegistryOperationType',
            //     label: 'id',
            //     type: 'number',
            //     defaultSort: 'asc'
            // },


            {
                name: 'registryOperationType',
                label: 'operationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/operation-type-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                // gridFilterSelectFormat: {
                //      label: function(el){
                //         return el.code + '-' + el.shortDescription
                //     }, 
                //      value: 'id' 
                // },
                gridFilterName: 'idRegistryOperationType'
            },
            {
                name: 'idRegistryOperationType',
                label: 'operationType.fk',
                type: 'select',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                // selectFormat: { 
                //     label: function(el){
                //         return el.code + '-' + el.shortDescription
                //     }, 
                //     value: 'id' 
                // },
                gridHide: true,
                validations: {
                    required: true
                }
            },


            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    EnergyProduct: {
        title: 'energyProduct.page_title',
        api: 'masterdata/energy-product',
        enableUpdate: true,
        editButtonName: 'energyProductEditButton',
        enableDelete: true,
        deleteButtonName: 'energyProductDeleteButton',
        enableInsert: true,
        addButtonName: 'energyProductAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'nc',
                label: 'energyProduct.nC',
                type: 'string',
                validations: {
                    maxLength: 8,
                    required: true
                }
            },
            {
                name: 'infoilRegisterCode',
                label: 'energyProduct.infoilRegisterCode',
                type: 'string',
                validations: {
                    maxLength: 5
                }
            },
            {
                name: 'code',
                label: 'code',
                type: 'number',
                validations: {
                    maxLength: 5,
                    required: true
                }
            },
            {
                name: 'product',
                label: 'product.product',
                type: 'string',
                validations: {
                    maxLength: 255,
                    required: true
                }
            },
            {
                name: 'unitOfMeasureCode',
                label: 'unitOfMeasure.fk',
                type: 'select',
                selectApi: 'masterdata/unit-of-measure',
                selectFormat: { label: 'code', value: 'code' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'unitOfMeasureCode',
                label: 'unitOfMeasure.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/unit-of-measure',
                gridFilterSelectFormat: { label: 'code', value: 'code' },
                gridFilterName: 'unitOfMeasureCode'
            },
            {
                name: 'unitOfMeasureEad',
                label: 'energyProduct.unitOfMeasureEad',
                type: 'select',
                selectApi: 'masterdata/unit-of-measure',
                selectFormat: { label: 'code', value: 'code' },
                gridHide: true
            },
            {
                name: 'unitOfMeasureEad',
                label: 'energyProduct.unitOfMeasureEad',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/unit-of-measure',
                gridFilterSelectFormat: { label: 'code', value: 'code' },
                gridFilterName: 'unitOfMeasureCode'
            },
            {
                name: 'idInfoilRegistryFamilyProduct',
                label: 'infoilFamilyProduct.fk',
                type: 'select',
                selectApi: 'masterdata/infoil-family-product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'infoilRegistryFamilyProduct',
                label: 'infoilFamilyProduct.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/infoil-family-product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idInfoilRegistryFamilyProduct'
            },
            {
                name: 'packagingType',
                label: 'energyProduct.packagingType',
                type: 'string'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    EnergyProductDetail: {
        title: 'energyProductDetail.page_title',
        api: 'masterdata/energy-product-detail',
        enableUpdate: true,
        editButtonName: 'energyProductDetailEditButton',
        enableDelete: true,
        deleteButtonName: 'energyProductDetailDeleteButton',
        enableInsert: true,
        addButtonName: 'energyProductDetailAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryEnergyProduct',
                label: 'energyProduct.fk',
                type: 'select',
                selectApi: 'masterdata/energy-product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryEnergyProduct',
                label: 'energyProduct.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/energy-product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryEnergyProduct'
            },
            {
                name: 'cpa',
                label: 'energyProductDetail.cPA',
                type: 'string',
                validations: {
                    maxLength: 4
                }
            },
            {
                name: 'taric',
                label: 'energyProductDetail.tARIC',
                type: 'string',
                validations: {
                    maxLength: 2
                }
            },
            {
                name: 'cadd',
                label: 'energyProductDetail.cADD',
                type: 'string',
                validations: {
                    maxLength: 4
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 255,
                    required: true
                }
            },
            {
                name: 'note',
                label: 'note',
                type: 'string',
                validations: {
                    maxLength: 255
                }
            },
            {
                name: 'isTaxed',
                label: 'energyProductDetail.isTaxed',
                type: 'string',
                validations: {
                    maxLength: 2
                }
            },
            {
                name: 'fiscalSectionCode',
                label: 'energyProductDetail.fiscalSectionCode',
                type: 'string',
                validations: {
                    maxLength: 4
                }
            },
            {
                name: 'regionalAccountCode',
                label: 'energyProductDetail.regionalAccountCode',
                type: 'string',
                validations: {
                    maxLength: 5
                }
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date'
            },
            {
                name: 'endDate',
                label: 'endDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            // {
            //     name: 'isDeleted',
            //     label: 'isDeleted',
            //     type: 'boolean'
            // },
            {
                name: 'isEnabled',
                label: 'isEnabled',
                type: 'boolean',
                validations: {
                    required: true
                }
            }
        ]
    },
    LaytimeUoM: {
        title: 'laytimeUoM.page_title',
        api: 'masterdata/laytime-uom',
        enableUpdate: true,
        editButtonName: 'laytimeUoMEditButton',
        enableDelete: true,
        deleteButtonName: 'laytimeUoMDeleteButton',
        enableInsert: true,
        addButtonName: 'laytimeUoMAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isEnabled',
                label: 'isEnabled',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    PortAndLocation: {
        title: 'portAndLocation.page_title',
        api: 'masterdata/port-and-location',
        enableUpdate: true,
        editButtonName: 'portAndLocationEditButton',
        enableDelete: true,
        deleteButtonName: 'portAndLocationDeleteButton',
        enableInsert: true,
        addButtonName: 'portAndLocationAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'name',
                label: 'name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'countryAndTerritoryId',
                label: 'countryAndTerritory.countryAndTerritory',
                type: 'select',
                selectApi: 'masterdata/country-and-territory-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'countryAndTerritoryCode',
                label: 'countryAndTerritory.countryAndTerritory',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/country-and-territory-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'countryAndTerritoryId'
            },
            {
                name: 'transportDepartmentCode',
                label: 'transportDepartmentCode',
                type: 'string',
                validations: {
                    maxLength: 7
                }
            },
            {
                name: 'transportDepartmentDescription',
                label: 'transportDepartmentDescription',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    Product: {
        title: 'product.page_title',
        api: 'masterdata/product',
        enableUpdate: true,
        editButtonName: 'productEditButton',
        enableDelete: true,
        deleteButtonName: 'productDeleteButton',
        enableInsert: true,
        addButtonName: 'productAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 60,
                    required: true
                }
            },
            {
                name: 'englishDescription',
                label: 'englishDescription',
                type: 'string',
                validations: {
                    maxLength: 60,
                    required: true
                }
            },
            {
                name: 'isGas',
                label: 'isGas',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isEthylene',
                label: 'isEthylene',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isMethanol',
                label: 'isMethanol',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isBiofeed',
                label: 'isBiofeed',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
             {
                name: 'isBlackProduct',
                label: 'isBlackProduct',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'familyDescription',
                label: 'familyDescription',
                type: 'string',
                validations: {
                    maxLength: 30
                }
            },
            {
                name: 'transportDepartmentCode',
                label: 'transportDepartmentCode',
                type: 'string',
                validations: {
                    maxLength: 7
                }
            },
            {
                name: 'transportDepartmentDescription',
                label: 'transportDepartmentDescription',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },           
            {
                name: 'idOriginCountry',
                label: 'countryAndTerritory.countryAndTerritory',
                type: 'select',
                selectApi: 'masterdata/country-and-territory-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'originCountry',
                label: 'countryAndTerritory.countryAndTerritory',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/country-and-territory-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idOriginCountry'
            },
            {
                name: 'shortDescription',
                label: 'shortDescription',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'imoCode',
                label: 'product.imoCode',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'imoDescription',
                label: 'product.imoDescription',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'isEnabled',
                label: 'isEnabled',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                // formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'familyDescription',
                label: 'product.familyDescription',
                type: 'string',
                validations: {
                    maxLength: 30
                }
            },
            {
                name: 'dealNoteCode',
                label: 'product.dealNoteCode',
                type: 'string',
                validations: {
                    maxLength: 4
                }
            },
            {
                name: 'dealNoteSheetFamily',
                label: 'product.dealNoteSheetFamily',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            }
        ]
    },
    DealNoteColumnCombo: {
        title: 'dealNoteColumnCombo.page_title',
        api: 'masterdata/registry-deal-note-column',
        enableUpdate: true,
        editButtonName: 'dealNoteColumnComboEditButton',
        enableDelete: true,
        deleteButtonName: 'dealNoteColumnComboDeleteButton',
        enableInsert: true,
        addButtonName: 'dealNoteColumnComboAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'family',
                label: 'dealNoteColumnCombo.family',
                type: 'select',
                selectApi: 'daily-profiles/families-combo',
                selectFormat: { label: 'label', value: 'valueString' },
                gridHide: true
            },
            {
                name: 'family',
                label: 'dealNoteColumnCombo.family',
                type: 'string',
                readOnly: true             
            },
            {
                name: 'idColumnType',
                label: 'dealNoteColumnCombo.columnType',
                type: 'select',
                selectApi: 'masterdata/registry-deal-note-column-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'columnType',
                label: 'dealNoteColumnCombo.columnType',
                type: 'string',
                readOnly: true,
                formHide: true
            },
            {
                name: 'description',
                label: 'dealNoteColumnCombo.description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'referenceDate',
                label: 'dealNoteColumnCombo.referenceDate',
                type: 'date',
                validations: {  
                    required: true             
                }
            },
            {
                name: 'isDeleted',
                label: 'dealNoteColumnCombo.isDeleted',
                type: 'boolean',   
                readOnly: true,
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true      
            }
        ]
    },
    ProcessingProductionProduct: {
        title: 'processingProductionProduct.page_title',
        api: 'masterdata/registry-processing-production-product',
        enableUpdate: true,
        editButtonName: 'processingProductionProductEditButton',
        enableDelete: true,
        deleteButtonName: 'processingProductionProductDeleteButton',
        enableInsert: true,
        addButtonName: 'processingProductionProductAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 20,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 60,
                    required: false
                }
            },
            {
                name: 'idRegistryProduct',
                label: 'processingProductionProduct.smartsim_product',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'registryProduct',
                label: 'processingProductionProduct.productLabel',
                type: 'string',
                readOnly: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                validations: {
                    required: false
                }
            },
            {
                name: 'isExcludedFromImport',
                label: 'isExcludedFromImport',
                type: 'boolean',
                fieldset: 'general',
                validations: {
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                fieldset: 'general',
                validations: {
                    required: true
                }
            }
        ]
    },
    ProductCompanyOwner: {
        title: 'productCompanyOwner.page_title',
        api: 'masterdata/product-company-owner',
        enableUpdate: true,
        editButtonName: 'productCompanyOwnerEditButton',
        enableDelete: true,
        deleteButtonName: 'productCompanyOwnerDeleteButton',
        enableInsert: true,
        addButtonName: 'productCompanyOwnerAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'productPool',
                label: 'productCompanyOwner.productPool',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'productType',
                label: 'productCompanyOwner.productType',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'productNature',
                label: 'productCompanyOwner.productNature',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'idRegistryProduct',
                label: 'productCompanyOwner.smartsim_product',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'registryProduct',
                label: 'productCompanyOwner.smartsim_product',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'idRegistryCompanyOwner',
                label: 'company.fk',
                type: 'select',
                selectApi: 'masterdata/company-combo?getOnlyValid=true',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'registryCompanyOwner',
                label: 'company.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company-combo?getOnlyValid=true',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryCompanyOwner'
            },
            {
                name: 'validityStartDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'validityEndDate',
                label: 'endDate',
                type: 'date',
                validations: {
                    required: true
                }
            }
        ],
        toolbarActions: [
            {
                type:'button',
                visible: true,
                name: 'productCompanyOwnerImport',
                icon: 'pi pi-upload',
                title: 'productCompanyOwner.import',
                cssClass: 'p-button-success p-button-sm',
                action: function (data, caller) {
                    caller.$store.dispatch("generic/setLoading", true);             
                    genericService.post('masterdata/product-company-owner-import').then(response => {
                        caller.$store.dispatch("generic/setLoading", false); 

                        if(response.success){
                            caller.$toast.add({severity:'success', summary: 'Success Message', detail: caller.$t('messages.import_message_success'), life: 3000});
                        }else{
                            caller.$toast.add({ detail: response.message || caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
            }
        ]
    },
    TypeMovementInstructionLot: {
        title: 'typeMovementInstructionLot.page_title',
        api: 'masterdata/type-movement-instruction-lot',
        enableUpdate: true,
        editButtonName: 'typeMovementInstructionLotEditButton',
        enableDelete: true,
        deleteButtonName: 'typeMovementInstructionLotDeleteButton',
        enableInsert: true,
        addButtonName: 'typeMovementInstructionLotAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 20,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    CargoByLandEquipment: {
        title: 'cargoByLandEquipment.page_title',
        api: 'masterdata/equipment-cargo-by-land',
        enableUpdate: true,
        editButtonName: 'cargoByLandEquipmentEditButton',
        enableDelete: true,
        deleteButtonName: 'cargoByLandEquipmentDeleteButton',
        enableInsert: true,
        addButtonName: 'cargoByLandEquipmentAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryEquipment',
                label: 'cargoByLandEquipment.registryEquipment',
                type: 'select',
                selectApi: 'masterdata/equipment-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryEquipment',
                label: 'cargoByLandEquipment.registryEquipment',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryEquipment'
            },
            {
                name: 'disableSendToInfoil',
                label: 'cargoByLandEquipment.disableSendToInfoil',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isLPG',
                label: 'cargoByLandEquipment.isLPG',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'validityStartDate',
                label: 'cargoByLandEquipment.validityStartDate',
                type: 'date',
                format: "dd/mm/yy",
                validations: {
                    required: true
                }
            },
            {
                name: 'validityEndDate',
                label: 'cargoByLandEquipment.validityEndDate',
                type: 'date',
                format: "dd/mm/yy"
            }
            

        ]
    },
    TaxWarehouse: {
        title: 'taxWarehouse.page_title',
        api: 'masterdata/tax-warehouse',
        enableUpdate: true,
        editButtonName: 'taxWarehouseEditButton',
        enableDelete: true,
        deleteButtonName: 'taxWarehouseDeleteButton',
        enableInsert: true,
        addButtonName: 'taxWarehouseAddButton',
        idColumn: "id",
        fieldsets: {
            general: {
                label: 'general',
                columns: []
            },
            consignee: {
                label: 'taxWarehouse.consignee',
                columns: []
            },
            placeOfDelivery: {
                label: 'taxWarehouse.placeOfDelivery',
                columns: []
            }
        },
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                fieldset: 'general'
            },
            {
                name: 'idRegistryAuthorizationType',
                label: 'authorizationType.fk',
                type: 'select',
                selectApi: 'masterdata/authorization-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                fieldset: 'general',
                validations: {
                    required: true
                },
            },
            {
                name: 'registryAuthorizationType',
                label: 'authorizationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/authorization-type-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryAuthorizationType'
            },
            {
                name: 'consigneeExciseCode',
                label: 'taxWarehouse.consigneeExciseCode',
                type: 'string',
                validations: {
                    maxLength: 14
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeName',
                label: 'taxWarehouse.consigneeName',
                type: 'string',
                validations: {
                    maxLength: 255,
                    required: true
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeAddress',
                label: 'taxWarehouse.consigneeAddress',
                type: 'string',
                validations: {
                    maxLength: 255
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeStreetNumber',
                label: 'taxWarehouse.consigneeStreetNumber',
                type: 'string',
                validations: {
                    maxLength: 10
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeZipCode',
                label: 'taxWarehouse.consigneeZipCode',
                type: 'string',
                validations: {
                    maxLength: 10
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeCity',
                label: 'taxWarehouse.consigneeCity',
                type: 'string',
                validations: {
                    maxLength: 100,
                    required: true
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeLanguageCode',
                label: 'taxWarehouse.consigneeLanguageCode',
                type: 'select',
                selectApi: 'masterdata/language',
                selectFormat: { label: 'isoCode', value: 'isoCode' },
                gridHide: true,
                fieldset: 'consignee'
            },
            {
                name: 'consigneeLanguageCode',
                label: 'taxWarehouse.consigneeLanguageCode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/language',
                gridFilterSelectFormat: { label: 'isoCode', value: 'isoCode' },
                gridFilterName: 'consigneeLanguageCode',
                fieldset: 'consignee'
            },
            {
                name: 'consigneeEoriCode',
                label: 'taxWarehouse.consigneeEoriCode',
                type: 'string',
                validations: {
                    maxLength: 50
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeCountryTerritoryCode',
                label: 'taxWarehouse.consigneeCountryTerritoryCode',
                type: 'select',
                selectApi: 'masterdata/country-and-territory',
                selectFormat: { label: 'isoCode', value: 'isoCode' },
                gridHide: true,
                validations: {
                    required: true
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeCountryTerritoryCode',
                label: 'taxWarehouse.consigneeCountryTerritoryCode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/country-and-territory',
                gridFilterSelectFormat: { label: 'isoCode', value: 'isoCode' },
                gridFilterName: 'consigneeCountryTerritoryCode'
            },
            {
                name: 'consigneeVatNumber',
                label: 'taxWarehouse.consigneeVatNumber',
                type: 'string',
                validations: {
                    maxLength: 20
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeActivationDate',
                label: 'taxWarehouse.consigneeActivationDate',
                type: 'date',
                validations: {
                    required: true
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeStartDate',
                label: 'taxWarehouse.consigneeStartDate',
                type: 'date',
                validations: {
                    required: true
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeEndDate',
                label: 'taxWarehouse.consigneeEndDate',
                type: 'date',
                fieldset: 'consignee'
            },
            {
                name: 'competentAuthorityDispatchOfficeReferenceNumber',
                label: 'taxWarehouse.competentAuthorityDispatchOfficeReferenceNumber',
                type: 'string',
                validations: {
                    maxLength: 8
                },
                fieldset: 'general'
            },
            {
                name: 'placeOfDeliveryExciseCode',
                label: 'taxWarehouse.placeOfDeliveryExciseCode',
                type: 'string',
                validations: {
                    maxLength: 14
                },
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryName',
                label: 'taxWarehouse.placeOfDeliveryName',
                type: 'string',
                validations: {
                    maxLength: 100
                },
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryAddress',
                label: 'taxWarehouse.placeOfDeliveryAddress',
                type: 'string',
                validations: {
                    maxLength: 255
                },
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryStreetNumber',
                label: 'taxWarehouse.placeOfDeliveryStreetNumber',
                type: 'string',
                validations: {
                    maxLength: 10
                },
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryZipCode',
                label: 'taxWarehouse.placeOfDeliveryZipCode',
                type: 'string',
                validations: {
                    maxLength: 10
                },
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryCity',
                label: 'taxWarehouse.placeOfDeliveryCity',
                type: 'string',
                validations: {
                    maxLength: 255
                },
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryLanguageCode',
                label: 'taxWarehouse.placeOfDeliveryLanguageCode',
                type: 'select',
                selectApi: 'masterdata/language',
                selectFormat: { label: 'isoCode', value: 'isoCode' },
                gridHide: true,
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryLanguageCode',
                label: 'taxWarehouse.placeOfDeliveryLanguageCode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/language',
                gridFilterSelectFormat: { label: 'isoCode', value: 'isoCode' },
                gridFilterName: 'placeOfDeliveryLanguageCode',
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryActivationDate',
                label: 'taxWarehouse.placeOfDeliveryActivationDate',
                type: 'date',
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryStartDate',
                label: 'taxWarehouse.placeOfDeliveryStartDate',
                type: 'date',
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryEndDate',
                label: 'taxWarehouse.placeOfDeliveryEndDate',
                type: 'date',
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'isEnabled',
                label: 'isEnabled',
                type: 'boolean',
                fieldset: 'general',
                validations: {
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                fieldset: 'general',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },

    TaxWarehouseMovementInstruction: {
        title: 'taxWarehouse.page_title',
        api: 'masterdata/tax-warehouse',
        idColumn: "id",
        preventAutoLoad: true,
        hideRefresh: true,
        preventFilters: true,
        customActions: [
            {
                name: 'taxWarehouseMovementInstructionSelectButton',
                title: 'btn.select',
                visible: false,
                cssClass: 'p-button-rounded p-button-success mr-2',
                icon: 'pi pi-check',
                data: {},
                fields: [],
                action: function (data, caller) {
                    caller.$emit('onTaxWarehouseSelected', data);
                }
            }
        ],
        columns: [
            {
                name: 'consigneeExciseCode',
                label: 'taxWarehouse.consigneeExciseCode',
                type: 'string',
                validations: {
                    maxLength: 14
                },
                fieldset: 'consignee'
            },
            {
                name: 'consigneeName',
                label: 'taxWarehouse.consigneeName',
                type: 'string',
                validations: {
                    maxLength: 255,
                    required: true
                },
                fieldset: 'consignee'
            },
            {
                name: 'placeOfDeliveryExciseCode',
                label: 'taxWarehouse.placeOfDeliveryExciseCode',
                type: 'string',
                validations: {
                    maxLength: 14
                },
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryName',
                label: 'taxWarehouse.placeOfDeliveryName',
                type: 'string',
                validations: {
                    maxLength: 100
                },
                fieldset: 'placeOfDelivery'
            },
            {
                name: 'placeOfDeliveryCity',
                label: 'taxWarehouse.placeOfDeliveryCity',
                type: 'string',
                validations: {
                    maxLength: 255
                },
                fieldset: 'placeOfDelivery'
            },
        ]
    },
    TypeOperationEnergyProductDetailAndProductLink: {
        title: 'typeOperationEnergyProductDetailAndProductLink.page_title',
        api: 'masterdata/type-operation-energy-product-detail-and-product-link',
        enableUpdate: true,
        editButtonName: 'typeOperationEnergyProductDetailAndProductLinkEditButton',
        enableDelete: true,
        deleteButtonName: 'typeOperationEnergyProductDetailAndProductLinkDeleteButton',
        enableInsert: true,
        addButtonName: 'typeOperationEnergyProductDetailAndProductLinkAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'registryTransportTypeCode',
                label: 'transportType.fk',
                type: 'string',
                validations: {
                    maxLength: 13
                }
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'idRegistryEnergyProductDetail',
                label: 'energyProductDetail.fk',
                type: 'select',
                selectApi: 'masterdata/energy-product-detail-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'registryEnergyProductDetail',
                label: 'energyProductDetail.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/energy-product-detail-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryEnergyProductDetail'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    UnitOfMeasure: {
        title: 'unitOfMeasure.page_title',
        api: 'masterdata/unit-of-measure',
        enableUpdate: true,
        editButtonName: 'unitOfMeasureEditButton',
        enableDelete: true,
        deleteButtonName: 'unitOfMeasureDeleteButton',
        enableInsert: true,
        addButtonName: 'unitOfMeasureAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                disabledInEdit: true,
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'formatQuantity',
                label: 'formatQuantity',
                type: 'string',
            },
            {
                name: 'conversionTableCode',
                label: 'conversionTableCode',
                type: 'string',
            },
            {
                name: 'isMain',
                label: 'isMain',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    UnitOfMeasureUsageScopeConfig: {
        title: 'unitOfMeasureUsageScopeConfig.page_title',
        api: 'masterdata/unit-of-measure-usage-scope-config',
        enableUpdate: true,
        editButtonName: 'unitOfMeasureUsageScopeConfigEditButton',
        enableDelete: true,
        deleteButtonName: 'unitOfMeasureUsageScopeConfigDeleteButton',
        enableInsert: true,
        addButtonName: 'unitOfMeasureUsageScopeConfigAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryUnitOfMeasure',
                label: 'unitOfMeasure.fk',
                type: 'select',
                selectApi: 'masterdata/unit-of-measure',
                selectFormat: { label: 'code', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryUnitOfMeasure',
                label: 'unitOfMeasure.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/unit-of-measure',
                gridFilterSelectFormat: { label: 'code', value: 'id' },
                gridFilterName: 'idRegistryUnitOfMeasure'
            },
            {
                name: 'scope',
                label: 'scope',
                type: 'string',
                validations: {
                    maxLength: 200,
                    required: true
                }
            },
            {
                name: 'printOrder',
                label: 'unitOfMeasureUsageScopeConfig.printOrder',
                type: 'number',
                
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ]
    },
    CustomsSituation: {
        title: 'customsSituation.page_title',
        api: 'masterdata/custom-situation',
        enableUpdate: true,
        editButtonName: 'customsSituationEditButton',
        enableDelete: true,
        deleteButtonName: 'customsSituationDeleteButton',
        enableInsert: true,
        addButtonName: 'customsSituationAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'name',
                label: 'name',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                format: "dd/mm/yy",
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    OperationType: {
        title: 'operationType.page_title',
        api: 'masterdata/operation-type',
        enableUpdate: true,
        editButtonName: 'operationTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'operationTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'operationTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 2,
                    required: true
                },
                disabledInEdit: true
            },
            {
                name: 'shortDescription',
                label: 'shortDescription',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'idMacroMovementType',
                label: 'macroMovementType.fk',
                type: 'select',
                selectApi: 'masterdata/macro-movement-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'macroMovementType',
                label: 'macroMovementType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/macro-movement-type-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idMacroMovementType'
            },
            {
                name: 'idTransportType',
                label: 'transportType.fk',
                type: 'select',
                selectApi: 'masterdata/transport-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'transportType',
                label: 'transportType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/transport-type-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idTransportType'
            },
            {
                name: 'idCommercialOperationType',
                label: 'commercialOperationType.fk',
                type: 'select',
                selectApi: 'masterdata/commercial-operation-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'commercialOperationType',
                label: 'commercialOperationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/commercial-operation-type-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idCommercialOperationType'
            },
            {
                name: 'isNotifyToCustomAgency',
                label: 'operationType.isNotifyToCustomAgency',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'checkCargoByLandSendToInfoilIsEnabled',
                label: 'operationType.checkCargoByLandSendToInfoilIsEnabled',
                type: 'boolean',
            },
            {
                name: 'isFiscal',
                label: 'isFiscal',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'tankOperationFlag',
                label: 'operationType.tankOperationFlag',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },
            {
                name: 'isPunctual',
                label: 'operationType.punctualFlag',
                type: 'boolean'
            },
            {
                name: 'accountingOperationFlag',
                label: 'operationType.accountingOperationFlag',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },
            {
                name: 'isManual',
                label: 'isManual',
                type: 'boolean'
            },
            {
                name: 'chargeDischargeFlag',
                label: 'operationType.chargeDischargeFlag',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },
            {
                name: 'tankGridFlag',
                label: 'operationType.tankGridFlag',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },
            {
                name: 'movementDirectionFlag',
                label: 'operationType.movementDirectionFlag',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },
            {
                name: 'isVisible',
                label: 'isVisible',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'operationPlanFlag',
                label: 'operationType.operationPlanFlag',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },
            {
                name: 'isInsideOfRefineryWarehouse',
                label: 'operationType.isInsideOfRefineryWarehouse',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'idClearanceMovementType',
                label: 'clearanceMovementType.fk',
                type: 'select',
                selectApi: 'masterdata/clearance-movement-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'clearanceMovementType',
                label: 'clearanceMovementType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/clearance-movement-type-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idClearanceMovementType'
            },
            {
                name: 'denaturationProcessingFlag',
                label: 'operationType.denaturationProcessingFlag',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },
            {
                name: 'isNotIncludedIntoOperationPlan',
                label: 'operationType.isNotIncludedIntoOperationPlan',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'sourceOrRecipientFlag',
                label: 'operationType.sourceOrRecipientFlag',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },
            {
                name: 'isSetStorage',
                label: 'operationType.isSetStorage',
                type: 'boolean'
            },
            {
                name: 'isFip',
                label: 'isFip',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                }
            },
            {
                name: 'isOnlyOneLotPermitted',
                label: 'isOnlyOneLotPermitted',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isPartialEndNotificationWithOnlyInputParams',
                label: 'isPartialEndNotificationWithOnlyInputParams',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'measurementReportType',
                label: 'operationType.measurementReportType',
                type: 'string',
                validations: {
                    maxLength: 1
                }
            },


            {
                name: 'infoilCode',
                label: 'operationType.infoilCode',
                type: 'string',
                validations: {
                    maxLength: 2
                }
            },
            {
                name: 'infoilDescription',
                label: 'operationType.infoilDescription',
                type: 'string',
                validations: {
                    maxLength: 20
                }
            },
            
            {
                name: 'isDischargeTrackingEnabled',
                label: 'isDischargeTrackingEnabled',
                type: 'boolean'
            },
            {
                name: 'isEquipmentTrackingEnabled',
                label: 'isEquipmentTrackingEnabled',
                type: 'boolean'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                format: "dd/mm/yy",
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            },

            {
                name: 'idMovementInstructionCounterType',
                label: 'counterFieldType.fk',
                type: 'select',
                selectApi: 'masterdata/counter-field-type',
                selectFormat: { label: 'counterTypeName', value: 'id' },
                gridHide: true
            },
            {
                name: 'movementInstructionCounterType',
                label: 'counterFieldType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/counter-field-type',
                gridFilterSelectFormat: { label: 'counterTypeName', value: 'id' },
                gridFilterName: 'idMovementInstructionCounterType'
            }
        ]
    },

    MovementInstruction: {
        title: 'movementInstructions.page_title',
        api: 'movement-instruction/header',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        selection: false,
        sidebarFilters: true,
        children: {
            name: 'movementInstructionLots',
            colums: [
                {
                    name: 'lotCode',
                    label: 'lotCode',
                    type: 'string',

                },
                {
                    name: 'product',
                    label: 'productCode',
                    type: 'string',

                },
                {
                    name: 'isOutCDep',
                    label: 'isOutCDep',
                    type: 'boolean',

                },
                {
                    name: 'quantity',
                    label: 'quantity',
                    type: 'string',

                },
                {
                    name: 'quantityUoMCode',
                    label: 'quantityUoMCode',
                    type: 'string',

                },
                {
                    name: 'typeMovementInstructionLotCode',
                    label: 'typeMovementInstructionLotCode',
                    type: 'string',

                },
                {
                    name: 'billofLadingHolder',
                    label: 'billofLadingHolder',
                    type: 'string',

                },
                {
                    name: 'note',
                    label: 'note',
                    type: 'string',

                },
                {
                    name: 'documentLanguageCode',
                    label: 'documentLanguageCode',
                    type: 'string',

                },
                {
                    name: 'analysisDocumentLanguageCode',
                    label: 'analysisDocumentLanguageCode',
                    type: 'string',

                },
            ],
            customActions: [
                {
                    title: 'movementInstructions.btn.vesselProtocol',
                    cssClass: 'p-button-rounded p-button-success mr-2',
                    name: 'movementInstructionVesselProtocolFromLotButton',
                    icon: 'pi pi-directions',
                    action: function (data, caller) {
                        let uri = `/vessel-protocol?id=${data.id}`;
                        caller.$router.push(uri);
                    }
                },
                {
                    title: 'btn.attachments',
                    visible: false,
                    name: 'OpenSchedulingAttachments',
                    icon: 'pi pi-paperclip',
                    data: {},
                    fields: [],
                    action: function (record, caller) {
                        caller.$emit('showSchedulingAttachmentsDialog', record);
                    }
                }
            ]
        },
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
            },
            {
                name: 'version',
                label: 'version',
                type: 'number',
            },
            {
                name: 'voyageNumber',
                label: 'movementInstructions.voyageNumber',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 20
                }
            },
            {
                name: 'operationType',
                label: 'operationType.fk',
                type: 'string',
                formHide: true,
                selectName: 'operation-types',
                gridFilterType: 'master-data-select',
                gridFilterSelectApi: 'masterdata/operation-type-combo',
                gridFilterSelectFormat: {
                    label: function (el) {
                        return el.code + '-' + el.shortDescription
                    },
                    value: 'id'
                },
                gridFilterName: 'idOperationType'
            },
            {
                name: 'idOperationType',
                label: 'operationType.fk',
                type: 'master-data-select',
                selectName: 'operation-types',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: {
                    label: function (el) {
                        return el.code + '-' + el.shortDescription
                    },
                    value: 'id'
                },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'plant',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true,
                selectName: 'registry-plant',
                gridFilterType: 'master-data-select',
                gridFilterSelectApi: 'masterdata/registry-plant-combo',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'idPlant'
            },
            {
                name: 'idPlant',
                label: 'registryPlant.fk',
                type: 'master-data-select',
                selectName: 'registry-plant',
                selectApi: 'masterdata/registry-plant-combo',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            // {
            //     name: 'vessel',
            //     label: 'vessel.fk',
            //     type: 'string'
            // },

            {
                name:'idRegistryVessel',
                label: 'vessel.fk',
                type: 'select',
                selectApi: 'masterdata/vessel-combo?getOnlyValid=true',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'vessel',
                label: 'vessel.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/vessel-combo?getOnlyValid=true',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryVessel'
            },

            {
                name: 'laycanStartDate',
                label: 'movementInstructions.laycanFrom',
                type: 'date',
                validations: {
                    required: true
                },
                defaultFilter: true,
                defaultFilterValue: Utils.formatDatePlusDays(new Date(), -15),
                defaultFilterType: FilterMatchMode.DATE_AFTER
            },
            {
                name: 'laycanEndDate',
                label: 'movementInstructions.laycanTo',
                type: 'date',
                validations: {
                    required: true
                },
                defaultFilter: true,
                defaultFilterValue: Utils.formatDatePlusDays(new Date(), 15),
                defaultFilterType: FilterMatchMode.DATE_BEFORE
            },
            {
                name: 'movementInstructionState',
                label: 'status',
                type: 'string',
                defaultFilter: true,
                localized: true,
                defaultFilterValue: ['CREATED', 'ONGOING'],
                gridFilterDisableStore: true,
                gridFilterType: 'master-data-multi-select',
                gridFilterArray: [
                    { label: "CREATED", value: "CREATED" },
                    { label: "ONGOING", value: "ONGOING" },
                    { label: "CANCELED", value: "CANCELED" },
                    { label: "CLOSED", value: "CLOSED" }
                ],
                gridFilterName: 'movementInstructionState'
            },
            {
                name: 'versionNote',
                label: 'movementInstructions.versionNote',
                type: 'string'
            },
        ],
        customActions: [
            {
                title: 'btn.viewDetails',
                visible: false,
                cssClass: 'p-button-rounded p-button-success mr-2',
                name: 'movementInstructionViewDetailsButton',
                icon: 'pi pi-pencil',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/movement-instruction/${data.id}`;
                    caller.$router.push(uri);
                }
            },
            {
                title: 'btn.export',
                name: 'export_movement_instruction',
                cssClass: 'p-button-rounded p-button-danger mr-2',
                icon: 'pi pi-file-pdf',
                action: function (data, caller) {
                    let params = {
                        movementInstructionCode: data.code
                    };
                    caller.$store.dispatch("generic/setLoading", true);
                    MovementInstructionService.printMovementInstruction(params, response => {

                        caller.$store.dispatch("generic/setLoading", false);
                        if (response.success) {
                            var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            var fileLink = document.createElement('a');

                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', data.voyageNumber + '-' + data.code + '.pdf');
                            document.body.appendChild(fileLink);

                            fileLink.click();
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
            },
            // {
            //     title: 'btn.clone',
            //     name: 'clone_movement_instruction_old',
            //     icon: 'pi pi-copy',
            //     action: function (data, caller) {
                    

            //         caller.$confirm.require({
            //             message: caller.$t('movementInstructions.cloneMessageBtn'),
            //             icon: 'pi pi-exclamation-triangle',
            //             acceptLabel: caller.$t('yes'),
            //             rejectLabel: caller.$t('no'),
            //             accept: () => {
            //                 let params = {
            //                     headerId: data.id
            //                 };
            //                 caller.$store.dispatch("generic/setLoading", true);
            //                 MovementInstructionService.cloneMovmentIstruction(params, response => {

            //                     caller.$store.dispatch("generic/setLoading", false);
            //                     if (response.success) {
            //                         caller.$router.push(
            //                             {
            //                                 name: 'movement-instruction-details',
            //                                 params: {
            //                                     id: response.data,
            //                                     startInEdit: true
            //                                 }
            //                             }
            //                         );
            //                     } else {
            //                         caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
            //                     }
            //                 })
            //             },
            //             reject: () => {
            //                 //callback to execute when user rejects the action
            //             }
            //         });
            //     }
            // },
            {
                visible: false,
                type: 'window',
                title: 'btn.clone',
                name: 'clone_movement_instruction',
                icon: 'pi pi-copy',
                message: 'movementInstructions.cloneMessageBtn',
                confirmBtnText: 'btn.clone',
                data: {},
                fields: [
                    {
                        name: 'idRegistryPlant',
                        label: 'registryPlant.fk',
                        type: 'select',
                        selectApi: 'masterdata/registry-plant-combo', 
                        selectFormat: {
                            label: 'label',
                            value: 'value'
                        }
                    },
                    {
                        name: 'laycanStartDate',
                        label: 'laycanStartDate',
                        type: 'datetime'//,
                        // format: 'dd/mm/yy hh:ii'
                    },
                    {
                        name: 'laycanEndDate',
                        label: 'laycanEndDate',
                        type: 'datetime'
                        // format: 'dd/mm/yy hh:ii'
                    },
                    // {
                    //     name: 'isShipToShip',
                    //     label: 'movementInstructions.shipToShip',
                    //     type: 'boolean'
                    
                    // },
                    {
                        name: 'idRegistryInspector',
                        label: 'inspector.fk',
                        type: 'select',
                        selectApi: 'masterdata/inspector-combo', 
                        selectFormat: {
                            label: 'label',
                            value: 'value'
                        }
                    },
                ],
                action: function (data, model, caller) {
                    let params = {
                        headerId: data.id,
                        laycanStartDate: model.laycanStartDate,
                        laycanEndDate: model.laycanEndDate,
                        idRegistryPlant: model.idRegistryPlant,
                        isShipToShip: model.isShipToShip,
                        idRegistryInspector: model.idRegistryInspector
                    };

                    caller.$store.dispatch("generic/setLoading", true);
                    MovementInstructionService.cloneMovmentIstruction(params, response => {

                        caller.$store.dispatch("generic/setLoading", false);
                        if (response.success) {
                            caller.$router.push(
                                {
                                    name: 'movement-instruction-details',
                                    params: {
                                        id: response.data,
                                        startInEdit: true
                                    }
                                }
                            );
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
            },
            {
                title: 'movementInstructions.eta',
                name: 'movement_instruction_eta',
                icon: 'pi pi-calendar',
                disable: (data) => data.transportType !== 'N' && data.transportType !== 'B',
                action: function (data, caller) {
                    caller.$router.push(
                        {
                            name: 'jetty-eta',
                            query: {
                                id: data.id
                            }
                        }
                    );
                }
            },
            {
                title: 'btn.timesheet',
                visible: false,
                name: 'open_timesheet',
                icon: 'pi pi-clock',
                disable: (data) => data.transportType !== 'N' && data.transportType !== 'B',
                data: {},
                fields: [],
                action: function (data, caller) {

                    caller.$router.push(
                        {
                            name: 'timesheet',
                            query: {
                                defaultVoyageNumber: data.voyageNumber
                            }
                        }
                    );
                }
            },
            {
                title: 'VesselNomination.page_title',
                visible: false,
                name: 'open_mails',
                icon: 'pi pi-envelope',
                disable: (data) => data.transportType !== 'N' && data.transportType !== 'B',
                data: {},
                fields: [],
                action: function (data, caller) {

                    caller.$router.push(
                        {
                            name: 'vessel-nomination',
                            query: {
                                defaultVoyageNumber: data.voyageNumber
                            }
                        }
                    );
                }
            },
        ],
        toolbarActions: [
            {
                type:'button',
                visible: true,
                name: 'theoreticalLotsAddMovementInstructionsButton',
                icon: 'pi pi-plus',
                title: 'btn.theoreticalLots',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    // let body = {
                    //     onlyOpened: true,
                    //     onlyConfirmed: true
                    // }
                    // PlanningService.getTheoreticalLots(body, response => {
                    //     console.log(response)
                    // })
                    // caller.showTheoreticalLotsDialog();
                    caller.$emit('onShowTheoreticalLotsDialog');
                }
            },
            {
                type: 'button',
                visible: true,
                name: 'movementInstructionAddButton',
                icon: 'pi pi-plus',
                title: 'btn.add',
                message: 'actionMessage',
                cssClass: 'p-button-success',
                confirmBtnText: 'btn.text',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/movement-instruction/0`;
                    caller.$router.push(uri);
                }
            }
            // {
            //     type: 'button',
            //     visible: true,
            //     name: 'cloneItem',
            //     icon: 'pi pi-clone',
            //     title: 'btn.clone',
            //     message: 'actionMessage',
            //     confirmBtnText: 'btn.text',
            //     data: {},
            //     fields: [],
            //     action: function(data, caller){
            //         console.log(data)
            //     }
            // }
        ]
    },
    ProductSpecificationHeader: {
        title: 'productSpecificationHeader.page_title',
        api: 'masterdata/product-specification-header',
        enableUpdate: true,
        editButtonName: 'productSpecificationEditButton',
        enableDelete: true,
        deleteButtonName: 'productSpecificationDeleteButton',
        enableInsert: true,
        addButtonName: 'productSpecificationAddButton',
        idColumn: "id",
        dataTables: [
            {
                label: 'productSpecificationHeader.page_title',
                dataTableUnderForm: 'ProductSpecificationDetail',
                dataTableDefaultKey: 'idProductSpecificationHeader',
            },
            {
                label: 'productSpecificationNote.page_title',
                dataTableUnderForm: 'ProductSpecificationNote',
                dataTableDefaultKey: 'idProductSpecificationHeader',
            }
        ],
        customActions: [
            {
                visible: false,
                type: 'window',
                name: 'cloneProductSpecificationHeaderButton',
                icon: 'pi pi-clone',
                title: 'cloneProductSpecification',
                message: 'cloneProductSpecificationMessage',
                confirmBtnText: 'btn.save',
                data: {},
                fields: [
                    {
                        name: 'newValidityStartDate',
                        label: 'newValidityStartDate',
                        type: 'date'
                    }
                ],
                action: function (data, model, caller) {
                    let body = {
                        referenceProductSpecificationHeaderId: data.id,
                        newValidityStartDate: model.newValidityStartDate
                    };

                    CommonService.createItem('masterdata/product-specification-clone', body, resp => {
                        if (resp.success) {
                            caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                            caller.loadData();
                        } else {
                            caller.$toast.add({ detail: resp.message, life: 3000, severity: 'error' });
                        }
                    })

                    // SecurityService.passwordReset(body, resp => {
                    //     if (resp.success) {
                    //         this.visible = false;
                    //         model.newPassword = null;
                    //         caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                    //         caller.loadData();
                    //     } else {
                    //         caller.$toast.add({ detail: resp.message, life: 3000, severity: 'error' });
                    //     }
                    // });

                }
            },
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },

            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryProductCode',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryProduct',
                gridFilterSortField: 'Code'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'validFrom',
                label: 'validFrom',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'validTo',
                label: 'validTo',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'revision',
                label: 'revision',
                type: 'string',
            },
            {
                name: 'group',
                label: 'group',
                type: 'string',
            },
            {
                name: 'name',
                label: 'name',
                type: 'string',
                validations: {
                    required: true
                }
            },

        ],
        formCustomActions: [
            // {
            //     visible: false,
            //     name: 'view_product_specification_detail',
            //     title: 'btn.viewDetails',
            //     icon: 'pi pi-list',
            //     data: {},
            //     fields: [],
            //     onlyUpdate: true,
            //     action: function(data, caller){
            //         let uri = `/masterData/ProductSpecificationDetail?idProductSpecificationHeader=${data.id}`;
            //         caller.$router.push(uri);
            //     }
            // },
            {
                visible: false,
                name: 'print_product_specification',
                title: 'btn.print',
                icon: 'pi pi-file-pdf',
                data: {},
                fields: [],
                onlyUpdate: true,
                action: function (data, caller) {
                    let params = {
                        idProductSpecificationHeader: data.id
                    };
                    caller.$store.dispatch("generic/setLoading", true);
                    MovementInstructionService.printProductSpecHeader(params, response => {

                        caller.$store.dispatch("generic/setLoading", false);
                        if (response.success) {
                            var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            var fileLink = document.createElement('a');
                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', data.registryProductCode + '.pdf');
                            document.body.appendChild(fileLink);

                            fileLink.click();
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
            },
        ],
    },
    MovementInstructionSavedAsFavourite: {
        title: 'movementInstructionSavedAsFavourite.page_title',
        api: 'movement-instruction-save-as-favorite',
        idColumn: "id",
        selection: true,
        selectionMode: 'multiple',
        preventFilters: true,
        enableDelete: true,
        onCellEditComplete: (event, caller) => {
            const originalData = event.data[event.field];
            const newData = event.newData[event.field];

            if (originalData !== newData) {
                caller.data[event.index][event.field] = newData;

                if (event.field === 'departmentNote') {
                    MovementInstructionService.updateFavourite((
                        {
                            id: event.data.id,
                            departmentNote: newData
                        }
                    ), (response) => {
                        if (response.success) {
                            caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
            }
        },
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true
            },
            {
                name: 'departmentNote',
                label: 'departmentNote',
                type: 'string',
                inLineEdit: true,
            },
            {
                name: 'company',
                label: 'companyDescription',
                type: 'string',
            },
            {
                name: 'counterParty',
                label: 'counterparty',
                type: 'string',
            },
            {
                name: 'operationTypeDescription',
                label: 'operationTypeDescription',
                type: 'string',
            },
            {
                name: 'plant',
                label: 'plant',
                type: 'string',
            },
            {
                name: 'product',
                label: 'productDescription',
                type: 'string',
            },
            {
                name: 'lastInsertDate',
                label: 'lastInsertDate',
                type: 'date',
            },
            {
                name: 'quantity',
                label: 'quantity',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'minimumQuantity',
                label: 'minimumQuantity',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'maximumQuantity',
                label: 'maximumQuantity',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'laycanStartDate',
                label: 'laycanStartDate',
                type: 'date',
                inLineEdit: true,
            },
            {
                name: 'laycanEndDate',
                label: 'laycanEndDate',
                type: 'date',
                inLineEdit: true,
            },

        ],
        toolbarActions: [
            {
                type: 'button',
                visible: true,
                name: 'movementInstructionCloneSavedAsFavourite',
                icon: 'pi pi-plus',
                title: 'btn.create',
                disabled: function (data) {
                    if (data && data.length > 0) {
                        const dto = ["idMovementInstructionHeader", "quantity", "minimumQuantity", "maximumQuantity", "laycanStartDate", "laycanEndDate"]
                        return !data.every((item) => dto.every((key) => Boolean(item?.[key])))
                    } else {
                        return true
                    }
                },
                action: function (data, caller) {
                    caller.$store.dispatch("generic/setLoading", true);
                    const dto = ["idMovementInstructionHeader", "quantity", "minimumQuantity", "maximumQuantity", "laycanStartDate", "laycanEndDate"]
                    let body = data.map((item) => {
                        let obj = {};
                        dto.forEach((key) => obj[key] = item[key])
                        return obj
                    })
                    MovementInstructionService.saveAsFavouriteClone(body, response => {
                        caller.$store.dispatch("generic/setLoading", false);

                        if (response.success) {
                            caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
            }
        ]
    },
    ProductAnalysis: {
        title: 'productAnalysis.page_title',
        api: 'masterdata/product-analysis',
        enableUpdate: true,
        editButtonName: 'productAnalysisEditButton',
        enableDelete: true,
        deleteButtonName: 'productAnalysisDeleteButton',
        enableInsert: true,
        addButtonName: 'productAnalysisAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }

        ]
    },
    ProductAnalysisTestMethod: {
        title: 'productAnalysisTestMethod.page_title',
        api: 'masterdata/test-methods',
        enableUpdate: true,
        editButtonName: 'productAnalysisTestMethodEditButton',
        enableDelete: true,
        deleteButtonName: 'productAnalysisTestMethodDeleteButton',
        enableInsert: true,
        addButtonName: 'productAnalysisTestMethodAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 100
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }

        ]
    },

    ProductSpecificationNote: {
        title: 'productSpecificationNote.page_title',
        api: 'masterdata/product-specification-note',
        enableUpdate: false,
        enableDelete: true,
        deleteButtonName: 'productSpecificationNoteDeleteButton',
        enableInsert: false,
        disableBack: true,
        disableSave: true,
        skipCache: true,
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idProductSpecificationHeader',
                label: 'idProductSpecificationHeader',
                type: 'number',
                readOnly: true,
                gridHide: true,
                formHide: true
            },

            {
                name: 'code',
                label: 'code',
                type: 'string'
            },
            {
                name: 'description',
                label: 'description',
                type: 'editor'
            },
        ],
        customActions: [
            {
                title: 'btn.edit',
                visible: false,
                name: 'productSpecificationNoteEditButton',
                icon: 'pi pi-pencil',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/masterData/ProductSpecificationNote/${data.id}?idProductSpecificationHeader=${data.idProductSpecificationHeader}`;
                    caller.$router.push(uri);
                }
            },
        ],
        formCustomActions: [
            {
                name: 'btn_back',
                title: 'btn.back',
                icon: 'pi pi-arrow-left',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/masterData/ProductSpecificationHeader/${data.idProductSpecificationHeader}`;
                    caller.$router.push(uri);
                }
            },
            {
                name: 'btn_save',
                title: 'btn.save',
                icon: 'pi pi-save',
                data: {},
                fields: [],
                action: function (data, caller) {

                    if(data.id){
                        // UPDATE
                        CommonService.updateItem('masterdata/product-specification-note', data, response => {
                            caller.disabled = false;
                            caller.$store.dispatch("generic/setLoading", false);
                            if(response.success){
                                caller.$toast.add({severity:'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000});
                            }else{
                                caller.$toast.add({detail: response.message && response.message != '' ? response.message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        }, 'id')
                    }else{
                        // SAVE
                        CommonService.createItem('masterdata/product-specification-note', caller.data, response => {
                            caller.disabled = false;
                            caller.$store.dispatch("generic/setLoading", false);
                            if(response.success){
                                let uri = `/masterData/ProductSpecificationHeader/${data.idProductSpecificationHeader}`;
                                caller.$router.push(uri);
                            }else{
                                caller.$toast.add({detail: response.message && response.message != '' ? response.message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        })
                    }

                    
                }
            }
        ],
        toolbarActions: [
            {
                type: 'button',
                visible: true,
                name: 'productSpecificationNoteAddButton',
                icon: 'pi pi-plus',
                title: 'btn.add',
                action: function (data, caller) {
                    let uri = `/masterData/ProductSpecificationNote/0?idProductSpecificationHeader=${caller.defaultId}`;
                    caller.$router.push(uri);
                }
            }
        ]
    },

    ProductSpecificationDetail: {
        title: 'productSpecificationDetail.page_title',
        api: 'masterdata/product-specification-detail',
        enableUpdate: false,
        enableDelete: true,
        deleteButtonName: 'productSpecificationDetailDeleteButton',
        enableInsert: false,
        disableBack: true,
        disableSave: true,
        // preventAutoLoad: true,
        skipCache: true,
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idProductSpecificationHeader',
                label: 'idProductSpecificationHeader',
                type: 'number',
                readOnly: true,
                gridHide: true,
                formHide: true
            },
            {
                name: 'description',
                label: 'description',
                type: 'select',
                selectApi: 'masterdata/product-analysis-combo',
                selectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridHide: true,
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-analysis-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridFilterName: 'description'
            },
            {
                name: 'testMethods',
                label: 'testMethods',
                type: 'select',
                selectApi: 'masterdata/test-methods-combo',
                selectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridHide: true,
            },
            {
                name: 'testMethods',
                label: 'testMethods',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/test-methods-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridFilterName: 'testMethods'
            },
            {
                name: 'value',
                label: 'value',
                type: 'string'
            },
            {
                name: 'minValue',
                label: 'minValue',
                type: 'string'
            },
            {
                name: 'maxValue',
                label: 'maxValue',
                type: 'string'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                formHide: true,
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                }
            },
            {
                name: 'uoMCode',
                label: 'uoMCode',
                type: 'select',
                selectApi: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=ProductAnalysis',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true,
            },
            {
                name: 'uoMCode',
                label: 'uoMCode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=ProductAnalysis',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'uoMCode'
            },
            {
                name: 'propertyCode',
                label: 'propertyCode',
                type: 'string'
            },
            {
                name: 'notes',
                label: 'notes',
                type: 'multi-select-grid',
                columns: [
                    {
                        name: 'label',
                        label: 'description',
                        type: 'html',
                    },
                ],
                selectApi: function (data) {
                    return `masterdata/product-specification-note-combo/${data.idProductSpecificationHeader}`;
                },
                gridHide: true,
            },
        ],
        customActions: [
            {
                title: 'btn.edit',
                visible: false,
                name: 'productSpecificationDetailEditButton',
                icon: 'pi pi-pencil',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/masterData/ProductSpecificationDetail/${data.id}?idProductSpecificationHeader=${data.idProductSpecificationHeader}`;
                    caller.$router.push(uri);
                }
            },
        ],
        formCustomActions: [
            {
                name: 'btn_back',
                title: 'btn.back',
                icon: 'pi pi-arrow-left',
                data: {},
                fields: [],
                action: function (data, caller) {
                    // let uri = `/masterData/ProductSpecificationDetail?IdProductSpecificationHeader=${data.idProductSpecificationHeader}`;
                    let uri = `/masterData/ProductSpecificationHeader/${data.idProductSpecificationHeader}`;
                    caller.$router.push(uri);
                }
            },
            {
                name: 'btn_save',
                title: 'btn.save',
                icon: 'pi pi-save',
                data: {},
                fields: [],
                action: function (data, caller) {

                    if(data.id){
                        // UPDATE
                        CommonService.updateItem('masterdata/product-specification-detail', data, response => {
                            caller.disabled = false;
                            caller.$store.dispatch("generic/setLoading", false);
                            if(response.success){
                                caller.$toast.add({severity:'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000});
                            }else{
                                caller.$toast.add({detail: response.message && response.message != '' ? response.message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        }, 'id')
                    }else{
                        // SAVE
                        CommonService.createItem('masterdata/product-specification-detail', caller.data, response => {
                            caller.disabled = false;
                            caller.$store.dispatch("generic/setLoading", false);
                            if(response.success){
                                let uri = `/masterData/ProductSpecificationHeader/${data.idProductSpecificationHeader}`;
                                caller.$router.push(uri);
                            }else{
                                caller.$toast.add({detail: response.message && response.message != '' ? response.message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        })
                    }

                    
                }
            }
        ],
        toolbarActions: [
            {
                type: 'button',
                visible: true,
                name: 'productSpecificationDetailAddButton',
                icon: 'pi pi-plus',
                title: 'btn.add',
                action: function (data, caller) {
                    let uri = `/masterData/ProductSpecificationDetail/0?idProductSpecificationHeader=${caller.defaultId}`;
                    caller.$router.push(uri);
                }
            }
        ]
    },

    Logs: {
        title: 'logs.page_title',
        api: 'logs',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        preventAutoLoad: true,
        idColumn: "id",
        toolbarActions: [
            {
                name: 'logsCollectionButton',
                type: 'dropdown',
                options: ['MasterData', 'MovementInstruction', 'Security', 'OperativePlan', 'EquipmentOperation', 'FiscalNotification', 'Jetty', 'ClearanceDocuments', 'MaterialBalance'],
                visible: true,
                label: 'logs.collection',
                data: {},
                fields: [],
                action: function (data, caller) {
                    caller.params.collection = data;
                    caller.loadData();
                }
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'level',
                label: 'logs.level',
                type: 'string'
            },
            {
                name: 'utcTimeStamp',
                label: 'Timestamp',
                type: 'date',
                datetime: true
            },
            {
                name: 'messageTemplate',
                label: 'logs.messageTemplate',
                type: 'json',
                propName: 'text'
            },
            {
                name: 'renderedMessage',
                label: 'logs.renderedMessage',
                type: 'string'
            },
            {
                name: 'properties',
                label: 'logs.properties',
                type: 'json',
                propName: 'actionName'
            },
            {
                name: 'exception',
                label: 'logs.exception',
                type: 'json'
            },
        ]
    },

    MovementInstructionCompaniesByOperation: {
        title: 'movementInstructionCompaniesByOperation.page_title',
        api: 'movement-instruction/companies-by-operation',
        enableUpdate: true,
        editButtonName: 'movementInstructionCompaniesByOperationEditButton',
        enableDelete: true,
        deleteButtonName: 'movementInstructionCompaniesByOperationDeleteButton',
        enableInsert: true,
        addButtonName: 'movementInstructionCompaniesByOperationAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryOperationType',
                label: 'operationType.fk',
                type: 'select',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                sortField: 'Code',
                gridHide: true
            },
            {
                name: 'operationType',
                label: 'operationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/operation-type-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryOperationType'
            },

            {
                name: 'idRegistryCompany',
                label: 'company.fk',
                type: 'select',
                selectApi: 'masterdata/company',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                sortField: 'Description',
                gridHide: true
            },
            {
                name: 'company',
                label: 'company.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'idRegistryCompany'
            },
            {
                name: 'isCounterparty',
                label: 'counterparty',
                type: 'boolean'
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    MovementInstructionOutcDepRules: {
        title: 'movementInstructionOutcDepRules.page_title',
        api: 'movement-instruction-outcdep-configuration',
        enableUpdate: true,
        editButtonName: 'movementInstructionOutcDepRulesEditButton',
        enableDelete: true,
        deleteButtonName: 'movementInstructionOutcDepRulesDeleteButton',
        enableInsert: true,
        addButtonName: 'movementInstructionOutcDepRulesAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryOperationType',
                label: 'operationType.fk',
                type: 'select',
                selectApi: 'masterdata/operation-type',
                selectFormat: {
                    label: function (el) {
                        return el.code + ' - ' + el.description
                    },
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true
            },
            {
                name: 'registryOperationTypeDescription',
                label: 'operationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/operation-type',
                gridFilterSelectFormat: {
                    label: function (el) {
                        return el.code + ' - ' + el.description
                    },
                    value: 'id'
                },
                gridFilterName: 'idRegistryOperationType'
            },

            {
                name: 'idRegistryCompany',
                label: 'company.fk',
                type: 'select',
                selectApi: 'masterdata/company',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                sortField: 'Description',
                gridHide: true
            },
            {
                name: 'registryCompanyDescription',
                label: 'company.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'idRegistryCompany'
            },
            {
                name: 'idRegistryCounterparty',
                label: 'counterparty',
                type: 'select',
                selectApi: 'masterdata/company',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                sortField: 'Description',
                gridHide: true
            },
            {
                name: 'registryCounterpartyDescription',
                label: 'counterparty',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'idRegistryCounterparty'
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'expireDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    TankManagementType: {
        title: 'tankManagementTypes.page_title',
        api: 'tank-management/tank-type',
        enableUpdate: true,
        editButtonName: 'tankManagementTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'tankManagementTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'tankManagementTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 2,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 200,
                    required: true
                }
            },
            {
                name: 'volumeCalculationRoutine',
                label: 'TmTankType.volumeCalculationRoutine',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            }
        ]
    },

    TmTankConfiguration: {
        title: 'tankConfiguration.page_title',
        api: 'tank-management/tank-configuration',
        enableUpdate: true,
        editButtonName: 'tmTankConfigurationEditButton',
        enableDelete: true,
        deleteButtonName: 'tmTankConfigurationDeleteButton',
        enableInsert: true,
        addButtonName: 'tmTankConfigurationAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'tankCode',
                label: 'tankConfiguration.tankCode',
                type: 'string',
                validations: {
                    maxLength: 20,
                    required: true
                }
            },
            {
                name: 'isCalculationEnabled',
                label: 'tankConfiguration.isCalculationEnabled',
                type: 'boolean'
            },
            {
                name: 'densityCorrectionFactor',
                label: 'tankConfiguration.densityCorrectionFactor',
                type: 'decimal',
                digits: 4,
                max: Constants.MAX_6_4
            },
            {
                name: 'isThermalExpansionEnabled',
                label: 'tankConfiguration.isThermalExpansionEnabled',
                type: 'boolean'
            },
            {
                name: 'isPromiscousCalculationEnabled',
                label: 'tankConfiguration.isPromiscousCalculationEnabled',
                type: 'boolean'
            }
        ]
    },
    TmProductConfiguration: {
        title: 'tmProductConfiguration.page_title',
        api: 'tank-management/product-configuration',
        enableUpdate: true,
        editButtonName: 'tmProductConfigurationEditButton',
        enableDelete: true,
        deleteButtonName: 'tmProductConfigurationDeleteButton',
        enableInsert: true,
        addButtonName: 'tmProductConfigurationAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },

            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product',
                selectFormat: { label: 'code', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                selectApi: 'masterdata/product-combo', 
                updateField: 'idRegistryProduct', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/product-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryProduct', 
            },

            {
                name: 'f54Routine',
                label: 'tmProductConfiguration.f54Routine',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'enabledTankTypes',
                label: 'tmProductConfiguration.enabledTankTypes',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'fixedDensity',
                label: 'tmProductConfiguration.fixedDensity',
                type: 'decimal',
                digits: 4,
                max: Constants.MAX_5_4
            },
            {
                name: 'volumeExpansionFactor',
                label: 'tmProductConfiguration.volumeExpansionFactor',
                type: 'decimal',
                digits: 4,
                max: Constants.MAX_5_4
            },
            {
                name: 'isEthylene',
                label: 'tmProductConfiguration.isEthylene',
                type: 'boolean'
            },
            {
                name: 'gasType',
                label: 'tmProductConfiguration.gasType',
                type: 'string',
                validations: {
                    maxLength: 20
                }
            }
        ]
    },
    EquipmentType: {
        title: 'equipmentType.page_title',
        api: 'masterdata/equipment-type',
        enableUpdate: true,
        editButtonName: 'equipmentTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'equipmentTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'equipmentTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 100,
                    required: true
                }
            },
            {
                name: 'infoilCode',
                label: 'infoilCode',
                type: 'string',
                validations: {
                    maxLength: 2,
                }
            },
        ]
    },
    Equipment: {
        title: 'equipment.page_title',
        api: 'masterdata/equipment',
        enableUpdate: true,
        editButtonName: 'equipmentEditButton',
        enableDelete: true,
        deleteButtonName: 'equipmentDeleteButton',
        enableInsert: true,
        addButtonName: 'equipmentAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },

            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 20,
                    required: true
                }
            },
            {
                name: 'typeId',
                label: 'equipmentType.fk',
                type: 'select',
                selectApi: 'masterdata/equipment-type',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'type',
                label: 'equipmentType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-type',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'typeId'
            },

            {
                name: 'plantId',
                label: 'registryPlant.fk',
                type: 'select',
                selectApi: 'masterdata/registry-plant',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                sortField: 'Description',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'plant',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/registry-plant',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'plantId'
            },

            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },

            {
                name: 'alternativeDescription',
                label: 'alternativeDescription',
                type: 'string',
                validations: {
                    maxLength: 50
                }
            },
            {
                name: 'isTankOperationRegistryEnabled',
                label: 'isTankOperationRegistryEnabled',
                type: 'boolean'
            },
            {
                name: 'isReal',
                label: 'isReal',
                type: 'boolean'
            },
            {
                name: 'IsBufferTank',
                label: 'IsBufferTank',
                type: 'boolean'
            },
            {
                name: 'validityStartDate',
                label: 'validityStartDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'validityEndDate',
                label: 'validityEndDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                formHide: true
            },
        ]

    },
    EquipmentTypeProperty: {
        title: 'equipmentTypeProperty.page_title',
        api: 'masterdata/equipment-type-property',
        enableUpdate: true,
        editButtonName: 'equipmentTypePropertyEditButton',
        enableDelete: true,
        deleteButtonName: 'equipmentTypePropertyDeleteButton',
        enableInsert: true,
        addButtonName: 'equipmentTypePropertyAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 100,
                    required: true
                }
            },
            {
                name: 'equipmentTypeId',
                label: 'equipmentType.fk',
                type: 'select',
                selectApi: 'masterdata/equipment-type',
                selectFormat: {
                    label: function (el) {
                        return el.code + ' - ' + el.description
                    },
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'equipmentType',
                label: 'equipmentType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-type',
                gridFilterSelectFormat: {
                    label: function (el) {
                        el.code + ' - ' + el.description
                    },
                    value: 'id'
                },
                gridFilterName: 'equipmentTypeId'
            },
            {
                name: 'unitOfMeasureCode',
                label: 'unitOfMeasure.fk',
                type: 'select',
                selectApi: 'masterdata/unit-of-measure',
                selectFormat: { label: 'code', value: 'code' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'unitOfMeasureCode',
                label: 'unitOfMeasure.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/unit-of-measure',
                gridFilterSelectFormat: { label: 'code', value: 'code' },
                gridFilterName: 'unitOfMeasureCode'
            },
        ]

    },
    EquipmentProperty: {
        title: 'equipmentProperty.page_title',
        api: 'masterdata/equipment-property',
        enableUpdate: true,
        editButtonName: 'equipmentPropertyEditButton',
        enableDelete: true,
        deleteButtonName: 'equipmentPropertyDeleteButton',
        enableInsert: true,
        addButtonName: 'equipmentPropertyAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'equipmentId',
                label: 'equipment.fk',
                type: 'select',
                selectApi: 'masterdata/equipment',
                selectFormat: {
                    label: 'code',
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'equipment',
                label: 'equipment.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment',
                gridFilterSelectFormat: {
                    label: 'code',
                    value: 'id'
                },
                gridFilterName: 'equipmentId'
            },

            {
                name: 'registryEquipmentTypePropertyId',
                label: 'equipmentTypeProperty.fk',
                type: 'select',
                selectApi: 'masterdata/equipment-type-property',
                selectFormat: {
                    label: 'code',
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryEquipmentTypeProperty',
                label: 'equipmentTypeProperty.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-type-property',
                gridFilterSelectFormat: {
                    label: 'code',
                    value: 'id'
                },
                gridFilterName: 'registryEquipmentTypePropertyId'
            },

            {
                name: 'intValue',
                label: 'intValue',
                type: 'decimal',
                digits: 0,
                max: Constants.MAX_18
            },
            {
                name: 'decimalValue',
                label: 'decimalValue',
                type: 'decimal',
                digits: 6,
                max: Constants.MAX_18_6
            },
            {
                name: 'textValue',
                label: 'textValue',
                type: 'string',
                validations: {
                    maxLength: 2000
                }
            },
            {
                name: 'validityStartDate',
                label: 'validityStartDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'validityEndDate',
                label: 'validityEndDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                validations: {
                    required: true
                },
                formHide: true
            }
        ]

    },
    TankOperations: {
        title: 'title',
        api: 'equipment-operation/tank',
        enableUpdate: false,
        enableDelete: true,
        deleteButtonName: 'tankOperationsDeleteButton',
        enableInsert: false,
        idColumn: "id",
        defaultSortField: 'operationDate',
        preventAutoLoad: true,
        preventFilters: true,
        hideRefresh: true,
        showDetailDialog: true,
        defaultLimit: 100000,
        preventPagination: true,
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, defaultSort: 'asc' },
            { label: 'tankOperations.operationStatus', name: 'operationStatus', type: 'string', readOnly: true },
            { label: 'tankOperations.fiscalCommunicationCode', name: 'fiscalCommunicationCode', type: 'string', readOnly: true },
            { label: 'tankOperations.operationDate', name: 'operationDate', type: 'date', datetime: true },
            { label: 'tankOperations.operationType', name: 'operationType', type: 'string', formHide: true },
            { label: 'tankOperations.product', name: 'product', type: 'string', formHide: true },
            { label: 'tankOperations.combinedNomenclature', name: 'combinedNomenclature', type: 'string', readOnly: true },
            { label: 'tankOperations.operationCircuit', name: 'operationCircuit', type: 'string' },
            { label: 'tankOperations.isSender', name: 'isSender', type: 'boolean', trueValue: 'M', falseValue: 'R' },
            { label: 'tankOperations.measureType', name: 'measureType', type: 'string' },
            { label: 'tankOperations.movedWeight', name: 'movedWeight', type: 'number' },
            { label: 'tankOperations.fiscalMovedWeight', name: 'fiscalMovedWeight', type: 'number' },
            { label: 'tankOperations.stockWeight', name: 'stockWeight', type: 'number' },
            { label: 'tankOperations.fiscalStockWeight', name: 'fiscalStockWeight', type: 'number' },
            { label: 'tankOperations.movedVolume', name: 'movedVolume', type: 'number' },
            { label: 'tankOperations.fiscalMovedVolume', name: 'fiscalMovedVolume', type: 'number' },
            { label: 'tankOperations.stockVolume15', name: 'stockVolume15', type: 'number' },
            { label: 'tankOperations.fiscalStockVolume', name: 'fiscalStockVolume', type: 'number' },
            { label: 'tankOperations.productLevel', name: 'productLevel', type: 'number' },
            { label: 'tankOperations.waterLevel', name: 'waterLevel', type: 'number' },
            { label: 'tankOperations.temperature', name: 'temperature', type: 'decimal', digits: 2 },
            { label: 'tankOperations.environmentTemperature', name: 'environmentTemperature', type: 'decimal', digits: 2 },
            { label: 'tankOperations.density15', name: 'density15', type: 'decimal', digits: 5 },
            { label: 'tankOperations.gasPressure', name: 'gasPressure', type: 'number' },
            { label: 'tankOperations.molecularWeight', name: 'molecularWeight', type: 'number' },
            { label: 'tankOperations.shrinkage', name: 'shrinkage', type: 'number' },
            { label: 'tankOperations.shrinkageGas', name: 'shrinkageGas', type: 'number' },
            { label: 'tankOperations.gasTemperature', name: 'gasTemperature', type: 'decimal', digits: 2 },
            { label: 'tankOperations.sulfurPercentage', name: 'sulfurPercentage', type: 'decimal', digits: 2 },
            { label: 'tankOperations.denaturantQuantity', name: 'denaturantQuantity', type: 'number' },
            { label: 'tankOperations.stockVolume', name: 'stockVolume', type: 'number' },
            { label: 'tankOperations.waterVolume', name: 'waterVolume', type: 'number' },
            // label ntankOperations.ame:'//', type{ name:'//', type:''},
            { label: 'tankOperations.grossVolume', name: 'grossVolume', type: 'number' },
            { label: 'tankOperations.fX54', name: 'fX54', type: 'decimal', digits: 4 },
            { label: 'tankOperations.fX56', name: 'fX56', type: 'decimal', digits: 4 },
            { label: 'tankOperations.shellTemperature', name: 'shellTemperature', type: 'decimal', digits: 2 },
            { label: 'tankOperations.deltaTemperatura', name: 'deltaTemperatura', type: 'decimal', digits: 2 },
            { label: 'tankOperations.thermalExpansionFactor', name: 'thermalExpansionFactor', type: 'decimal', digits: 5 },
            { label: 'tankOperations.steamVolume', name: 'steamVolume', type: 'number' },
            { label: 'tankOperations.steamWeight', name: 'steamWeight', type: 'number' },
            { label: 'tankOperations.roofWeight', name: 'roofWeight', type: 'number' },
            { label: 'tankOperations.roofVolume', name: 'roofVolume', type: 'number' },
            { label: 'tankOperations.removableVolume', name: 'removableVolume', type: 'number' },
            { label: 'tankOperations.removableWeight', name: 'removableWeight', type: 'number' },
            { label: 'tankOperations.feetPosition', name: 'feetPosition', type: 'string' },
            { label: 'tankOperations.productOwner', name: 'productOwner', type: 'string' },
            // { label:'tankOperations.plantCode',name:'plantCode', type:'string'},
            { label: 'tankOperations.voyageNumber', name: 'voyageNumber', type: 'string' },
            { label: 'tankOperations.managementType', name: 'managementType', type: 'string' },
            { label: 'tankOperations.introducedGrossVolume', name: 'introducedGrossVolume', type: 'number' },
            { label: 'tankOperations.introducedGrossWeight', name: 'introducedGrossWeight', type: 'number' },
            { label: 'tankOperations.sampleBarrelDensity15', name: 'sampleBarrelDensity15', type: 'decimal', digits: 5 },
            { label: 'tankOperations.weightedAverageDensity', name: 'weightedAverageDensity', type: 'decimal', digits: 5 },
            { label: 'tankOperations.waterAndSedimentsVolumePercentage', name: 'waterAndSedimentsVolumePercentage', type: 'number' },
            { label: 'tankOperations.waterAndSedimentsCorrectionFactor', name: 'waterAndSedimentsCorrectionFactor', type: 'decimal', digits: 4 },
            { label: 'tankOperations.waterAndSedimentsIntroducedVolume', name: 'waterAndSedimentsIntroducedVolume', type: 'number' },
            { label: 'tankOperations.volumeExpansionFactor', name: 'volumeExpansionFactor', type: 'decimal', digits: 4 },
            { label: 'tankOperations.biofeedWeightCorrectiornFactor', name: 'biofeedWeightCorrectiornFactor', type: 'decimal', digits: 4 },
            { label: 'tankOperations.emptyStockWeight', name: 'emptyStockWeight', type: 'number' },
            { label: 'tankOperations.automaticProductLevel', name: 'automaticProductLevel', type: 'number' },
            { label: 'tankOperations.automaticTemperature', name: 'automaticTemperature', type: 'decimal', digits: 2 },
            { label: 'tankOperations.automaticStockWeight', name: 'automaticStockWeight', type: 'number' },
            { label: 'tankOperations.automaticMovedWeight', name: 'automaticMovedWeight', type: 'number' },
            { label: 'tankOperations.automaticStockVolume', name: 'automaticStockVolume', type: 'number' },
            { label: 'tankOperations.automaticMovedVolume', name: 'automaticMovedVolume', type: 'number' },
            { label: 'tankOperations.deltaMovedQuantity', name: 'deltaMovedQuantity', type: 'number' },
            { label: 'tankOperations.deltaMovedPercentage', name: 'deltaMovedPercentage', type: 'number' }
        ],
        customActions: [
            {
                title: 'btn.edit',
                visible: false,
                name: 'tankOperationsEditButton',
                icon: 'pi pi-pencil',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/tank/operations/${data.id}`;
                    caller.$router.push(uri);
                }
            },
            {
                title: 'tankOperations.exportInCalculator',
                visible: false,
                name: 'tankCalculatorExportButton',
                icon: 'pi pi-table',
                data: {},
                fields: [],
                action: function (data, caller) {
                    caller.$emit('populateCalculator', data);
                }
            },
        ]

    },
    LotsManagement: {
        title: 'lotsManagement.page_title',
        api: 'material-balance-lot-management',
        updateApi: 'material-balance-lot-management',
        enableUpdate: function (data) {
            return !data.isClosed
        },
        strechColumn: true,
        editButtonName: 'lotsManagementEditButton',
        enableDelete: true,
        deleteButtonName: 'lotsManagementDeleteButton',
        enableInsert: false,
        idColumn: "id",
        defaultSortField: 'id',
        preventAutoLoad: true,
        preventFilters: false,
        hideRefresh: true,
        showDetailDialog: true,
        editMode: 'dialog',
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true,  defaultSort: 'asc' },
            { name: 'idMovementInstructionLot', label: 'lotsManagement.idMovementInstructionLot', type: 'number', gridHide: true, readOnly: true },
            { label: 'lotsManagement.voyageNumber', name: 'voyageNumber', type: 'string', readOnly: true },
            { label: 'lotsManagement.lotCode', name: 'lotCode', type: 'string', readOnly: true },
            { label: 'lotsManagement.externalVoyageNumber', name: 'externalVoyageNumber', type: 'string', readOnly: true, inLineEdit: true },
            { label: 'lotsManagement.balanceSheetDate', name: 'balanceSheetDate', type: 'date', gridHide: true, readOnly: true },
            { 
                label: 'lotsManagement.registryPlant', 
                name: 'registryPlant', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/registry-plant-combo', 
                updateField: 'idRegistryPlant', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/registry-plant-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryPlant',
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
                addLot: true
            },
            { 
                label: 'lotsManagement.registryProduct', 
                name: 'registryProduct', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/product-combo', 
                updateField: 'idRegistryProduct', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/product-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryProduct', 
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
                addLot: true
            },
            { 
                label: 'lotsManagement.registryTransportType', 
                name: 'registryTransportType', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/transport-type-combo', 
                updateField: 'idRegistryTransportType', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/transport-type-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryTransportType', 
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
                addLot: true
            },
            { 
                label: 'lotsManagement.registryCommercialOperationType', 
                name: 'registryCommercialOperationType', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/commercial-operation-type-combo', 
                updateField: 'idRegistryCommercialOperationType', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/commercial-operation-type-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryCommercialOperationType', 
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
                addLot: true
            },
            { label: 'lotsManagement.chargeDischargeFlag', name: 'chargeDischargeFlag', type: 'string', readOnly: true },
            { 
                label: 'lotsManagement.isInternalConsumption', 
                name: 'isInternalConsumption', 
                type: 'boolean',
                inLineEdit: true, 
                addLot: true
            },
            { label: 'lotsManagement.isFip', name: 'isFip', type: 'boolean', readOnly: true, gridHide: true },
            { label: 'lotsManagement.expectedQuantity', name: 'expectedQuantity', type: 'decimal', digits: 3, inLineEdit: true, editEnabled: (data) => data.voyageNumber.includes('PB'), addLot: true },
            { label: 'lotsManagement.laycanStartDate', name: 'laycanStartDate', type: 'date', inLineEdit: true, editEnabled: (data) => data.voyageNumber.includes('PB'), addLot: true },
            { label: 'lotsManagement.laycanEndDate', name: 'laycanEndDate', type: 'date', inLineEdit: true, editEnabled: (data) => data.voyageNumber.includes('PB'), addLot: true },
            { label: 'lotsManagement.etaDate', name: 'etaDate', type: 'date', datetime: true, editEnabled: (data) => data.voyageNumber.includes('PB'), inLineEdit: true },
            { 
                label: 'lotsManagement.portOrLocationOfLoading', 
                name: 'portOrLocationOfLoading', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/port-and-location-combo', 
                updateField: 'idPortOrLocationOfLoading', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/port-and-location-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idPortOrLocationOfLoading',
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
            },
            { 
                label: 'lotsManagement.portOrLocationOfDischarge', 
                name: 'portOrLocationOfDischarge', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/port-and-location-combo', 
                updateField: 'idPortOrLocationOfDischarge', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/port-and-location-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idPortOrLocationOfDischarge', 
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
            },
            { label: 'lotsManagement.vesselName', name: 'vesselName', type: 'string', readOnly: true, editEnabled: (data) => data.voyageNumber.includes('PB'), inLineEdit: true },
            { 
                label: 'lotsManagement.registryInspector', 
                name: 'registryInspector', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/inspector-combo', 
                updateField: 'idRegistryInspector', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/inspector-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryInspector', 
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
            },
            {
                label: 'lotsManagement.company', 
                name: 'company', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/company-combo', 
                updateField: 'idCompany', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/company-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idCompany', 
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
                addLot: true
            },
            { 
                label: 'lotsManagement.counterparty', 
                name: 'counterparty', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/company-combo', 
                updateField: 'idCounterparty', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/company-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idCounterparty', 
                inLineEdit: true, 
                editEnabled: (data) => data.voyageNumber.includes('PB'),
                addLot: true
            },
            { 
                label: 'lotsManagement.registryContractType', 
                name: 'registryContractType', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/contract-type-combo', 
                updateField: 'idRegistryContractType', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/contract-type-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryContractType', 
                inLineEdit: true
            },
            { label: 'lotsManagement.contractStartDate', name: 'contractStartDate', type: 'date', inLineEdit: true },
            { label: 'lotsManagement.contractEndDate', name: 'contractEndDate', type: 'date', inLineEdit: true },
            { label: 'lotsManagement.contractNumberReference', name: 'contractNumberReference', type: 'string', inLineEdit: true },
            { 
                label: 'lotsManagement.registryAccountType', 
                name: 'registryAccountType', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/account-type-combo', 
                updateField: 'idRegistryAccountType', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/account-type-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryAccountType', 
                inLineEdit: true, 
                addLot: true
            },
            { label: 'lotsManagement.incotermCode', name: 'incotermCode', type: 'string', datetime: true, inLineEdit: true, editEnabled: (data) => data.voyageNumber.includes('PB') },
            { label: 'lotsManagement.loadingStartDate', name: 'loadingStartDate', type: 'date', datetime: true, inLineEdit: true },
            { label: 'lotsManagement.loadingEndDate', name: 'loadingEndDate', type: 'date', datetime: true, inLineEdit: true },
            { label: 'lotsManagement.dischargingStartDate', name: 'dischargingStartDate', type: 'date', datetime: true, inLineEdit: true },
            { label: 'lotsManagement.dischargingEndDate', name: 'dischargingEndDate', type: 'date', datetime: true, inLineEdit: true },
            { label: 'lotsManagement.finalDate', name: 'finalDate', type: 'date', datetime: true, inLineEdit: true },
            { label: 'lotsManagement.finalQuantity', name: 'finalQuantity', type: 'decimal', digits: 3, inLineEdit: true },
            { label: 'lotsManagement.actualQuantity', name: 'actualQuantity', type: 'decimal', digits: 3, inLineEdit: true, editEnabled: (data) => data.voyageNumber.includes('PB') },
            { label: 'lotsManagement.billOfLadingDate', name: 'billOfLadingDate', type: 'date', datetime: true, inLineEdit: true },
            { label: 'lotsManagement.billOfLadingQuantity', name: 'billOfLadingQuantity', type: 'decimal', digits: 3, inLineEdit: true },
            { label: 'lotsManagement.counterpartyLaycanStartDate', name: 'counterpartyLaycanStartDate', type: 'date', inLineEdit: true },
            { label: 'lotsManagement.counterpartyLaycanEndDate', name: 'counterpartyLaycanEndDate', type: 'date', inLineEdit: true },
            { 
                label: 'lotsManagement.mooringPlatform', 
                name: 'mooringPlatform', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/equipment-combo?type=PTF', 
                updateField: 'idMooringPlatform', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/equipment-combo?type=PTF', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idMooringPlatform'
            },
            { label: 'lotsManagement.mooringDate', name: 'mooringDate', type: 'date', datetime: true, inLineEdit: true },
            { label: 'lotsManagement.unmooringDate', name: 'unmooringDate', type: 'date', datetime: true, inLineEdit: true },
            { label: 'lotsManagement.note', name: 'note', type: 'textarea', inLineEdit: true },
            { label: 'lotsManagement.isCreateSelfInvoice', name: 'isCreateSelfInvoice', type: 'boolean', readOnly: true, gridHide: true },
            { label: 'lotsManagement.protocolNumberofIntentLetter', name: 'protocolNumberofIntentLetter', type: 'string', gridHide: true, formHide: true },
            { label: 'lotsManagement.isClosed', name: 'isClosed', type: 'boolean', readOnly: true, gridHide: true, formHide: true, addLot: true },
            { label: 'lotsManagement.isDeleted', name: 'isDeleted', type: 'boolean', readOnly: true, gridHide: true, formHide: true },
        ],
    },
    MaterialBalanceTemplateLot: {
        title: 'materialBalanceTemplateLot.page_title',
        api: 'material-balance-template-lot',
        enableUpdate: true,
        editButtonName: 'materialBalanceTemplateLotEditButton',
        enableDelete: true,
        deleteButtonName: 'materialBalanceTemplateLotDeleteButton',
        enableInsert: true,
        addButtonName: 'materialBalanceTemplateLotAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product',
                selectFormat: {
                    label: function (el) {
                        return el.code + '-' + el.description
                    },
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product',
                gridFilterSelectFormat: {
                    label: function (el) {
                        return el.code + '-' + el.description
                    },
                    value: 'id'
                },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'idRegistryPlant',
                label: 'registryPlant.fk',
                type: 'select',
                selectApi: 'masterdata/registry-plant',
                selectFormat: {
                    label: 'code',
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryPlant',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/registry-plant',
                gridFilterSelectFormat: {
                    label: 'code',
                    value: 'id'
                },
                gridFilterName: 'idRegistryPlant'
            },
            {
                name: 'idRegistryCommercialOperationType',
                label: 'commercialOperationType.fk',
                type: 'select',
                selectApi: 'masterdata/commercial-operation-type-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'registryCommercialOperationType',
                label: 'commercialOperationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/commercial-operation-type-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryCommercialOperationType'
            },
            {
                name: 'idCompany',
                label: 'company.fk',
                type: 'select',
                selectApi: 'masterdata/company-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'company',
                label: 'company.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idCompany'
            },
            {
                name: 'idCounterparty',
                label: 'counterparty',
                type: 'select',
                selectApi: 'masterdata/company-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'counterparty',
                label: 'counterparty',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idCounterparty'
            },
            {
                name: 'idRegistryAccountType',
                label: 'accountType.fk',
                type: 'select',
                selectApi: 'masterdata/account-type-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'registryAccountType',
                label: 'accountType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/account-type-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryAccountType'
            },
            {
                name: 'isInternalConsumption',
                label: 'lotsManagement.isInternalConsumption',
                type: 'boolean'
            },
            {
                name: 'idRegistryTransportType',
                label: 'transportType.fk',
                type: 'select',
                selectApi: 'masterdata/transport-type-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'registryTransportType',
                label: 'transportType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/transport-type-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryTransportType'
            },
        ]

    },
    ProductStock: {
        title: 'productStock.page_title',
        api: 'material-balance-product-stock',
        updateApi: 'material-balance-product-stock',
        enableUpdate: true,
        editButtonName: 'productStockUpdateButton',
        enableDelete: true,
        deleteButtonName: 'productStockDeleteButton',
        enableInsert: false,
        idColumn: "id",
        defaultSortField: 'id',
        preventAutoLoad: true,
        preventFilters: false,
        hideRefresh: true,
        editMode: 'dialog',
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true,  defaultSort: 'asc', formHide: true },
            { label: 'productStock.stockDate', name: 'balanceSheetDate', type: 'date', onlyDate: true, gridHide: true, readOnly: true, inLineEdit: true, editEnabled: () => false  },

            { 
                label: 'product.fk', 
                name: 'registryProduct', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/product-combo', 
                updateField: 'idRegistryProduct', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/product-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryProduct', 
                inLineEdit: true, 
                editEnabled: () => false
            },
            { label: 'quantity', name: 'quantity', type: 'decimal', digits: 3, inLineEdit: true },
            { 
                label: 'registryPlant.fk', 
                name: 'registryPlant', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/registry-plant-combo', 
                updateField: 'idRegistryPlant', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/registry-plant-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryPlant',
                gridHide: true
            },
            { 
                label: 'company.fk', 
                name: 'company', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/company-combo', 
                updateField: 'idRegistryCompany', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/company-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryCompany', 
                gridHide: true
            },
            { 
                label: 'originStorage.fk', 
                name: 'registryOriginStorage', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/origin-storage-combo', 
                updateField: 'idRegistryOriginStorage',
                defaultValue: 'MANUALE',
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/origin-storage-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryOriginStorage', 
            },
        ],
    },
    MonthlyMaterialBalanceDetail: {
        title: 'monthlyMaterialBalance.page_title',
        api: 'material-balance-sheet-detail',
        updateApi: 'material-balance-sheet-detail',
        enableUpdate: false,
        editButtonName: '',
        enableDelete: false,
        deleteButtonName: '',
        enableInsert: false,
        idColumn: "id",
        defaultSortField: 'id',
        preventPagination: true,
        preventAutoLoad: true,
        preventFilters: false,
        hideRefresh: true,
        queryParamFilters: true,
        highlightConditions: [
            {
                field: 'idRegistryProduct',
                value: null,
                class: 'row-azure'
            }
        ],
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true,  defaultSort: 'asc', formHide: true, gridHide: true },
            { label: 'product.fk', name: 'registryProduct', type: 'string' },
            { label: 'monthlyMaterialBalance.initialStockQty', name: 'initialStockQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.processingProductionQty', name: 'processingProductionQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.inputBySeaQty', name: 'inputBySeaQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.inputByPipeQty', name: 'inputByPipeQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.inputByLandQty', name: 'inputByLandQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.inputByStockTransferQty', name: 'inputByStockTransferQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.outputBySeaQty', name: 'outputBySeaQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.outputByPipeQty', name: 'outputByPipeQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.outputByLandQty', name: 'outputByLandQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.outputByStockTransferQty', name: 'outputByStockTransferQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.internalConsumptionQty', name: 'internalConsumptionQty', type: 'decimal', digits: 3 },
            { label: 'monthlyMaterialBalance.finalStockQty', name: 'finalStockQty', type: 'decimal', digits: 3 },
        ],
    },
    ProcessingProductionProposed: {
        title: 'processingProduction.page_title',
        api: 'material-balance-processing-production-temp-detail',
        updateApi: 'material-balance-processing-production-temp-detail',
        enableUpdate: true,
        editButtonName: 'processingProductionProposedUpdateButton',
        enableDelete: false,
        deleteButtonName: 'processingProductionProposedDeleteButton',
        enableInsert: false,
        idColumn: "id",
        defaultSortField: 'id',
        preventAutoLoad: true,
        preventFilters: false,
        hideRefresh: true,
        editMode: 'dialog',
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true,  defaultSort: 'asc', formHide: true, gridHide: true },
            { label: 'processingProduction.externalProduct', name: 'externalProduct', type: 'string', inLineEdit: true, editEnabled: () => false},
            { 
                label: 'product.fk', 
                name: 'registryProduct', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/product-combo', 
                updateField: 'idRegistryProduct', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/product-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryProduct', 
                inLineEdit: true, 
            },
            { label: 'processingProduction.dailyAverageQuantity', name: 'dailyAverageQuantity', type: 'decimal', digits: 3 },
            { label: 'processingProduction.totalQuantity', name: 'totalQuantity', type: 'decimal', digits: 3, inLineEdit: true },
            { label: 'processingProduction.dailyAverageQuantitySud', name: 'dailyAverageQuantitySud', type: 'decimal', digits: 3 },
            { label: 'processingProduction.totalQuantitySud', name: 'totalQuantitySud', type: 'decimal', digits: 3, inLineEdit: true },
            { label: 'processingProduction.dailyAverageQuantityNord', name: 'dailyAverageQuantityNord', type: 'decimal', digits: 3 },
            { label: 'processingProduction.totalQuantityNord', name: 'totalQuantityNord', type: 'decimal', digits: 3, inLineEdit: true },
            { label: 'processingProduction.errorMessage', name: 'errorMessage', type: 'string'},
            { 
                label: 'processingProduction.recordFileState', 
                name: 'registryRecordFileState', 
                type: 'select', 
                readOnly: true, 
                selectApi: 'masterdata/record-file-state-combo', 
                updateField: 'idRegistryRecordFileState', 
                gridFilterType: 'select', 
                gridFilterSelectApi: 'masterdata/record-file-state-combo', 
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryRecordFileState',
                icon: ({registryRecordFileState: state}) => state === 'Importabile' ? 'text-green-400 pi pi-check' : ((state === 'Ignora' || state === 'Escludi') ? 'text-orange-400 pi pi-exclamation-triangle' : (state === 'Non Importabile' ? 'text-red pi pi-exclamation-circle' : ''))
            },
        ],
        customActions: [
            {
                title: 'processingProduction.btn.exclude',
                visible: (data) => data.registryRecordFileState !== 'Escludi',
                name: 'exclude_detail',
                icon: 'pi pi-times',
                cssClass: 'p-button-rounded p-button-warning',
                data: {},
                fields: [],
                action: function(data, caller){
                    caller.$confirm.require({
                        message: caller.$t('messages.exclude_message_question'),
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: caller.$t('yes'),
                        rejectLabel: caller.$t('no'),
                        accept: () => {
                            caller.$store.dispatch("generic/setLoading", true);
                            MaterialBalanceService.excludeDetail(data, (response) => {
                                caller.$store.dispatch("generic/setLoading", false);
                                if (response.success) {
                                    caller.loadData();
                                    caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                                } else {
                                    caller.$toast.add({ detail: response?.message && response?.message != '' ? response?.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                                }
                            })
                        },
                        reject: () => {
                            //callback to execute when user rejects the action
                            caller.$confirm.close();
                        }
                    });
                }
            },
            {
                title: 'processingProduction.btn.include',
                visible: (data) => data.registryRecordFileState === 'Escludi',
                name: 'include_detail',
                icon: 'pi pi-replay',
                cssClass: 'p-button-rounded p-button-primary',
                data: {},
                fields: [],
                action: function(data, caller){
                    caller.$confirm.require({
                        message: caller.$t('messages.include_message_question'),
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: caller.$t('yes'),
                        rejectLabel: caller.$t('no'),
                        accept: () => {
                            caller.$store.dispatch("generic/setLoading", true);
                            MaterialBalanceService.includeDetail(data, (response) => {
                                caller.$store.dispatch("generic/setLoading", false);
                                if (response.success) {
                                    caller.loadData();
                                    caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                                } else {
                                    caller.$toast.add({ detail: response?.message && response?.message != '' ? response?.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                                }
                            })
                        },
                        reject: () => {
                            //callback to execute when user rejects the action
                            caller.$confirm.close();
                        }
                    });
                }
            },
        ]
    },
    OperativePlanScheduled: {
        title: '',
        api: 'operative-plan/scheduled-operation-scheduled',
        updateApi: 'operative-plan/scheduled-operation',
        enableUpdate: true,
        enableDelete: false,
        enableInsert: false,
        strechColumn: true,
        idColumn: "id",
        preventAutoLoad: true,
        preventFilters: false,
        preventPagination: true,
        hideRefresh: true,
        filterLocal: true,
        queryParamFilters: true,
        editRule: function(data){
            return data.fiscalNotificationStatus != 'CC';
        },
        emitters: ['showEquipmentDialog'],
        editMode: 'dialog',
        editButtonName: 'operativePlanScheduledEditButton',
        alertField: 'notes',
        alertFieldScheduling: 'noteScheduling',
        highlightConditions: [
            {
                field: 'isMovementInstructionLotDeleted',
                value: true,
                class: 'row-red'
            }
        ],
        showDetailDialog: true,
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, defaultSort: 'asc' },
            { label: 'operativePlan.voyageNumber', name: 'voyageNumber', type: 'string', width: 150 },
            // { label: 'operativePlan.vessel', name: 'vessel', type: 'string' },
            { label: 'operativePlan.vessel', name: 'vessel', type: 'string',
                textButton: true,
                renderClass(data) {
                    switch (data.vesselAcceptanceStatus) {
                        case 'PARTIAL':
                            return 'p-button-warning';
                        case 'REJECTED':
                            return 'p-button-danger';
                        case 'NO_CHECK':
                        case 'ACCEPTED':
                        case 'ALREADY_NOTIFIED':
                            return 'p-button-success';   
                        default:
                            return '';
                    }
                },
                tooltip(data, t$){
                    let vai = data.vesselAcceptanceInfo;
                    const alreadyNotified = data.vesselAcceptanceStatus == 'ALREADY_NOTIFIED';
                    let clearanceDate = '';
                    let validFrom = '';
                    let validTo = '';
                    let clearanceText = '';
                    let vaiText = '<i class="pi pi-times" style="color: red"></i> <b>Dati accettazione non disponibili.</b>';
                    
                    if(vai){
                        clearanceDate = vai.clearanceDate ? Utils.formatDate(vai.clearanceDate) : '';
                        validFrom = vai.validFrom ? Utils.formatDate(vai.validFrom) : '';
                        validTo = vai.validTo ? Utils.formatDate(vai.validTo) : '';
                        clearanceText = vai.clearanceAccepted ? 
                            `<i class="pi pi-check" style="color: green"></i> Nave accettata il: ${clearanceDate} per il sito ${vai.clearanceSite}. Valida dal ${validFrom} al ${validTo}`: 
                            '<i class="pi pi-times" style="color: red"></i><b> Nave non accettata.</b>';

                        vaiText = 
                            `
                                <p>${clearanceText}<br/>
                            `;
                    }
                    
                    let tooltipText = `
                        <p>
                            ${alreadyNotified ? '<i class="pi pi-check" style="color: green"></i> Comunicazione fiscale già inviata<br/>' : ''}
                            ${vaiText}
                        </p>`;
                    
                    return tooltipText;
                },
                showButton(data){
                    return data.vessel
                }
             },
            { label: 'operativePlan.product', name: 'product', type: 'string' }, // BLOCCARE QUA
            { label: 'operativePlan.plant', name: 'plant', type: 'string' },
            { label: 'operativePlan.company', name: 'company', type: 'string' },
            { label: 'operativePlan.operationType', name: 'operationType', type: 'string' },
            { label: 'operativePlan.inspector', name: 'inspector', type: 'string' },
            { label: 'operativePlan.quantity', name: 'quantity', type: 'number' },
            {
                label: 'operativePlan.tankListString', name: 'tankListString', type: 'string', action: function (caller, record) {

                    caller.$emit('showEquipmentDialog', record);
                    // caller.$parent.showEquipmentDialog(record);
                }
            },
            {
                label: 'operativePlan.pipilineListString', name: 'pipelineListString', type: 'string', action: function (caller, record) {
                    caller.$emit('showEquipmentDialog', record);
                    // caller.$parent.showEquipmentDialog(record);
                }
            },
            {
                label: 'operativePlan.mooringPlatform',
                name: 'mooringPlatform',
                type: 'select',
                inLineEdit: true,
                selectApi: 'masterdata/equipment-combo?type=PTF',
                refreshOnUpdateDialog: true,
                selectApiFn: function(el){
                    return 'masterdata/equipment-combo?type=PTF&plantId=' + el.idRegistryPlant
                },
                updateField: 'idMooringPlatform',
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=PTF',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridFilterName: 'mooringPlatform'
            },
            { label:'operativePlan.isVesselInRoadstead',name:'isVesselInRoadstead', type:'boolean', inLineEdit: true, gridHide:true},     //EDITABILE
            { label: 'operativePlan.etaDate', name: 'etaDate', type: 'date', datetime: true, inLineEdit: true },    //EDITABILE
            { label: 'operativePlan.laycanStartDate', name: 'laycanStartDate', type: 'date', datetime: true },
            { label: 'operativePlan.laycanEndDate', name: 'laycanEndDate', type: 'date', datetime: true },
            { label: 'operativePlan.mooringDate', name: 'mooringDate', type: 'date', datetime: true, inLineEdit: true },   //EDITABILE
            { label: 'operativePlan.unmooringDate', name: 'unmooringDate', type: 'date', datetime: true, inLineEdit: true },   //EDITABILE
            { label: 'operativePlan.scheduledOperationDate', name: 'scheduledOperationDate', type: 'date', datetime: true, inLineEdit: true }, //EDITABILE
            { label: 'operativePlan.status', name: 'status', type: 'string' },
            {
                label: 'operativePlan.movementInstructionCode', name: 'movementInstructionCode', type: 'string', style: 'text-decoration: underline;', action: function (caller, record) {
                    let params = {
                        movementInstructionCode: record.movementInstructionCode
                    };
                    caller.$store.dispatch("generic/setLoading", true);
                    MovementInstructionService.printMovementInstruction(params, response => {

                        caller.$store.dispatch("generic/setLoading", false);
                        if (response.success) {
                            var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            var fileLink = document.createElement('a');

                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', record.voyageNumber + '-' + record.movementInstructionCode + '.pdf');
                            document.body.appendChild(fileLink);

                            fileLink.click();
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })

                }
            },
            { label: 'operativePlan.notes', name: 'notes', type: 'string', inLineEdit: true }, //EDITABILE
            { label: 'operativePlan.noteScheduling', name: 'noteScheduling', type: 'string', inLineEdit: true, gridHide: true }, //EDITABILE
            { label: 'operativePlan.minimumQuantity', name: 'minimumQuantity', type: 'number' },
            { label: 'operativePlan.maximumQuantity', name: 'maximumQuantity', type: 'number' },
            // { label: 'operativePlan.draught', name: 'draught', type: 'decimal', digits: 3 },
            { label: 'operativePlan.deadWeightTonnage', name: 'deadWeightTonnage', type: 'decimal', digits: 3 },
            { label: 'operativePlan.grossTonnage', name: 'grossTonnage', type: 'number' },
            //{ label:'operativePlan.demurrageRate',name:'demurrageRate', type:'decimal', digits: 2},
            { label: 'operativePlan.loadingLaytime', name: 'loadingLaytime', type: 'number' },
            { label: 'operativePlan.dischargeLaytime', name: 'dischargeLaytime', type: 'number' },
            // { label:'operativePlan.currencyCode',name:'currencyCode', type:'string'},
            {
                label: 'operativePlan.shippingAgency',
                name: 'shippingAgency',
                type: 'select',
                inLineEdit: true,
                selectApi: 'masterdata/shipping-agency-combo',
                updateField: 'idRegistryShippingAgency',
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/shipping-agency-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridFilterName: 'shippingAgency'
            },
            // {
            //     name: 'idRegistryShippingAgency',  //idProductSource, productSource
            //     label: 'operativePlan.shippingAgency',
            //     type: 'select',
            //     selectApi: 'masterdata/shipping-agency-combo',
            //     selectFormat: { label: 'label', value: 'value' },
            //     gridHide: true,
            //     inLineEdit: true
            // },
            // {
            //     name: 'shippingAgency',
            //     label: 'operativePlan.shippingAgency',
            //     type: 'string',
            //     formHide: true,
            //     gridFilterType: 'select',
            //     gridFilterSelectApi: 'masterdata/shipping-agency-combo',
            //     gridFilterSelectFormat: { label: 'label', value: 'value' },
            //     gridFilterName: 'idRegistryShippingAgency'
            // },

            // { label: 'operativePlan.shippingAgency', name: 'shippingAgency', type: 'string' },
            // { label: 'operativePlan.shippingAgency', name: 'shippingAgency', type: 'string' },
            { label: 'operativePlan.isMovementInstructionLotDeleted', name: 'isMovementInstructionLotDeleted', type: 'boolean', defaultFilter: true, defaultFilterValue: false },

            // IS HIDDEN EDITABILE EVIDENZIATO
            // isMovementInstructionLotDeleted NON EDITABILE EVIDENZIATO

            // DATI COMUNICAZIONE
            { label: 'operativePlan.utifProgramCode', name: 'utifProgramCode', type: 'string' },
            { label: 'operativePlan.fiscalNotificationStatus', name: 'fiscalNotificationStatus', type: 'string' },
            { label: 'operativePlan.fiscalNotificationCode', name: 'fiscalNotificationCode', type: 'string' },
            { label: 'operativePlan.fiscalNotificationDate', name: 'fiscalNotificationDate', type: 'date' },
            { label: 'operativePlan.isHidden', name: 'isHidden', type: 'boolean', inLineEdit: true, defaultFilter: true, defaultFilterValue: false },     //EDITABILE
        ],
        customActions: [
            {
                title: 'btn.editAssets',
                visible: false,
                name: 'operativePlanUpdateAssetsButton',
                icon: 'pi pi-cog',
                data: {},
                fields: [],
                action: function (record, caller) {
                    caller.$emit('showEquipmentDialog', record);
                }
            },
            {
                title: 'btn.attachments',
                visible: false,
                name: 'OpenSchedulingAttachments',
                icon: 'pi pi-paperclip',
                data: {},
                fields: [],
                action: function (record, caller) {
                    caller.$emit('showSchedulingAttachmentsDialog', record);
                }
            },
            {
                title: 'btn.export',
                visible: false,
                name: 'operativePlanPrintTelex',
                icon: 'pi pi-file-pdf',
                cssClass: 'p-button-danger',
                data: {},
                fields: [],
                action: function (record, caller) {
                    let params = {
                        movementInstructionCode: record.movementInstructionCode
                    };
                    caller.$store.dispatch("generic/setLoading", true);
                    MovementInstructionService.printMovementInstruction(params, response => {

                        caller.$store.dispatch("generic/setLoading", false);
                        if (response.success) {
                            var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            var fileLink = document.createElement('a');

                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', record.voyageNumber + '-' + record.movementInstructionCode + '.pdf');
                            document.body.appendChild(fileLink);

                            fileLink.click();
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
                
            }
        ]

    },
    ScheduledEquipmentOperation: {
        title: '',
        api: 'operative-plan/scheduled-equipment-operation',
        updateApi: 'operative-plan/scheduled-equipment-operation',
        enableUpdate: true,
        enableDelete: true,
        enableInsert: false,
        idColumn: "id",
        preventAutoLoad: true,
        preventFilters: true,
        hideRefresh: true,
        preventPagination: true,
        queryParamFilters: true,
        filterLocal: true,
        editMode: 'dialog',
        alertField: 'notes',
        editButtonName: 'scheduledEquipmentOperationEditButton',
        deleteButtonName: 'scheduledEquipmentOperationDeleteButton',
        enableInsert: false,
        editRule: function(data){
            return data.fiscalNotificationStatus != 'CC';
        },
        deleteRule: function(data){
            return data.fiscalNotificationStatus == null || data.fiscalNotificationStatus == ''
        },
        showDetailDialog: true,
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, defaultSort: 'asc' },
            
            { name: 'sentToDailyPlan', label: 'operativePlan.sentToDailyPlan', type: 'boolean', readOnly: true},
            { label: 'registryPlant.fk', name: 'plant', type: 'select', inLineEdit: true, selectApi: 'masterdata/registry-plant-combo', updateField: 'idRegistryPlant' },
            { label: 'operativePlan.scheduledOperationDate', name: 'scheduledOperationDate', type: 'date', datetime: true, inLineEdit: true }, //EDITABILE
            { label: 'operationType.fk', name: 'registryOperationType', type: 'select', inLineEdit: true, selectApi: 'masterdata/operation-type-combo?tankOperationFlag=S', updateField: 'idRegistryOperationType' },
            { label: 'operationTypeClass.fk', name: 'registryOperationTypeClass', type: 'select', inLineEdit: true, selectApi: 'masterdata/operation-type-class-combo', updateField: 'idRegistryOperationTypeClass' },
            { label: 'tankOperations.tank', name: 'equipmentOperationTank', 
                type: 'select', inLineEdit: true, selectApi: 'masterdata/equipment-combo?type=SERB', 
                
                refreshOnUpdateDialog: true,
                selectApiFn: function(el){
                    return 'masterdata/equipment-combo?type=SERB&plantId=' + el.idRegistryPlant
                },
                updateField: 'idEquipmentOperationTank' },
            {
                label: 'operativePlan.equipmentLayout', name: 'equipmentLayout', type: 'string', action: function (caller, record) {
                    caller.$emit('showEquipmentOperationDialog', record);
                    // caller.$parent.showEquipmentOperationDialog(record);
                }
            },
            { label: 'product.fk', name: 'registryProduct', type: 'select', inLineEdit: true, selectApi: 'masterdata/product-combo', updateField: 'idRegistryProduct' },
            { label: 'operativePlan.quantity', name: 'equipmentOperationQty', type: 'number', inLineEdit: true },
            { label: 'operativePlan.notes', name: 'notes', type: 'string', inLineEdit: true },
            { label: 'ATK', name: 'atk', type: 'number', inLineEdit: true },
            { label: 'ATB', name: 'atb', type: 'number', inLineEdit: true },
            { label: 'operativePlan.company', name: 'company', type: 'select', inLineEdit: true, selectApi: 'masterdata/company-combo', updateField: 'idRegistryCompanyOwner' },
            { label: 'operativePlan.denaturingFlag', name: 'denaturingFlag', type: 'boolean', inLineEdit: true },
            { label: 'operativePlan.utifProgramCode', name: 'utifProgramCode', type: 'string' },
            { label: 'operativePlan.fiscalNotificationStatus', name: 'fiscalNotificationStatus', type: 'string' },
            { label: 'operativePlan.fiscalNotificationCode', name: 'fiscalNotificationCode', type: 'string' },
            { label: 'operativePlan.fiscalNotificationDate', name: 'fiscalNotificationDate', type: 'date' },
        ],
        customActions: [
            {
                title: 'btn.editAssets',
                visible: false,
                name: 'operativePlanUpdateAssetsButton',
                icon: 'pi pi-cog',
                data: {},
                fields: [],
                action: function (record, caller) {
                    caller.$emit('showEquipmentOperationDialog', record);
                }
            },
            {
                title: 'btn.copyToTomorrow',
                visible: false,
                name: 'operativePlanDuplicateAssetsButton',
                icon: 'pi pi-copy',
                data: {},
                fields: [],
                action: function (record, caller) {
                    caller.$emit('copyInTomorrowPlan', record);
                }
            }
        ],

    },
    FiscalNotificationList: {
        title: '',
        api: 'fiscal-notification',
        updateApi: 'fiscal-notification',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        preventAutoLoad: true,
        preventFilters: false,
        preventPagination: true,
        hideRefresh: true,
        filterLocal: true,
        queryParamFilters: true,
        selection: true,
        selectionMode: 'single',
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, defaultSort: 'asc' },

            { label: 'fiscalNotification.utifProgramCode', name: 'utifProgramCode', type: 'string' },
            { label: 'fiscalNotification.notificationNumber', name: 'notificationNumber', type: 'number' },

            { label: 'fiscalNotification.notificationSendDate', name: 'notificationSendDate', type: 'date', datetime: true },
            { label: 'fiscalNotification.fiscalNotificationState', name: 'fiscalNotificationState', type: 'string' },
            { label: 'fiscalNotification.plant', name: 'registryPlant', type: 'string' },
            { label: 'fiscalNotification.nc', name: 'nc', type: 'number' },
            { label: 'fiscalNotification.fiscalCircuitCode', name: 'fiscalCircuitCode', type: 'string' },
            { label: 'fiscalNotification.endOperationDate', name: 'endOperationDate', type: 'date', datetime: true },
            { label: 'fiscalNotification.sentBy', name: 'sentBy', type: 'string' },
            { label: 'fiscalNotification.productDescription', name: 'productDescription', type: 'string' },
            { label: 'fiscalNotification.quantity', name: 'quantity', type: 'string' },
            { label: 'fiscalNotification.voyageNumber', name: 'voyageNumber', type: 'string' },
            { label: 'fiscalNotification.vesselName', name: 'vesselName', type: 'string' },
            { label: 'fiscalNotification.registryOperation', name: 'registryOperation', type: 'string' },
            { label: 'fiscalNotification.note', name: 'note', type: 'string' },
            { label: 'operativePlan.movementInstructionCode', name: 'movementInstructionCode', type: 'string' },
            { label: 'fiscalNotification.tankLayoutString', name: 'tankLayoutString', type: 'string' },
        ],
    },
    DailyPlanValidationMovement: {
        title: '',
        api: 'operative-plan/daily-plan-validation',
        showDetailDialog: true,
        strechColumn: true,
        // updateApi: 'operative-plan/scheduled-equipment-operation',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        preventPagination: true,
        preventAutoLoad: true,
        preventFilters: true,
        hideRefresh: true,
        queryParamFilters: true,
        filterLocal: true,
        inLineEditPermission: 'DailyPlanValidation.edit',
        // selection: true,
        // selectionMode: 'multiple',
        // editMode: 'immediate',
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, defaultSort: 'asc' },
            { label: 'dailyPlanValidation.isValidated', name: 'isValidated', type: 'boolean', inLineEdit: true },
            { label: 'dailyPlanValidation.status', name: 'status', type: 'string' },
            { label: 'fiscalNotification.plant', name: 'registryPlant', type: 'string' },
            // STATO COM?
            // COM DA MODIF
            { label: 'operativePlan.scheduledOperationDate', name: 'scheduledOperationDate', type: 'date', datetime: true },
            { label: 'operationType.fk', name: 'registryOperationType', type: 'select', selectApi: 'masterdata/operation-type-combo' },

            {
                name: 'voyageNumber',
                label: 'movementInstructions.voyageNumber',
                type: 'string'
            },
            { label: 'operativePlan.vessel', name: 'vessel', type: 'string' },
            { label: 'product.fk', name: 'registryProduct', type: 'select', selectApi: 'masterdata/product-combo' },
            { label: 'operativePlan.company', name: 'ownerCompany', type: 'string' },
            { label: 'operativePlan.quantity', name: 'qty', type: 'number' },
            { label: 'operativePlan.minimumQuantity', name: 'qtyMin', type: 'number' },
            { label: 'operativePlan.maximumQuantity', name: 'qtyMax', type: 'number' },
            { label: 'operativePlan.tankListString', name: 'tanksList', type: 'string' },
            { label: 'operativePlan.aspiratorsListString', name: 'aspiratorsList', type: 'string' },
            { label: 'operativePlan.pipilineListString', name: 'pipelinesList', type: 'string' },
            { label: 'operativePlan.pumpsListString', name: 'pumpsList', type: 'string' },
            { label: 'operativePlan.measurersListString', name: 'measurersList', type: 'string' },
            { label: 'operativePlan.mooringPlatform', name: 'mooringPlatform', type: 'select', selectApi: 'masterdata/equipment-combo?type=PTF' },
            { label: 'operativePlan.laycanStartDate', name: 'laycanStartDate', type: 'date', datetime: true },
            { label: 'operativePlan.laycanEndDate', name: 'laycanEndDate', type: 'date', datetime: true },
            { label: 'operativePlan.etaDate', name: 'etaDate', type: 'date', datetime: true },
            {
                label: 'operativePlan.movementInstructionCode', name: 'movementInstructionCode', type: 'string', style: 'text-decoration: underline;', action: function (caller, record) {
                    let params = {
                        movementInstructionCode: record.movementInstructionCode
                    };
                    caller.$store.dispatch("generic/setLoading", true);
                    MovementInstructionService.printMovementInstruction(params, response => {

                        caller.$store.dispatch("generic/setLoading", false);
                        if (response.success) {
                            var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            var fileLink = document.createElement('a');

                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', record.voyageNumber + '-' + record.movementInstructionCode + '.pdf');
                            document.body.appendChild(fileLink);

                            fileLink.click();
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })

                }
            },
            { label: 'operativePlan.notes', name: 'notes', type: 'string' },
            { label: 'operativePlan.utifProgramCode', name: 'utifProgramCode', type: 'string' }
        ]

    },
    // DailyPlan: {
    //     id: { readOnly: true , hidden: true, },
    //     idOperativePlan: { readOnly: true , hidden: true},
    //     idRegistryPlant: { readOnly: true , hidden: true},
    //     registryPlant: { readOnly: true , hidden: true},
    //     allowedOperationFlag: { readOnly: true , hidden: true},
    //     allowedOperationIcon: { readOnly: true , hidden: true},
    //     isSelected: { readOnly: true , hidden: true},
    //     selected: { readOnly: true , hidden: true},
    //     status: { readOnly: true , hidden: false},
    //     statusInfoilFlag: { readOnly: true , hidden: true},
    //     statusInfoilIcon: { readOnly: true , hidden: true},
    //     idScheduledOperation: { readOnly: true , hidden: true},
    //     idRegistryOperationType: { readOnly: true , hidden: true},
    //     registryOperationType: { readOnly: true , hidden: false},
    //     voyageNumber: { readOnly: true , hidden: false},
    //     vessel: { readOnly: true , hidden: false},
    //     idRegistryProduct: { readOnly: true , hidden: true},
    //     registryProduct: { readOnly: true , hidden: false},
    //     tank: { readOnly: true , hidden: false},
    //     idTank: { readOnly: true , hidden: true},
    //     equipmentLayout: { readOnly: true , hidden: true},
    //     tanksList: { readOnly: true , hidden: false},
    //     aspiratorsList: { readOnly: true , hidden: false},
    //     pipelinesList: { readOnly: true , hidden: false},
    //     pumpsList: { readOnly: true , hidden: false},
    //     measurersList: { readOnly: true , hidden: false},
    //     mooringPlatform: { readOnly: true , hidden: false},
    //     qty: { readOnly: true , hidden: false},
    //     qtyMin: { readOnly: true , hidden: false},
    //     qtyMax: { readOnly: true , hidden: false},
    //     idFiscalNotification: { readOnly: true , hidden: true},
    //     fiscalNotificationCode: { readOnly: true , hidden: false},
    //     fiscalNotificationDate: { readOnly: true , hidden: false},
    //     fiscalNotificationInfoilCode: { readOnly: true , hidden: false},
    //     fiscalNotificationInfoilDate: { readOnly: true , hidden: false},
    //     ownerCompany: { readOnly: true , hidden: false},
    //     scheduledOperationDate: { readOnly: true , hidden: false},
    //     etaDate: { readOnly: true , hidden: false},
    //     laycanStartDate: { readOnly: true , hidden: false},
    //     laycanEndDate: { readOnly: true , hidden: false},
    //     movementInstructionCode: { readOnly: true , hidden: false},
    //     idMovementInstructionHeader: { readOnly: true , hidden: true},
    //     idMovementInstructionLot: { readOnly: true , hidden: true},
    //     notes: { readOnly: true , hidden: true},
    //     utifProgramCode: { readOnly: true , hidden: true},
    //     isEquipmentOperation: { readOnly: true , hidden: true},
    //     isDeleted: { readOnly: true , hidden: true},
    //     isValidated: { readOnly: true , hidden: true},
    //     isRecurringOperation: { readOnly: true , hidden: true},
    // },
    FiscalNotification: {
        id: { readOnly: true, hidden: true, type: 'number' },
        infoilNumber: { readOnly: true, hidden: false, type: 'number' },
        infoilDate: { readOnly: true, hidden: false, type: 'date' },
        utifProgramCode: { readOnly: true, hidden: false, type: 'text' },
        notificationSendDate: { readOnly: true, hidden: false, type: 'datetime' },
        notificationCode: { readOnly: true, hidden: false, type: 'text' },
        notificationNumber: { readOnly: true, hidden: false, type: 'number' },
        idRegistryFiscalNotificationState: { readOnly: true, hidden: true, type: 'number' },
        fiscalNotificationState: { readOnly: true, hidden: false, type: 'text' },
        idRegistryOperation: { readOnly: true, hidden: true, type: 'number' },
        registryOperation: { readOnly: true, hidden: true, type: 'text' },
        isMovementInstruction: { readOnly: true, hidden: true, type: 'boolean' },
        isBySea: { readOnly: true, hidden: true, type: 'boolean' },
        movementDirectionFlag: { readOnly: true, hidden: true, type: 'text' },
        endOperationDate: { readOnly: true, hidden: true, type: 'datetime' },
        idRegistryProduct: { readOnly: true, hidden: true, type: 'number' },
        productDescription: { readOnly: true, hidden: true, type: 'text' },
        idFiscalCircuit: { readOnly: true, hidden: true, type: 'number' },
        circuitCode: { readOnly: true, hidden: true, type: 'text' },
        nc: { readOnly: true, hidden: true, type: 'text' },
        quantity: { readOnly: true, hidden: ({ isMovementInstruction }) => !isMovementInstruction, type: 'number' },
        sentBy: { readOnly: true, hidden: false, type: 'text' },
        voyageNumber: { readOnly: true, hidden: ({ isMovementInstruction }) => !isMovementInstruction, type: 'text' },
        vesselName: { readOnly: true, hidden: ({ isMovementInstruction, isBySea }) => !isMovementInstruction || !isBySea, type: 'text' },
        portorLocationofFiscalDestinationDescription: { readOnly: true, hidden: ({ isMovementInstruction, isBySea, movementDirectionFlag }) => !isMovementInstruction || !isBySea || (movementDirectionFlag === 'E'), type: 'text' },
        placeOfDeliveryName: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'E', type: 'text' },
        portOrLocationOfLoading: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'U', type: 'text' },
        placeofFiscalDestinationDescription: { readOnly: true, hidden: ({ isMovementInstruction, isBySea, movementDirectionFlag }) => !isMovementInstruction || !isBySea || (movementDirectionFlag === 'E'), type: 'text' },
        customAgencyDescription: { readOnly: true, hidden: ({ isMovementInstruction }) => !isMovementInstruction, type: 'text' },
        idRegistryFiscalPosition: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'E', type: 'number' },
        registryFiscalPositionCode: { readOnly: true, hidden: false, type: 'text' },
        placeOfDeliveryExciseCode: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'E', type: 'text' },
        originCountry: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'U', type: 'text' },
        exciseDuty: { readOnly: true, hidden: ({ movementDirectionFlag }) => movementDirectionFlag === 'E', type: 'number' },
        exciseDue: { readOnly: true, hidden: ({ movementDirectionFlag }) => movementDirectionFlag === 'E', type: 'number' },
        documentReference: { readOnly: true, hidden: ({ isMovementInstruction }) => !isMovementInstruction, type: 'text' },
        note: { readOnly: true, hidden: false, type: 'text' },
        plant: { readOnly: true, hidden: false, type: 'text' },
        totalFiscalMovedWeight: { readOnly: true, hidden: true, type: 'number' },
        totalFiscalMovedVolume: { readOnly: true, hidden: true, type: 'number' },
    },

    FiscalNotificationHeader: {
        id: { readOnly: true, hidden: true, type: 'number' },
        infoilNumber: { readOnly: true, hidden: false, type: 'number' },
        infoilDate: { readOnly: true, hidden: false, type: 'date' },
        utifProgramCode: { readOnly: true, hidden: false, type: 'text' },
        notificationSendDate: { readOnly: (editable) => !editable, hidden: false, type: 'datetime', required: true },
        notificationCode: { readOnly: true, hidden: false, type: 'text' },
        notificationNumber: { readOnly: true, hidden: false, type: 'number' },
        idRegistryFiscalNotificationState: { readOnly: true, hidden: true, type: 'number' },
        fiscalNotificationState: { readOnly: true, hidden: false, type: 'state-combo', format: { label: 'label', value: 'label' } },
        idRegistryOperation: { readOnly: true, hidden: false, type: 'combo', api: 'masterdata/operation-type-combo', format: { label: 'label', value: 'value' } },
        registryOperation: { readOnly: true, hidden: true, type: 'text' },
        isMovementInstruction: { readOnly: true, hidden: true, type: 'boolean' },
        isBySea: { readOnly: true, hidden: true, type: 'boolean' },
        movementDirectionFlag: { readOnly: true, hidden: true, type: 'text' },
        endOperationDate: { readOnly: (editable, header) => !(editable && header.fiscalNotificationState == 'CC'), hidden: false, type: 'datetime', required: ({ isFinal }) => isFinal },
        // endOperationDate: { readOnly: (editable, header) => !(editable && header.isFinal), hidden: false, type: 'datetime', required: ({isFinal}) => isFinal },
        idRegistryProduct: { readOnly: true, hidden: false, type: 'combo', api: 'masterdata/product-combo', format: { label: 'label', value: 'value' } },
        productDescription: { readOnly: true, hidden: true, type: 'text' },
        idFiscalCircuit: { readOnly: true, hidden: true, type: 'number' },
        fiscalCircuitCode: { readOnly: true, hidden: true, type: 'text' },
        nc: { readOnly: true, hidden: true, type: 'text' },
        quantity: { readOnly: (editable) => !editable, hidden: ({ isMovementInstruction }) => !isMovementInstruction, type: 'number' },
        sentBy: { readOnly: true, hidden: false, type: 'text' },
        idRegistryPlant: { readOnly: true, hidden: false, type: 'combo', api: 'masterdata/registry-plant-combo', format: { label: 'label', value: 'value' } },
        voyageNumber: { readOnly: true, hidden: ({ isMovementInstruction }) => !isMovementInstruction, type: 'text' },
        vesselName: { readOnly: true, hidden: ({ isMovementInstruction, isBySea }) => !isMovementInstruction || !isBySea, type: 'text' },
        portOrLocationOfFiscalDestinationDescription: { readOnly: (editable) => !editable, hidden: ({ isMovementInstruction, isBySea, movementDirectionFlag }) => !isMovementInstruction || !isBySea || (movementDirectionFlag === 'E'), type: 'text', required: ({ isMovementInstruction, isBySea, movementDirectionFlag }) => isMovementInstruction && isBySea && movementDirectionFlag != 'E' },
        placeOfDeliveryName: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'E', type: 'text' },
        portOrLocationOfLoading: { readOnly: (editable) => !editable, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'U', type: 'text', required: ({ isMovementInstruction, movementDirectionFlag }) => isMovementInstruction && movementDirectionFlag != 'U' },
        placeOfFiscalDestinationDescription: { readOnly: (editable) => !editable, hidden: ({ isMovementInstruction, isBySea, movementDirectionFlag }) => !isMovementInstruction || !isBySea || (movementDirectionFlag === 'E'), type: 'text' },
        customAgencyDescription: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag != 'U', type: 'text' },
        idRegistryFiscalPosition: { readOnly: (editable) => !editable, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction, type: 'combo', api: 'masterdata/fiscal-position-combo', format: { label: 'label', value: 'value' }, required: ({ isMovementInstruction, movementDirectionFlag }) => isMovementInstruction && movementDirectionFlag != 'E' },
        registryFiscalPositionCode: { readOnly: true, hidden: true, type: 'text' },
        placeOfDeliveryExciseCode: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'E', type: 'text', required: ({ isMovementInstruction, movementDirectionFlag }) => isMovementInstruction && movementDirectionFlag != 'E' },
        originCountry: { readOnly: (editable) => !editable, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'U', type: 'text', required: ({ isMovementInstruction, movementDirectionFlag }) => isMovementInstruction && movementDirectionFlag != 'U' },
        exciseDuty: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'E', type: 'number' },
        exciseDue: { readOnly: true, hidden: ({ isMovementInstruction, movementDirectionFlag }) => !isMovementInstruction || movementDirectionFlag === 'E', type: 'number' },
        documentReference: { readOnly: (editable) => !editable, hidden: ({ isMovementInstruction }) => !isMovementInstruction, type: 'text', required: ({ isMovementInstruction }) => isMovementInstruction },
        note: { readOnly: (editable) => !editable, hidden: false, type: 'text' },
        totalFiscalMovedWeight: { hidden: true, readOnly: true, type: 'number' },
        totalFiscalMovedVolume: { hidden: true, readOnly: true, type: 'number' },
    },

    FiscalNotificationDetails: {
        // isIncluded: { readOnly: true, hidden: false, type: 'boolean' },
        tank: { readOnly: true, hidden: false, type: 'text' },
        operationDate: { readOnly: true, hidden: false, type: 'datetime' },
        fiscalNotificationState: { readOnly: true, hidden: false, type: 'text' },
        fiscalNotificationCircuit: { readOnly: true, hidden: false, type: 'text' },
        isReceiver: { readOnly: true, hidden: false, type: 'text' },
        nc: { readOnly: true, hidden: false, type: 'text' },
        fiscalMovedWeight: { readOnly: true, hidden: false, type: 'number' },
        fiscalMovedVolume: { readOnly: true, hidden: false, type: 'number' },
        fiscalStockWeight: { readOnly: true, hidden: false, type: 'number' },
        fiscalStockVolume: { readOnly: true, hidden: false, type: 'number' },
        waterLevel: { readOnly: true, hidden: false, type: 'number' },
        productLevel: { readOnly: true, hidden: false, type: 'number' },
        denaturantQuantity: { readOnly: true, hidden: false, type: 'number' },
        density15: { readOnly: true, hidden: false, type: 'number' },
        gasPressure: { readOnly: true, hidden: false, type: 'number' },
        temperature: { readOnly: true, hidden: false, type: 'number' },
        sulfurPercentage: { readOnly: true, hidden: false, type: 'number' },
    },
    DailyPlanValidationEquipment: {
        title: '',
        api: 'operative-plan/daily-plan-validation',
        showDetailDialog: true,
        strechColumn: true,
        // updateApi: 'operative-plan/scheduled-equipment-operation',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        preventPagination: true,
        preventAutoLoad: true,
        preventFilters: true,
        hideRefresh: true,
        queryParamFilters: true,
        filterLocal: true,
        inLineEditPermission: 'DailyPlanValidation.edit',
        // selection: true,
        // selectionMode: 'multiple',
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, defaultSort: 'asc' },
            { label: 'dailyPlanValidation.isValidated', name: 'isValidated', type: 'boolean', inLineEdit: true, trueValue: 'M', falseValue: 'R' },
            { label: 'dailyPlanValidation.status', name: 'status', type: 'string' },
            { label: 'fiscalNotification.plant', name: 'registryPlant', type: 'string' },
            // STATO COM?
            // COM DA MODIF
            { label: 'operativePlan.scheduledOperationDate', name: 'scheduledOperationDate', type: 'date', datetime: true },
            { label: 'operationType.fk', name: 'registryOperationType', type: 'select', selectApi: 'masterdata/operation-type-combo' },
            { label: 'tankOperations.tank', name: 'tank', type: 'string' },
            {
                label: 'operativePlan.equipmentLayout', name: 'equipmentLayout', type: 'string', action: function (caller, record) {
                    if (record.isRecurringOperation) {
                        caller.$emit('showEquipmentOperationDialog', record);
                    }
                }
            },
            { label: 'product.fk', name: 'registryProduct', type: 'select', selectApi: 'masterdata/product-combo' },
            { label: 'operativePlan.quantity', name: 'qty', type: 'number' },
            { label: 'operativePlan.company', name: 'ownerCompany', type: 'string' },
            { label: 'operativePlan.notes', name: 'notes', type: 'string' },
            { label: 'operativePlan.utifProgramCode', name: 'utifProgramCode', type: 'string' }
        ]
    },
    VesselProtocol: {
        dataFields: [
            'protocolNumber',
            'operationType',
            'originCountry',
            'voyageNumber',
            'vessel',
            'destinationCountry',
            'plant',
            'productDescription',
            'customer',
            'incoterms',
            'combinedNomenclature',
            'inspectionCompany',
            'externalVoyageNumber',
            'fiscalCommunicationNumber',
            'cargoDestination',
        ],
        assetsFields: [
            'tanks', 'pumps', 'aspirations', 'pipes', 'measurers', "platform"
        ],
        customsInformation: {
            fields: {
                notice: [
                    {
                        name: 'noticeECustomBillCode',
                        type: 'string',
                        label: 'billCode'
                    },
                    {
                        name: 'noticeECustomBillDate',
                        type: 'date',
                        label: 'billDate'
                    },
                ],
                final: [
                    {
                        name: 'finalECustomBillCode',
                        type: 'string',
                        label: 'billCode'
                    },
                    {
                        name: 'finalECustomBillDate',
                        type: 'date',
                        label: 'billDate',
                    },
                    {
                        name: 'weight',
                        type: 'number',
                        label: 'declaredQuantity',
                    },
                    {
                        name: 'registerType',
                        type: 'string',
                        label: 'registerType',
                    },
                    {
                        name: 'registerDate',
                        type: 'date',
                        label: 'registerDate',
                    },
                    {
                        name: 'eRegistrationCode',
                        type: 'string',
                        label: 'registerNumber',
                    },
                    {
                        name: 'field22',
                        type: 'decimal',
                        digits: 2,
                        label: 'field22',
                    },
                    {
                        name: 'field33',
                        type: 'string',
                        label: 'field33',
                    },
                    {
                        name: 'statisticValue',
                        type: 'decimal',
                        digits: 2,
                        label: 'field46',
                    },
                    {
                        name: 'totalTaxAmount',
                        type: 'decimal',
                        digits: 2,
                        label: 'field47',
                    },
                    {
                        name: 'a93Number',
                        type: 'string',
                        label: 'a93Number',
                    },
                    {
                        name: 'periodicPaymentExpiration',
                        type: 'date',
                        label: 'a93Date',
                    },
                    {
                        name: 'mrn',
                        type: 'string',
                        label: 'mrn',
                    },
                    {
                        name: 'customScheme',
                        type: 'string',
                        label: 'customsClearanceProcedure',
                    },
                    {
                        name: 'releaseCode',
                        type: 'string',
                        label: 'releaseCode',
                    },
                ],
                incomingGoodsManifest: [
                    {
                        name: 'a2Number',
                        type: 'string',
                        label: 'a2Number'
                    },
                    {
                        name: 'a2Date',
                        type: 'date',
                        label: 'a2Date'
                    },
                ],
            }
        },
        valueReviewTable: {
            title: 'valueReviewTable',
            api: 'vessel-protocol-ci-value-review-declaration',
            idColumn: "id",
            enableUpdate: false,
            enableDelete: true,
            deleteButtonName: 'vesselProtocolCustomsInformationValueReviewDeleteButton',
            enableInsert: false,
            preventAutoLoad: true,
            preventFilters: true,
            hideRefresh: true,
            queryParamFilters: false,
            preventPagination: true,
            columns: [
                { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, formHide: true, defaultSort: 'asc' },
                { label: 'vesselProtocol.billCode', name: 'billCode', type: 'string' },
                { label: 'vesselProtocol.registerType', name: 'registrationType', type: 'string' },
                { label: 'vesselProtocol.registerDate', name: 'registrationDate', type: 'date', datetime: true },
                { label: 'vesselProtocol.registerNumber', name: 'registrationNumber', type: 'string' },
                { label: 'vesselProtocol.note', name: 'notes', type: 'string' },
            ]
        },
        incomingGoodsManifestTable: {
            title: 'incomingGoodsManifestTable',
            api: 'vessel-protocol-ci-incoming-goos-manifest',
            idColumn: "id",
            enableUpdate: false,
            enableDelete: true,
            deleteButtonName: 'vesselProtocolCustomsInformationIncomingGoodsManifestDeleteButton',
            enableInsert: false,
            preventAutoLoad: true,
            preventFilters: true,
            hideRefresh: true,
            queryParamFilters: false,
            preventPagination: true,
            showTotalQuantity: true,
            columns: [
                { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, formHide: true, defaultSort: 'asc' },
                { label: 'vesselProtocol.numberA3', name: 'number', type: 'string' },
                { label: 'vesselProtocol.date', name: 'date', type: 'date', datetime: true },
                { label: 'vesselProtocol.quantity', name: 'quantity', type: 'number' },
            ]
        },
        extraCEEGoodsDestinationsTable: {
            title: '',
            api: 'vessel-protocol-ci-goods-destination',
            idColumn: "id",
            enableDelete: true,
            deleteButtonName: 'vesselProtocolCustomsInformationExtraCEEGoodsDestinationsDeleteButton',
            preventAutoLoad: true,
            preventFilters: true,
            hideRefresh: true,
            queryParamFilters: false,
            preventPagination: true,
            showTotalQuantity: true,
            columns: [
                { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, formHide: true, defaultSort: 'asc' },
                { label: 'vesselProtocol.country', name: 'country', type: 'string', formHide: true },
                {
                    name: 'idCountry',
                    label: 'vesselProtocol.country',
                    type: 'select',
                    selectApi: 'masterdata/country-and-territory-combo?getOnlyValid=true&fiscalDestinationFlag=EST',
                    selectFormat: { label: 'label', value: 'value' },
                    gridHide: true,
                    validations: {
                        required: true
                    }
                },
                { label: 'vesselProtocol.quantity', name: 'quantity', type: 'number' },
            ]
        },
        CEEGoodsDestinationsTable: {
            title: '',
            api: 'vessel-protocol-ci-goods-destination',
            idColumn: "id",
            enableDelete: true,
            deleteButtonName: 'vesselProtocolCustomsInformationCEEGoodsDestinationsDeleteButton',
            preventAutoLoad: true,
            preventFilters: true,
            hideRefresh: true,
            queryParamFilters: false,
            preventPagination: true,
            showTotalQuantity: true,
            columns: [
                { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, formHide: true, defaultSort: 'asc' },
                { label: 'vesselProtocol.country', name: 'country', type: 'string', formHide: true },
                {
                    name: 'idCountry',
                    label: 'vesselProtocol.country',
                    type: 'select',
                    selectApi: 'masterdata/country-and-territory-combo?getOnlyValid=true&fiscalDestinationFlag=UE',
                    selectFormat: { label: 'label', value: 'value' },
                    gridHide: true,
                    validations: {
                        required: true
                    }
                },
                { label: 'vesselProtocol.quantity', name: 'quantity', type: 'number' },
            ]
        },
        loading: {
            fieldsets: {
                shoreData: {
                    label: 'vesselProtocol.shoreData',
                    fields: [
                        {
                            label: 'expectedQuantity',
                            name: 'expectedQuantity',
                            type: 'decimal',
                            digits: 3,
                            readOnly: true,
                            uom: 'TM'
                        },
                        {
                            label: 'shoreWeightBoL',
                            name: 'shoreWeightBoL',
                            type: 'number',
                            readOnly: true,
                            uom: 'KG'
                        },
                        {
                            label: 'shoreVolumeBoL',
                            name: 'shoreVolumeBoL',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT at 15 °C'
                        },
                        {
                            label: 'density',
                            name: 'density',
                            type: 'decimal',
                            digits: 4,
                            readOnly: false,
                            uom: 'LT/KG'
                        },
                        {
                            label: 'sulfurPercentage',
                            name: 'sulfurPercentage',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false,
                            uom: '%'
                        },
                    ]
                },
                shipShoreDataComparison: {
                    label: 'vesselProtocol.shipShoreDataComparison',
                    fields: [
                        {
                            label: 'shipLessShoreWeight',
                            name: 'shipLessShoreWeight',
                            type: 'number',
                            readOnly: true,
                            uom: 'KG',
                            percentageField: 'percShipLessShoreWeight'
                        },
                        {
                            label: 'shipWithVEFLessShoreWeight',
                            name: 'shipWithVEFLessShoreWeight',
                            type: 'number',
                            readOnly: true,
                            uom: 'KG',
                            percentageField: 'percShipWithVEFLessShoreWeight'
                        },
                        {
                            label: 'shipWithVEFLessShoreVolume',
                            name: 'shipWithVEFLessShoreVolume',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT at 15 °C',
                            percentageField: 'percShipWithVEFLessShoreVolume'
                        },
                    ]
                },
                shipData: {
                    label: 'vesselProtocol.shipData',
                    fields: [
                        {
                            label: 'measuredOnBoardWeight',
                            name: 'measuredOnBoardWeight',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: 'TM'
                        },
                        {
                            label: 'measuredOnBoardVolume',
                            name: 'measuredOnBoardVolume',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT at 15 °C'
                        },
                        {
                            label: 'vef',
                            name: 'vef',
                            type: 'decimal',
                            digits: 4,
                            readOnly: false
                        },
                        {
                            label: 'measuredOnBoardWeightKgVEFApplied',
                            name: 'measuredOnBoardWeightKgVEFApplied',
                            type: 'number',
                            readOnly: true,
                            uom: 'KG'
                        },
                        {
                            label: 'measuredOnBoardVolumeLtVEFApplied',
                            name: 'measuredOnBoardVolumeLtVEFApplied',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT at 15 °C'
                        },
                    ]
                },
                receiverShoreDifferences: {
                    label: 'vesselProtocol.receiverShoreDifferences',
                    fields: [
                        {
                            label: 'receiverLessShoreWeight',
                            name: 'receiverLessShoreWeight',
                            type: 'number',
                            readOnly: true,
                            uom: 'KG',
                            percentageField: 'percReceiverLessShoreWeight'
                        },
                        {
                            label: 'receiverLessShoreVolume',
                            name: 'receiverLessShoreVolume',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT',
                            percentageField: 'percReceiverLessShoreVolume'
                        },
                    ]
                },
                receiverData: {
                    label: 'vesselProtocol.receiverData',
                    fields: [
                        {
                            label: 'receiverMeasuredWeightKg',
                            name: 'receiverMeasuredWeightKg',
                            type: 'number',
                            readOnly: false,
                            uom: 'KG'
                        },
                        {
                            label: 'receiverMeasuredVolumeLt',
                            name: 'receiverMeasuredVolumeLt',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT at 15 °C'
                        },
                    ]
                },
                receiverShipDifferences: {
                    label: 'vesselProtocol.receiverShipDifferences',
                    fields: [
                        {
                            label: 'receiverLessShipWithVEFWeight',
                            name: 'receiverLessShipWithVEFWeight',
                            type: 'number',
                            readOnly: true,
                            uom: 'KG',
                            percentageField: 'percReceiverLessShipWithVEFWeight'
                        },
                        {
                            label: 'receiverLessShipWithVEFVolume',
                            name: 'receiverLessShipWithVEFVolume',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT',
                            percentageField: 'percReceiverLessShipWithVEFVolume'
                        },
                    ]
                },
                harbourTaxes: {
                    label: 'vesselProtocol.harbourTaxes',
                    fields: [
                        {
                            label: 'harbourTaxes',
                            name: 'harbourTaxes',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                    ]
                },
                notes: {
                    label: 'vesselProtocol.notes',
                    fields: [
                        {
                            label: 'notes',
                            name: 'notes',
                            type: 'textarea',
                            readOnly: false
                        },
                    ]
                },
            }
        },
        discharge: {
            fieldsets: {
                billOfLadingData: {
                    label: 'vesselProtocol.billOfLadingData',
                    fields: [
                        {
                            label: 'billOfLadingGrossWeightKg',
                            name: 'billOfLadingGrossWeightKg',
                            type: 'number',
                            readOnly: false,
                            uom: 'KG'
                        },
                        {
                            label: 'billOfLadingVolumeLt15',
                            name: 'billOfLadingVolumeLt15',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT at 15 °C'
                        },
                        {
                            label: 'billOfLadingDensity',
                            name: 'billOfLadingDensity',
                            type: 'decimal',
                            digits: 4,
                            readOnly: false,
                            uom: 'LT/KG'
                        },
                        {
                            label: 'billOfLadingFX56',
                            name: 'billOfLadingFX56',
                            type: 'decimal',
                            digits: 4,
                            readOnly: false,
                            uom: 'LT/KG'
                        },
                        {
                            label: 'billOfLadingApiGrade',
                            name: 'billOfLadingApiGrade',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                        },
                    ]
                },
                refineryAscertainmentData: {
                    label: 'vesselProtocol.refineryAscertainmentData',
                    fields: [
                        {
                            label: 'finalMeasureNetWeightOfH2OKg',
                            name: 'finalMeasureNetWeightOfH2OKg',
                            type: 'number',
                            readOnly: false,
                            uom: 'KG',
                        },
                        {
                            label: 'finalMeasureNetVolumeOfH2OLt',
                            name: 'finalMeasureNetVolumeOfH2OLt',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT at 15 °C',
                        },
                        {
                            label: 'finalMeasureWaterAndSedimentsHogsHeadPercentage',
                            name: 'finalMeasureWaterAndSedimentsHogsHeadPercentage',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: '%',
                        },
                        {
                            label: 'finalMeasureWaterVolume',
                            name: 'finalMeasureWaterVolume',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT',
                        },
                        {
                            label: 'finalMeasureDensityHogsHead',
                            name: 'finalMeasureDensityHogsHead',
                            type: 'decimal',
                            digits: 4,
                            readOnly: false,
                            uom: 'KG/LT',
                        },
                        {
                            label: 'finalMeasureSulfurPercentage',
                            name: 'finalMeasureSulfurPercentage',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: '%',
                        },
                        {
                            label: 'finalMeasureGrossWeightWithH2O',
                            name: 'finalMeasureGrossWeightWithH2O',
                            type: 'number',
                            readOnly: true,
                            uom: 'KG',
                        },
                        {
                            label: 'finalMeasureGrossVolumeWithH2O',
                            name: 'finalMeasureGrossVolumeWithH2O',
                            type: 'decimal',
                            digits: 3,
                            readOnly: true,
                            uom: 'M3',
                        },
                    ]
                },
                shipFigureOnDeparture: {
                    label: 'vesselProtocol.shipFigureOnDeparture',
                    fields: [
                        {
                            label: 'loadingPortVolumeWithWaterAndOBQLt15',
                            name: 'loadingPortVolumeWithWaterAndOBQLt15',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT at 15 °C'
                        },
                        {
                            label: 'loadingPortVolumeWithWaterAndOBQVEFAppliedLt15',
                            name: 'loadingPortVolumeWithWaterAndOBQVEFAppliedLt15',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT at 15 °C'
                        },
                        {
                            label: 'loadingPortWeightWithWaterAndOBQMetricTons',
                            name: 'loadingPortWeightWithWaterAndOBQMetricTons',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: 'TM'
                        },
                    ]
                },
                volumeTrasportationPerfomance: {
                    label: 'vesselProtocol.volumeTrasportationPerfomance',
                    fields: [
                        {
                            label: 'shipsOnDepartureWithVEFLessGrossVolumeBoL',
                            name: 'shipsOnDepartureWithVEFLessGrossVolumeBoL',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT at 15 °C',
                            percentageField: 'percShipsOnDepartureWithVEFLessGrossVolumeBoL'
                        },
                        {
                            label: 'shipsOnArrivalLessShipsOnDepartureWithVEFLt',
                            name: 'shipsOnArrivalLessShipsOnDepartureWithVEFLt',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT at 15 °C',
                            percentageField: 'percShipsOnArrivalLessShipsOnDepartureWithVEFLt'
                        },
                        {
                            label: 'shipOnArrivalWithVEFLessGrossVolumeBoL',
                            name: 'shipOnArrivalWithVEFLessGrossVolumeBoL',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT at 15 °C',
                            percentageField: 'percShipOnArrivalWithVEFLessGrossVolumeBoL'
                        },
                    ]
                },
                shipFigureOnArrival: {
                    label: 'vesselProtocol.shipFigureOnArrival',
                    fields: [
                        {
                            label: 'loadingPortVolumeWithWaterAndOBQLt15',
                            name: 'dischargePortVolumeWithWaterAndOBQLt15',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT at 15 °C'
                        },
                        {
                            label: 'loadingPortVolumeWithWaterAndOBQVEFAppliedLt15',
                            name: 'dischargePortVolumeWithWaterAndOBQVEFAppliedLt15',
                            type: 'number',
                            readOnly: false,
                            uom: 'LT at 15 °C'
                        },
                        {
                            label: 'loadingPortWeightWithWaterAndOBQMetricTons',
                            name: 'dischargePortWeightWithWaterAndOBQMetricTons',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: 'TM'
                        },
                    ]
                },
                weightCustomsPerformance: {
                    label: 'vesselProtocol.weightCustomsPerformance',
                    fields: [
                        {
                            label: 'shoreNetOfH2OLessGrossWeightBoL',
                            name: 'shoreNetOfH2OLessGrossWeightBoL',
                            type: 'number',
                            readOnly: true,
                            uom: 'KG',
                            percentageField: 'percShoreNetOfH2OLessGrossWeightBoL'
                        },
                    ]
                },
                shipDischargingData: {
                    label: 'vesselProtocol.shipDischargingData',
                    fields: [
                        {
                            label: 'vef',
                            name: 'vef',
                            type: 'decimal',
                            digits: 4,
                            readOnly: false
                        },
                        {
                            label: 'obqCubicMeters',
                            name: 'obqCubicMeters',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: 'M3',
                        },
                        {
                            label: 'dischargeWaterVolumeCubicMeters',
                            name: 'dischargeWaterVolumeCubicMeters',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: 'M3',
                        },
                        {
                            label: 'dischargeVolumeWithoutH2OAndOBQLt15',
                            name: 'dischargeVolumeWithoutH2OAndOBQLt15',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: 'M3',
                        },
                        {
                            label: 'robCubicMeters',
                            name: 'robCubicMeters',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: 'M3',
                        },
                        {
                            label: 'dischargeGrossVolumeVEFAppliedCubicMeters',
                            name: 'dischargeGrossVolumeVEFAppliedCubicMeters',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false,
                            uom: 'M3',
                        },
                        {
                            label: 'dischargeGrossWeightVEFAppliedKg',
                            name: 'dischargeGrossWeightVEFAppliedKg',
                            type: 'number',
                            readOnly: false,
                            uom: 'KG',
                        },
                    ]
                },
                volumeCommercialPerformance: {
                    label: 'vesselProtocol.volumeCommercialPerformance',
                    fields: [
                        {
                            label: 'dischargeGrossVolumeVEFAppliedLessGrossVolumeBoL',
                            name: 'dischargeGrossVolumeVEFAppliedLessGrossVolumeBoL',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT at 15 °C',
                            percentageField: 'percDischargeGrossVolumeVEFAppliedLessGrossVolumeBoL'
                        },
                        {
                            label: 'dischargingLossLT',
                            name: 'dischargingLossLT',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT',
                            percentageField: 'percDischargingLossLT'
                        },
                        {
                            label: 'totalLossLt',
                            name: 'totalLossLt',
                            type: 'number',
                            readOnly: true,
                            uom: 'LT',
                            percentageField: 'percTotalLossLt'
                        },
                    ]
                },
                harbourTaxes: {
                    label: 'vesselProtocol.harbourTaxes',
                    fields: [
                        {
                            label: 'harbourTaxes',
                            name: 'harbourTaxes',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                    ]
                },
                notes: {
                    label: 'vesselProtocol.notes',
                    fields: [
                        {
                            label: 'notes',
                            name: 'notes',
                            type: 'textarea',
                            readOnly: false
                        },
                    ]
                },
            }
        },
        previsionalCommercialInfo: {
            fieldsets: {
                commercialValues: {
                    label: 'vesselProtocol.commercialValues',
                    buttons: [
                        {
                            label: 'vesselProtocol.calculateStatisticValue',
                            icon: 'pi pi-table',
                            action: (data, caller) => {
                                caller.calculateValue('StatisticValue');
                            }
                        },
                    ],
                    fields: [
                        {
                            label: 'currency',
                            name: 'currency',
                            type: 'dropdown',
                            options: ['USD', 'EUR'],
                            readOnly: false
                        },
                        {
                            label: 'customsExchangeRate',
                            name: 'customsExchangeRate',
                            type: 'number',
                            readOnly: true
                        },
                        {
                            label: 'documentNumber',
                            name: 'documentNumber',
                            type: 'text',
                            readOnly: false
                        },
                        {
                            label: 'documentDate',
                            name: 'documentDate',
                            type: 'date',
                            readOnly: false
                        },
                        {
                            label: 'documentQuantity',
                            name: 'documentQuantity',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false
                        },
                        {
                            label: 'documentUnitCost',
                            name: 'documentUnitCost',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'documentTotalAmount',
                            name: 'documentTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'documentAdjustedTotalAmount',
                            name: 'documentAdjustedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'rentalTotalAmount',
                            name: 'rentalTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'rentalAdjustedTotalAmount',
                            name: 'rentalAdjustedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'insuranceTotalAmount',
                            name: 'insuranceTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'insuranceAdjustedTotalAmount',
                            name: 'insuranceAdjustedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'otherCostsTotalAmount',
                            name: 'otherCostsTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'otherCostsAdjustedTotalAmount',
                            name: 'otherCostsAdjustedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'statisticalValueTotalAmount',
                            name: 'statisticalValueTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                    ]
                },
                valueTaxes: {
                    label: 'vesselProtocol.valueTaxes',
                    buttons: [
                        {
                            label: 'vesselProtocol.calculateValues',
                            icon: 'pi pi-table',
                            action: (data, caller) => {
                                caller.calculateValue('Value');
                            }
                        },
                        {
                            label: 'vesselProtocol.calculateTotalTaxes',
                            icon: 'pi pi-table',
                            action: (data, caller) => {
                                caller.calculateValue('TotalTax');
                            }
                        }
                    ],
                    fields: [
                        {
                            label: 'customDutiesRate',
                            name: 'customDutiesRate',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false
                        },
                        {
                            label: 'customDutiesTotalAmount',
                            name: 'customDutiesTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'experimentalStationRate',
                            name: 'experimentalStationRate',
                            type: 'decimal',
                            digits: 4,
                            readOnly: false
                        },
                        {
                            label: 'experimentalStationTotalAmount',
                            name: 'experimentalStationTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: '',
                            name: '',
                            type: '',
                            readOnly: true,
                            hidden: true
                        },
                        {
                            label: 'portTaxTotalAmount',
                            name: 'portTaxTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'taxableVatTotalAmount',
                            name: 'taxableVatTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'vatTotalAmount',
                            name: 'vatTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: '',
                            name: '',
                            type: '',
                            readOnly: true,
                            hidden: true
                        },
                        {
                            label: 'taxTotalAmount',
                            name: 'taxTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'customDutiesRateToBeGuaranteed',
                            name: 'customDutiesRateToBeGuaranteed',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false
                        },
                        {
                            label: 'customDutiesRToBeGuaranteedTotalAmount',
                            name: 'customDutiesRToBeGuaranteedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'intentDeclarationNumber',
                            name: 'intentDeclarationNumber',
                            type: 'number',
                            readOnly: true
                        },
                        {
                            label: 'intentDeclarationDate',
                            name: 'intentDeclarationDate',
                            type: 'date',
                            readOnly: true
                        },
                        {
                            label: 'intentDeclarationTotalAmount',
                            name: 'intentDeclarationTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                    ]
                },
                commercialValuesAndTributesOnReproportionedValues: {
                    label: 'vesselProtocol.commercialValuesAndTributesOnReproportionedValues',
                    buttons: [
                        {
                            label: 'vesselProtocol.calculateReproportionedValues',
                            icon: 'pi pi-table',
                            action: (data, caller) => {
                                let checkField = [
                                    {
                                        name: 'documentReproportionedTotalAmountCheck',
                                        label: 'documentTotalAmount'
                                    },
                                    {
                                        name: 'rentalReproportionedTotalAmountCheck',
                                        label: 'rentalTotalAmount'
                                    },
                                    {
                                        name: 'insuranceReproportionedTotalAmountCheck',
                                        label: 'insuranceTotalAmount'
                                    },
                                    {
                                        name: 'otherCostsReproportionedTotalAmountCheck',
                                        label: 'otherCostsTotalAmount'
                                    }
                                ]
                                let fieldToBeReproportioned = checkField.filter((item) => data[item.name]);

                                if (!fieldToBeReproportioned.length) return

                                let fieldMessage = "";
                                fieldToBeReproportioned.forEach((item, index) => {
                                    fieldMessage += (caller.$t('vesselProtocol.' + item.label) + (index === (fieldToBeReproportioned.length - 1) ? '' : ', '));
                                })
                                let message = caller.$t('vesselProtocol.areYouSureToReproportion') + ' ' + fieldMessage + "?";

                                caller.$confirm.require({
                                    message,
                                    icon: 'pi pi-exclamation-triangle',
                                    acceptLabel: caller.$t('yes'),
                                    rejectLabel: caller.$t('no'),
                                    accept: () => {
                                        caller.calculateValue('ReproportionedValue');
                                    },
                                    reject: () => {
                                        caller.$confirm.close();
                                    }
                                });
                            }
                        },
                        {
                            label: 'vesselProtocol.calculateReliquidatedTaxes',
                            icon: 'pi pi-table',
                            action: (data, caller) => {
                                caller.calculateValue('ReliquidatedValue');
                            }
                        }
                    ],
                    fields: [
                        {
                            label: 'documentReproportionedTotalAmount',
                            name: 'documentReproportionedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false,
                            recalculable: true,
                        },
                        {
                            label: 'customDutiesReliquidatedTotalAmount',
                            name: 'customDutiesReliquidatedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'rentalReproportionedTotalAmount',
                            name: 'rentalReproportionedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false,
                            recalculable: true,
                        },
                        {
                            label: 'experimentalStationReliquidatedTotalAmount',
                            name: 'experimentalStationReliquidatedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'insuranceReproportionedTotalAmount',
                            name: 'insuranceReproportionedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false,
                            recalculable: true,
                        },
                        {
                            label: 'portTaxTotalAmount',
                            name: 'portTaxTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'otherCostsReproportionedTotalAmount',
                            name: 'otherCostsReproportionedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false,
                            recalculable: true,
                        },
                        {
                            label: 'taxableVatReliquidatedTotalAmount',
                            name: 'taxableVatReliquidatedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'taxableVatTotalAmount',
                            name: 'taxableVatTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'statisticalValueReproportionedTotalAmount',
                            name: 'statisticalValueReproportionedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'taxReliquidatedTotalAmount',
                            name: 'taxReliquidatedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                    ]
                }
            }
        },
        finalCommercialInfo: {
            fieldsets: {
                commercialValues: {
                    label: 'vesselProtocol.commercialValues',
                    buttons: [
                        {
                            label: 'vesselProtocol.calculateStatisticValue',
                            icon: 'pi pi-table',
                            action: (data, caller) => {
                                caller.calculateValue('StatisticValue');
                            }
                        },
                    ],
                    documentsTable: {
                        title: 'documentsTable',
                        api: 'vessel-protocol-final-commercial-information-document',
                        idColumn: "id",
                        enableUpdate: false,
                        enableDelete: true,
                        deleteButtonName: 'vesselProtocolFCIDocumentDeleteButton',
                        enableInsert: false,
                        preventAutoLoad: true,
                        preventFilters: true,
                        hideRefresh: true,
                        queryParamFilters: false,
                        preventPagination: true,
                        enableInlineEdit: true,
                        inLineEditPermission: 'vesselProtocolFCIDocumentInlineEdit',
                        columns: [
                            { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, formHide: true, defaultSort: 'asc' },
                            {
                                name: 'documentNumber',
                                label: 'vesselProtocol.invoiceNumber',
                                type: 'string',
                                inLineEdit: true
                            },
                            {
                                name: 'documentType',
                                label: 'documentType.fk',
                                type: 'select',
                                api: 'masterdata/final-commercial-information-document-type-combo',
                                format: { label: 'label', value: 'value' },
                                updateField: 'documentType',
                            },
                            {
                                name: 'documentDate',
                                label: 'vesselProtocol.documentDate',
                                type: 'date',
                                inLineEdit: true
                            },
                            {
                                name: 'documentAmount',
                                label: 'vesselProtocol.documentAmount',
                                type: 'decimal',
                                digits: 2,
                                inLineEdit: true
                            },
                            {
                                name: 'notes',
                                label: 'vesselProtocol.notes',
                                type: 'textarea',
                                inLineEdit: true
                            }
                        ],
                        buttons: [
                            {
                                label: 'btn.add',
                                icon: 'pi pi-plus',
                                action: (caller) => {
                                    caller.addDocumentDialogVisible = true;
                                }
                            }
                        ]
                    }
                    ,
                    fields: [
                        {
                            label: 'currency',
                            name: 'currency',
                            type: 'dropdown',
                            options: ['USD', 'EURO'],
                            readOnly: false
                        },
                        {
                            label: 'customsExchangeRate',
                            name: 'customsExchangeRate',
                            type: 'number',
                            readOnly: true
                        },
                        {
                            label: 'documentQuantityFinal',
                            name: 'totalQuantity',
                            type: 'decimal',
                            digits: 3,
                            readOnly: false
                        },
                        {
                            label: 'totalAmountFinal',
                            name: 'totalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'documentAdjustedTotalAmount',
                            name: 'totalAdjustedAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'rentalTotalAmount',
                            name: 'rentalTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'rentalAdjustedTotalAmount',
                            name: 'rentalAdjustedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'insuranceTotalAmount',
                            name: 'insuranceTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'insuranceAdjustedTotalAmount',
                            name: 'insuranceAdjustedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'otherCostsTotalAmount',
                            name: 'otherCostsTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'otherCostsAdjustedTotalAmount',
                            name: 'otherCostsAdjustedTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'statisticalValueTotalAmount',
                            name: 'statisticalValueTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                    ]
                },
                valueTaxes: {
                    label: 'vesselProtocol.valueTaxes',
                    buttons: [
                        {
                            label: 'vesselProtocol.calculateTotalTaxes',
                            icon: 'pi pi-table',
                            action: (data, caller) => {
                                caller.calculateValue('TotalTax');
                            }
                        }
                    ],
                    fields: [
                        {
                            label: 'customDutiesRate',
                            name: 'customDutiesRate',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'customDutiesTotalAmount',
                            name: 'customDutiesTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'experimentalStationRate',
                            name: 'experimentalStationRate',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'experimentalStationTotalAmount',
                            name: 'experimentalStationTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'portTaxTotalAmount',
                            name: 'portTaxTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: true
                        },
                        {
                            label: 'taxableVatTotalAmount',
                            name: 'taxableVatTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'vatTotalAmount',
                            name: 'vatTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                        {
                            label: 'taxTotalAmount',
                            name: 'taxTotalAmount',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                    ]
                },
                valueReview: {
                    label: 'vesselProtocol.valueReview',
                    buttons: [
                        {
                            label: 'vesselProtocol.calculateValueReview',
                            icon: 'pi pi-table',
                            action: (data, caller) => {
                                caller.calculateValue('ValueRevision');
                            }
                        }
                    ],
                    fields: [
                        {
                            label: 'valueReview',
                            name: 'reviewValue',
                            type: 'decimal',
                            digits: 2,
                            readOnly: false
                        },
                    ]
                }
            }
        },
        paymentsTable: {
            title: 'paymentsTable',
            api: 'vessel-protocol-payments',
            updateApi: 'vessel-protocol-payments',
            idColumn: "id",
            enableUpdate: false,
            enableDelete: true,
            deleteButtonName: 'vesselProtocolPaymentsDeleteButton',
            enableInsert: false,
            preventAutoLoad: true,
            preventFilters: true,
            queryParamFilters: false,
            hideRefresh: true,
            enableInlineEdit: true,
            inLineEditPermission: 'vesselProtocolPaymentsInlineEdit',
            columns: [
                { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, formHide: true, defaultSort: 'asc' },
                { label: 'vesselProtocol.emailStatus', name: 'isNotifiedByEmail', type: 'string', inLineEdit: false, formHide: true, localized: true,
                    renderColumn: (elem) => {
                        if(elem.showMailButton){
                            return elem.isNotifiedByEmail ? "vesselProtocol.emailSent" : "vesselProtocol.emailNotSent"
                        }
                        return "";
                    }
                },
                {
                    label: 'vesselProtocol.paymentType',
                    name: 'idPaymentType',
                    type: 'select',
                    inLineEdit: true,
                    selectApi: 'masterdata/vessel-protocol-payment-type-combo',
                    editEnabled: (data) => !data.isExecuted,
                    gridHide: true
                },
                {
                    label: 'vesselProtocol.paymentType',
                    name: 'paymentType',
                    updateField: 'idPaymentType',
                    type: 'select',
                    inLineEdit: true,
                    selectApi: 'masterdata/vessel-protocol-payment-type-combo',
                    editEnabled: (data) => !data.isExecuted,
                    formHide: true
                },
                { label: 'vesselProtocol.paymentDescription', name: 'paymentDescription', type: 'string', inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.isRiliquidated', name: 'isRiliquidated', type: 'boolean', inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.referenceDate', name: 'referenceDate', type: 'date', datetime: true, inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.isPeriodicPayment', name: 'isPeriodicPayment', type: 'boolean', inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.isSpecialPayment', name: 'isSpecialPayment', type: 'boolean', inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.dueDate', name: 'dueDate', type: 'date', datetime: true, inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.paymentDescription', name: 'paymentDescription', type: 'string', gridHide: true },
                { label: 'vesselProtocol.totalAmount', name: 'totalAmount', type: 'decimal', digits: 2, showZero: true, inLineEdit: true, editEnabled: (data) => !data.isExecuted },              
                { label: 'vesselProtocol.isValueReview', name: 'isValueReview', type: 'boolean', inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.isExecuted', name: 'isExecuted', type: 'boolean', inLineEdit: true },
                { label: 'vesselProtocol.isDueDateModifiable', name: 'isDueDateModifiable', type: 'boolean', readOnly: true, gridHide: true, formHide: true },
                { label: 'vesselProtocol.isValid', name: 'isValid', allowNull: true, type: 'boolean', inLineEdit: false },
                { label: 'vesselProtocol.a93Date', name: 'a93Date', type: 'date', datetime: true, inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.a93Number', name: 'a93Number', type: 'string', inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.a98Date', name: 'a98Date', type: 'date', datetime: true, inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.a98Number', name: 'a98Number', type: 'string', inLineEdit: true, editEnabled: (data) => !data.isExecuted },
                { label: 'vesselProtocol.note', name: 'notes', type: 'textarea', inLineEdit: true, editEnabled: (data) => !data.isExecuted }
            ],
            customActions: [{
                title: 'vesselProtocol.sendEmail',
                visible: (elem) => {
                    return elem.showMailButton && !elem.isNotifiedByEmail;
                },
                cssClass: 'p-button-rounded p-button-success mr-2',
                name: 'vesselProtocolPaymentsDeleteButton',
                icon: 'pi pi-send',
                data: {},
                fields: [],
                action: function (data, caller) {
                    caller.$emit('onShowPaymentEmailDialog', data);
                }
            }],
        },
        reports: [
            {
                code: 7,
                category: "Prospetti - Istanze",
                description: "Istanza di revisione valore",
                fields: [
                    {
                        name: 'receiver',
                        label: 'vesselProtocol.receiver',
                        type: 'textarea',
                        initialValue: (caller) => {
                            console.log(caller);
                            return caller.plant === 'ISAB NORD' ? 'S.o.T. di AUGUSTA' : '';
                        }
                    },
                    {
                        name: 'description',
                        label: 'vesselProtocol.description',
                        type: 'textarea',
                        initialValue: 'La revisione è richiesta in quanto all\'atto della presentazione della dichiarazione di cui trattasi la scrivente non disponeva dei valori definitivi della transazione.'
                    },
                    {
                        name: 'protocolDate',
                        label: 'vesselProtocol.protocolDate',
                        type: 'date',
                        initialValue: null
                    },
                    {
                        name: 'protocolNumber',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string',
                    },

                    {
                        name: 'registryDate',
                        label: 'vesselProtocol.registryDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'registryType',
                        label: 'vesselProtocol.registryType',
                        type: 'string',
                        initialValue: (caller) => {
                            console.log(caller);
                            return caller.registryType;
                        }
                    },
                    {
                        name: 'registerNumber',
                        label: 'vesselProtocol.registerNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return String(caller.registryNumber ?? "");
                        }
                    },
                    {
                        name: 'company',
                        label: 'vesselProtocol.commitment',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.customer;
                        }
                    },
                    {
                        name: 'companyAddress',
                        label: 'vesselProtocol.commitmentAddress',
                        type: 'string'
                    },
                    {
                        name: 'companyPIva',
                        label: 'vesselProtocol.commitmentPIva',
                        type: 'string'
                    },
                    {
                        name: 'excisePayd',
                        label: 'vesselProtocol.excisePayd',
                        type: 'number',
                        digits: 2,
                        initialValue: 0
                    },
                    {
                        name: 'definitiveExcise',
                        label: 'vesselProtocol.definitiveExcise',
                        type: 'number',
                        digits: 2,
                        initialValue: 0
                    },
                    {
                        name: 'exciseDifference',
                        label: 'vesselProtocol.exciseDifference',
                        type: 'number',
                        digits: 2,
                        initialValue: 0
                    },
                    {
                        name: 'ivaPayd',
                        label: 'vesselProtocol.ivaPayd',
                        type: 'number',
                        digits: 2,
                        initialValue: 0
                    },
                    {
                        name: 'definitiveIva',
                        label: 'vesselProtocol.definitiveIva',
                        type: 'number',
                        digits: 2,
                        initialValue: 0
                    },
                    {
                        name: 'interest',
                        label: 'vesselProtocol.interest',
                        type: 'number',
                        digits: 2,
                        initialValue: 0
                    },
                    {
                        name: 'interestPayd',
                        label: 'vesselProtocol.interestPayd',
                        type: 'number',
                        digits: 2,
                        initialValue: 0
                    },
                    {
                        name: 'definitiveInterest',
                        label: 'vesselProtocol.definitiveInterest',
                        type: 'number',
                        digits: 2,
                        initialValue: 0
                    },
                    {
                        name: 'field2',
                        label: 'vesselProtocol.field2',
                        type: 'field',
                        initialValue: 'Data element 3/1 (ex campo 2)',
                        direction: 'D'
                    },
                    {
                        name: 'field8',
                        label: 'vesselProtocol.field8',
                        type: 'field',
                        initialValue: 'Data element 13 03 000 000 (ex campo 8)',
                        direction: 'C'
                    },
                    {
                        name: 'field12',
                        label: 'vesselProtocol.field12',
                        type: 'field',
                        initialValue: 'Data element 4/9 (ex campo 12)',
                        direction: 'D'
                    },
                    {
                        name: 'field20',
                        label: 'vesselProtocol.field20',
                        type: 'field',
                        initialValue: 'Data element 14 01 000 000 (ex campo 20)',
                        direction: 'C'
                    },
                    {
                        name: 'field20D',
                        label: 'vesselProtocol.field20',
                        type: 'field',
                        initialValue: 'Data element 4/1 (ex campo 20)',
                        direction: 'D'
                    },
                    {
                        name: 'field22',
                        label: 'vesselProtocol.field22',
                        type: 'field',
                        initialValue: 'Data element 14 06 000 000 (ex campo 22)',
                        direction: 'C'
                    },
                    {
                        name: 'field22D',
                        label: 'vesselProtocol.field22',
                        type: 'field',
                        initialValue: 'Data element 4/11 (ex campo 22)',
                        direction: 'D'
                    },
                    {
                        name: 'field35',
                        label: 'vesselProtocol.field35',
                        type: 'field',
                        initialValue: 'Data element 6/5 (ex campo 35)',
                        direction: 'D'
                    },
                    {
                        name: 'field38',
                        label: 'vesselProtocol.field38',
                        type: 'field',
                        initialValue: 'Data element 6/1 (ex campo 38)',
                        direction: 'D'
                    },
                    {
                        name: 'field42',
                        label: 'vesselProtocol.field42',
                        type: 'field',
                        initialValue: 'Data element 4/14 (ex campo 42)',
                        direction: 'D'
                    },
                    {
                        name: 'field44',
                        label: 'vesselProtocol.field44',
                        type: 'field',
                        initialValue: 'Data element 12 03 000 000 (ex campo 44)',
                        direction: 'C'
                    },
                    {
                        name: 'field44D',
                        label: 'vesselProtocol.field44',
                        type: 'field',
                        initialValue: 'Data element 2/3 (ex campo 44)',
                        direction: 'D'
                    },
                    {
                        name: 'field45',
                        label: 'vesselProtocol.field45',
                        type: 'field',
                        initialValue: 'Data element 4/9 (ex campo 45)',
                        direction: 'D'
                    },
                    {
                        name: 'field46',
                        label: 'vesselProtocol.field46',
                        type: 'field',
                        initialValue: 'Data element 99 06 001 000 (ex campo 46)',
                        direction: 'C'
                    },
                    {
                        name: 'field46D',
                        label: 'vesselProtocol.field46',
                        type: 'field',
                        initialValue: 'Data element 8/6 (ex campo 46)',
                        direction: 'D'
                    },
                    {
                        name: 'field47A00',
                        label: 'vesselProtocol.field47A00',
                        type: 'field',
                        initialValue: 'Data element 4/4 (ex campo 47-A00)',
                        direction: 'D'
                    },
                    {
                        name: 'field47B00',
                        label: 'vesselProtocol.field47B00',
                        type: 'field',
                        initialValue: 'Data element 4/4 (ex campo 47-B00)',
                        direction: 'D'
                    },
                    {
                        name: 'fields',
                        label: '',
                        type: 'addFields'
                    },
                    {
                        name: 'excludedArea',
                        label: 'vesselProtocol.excludedArea',
                        type: 'textarea',
                        initialValue: '- la liquidazione dei diritti\n- l\'originario regime doganale\n- gli aspetti di ordine "economico" dell\'operazione'
                    },
                    {
                        name: 'note',
                        label: 'vesselProtocol.note',
                        type: 'textarea'
                    },
                    {
                        name: 'signer',
                        label: 'vesselProtocol.signer',
                        type: 'string'
                    },
                ]
            },
            {
                code: 11,
                category: "Comunicazioni per Dogana",
                description: "Destinazione CEE senza INF 3",
                fields: [
                    {
                        name: 'receiverRow1',
                        label: 'vesselProtocol.receiverRow1',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.customsOffice
                    },
                    {
                        name: 'receiverRow2',
                        label: 'vesselProtocol.receiverRow2',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                        name: 'protocolNumber',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return (caller.protocolNumber || "");
                        }
                    },
                    {
                        name: 'comunicationPlace',
                        label: 'vesselProtocol.comunicationPlace',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'comunicationdate',
                        label: 'vesselProtocol.comunicationDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.protocolDate || new Date()
                        }
                    },
                    {
                        name: 'originalDestination',
                        label: 'vesselProtocol.originalDestination',
                        type: 'string'
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productDescription',
                        label: 'vesselProtocol.productDescription',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'registerType',
                        label: 'vesselProtocol.registerType',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.registryType;
                        }
                    },
                    {
                        name: 'registerNumber',
                        label: 'vesselProtocol.registerNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return String(caller.registryNumber ?? "");
                        }
                    },
                    {
                        name: 'registerDate',
                        label: 'vesselProtocol.registerDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'cnc',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.field33;
                        }
                    },
                    {
                        name: 'otherDocument',
                        label: 'vesselProtocol.otherDocument',
                        type: 'stringArray'
                    },
                    {
                        name: 'note',
                        label: 'vesselProtocol.note',
                        type: 'string'
                    },
                    {
                        name: 'destinations',
                        label: 'vesselProtocol.destinationsCee',
                        type: 'objectArray',
                        destinationType: 'CEE',
                        collapsible: true,
                        inputNumber: 4,
                        children: [
                            {
                                name: 'destination',
                                label: 'vesselProtocol.destination',
                                type: 'string',
                            },
                            {
                                name: 'quantity',
                                label: 'vesselProtocol.quantity',
                                type: 'number',
                            },
                            {
                                name: 'taxWarehouse',
                                label: 'vesselProtocol.taxWarehouse',
                                type: 'string',
                            }
                        ]
                    },
                    {
                        name: 'signer',
                        label: 'vesselProtocol.signer',
                        type: 'string'
                    },
                ]
            },
            {
                code: 12,
                category: "Comunicazioni per Dogana",
                description: "Destinazione CEE con INF 3",
                fields: [
                    {
                        name: 'receiverRow1',
                        label: 'vesselProtocol.receiverRow1',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.customsOffice
                    },
                    {
                        name: 'receiverRow2',
                        label: 'vesselProtocol.receiverRow2',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                        name: 'protocolNumber',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return (caller.protocolNumber || "");
                        }
                    },
                    {
                        name: 'comunicationPlace',
                        label: 'vesselProtocol.comunicationPlace',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'comunicationdate',
                        label: 'vesselProtocol.comunicationDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'originalDestination',
                        label: 'vesselProtocol.originalDestination',
                        type: 'string'
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productDescription',
                        label: 'vesselProtocol.productDescription',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'registerType',
                        label: 'vesselProtocol.registerType',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.registryType;
                        }
                    },
                    {
                        name: 'registerNumber',
                        label: 'vesselProtocol.registerNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return String(caller.registryNumber ?? "");
                        }
                    },
                    {
                        name: 'registerDate',
                        label: 'vesselProtocol.registerDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'cnc',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },
                    {
                        name: 'otherDocument',
                        label: 'vesselProtocol.otherDocument',
                        type: 'stringArray'
                    },
                    {
                        name: 'note',
                        label: 'vesselProtocol.note',
                        type: 'string'
                    },
                    {
                        name: 'destinations',
                        label: 'vesselProtocol.destinationsCee',
                        type: 'objectArray',
                        destinationType: 'CEE',
                        collapsible: true,
                        inputNumber: 4,
                        children: [
                            {
                                name: 'destination',
                                label: 'vesselProtocol.destination',
                                type: 'string',
                            },
                            {
                                name: 'quantity',
                                label: 'vesselProtocol.quantity',
                                type: 'number',
                            },
                            {
                                name: 'taxWarehouse',
                                label: 'vesselProtocol.taxWarehouse',
                                type: 'string',
                            }
                        ]
                    },
                    {
                        name: 'signer',
                        label: 'vesselProtocol.signer',
                        type: 'string'
                    },
                ]
            },
            {
                code: 13,
                category: "Comunicazioni per Dogana",
                description: "Destinazione extra CEE",
                fields: [
                    {
                        name: 'receiverRow1',
                        label: 'vesselProtocol.receiverRow1',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.customsOffice
                    },
                    {
                        name: 'receiverRow2',
                        label: 'vesselProtocol.receiverRow2',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                        name: 'protocolNumber',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return (caller.protocolNumber || "");
                        }
                    },
                    {
                        name: 'comunicationPlace',
                        label: 'vesselProtocol.comunicationPlace',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'comunicationdate',
                        label: 'vesselProtocol.comunicationDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'originalDestination',
                        label: 'vesselProtocol.originalDestination',
                        type: 'string'
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productDescription',
                        label: 'vesselProtocol.productDescription',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'registerType',
                        label: 'vesselProtocol.registerType',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.registryType;
                        }
                    },
                    {
                        name: 'registerNumber',
                        label: 'vesselProtocol.registerNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return String(caller.registryNumber ?? "");
                        }
                    },
                    {
                        name: 'registerDate',
                        label: 'vesselProtocol.registerDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'cnc',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },
                    {
                        name: 'otherDocument',
                        label: 'vesselProtocol.otherDocument',
                        type: 'stringArray'
                    },
                    {
                        name: 'note',
                        label: 'vesselProtocol.note',
                        type: 'string'
                    },
                    {
                        name: 'destinations',
                        label: 'vesselProtocol.destinationsCee',
                        type: 'objectArray',
                        destinationType: 'extraCEE',
                        collapsible: true,
                        inputNumber: 4,
                        children: [
                            {
                                name: 'destination',
                                label: 'vesselProtocol.destination',
                                type: 'string',
                            },
                            {
                                name: 'quantity',
                                label: 'vesselProtocol.quantity',
                                type: 'number',
                            },
                            {
                                name: 'taxWarehouse',
                                label: 'vesselProtocol.taxWarehouse',
                                type: 'string',
                            }
                        ]
                    },
                    {
                        name: 'signer',
                        label: 'vesselProtocol.signer',
                        type: 'string'
                    },
                ]
            },
            {
                code: 14,
                category: "Comunicazioni per Dogana",
                description: "Rientro parziale CEE e saldo extra CEE",
                fields: [
                    {
                        name: 'receiverRow1',
                        label: 'vesselProtocol.receiverRow1',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.customsOffice
                    },
                    {
                        name: 'receiverRow2',
                        label: 'vesselProtocol.receiverRow2',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                        name: 'protocolNumber',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return (caller.protocolNumber || "");
                        }
                    },
                    {
                        name: 'comunicationPlace',
                        label: 'vesselProtocol.comunicationPlace',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'comunicationdate',
                        label: 'vesselProtocol.comunicationDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'originalDestination',
                        label: 'vesselProtocol.originalDestination',
                        type: 'string'
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productDescription',
                        label: 'vesselProtocol.productDescription',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'registerType',
                        label: 'vesselProtocol.registerType',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.registryType;
                        }
                    },
                    {
                        name: 'registerNumber',
                        label: 'vesselProtocol.registerNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return String(caller.registryNumber ?? "");
                        }
                    },
                    {
                        name: 'registerDate',
                        label: 'vesselProtocol.registerDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'cnc',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },
                    {
                        name: 'otherDocument',
                        label: 'vesselProtocol.otherDocument',
                        type: 'stringArray'
                    },
                    {
                        name: 'note',
                        label: 'vesselProtocol.note',
                        type: 'string'
                    },
                    {
                        name: 'destinationsCee',
                        label: 'vesselProtocol.destinationsCee',
                        type: 'objectArray',
                        destinationType: 'CEE',
                        collapsible: true,
                        inputNumber: 4,
                        children: [
                            {
                                name: 'destination',
                                label: 'vesselProtocol.destination',
                                type: 'string',
                            },
                            {
                                name: 'quantity',
                                label: 'vesselProtocol.quantity',
                                type: 'number',
                            },
                            {
                                name: 'taxWarehouse',
                                label: 'vesselProtocol.taxWarehouse',
                                type: 'string',
                            }
                        ]
                    },
                    {
                        name: 'destinationsExtraCee',
                        label: 'vesselProtocol.destinationsExtraCee',
                        type: 'objectArray',
                        destinationType: 'extraCEE',
                        collapsible: true,
                        inputNumber: 4,
                        children: [
                            {
                                name: 'destination',
                                label: 'vesselProtocol.destination',
                                type: 'string',
                            },
                            {
                                name: 'quantity',
                                label: 'vesselProtocol.quantity',
                                type: 'number',
                            },
                            {
                                name: 'taxWarehouse',
                                label: 'vesselProtocol.taxWarehouse',
                                type: 'string',
                            }
                        ]
                    },
                    {
                        name: 'signer',
                        label: 'vesselProtocol.signer',
                        type: 'string'
                    },
                ]
            },
            {
                code: 16,
                category: "Lettere in Dogana",
                description: "Richiesta INF 4",
                fields: [
                    {
                        name: 'receiver',
                        label: 'vesselProtocol.receiver',
                        type: 'textarea',
                        initialValue: (caller) => {
                            return Constants.vesselProtocolReports.customsOffice + ((caller.plant === 'ISAB NORD') ? ' - S.O.T. di Augusta' : '')
                        }
                    },
                    {
                        name: 'protocolNumber',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return (caller.protocolNumber || "");
                        }
                    },
                    {
                        name: 'comunicationPlace',
                        label: 'vesselProtocol.comunicationPlace',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'comunicationDate',
                        label: 'vesselProtocol.comunicationDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productDescription',
                        label: 'vesselProtocol.productDescription',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'Rinfusa - ' + caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'registrationBillDate',
                        label: 'vesselProtocol.registrationBillDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date()
                        }
                    },
                    {
                        name: 'codeCNC',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },
                    {
                        name: 'destinationCountry',
                        label: 'vesselProtocol.destinationCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.destination;
                        }
                    },
                    {
                        name: 'reExportCountry',
                        label: 'vesselProtocol.reExportCountry',
                        type: 'string'
                    },
                    {
                        name: 'signer',
                        label: 'vesselProtocol.signer',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.signer
                    },
                ]
            },
            {
                code: 23,
                category: "Comunicazioni per Dogana",
                description: "Valori in dogana",
                fields: [
                    {
                        name: 'idValueType',
                        label: 'vesselProtocol.valueType',
                        type: 'select',
                        initialValue: 1,
                        selectApi: 'masterdata/vessel-protocol-commaercial-information-type-combo',
                        format: { label: 'label', value: 'value' },
                    },
                    {
                        name: 'customsDescription',
                        label: 'vesselProtocol.customsDescription',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.product;
                        }
                    },
                    {
                        name: 'originCountryProduct',
                        label: 'vesselProtocol.originCountryProduct',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.origin;
                        }
                    },
                    {
                        name: 'countryOfOrigin',
                        label: 'vesselProtocol.countryOfOrigin',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.destination;
                        }
                    },
                ]
            },
            {
                code: 24,
                category: "Comunicazioni per Dogana",
                description: "Comunicazione in Dogana per la presentazione del F.O.",
                fields: [
                    // {
                    //     name: 'documentType',
                    //     label: 'vesselProtocol.documentType',
                    //     type: 'string',
                    //     initialValue: 'svincolo merce presentata in procedura ordinaria'
                    // },
                    {
                        name: 'documentType',
                        label: 'vesselProtocol.documentType',
                        type: 'select',
                        // initialValue: 1,
                        staticOptions: [{value: 'svincolo merce presentata in procedura ordinaria'}, {value: 'controllo documentale'}, {value: 'visita merci'}],
                        // selectApi: 'masterdata/vessel-protocol-commaercial-information-type-combo',
                        format: { label: 'value', value: 'value' },
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'mrn',
                        label: 'vesselProtocol.mrn',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'MRN: ' + caller.mrn;
                        }
                    },
                    {
                        name: 'comunicationDate',
                        label: 'vesselProtocol.comunicationDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.protocolDate || new Date()
                        }
                    },
                ]
            },
            {
                code: 28,
                category: "Comunicazioni per Camera di Commercio",
                description: "Annullamento del certificato di origine emesso",
                fields: [
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'string'
                    },
                    {
                        name: 'originCertificateCode',
                        label: 'vesselProtocol.originCertificateCode',
                        type: 'string'
                    },
                    {
                        name: 'protocolNumber',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return (caller.protocolNumber || "");
                        }
                    },
                    {
                        name: 'dateOfCertificate',
                        label: 'vesselProtocol.dateOfCertificate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'notes',
                        label: 'vesselProtocol.notes',
                        type: 'textarea',
                        initialValue: 'La scrivente Raffinerie ISAB s.r.l. di Priolo Gargallo (SR), comunica che a causa di cattivo funzionamento della stampante, lo stampato in oggetto viene annullato'
                    },
                ]
            },
            {
                code: 30,
                category: "Autorizzazioni",
                description: "Presentazione a posteriori dell'EUR 1",
                fields: [
                    {
                        name: 'receiverRow1',
                        label: 'vesselProtocol.receiverRow1',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.customsOffice
                    },
                    {
                        name: 'receiverRow2',
                        label: 'vesselProtocol.receiverRow2',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                        name: 'company',
                        label: 'vesselProtocol.company',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.company
                    },
                    {
                        name: 'taxCodeOrVatNumber',
                        label: 'vesselProtocol.taxCodeOrVatNumber',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.taxCodeOrVatNumber
                    },
                    {
                        name: 'documentNumber',
                        label: 'vesselProtocol.invoiceNumber',
                        type: 'string',
                        initialValue: 'N954'
                    },
                    {
                        name: 'documentDescription',
                        label: 'vesselProtocol.description',
                        type: 'string',
                        initialValue: 'EUR 1'
                    },
                    {
                        name: 'documentIdentify',
                        label: 'vesselProtocol.documentIdentify',
                        type: 'string'
                    },
                    {
                        name: 'authorizationValidityFrom',
                        label: 'vesselProtocol.authorizationValidityFrom',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'authorizationValidityTo',
                        label: 'vesselProtocol.authorizationValidityTo',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'maxExpectedDischarges',
                        label: 'vesselProtocol.maxExpectedDischarges',
                        type: 'number',
                        initialValue: 1
                    },
                    {
                        name: 'documentPlace',
                        label: 'vesselProtocol.documentPlace',
                        type: 'string'
                    },
                    {
                        name: 'documentDate',
                        label: 'vesselProtocol.documentDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'requester',
                        label: 'vesselProtocol.requester',
                        type: 'string'
                    },
                ]
            },
            {
                code: 31,
                category: "Autorizzazioni",
                description: "Richiesta del rilascio a posteriori dell'EUR 1",
                fields: [
                    {
                        name: 'receiverRow1',
                        label: 'vesselProtocol.receiverRow1',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.customsOffice
                    },
                    {
                        name: 'receiverRow2',
                        label: 'vesselProtocol.receiverRow2',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                        name: 'company',
                        label: 'vesselProtocol.company',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.company
                    },
                    {
                        name: 'taxCodeOrVatNumber',
                        label: 'vesselProtocol.taxCodeOrVatNumber',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.taxCodeOrVatNumber
                    },
                    {
                        name: 'documentNumber',
                        label: 'vesselProtocol.invoiceNumber',
                        type: 'string',
                        initialValue: 'N954'
                    },
                    {
                        name: 'documentDescription',
                        label: 'vesselProtocol.description',
                        type: 'string',
                        initialValue: 'EUR 1'
                    },
                    {
                        name: 'documentIdentify',
                        label: 'vesselProtocol.documentIdentify',
                        type: 'string'
                    },
                    {
                        name: 'authorizationValidityFrom',
                        label: 'vesselProtocol.authorizationValidityFrom',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'authorizationValidityTo',
                        label: 'vesselProtocol.authorizationValidityTo',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'documentPlace',
                        label: 'vesselProtocol.documentPlace',
                        type: 'string'
                    },
                    {
                        name: 'documentDate',
                        label: 'vesselProtocol.documentDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'requester',
                        label: 'vesselProtocol.requester',
                        type: 'string'
                    },
                ]
            },
            {
                code: 33,
                category: "Autorizzazioni",
                description: "Richiesta del rilascio a posteriori dell'ATR",
                fields: [
                    {
                        name: 'receiverRow1',
                        label: 'vesselProtocol.receiverRow1',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.customsOffice
                    },
                    {
                        name: 'receiverRow2',
                        label: 'vesselProtocol.receiverRow2',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                        name: 'company',
                        label: 'vesselProtocol.company',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.company
                    },
                    {
                        name: 'taxCodeOrVatNumber',
                        label: 'vesselProtocol.taxCodeOrVatNumber',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.taxCodeOrVatNumber
                    },
                    {
                        name: 'documentNumber',
                        label: 'vesselProtocol.invoiceNumber',
                        type: 'string',
                        initialValue: 'N 018'
                    },
                    {
                        name: 'documentDescription',
                        label: 'vesselProtocol.description',
                        type: 'string',
                        initialValue: 'A.TR.'
                    },
                    {
                        name: 'documentIdentify',
                        label: 'vesselProtocol.documentIdentify',
                        type: 'string',
                        initialValue: 'A.TR.'
                    },
                    {
                        name: 'authorizationValidityFrom',
                        label: 'vesselProtocol.authorizationValidityFrom',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'authorizationValidityTo',
                        label: 'vesselProtocol.authorizationValidityTo',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'maxExpectedDischarges',
                        label: 'vesselProtocol.maxExpectedDischarges',
                        type: 'number',
                        initialValue: 1
                    },
                    {
                        name: 'documentPlace',
                        label: 'vesselProtocol.documentPlace',
                        type: 'string'
                    },
                    {
                        name: 'documentDate',
                        label: 'vesselProtocol.documentDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'requester',
                        label: 'vesselProtocol.requester',
                        type: 'string'
                    },
                ]
            },
            {
                code: 36,
                category: "Certificati",
                description: "Certificato di origine",
                fields: [
                    {
                        name: 'consignorTrader',
                        label: 'vesselProtocol.consignorTrader',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.consignorTrader
                    },
                    {
                        name: 'consignorTraderAddress',
                        label: 'vesselProtocol.consignorTraderAddress',
                        type: 'textarea',
                        initialValue: Constants.vesselProtocolReports.consignorTraderAddress
                    },
                    {
                        name: 'originCountryType',
                        label: 'vesselProtocol.originCountryType',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.originCountryType
                    },
                    {
                        name: 'originCountry',
                        label: 'vesselProtocol.originCountry',
                        type: 'string'
                    },
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'string',
                    },
                    {
                        name: 'consigneeTraderAddress',
                        label: 'vesselProtocol.consigneeTraderAddress',
                        type: 'textarea',
                    },
                    {
                        name: 'destinationCountry',
                        label: 'vesselProtocol.destinationCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.destination;
                        }
                    },
                    {
                        name: 'company',
                        label: 'vesselProtocol.commitment',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.customer;
                        }
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'Rinfusa - ' + caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.ascertainedQuantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.ascertainedQuantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'uom',
                        label: 'vesselProtocol.uom',
                        type: 'string',
                        initialValue: 'KG'
                    },
                    {
                        name: 'notes',
                        label: 'vesselProtocol.notes',
                        type: 'textarea',
                        initialValue: 'CERTIFICATO DI ORIGINE PROFORMA'
                    },
                ]
            },
            {
                code: 37,
                category: "Certificati",
                description: "EUR 1",
                fields: [
                    {
                        name: 'exporterName',
                        label: 'vesselProtocol.exporterName',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.company
                    },
                    {
                        name: 'exporterAddress',
                        label: 'vesselProtocol.exporterAddress',
                        type: 'textarea',
                        initialValue: Constants.vesselProtocolReports.companyAddress
                    },
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'textarea'
                    },
                    {
                        name: 'notes',
                        label: 'vesselProtocol.notes',
                        type: 'textarea',
                        initialValue: 'ISSUED RETROSPECTIVELY'
                    },
                    {
                        name: 'customsOffice',
                        label: 'vesselProtocol.customsOffice',
                        type: 'string',
                        initialValue: (caller) => {
                            return Constants.vesselProtocolReports.customsOffice + ((caller.plant === 'ISAB NORD') ? ' - S.O.T. di Augusta' : '')
                        }
                    },
                    {
                        name: 'registryType',
                        label: 'vesselProtocol.registryType',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.registryType;
                        }
                    },
                    {
                        name: 'registryNumber',
                        label: 'vesselProtocol.registryNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return String(caller.registryNumber ?? "");
                        }
                    },
                    {
                        name: 'registryDate',
                        label: 'vesselProtocol.registryDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'originCountry',
                        label: 'vesselProtocol.originCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.origin;
                        }
                    },
                    {
                        name: 'destinationCountry',
                        label: 'vesselProtocol.destinationCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.destination;
                        }
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'textarea',
                        initialValue: (caller) => {
                            return 'IN BULK - ' + caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'placeOfCertificate',
                        label: 'vesselProtocol.placeOfCertificate',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'dateOfCertificate',
                        label: 'vesselProtocol.dateOfCertificate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'territory',
                        label: 'vesselProtocol.territory',
                        type: 'string',
                        initialValue: 'UNIONE EUROPEA'
                    },
                ]
            },
            {
                code: 38,
                category: "Certificati",
                description: "ATR",
                fields: [
                    {
                        name: 'exporterName',
                        label: 'vesselProtocol.exporterName',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.exporterNameInf3
                    },
                    {
                        name: 'exporterAddress',
                        label: 'vesselProtocol.exporterAddress',
                        type: 'textarea',
                        initialValue: Constants.vesselProtocolReports.exporterAddressInf3
                    },
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'textarea'
                    },
                    {
                        name: 'notes',
                        label: 'vesselProtocol.notes',
                        type: 'textarea',
                        initialValue: 'ISSUED RETROSPECTIVELY'
                    },
                    {
                        name: 'customsOffice',
                        label: 'vesselProtocol.customsOffice',
                        type: 'string',
                        initialValue: (caller) => {
                            return Constants.vesselProtocolReports.atrCustomsOffice + ((caller.plant === 'ISAB NORD') ? ' - S.O.T. di Augusta' : '')
                        }
                    },
                    {
                        name: 'registryType',
                        label: 'vesselProtocol.registryType',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.registryType;
                        }
                    },
                    {
                        name: 'registryNumber',
                        label: 'vesselProtocol.registryNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return String(caller.registryNumber ?? "");
                        }
                    },
                    {
                        name: 'registryDate',
                        label: 'vesselProtocol.registryDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'exportCountry',
                        label: 'vesselProtocol.exportCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.origin;
                        }
                    },
                    {
                        name: 'destinationCountry',
                        label: 'vesselProtocol.destinationCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.destination;
                        }
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'Rinfusa - ' + caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'uom',
                        label: 'vesselProtocol.uom',
                        type: 'string',
                        initialValue: 'KG'
                    },
                    {
                        name: 'placeOfCertificate',
                        label: 'vesselProtocol.placeOfCertificate',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'dateOfCertificate',
                        label: 'vesselProtocol.dateOfCertificate',
                        type: 'date',
                        initialValue: new Date()
                    },
                ]
            },
            {
                code: 39,
                category: "Certificati",
                description: "XAB",
                fields: [
                    {
                        name: 'bdaNumber',
                        label: 'vesselProtocol.bdaNumber',
                        type: 'number',
                        initialValue: 0
                    },
                    {
                        name: 'bdaDate',
                        label: 'vesselProtocol.bdaDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'originPlace',
                        label: 'vesselProtocol.originPlace',
                        type: 'textarea',
                        initialValue: Constants.vesselProtocolReports.originPlaceXAB
                    },
                    {
                        name: 'company',
                        label: 'vesselProtocol.commitment',
                        type: 'textarea',
                        initialValue: Constants.vesselProtocolReports.companyXAB
                    },
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.company + ' - ' + Constants.vesselProtocolReports.declarationPlace
                    },
                    {
                        name: 'transportReason',
                        label: 'vesselProtocol.transportReason',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.direction === 'C' ? 'VENDITA' : 'ACQUISTO';
                        }
                    },
                    {
                        name: 'transportType',
                        label: 'vesselProtocol.transportType',
                        type: 'string'
                    },
                    {
                        name: 'transportVector',
                        label: 'vesselProtocol.transportVector',
                        type: 'number'
                    },
                    {
                        name: 'port',
                        label: 'vesselProtocol.port',
                        type: 'string'
                    },
                    {
                        name: 'goodsDescription',
                        label: 'vesselProtocol.goodsDescription',
                        type: 'string'
                    },
                    {
                        name: 'cnc',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'density',
                        label: 'vesselProtocol.density',
                        type: 'number'
                    },
                ]
            },
            { 
                code: 40, 
                category: "Certificati", 
                description: "INF 3",
                fields: [
                    {
                        name: 'exporterName',
                        label: 'vesselProtocol.exporterName',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.exporterNameInf3
                    },
                    {
                        name: 'exporterAddress',
                        label: 'vesselProtocol.exporterAddress',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.exporterAddressInf3
                    },
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'textarea'
                    },
                    {
                        name: 'registryType',
                        label: 'vesselProtocol.registryType',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.registryType;
                        }
                    },
                    {
                        name: 'registryNumber',
                        label: 'vesselProtocol.registryNumber',
                        type: 'string',
                        initialValue: (caller) => {
                            return String(caller.registryNumber ?? "");
                        }
                    },
                    {
                        name: 'registryDate',
                        label: 'vesselProtocol.registryDate',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'destinationCountry',
                        label: 'vesselProtocol.destinationCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.destination;
                        }
                    },
                    {
                        name: 'cnc',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'Rinfusa - ' + caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'statisticValue',
                        label: 'vesselProtocol.statisticValue',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.statisticValue;
                        }
                    },
                    {
                        name: 'ceeQuantity',
                        label: 'vesselProtocol.ceeQuantity',
                        type: 'number',
                        initialValue: async (caller) => {
                            let total = 0;
                            // caller.newReport['ceeQuantity'] = null;
                            let filters = [
                                {
                                  operator: 'eq',
                                  value: caller.vesselProtocolId,
                                  property: 'IdVesselProtocol'
                                },
                                {
                                  operator: 'eq',
                                  value: 'UE',
                                  property: 'CountryFlag'
                                }
                              ];
                              let params = {
                                  filter: JSON.stringify(filters),
                              }
                              await VesselProtocolService.getVesselProtocolGoodsDestination(params, (response) => {
                                if (response.success){
                                    
                                    let destinations = response.data;
                                    destinations.map(({quantity}) => total += quantity)
                                    //caller.newReport['ceeQuantity'] =  Utils.formatInteger(total);
                                }
                              })
                              return total;
                        }
                    },
                    {
                        name: 'placeOfCertificate',
                        label: 'vesselProtocol.placeOfCertificate',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'dateOfCertificate',
                        label: 'vesselProtocol.dateOfCertificate',
                        type: 'date',
                        initialValue: new Date()
                    },
                ]
            }, 
            { 
                code: 41, 
                category: "Certificati", 
                description: "INF 4",
                fields: [
                    {
                        name: 'exporterName',
                        label: 'vesselProtocol.exporterName',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.exporterNameInf4
                    },
                    {
                        name: 'exporterAddress',
                        label: 'vesselProtocol.exporterAddress',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.exporterAddressInf4
                    },
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'textarea'
                    },
                    {
                        name: 'destinationCountry',
                        label: 'vesselProtocol.destinationCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.destination;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'Rinfusa - ' + caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'notes',
                        label: 'vesselProtocol.notes',
                        type: 'textarea'
                    },
                    {
                        name: 'invoice',
                        label: 'vesselProtocol.invoice',
                        type: 'string'
                    },
                    {
                        name: 'placeOfCertificate',
                        label: 'vesselProtocol.placeOfCertificate',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'dateOfCertificate',
                        label: 'vesselProtocol.dateOfCertificate',
                        type: 'date',
                        initialValue: new Date()
                    },
                ]
            }, 
            { 
                code: 42, 
                category: "Certificati", 
                description: "T2L",
                fields: [
                    {
                        name: 'typeOfCertificate',
                        label: 'vesselProtocol.typeOfCertificate',
                        type: 'string',
                        initialValue: 'T2L'
                    },
                    {
                        name: 'exporterName',
                        label: 'vesselProtocol.exporterName',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.exporterNameInf4
                    },
                    {
                        name: 'exporterAddress',
                        label: 'vesselProtocol.exporterAddress',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.exporterAddressInf4
                    },
                    {
                        name: 'exporterIVA',
                        label: 'vesselProtocol.exporterIVA',
                        type: 'string'
                    },
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'textarea'
                    },
                    {
                        name: 'originCountry',
                        label: 'vesselProtocol.originCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.origin;
                        }
                    },
                    {
                        name: 'destinationCountry',
                        label: 'vesselProtocol.destinationCountry',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.destination;
                        }
                    },
                    {
                        name: 'cnc',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'Rinfusa - ' + caller.product;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'notes',
                        label: 'vesselProtocol.notes',
                        type: 'string',
                        initialValue: 'Procedura domiciliata'
                    },
                    {
                        name: 'formularies',
                        label: 'vesselProtocol.formularies',
                        type: 'string'
                    },
                    {
                        name: 'loadingSchedule',
                        label: 'vesselProtocol.loadingSchedule',
                        type: 'string'
                    },
                    {
                        name: 'goods',
                        label: 'vesselProtocol.goods',
                        type: 'number',
                        initialValue: 1
                    },
                    {
                        name: 'totalPackages',
                        label: 'vesselProtocol.totalPackages',
                        type: 'number',
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'flag',
                        label: 'vesselProtocol.flag',
                        type: 'string'
                    },
                    {
                        name: 'ctr',
                        label: 'vesselProtocol.ctr',
                        type: 'string'
                    },
                    {
                        name: 'transportIdentity',
                        label: 'vesselProtocol.transportIdentity',
                        type: 'string'
                    },
                    {
                        name: 'transportMode',
                        label: 'vesselProtocol.transportMode',
                        type: 'string'
                    },
                    {
                        name: 'loadingPlace',
                        label: 'vesselProtocol.loadingPlace',
                        type: 'string'
                    },
                    {
                        name: 'summaryDeclaration',
                        label: 'vesselProtocol.summaryDeclaration',
                        type: 'string'
                    },
                    {
                        name: 'specialMentions',
                        label: 'vesselProtocol.specialMentions',
                        type: 'string'
                    },
                    {
                        name: 'placeOfCertificate',
                        label: 'vesselProtocol.placeOfCertificate',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                        name: 'dateOfCertificate',
                        label: 'vesselProtocol.dateOfCertificate',
                        type: 'date',
                        initialValue: new Date()
                    },
                ]
            }, 
            { 
                code: 44, 
                category: "Certificati", 
                description: "Dichiarazione di origine",
                fields: [
                    {
                        name: 'company',
                        label: 'vesselProtocol.company',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.company
                    },
                    {
                        name: 'registrant',
                        label: 'vesselProtocol.registrant',
                        type: 'string'
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'Rinfusa - ' + caller.product;
                        }
                    },
                    {
                        name: 'port',
                        label: 'vesselProtocol.port',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.plant === 'ISAB SUD' ? 'S. Panagia Bay-ISAB Refinery - Italy' : 'Augusta - ISAB Refinery - Italy';
                        }
                    },
                    {
                        name: 'cnc',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number',
                        initialValue: (caller) => {
                            return caller.quantity;
                        }
                    },
                    {
                        name: 'dateBoL',
                        label: 'vesselProtocol.dateBoL',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'declarationDate',
                        label: 'vesselProtocol.declarationDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'declarationPlace',
                        label: 'vesselProtocol.declarationPlace',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.declarationPlace
                    },
                ]
            }, 
            { 
                code: 45, 
                category: "Certificati", 
                description: "Crude oil and mineral fuel declaration",
                fields: [
                    {
                        name: 'company',
                        label: 'vesselProtocol.company',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.company
                    },
                    {
                        name: 'originCountry',
                        label: 'vesselProtocol.originCountry',
                        type: 'string'
                    },
                    {
                        name: 'registrant',
                        label: 'vesselProtocol.registrant',
                        type: 'string'
                    },
                    {
                        name: 'vesselName',
                        label: 'vesselProtocol.vesselName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'N/c ' + caller.vessel;
                        }
                    },
                    {
                        name: 'productName',
                        label: 'vesselProtocol.productName',
                        type: 'string',
                        initialValue: (caller) => {
                            return 'Rinfusa - ' + caller.product;
                        }
                    },
                    {
                        name: 'port',
                        label: 'vesselProtocol.port',
                        type: 'string'
                    },
                    {
                        name: 'cncCode',
                        label: 'vesselProtocol.cnc',
                        type: 'string',
                        initialValue: (caller) => {
                            return caller.combinedNomenclature;
                        }
                    },

                    {
                        name: 'quantity',
                        label: 'vesselProtocol.quantity',
                        type: 'number'
                    },
                    {
                        name: 'dateBoL',
                        label: 'vesselProtocol.dateBoL',
                        type: 'date',
                        initialValue: (caller) => {
                            return caller.registryDate || new Date();
                        }
                    },
                    {
                        name: 'declarationDate',
                        label: 'vesselProtocol.declarationDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'declarationPlace',
                        label: 'vesselProtocol.declarationPlace',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.declarationPlace
                    },
                ]
            }, 
            { 
                code: 47, 
                category: "Lettere in Dogana", 
                description: "Consegna originale EUR 1",
                fields: [
                    {
                      name: 'company',
                      label: 'vesselProtocol.company',
                      type: 'string',
                      initialValue: Constants.vesselProtocolReports.company
                    },
                    {
                        name: 'receiverRow1',
                        label: 'vesselProtocol.receiverRow1',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.customsOffice
                    },
                    {
                        name: 'receiverRow2',
                        label: 'vesselProtocol.receiverRow2',
                        type: 'string',
                        initialValue: (caller) => {
                            return ((caller.plant === 'ISAB NORD') ? 'S.O.T. di Augusta' : '');
                        }
                    },
                    {
                      name: 'protocolNumber',
                      label: 'vesselProtocol.protocolNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        return (caller.protocolNumber || "");
                        }
                    },
                    {
                      name: 'certificateNumber',
                      label: 'vesselProtocol.certificateNumber',
                      type: 'string'
                    },
                    {
                      name: 'certificateDate',
                      label: 'vesselProtocol.certificateDate',
                      type: 'date',
                      initialValue: new Date()
                    },
                    {
                      name: 'vesselName',
                      label: 'vesselProtocol.vesselName',
                      type: 'string',
                      initialValue: (caller) => {
                        return 'N/c ' + caller.vessel;
                      }
                    },
                    {
                      name: 'productName',
                      label: 'vesselProtocol.productName',
                      type: 'string',
                      initialValue: (caller) => {
                          return caller.product;
                      }
                    },
                    {
                        name: 'selectedQuantity',
                        label: 'vesselProtocol.quantity',
                        type: 'selectQuantity',
                        selectApi: 'vessel-protocol-get-aggregate-lot-quantity',
                        format: { label: 'label', value: 'value' }
                    },
                    {
                      name: 'quantity',
                      label: 'vesselProtocol.quantity',
                      type: 'number',
                      initialValue: (caller) => {
                        return caller.quantity;
                    }
                    },
                    {
                      name: 'registryType',
                      label: 'vesselProtocol.registryType',
                      type: 'string',
                      initialValue: (caller) => {
                          return caller.registryType;
                      }
                    },
                    {
                      name: 'registryNumber',
                      label: 'vesselProtocol.registryNumber',
                      type: 'string',
                      initialValue: (caller) => {
                          return String(caller.registryNumber ?? "");
                      }
                    },
                    {
                      name: 'registryDate',
                      label: 'vesselProtocol.registryDate',
                      type: 'date',
                      initialValue: new Date()
                    },
                    {
                      name: 'authorizationNumber',
                      label: 'vesselProtocol.authorizationNumber',
                      type: 'string'
                    },
                    {
                      name: 'authorizationDate',
                      label: 'vesselProtocol.authorizationDate',
                      type: 'date',
                      initialValue: new Date()
                    },
                    {
                      name: 'documentDate',
                      label: 'vesselProtocol.documentDate',
                      type: 'date',
                      initialValue: new Date()
                    },
                    {
                      name: 'documentPlace',
                      label: 'vesselProtocol.documentPlace',
                      type: 'string',
                      initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                  ]
            }, 
            { 
                code: 52, 
                category: "Lettere ai Committenti", 
                description: "Consegna bolletta export",
                fields: [
                    {
                      name: 'isValueReview',
                      label: 'vesselProtocol.isValueReview',
                      type: 'boolean',
                      initialValue: false
                    },
                    {
                      name: 'isOriginalBillTransmission',
                      label: 'vesselProtocol.isOriginalBillTransmission',
                      type: 'boolean',
                      initialValue: false
                    },
                    {
                      name: 'protocolNumber',
                      label: 'vesselProtocol.protocolNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        return (caller.protocolNumber || "0");
                      }
                    },
                    {
                      name: 'consigneeTrader',
                      label: 'vesselProtocol.consigneeTrader',
                      type: 'textarea',
                      initialValue: 'Per archivio'
                    },
                    {
                      name: 'description',
                      label: 'vesselProtocol.description',
                      type: 'textarea',
                      dependence: 'isOriginalBillTransmission',
                      initialValue: (caller, data) => {
                        let mrn = caller.mrn ?? '';
                        //let registryType = caller.registryType ?? '';
                        let registryDate = caller.registryDate ? Utils.formatDate(caller.registryDate) : '';
                        return (data?.isOriginalBillTransmission ? '(in originale) ' : '') + 'MRN' +' n° ' + mrn + ' del ' + registryDate
                      }
                    },
                    {
                      name: 'date',
                      label: 'vesselProtocol.date',
                      type: 'date',
                      initialValue: new Date()
                    },
                    {
                      name: 'place',
                      label: 'vesselProtocol.place',
                      type: 'string',
                      initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                      name: 'subject',
                      label: 'vesselProtocol.subject',
                      type: 'string',
                      dependence: 'isValueReview',
                      initialValue: (caller, data) => {
                        return (data?.isValueReview ? 'DI REVISIONE' : '') + ' - RIF. N/c ' + caller.vessel + ' (' + caller.voyageNumber + ')'
                      }
                    },
                  ]
            }, 
            { 
                code: 53, 
                category: "Lettere ai Committenti", 
                description: "Trasmissione documenti amministrativi",
                fields: [
                    {
                        name: 'protocolNumberString',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string'
                    },  
                    {
                      name: 'isValueReview',
                      label: 'vesselProtocol.isValueReview',
                      type: 'boolean',
                      initialValue: false
                    },
                    {
                      name: 'date',
                      label: 'vesselProtocol.date',
                      type: 'date',
                      initialValue: new Date()
                    },
                    {
                      name: 'consigneeTrader',
                      label: 'vesselProtocol.consigneeTrader',
                      type: 'textarea',
                      initialValue: Constants.vesselProtocolReports.consigneeTrader53
                    },
                    {
                      name: 'description',
                      label: 'vesselProtocol.description',
                      type: 'textarea',
                      dependence: 'isValueReview',
                      initialValue: (caller, data) => {
                        //let registryType = caller.registryType ?? '';
                        let mrn = caller.mrn ?? '';
                        let registryDate = caller.registryDate ? Utils.formatDate(caller.registryDate) : '';
                        return 'Copia dichiarazione Telematica MRN'+' n° ' + mrn + ' del ' + registryDate + (data?.isValueReview ? ' con attestazione di  avvenuta revisione del valore da parte dell’Ufficio delle Dogane di Siracusa S.O.T. di Augusta' : '')
                      }
                    },
                    {
                      name: 'place',
                      label: 'vesselProtocol.place',
                      type: 'string',
                      initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                      name: 'subject',
                      label: 'vesselProtocol.allocation',
                      type: 'string',
                      initialValue: (caller) => {
                          return caller.voyageNumber;
                      }
                    },
                    {
                      name: 'vesselName',
                      label: 'vesselProtocol.vesselName',
                      type: 'string',
                      dependence: 'isValueReview',
                      initialValue: (caller, data) => {
                          return data?.isValueReview ? 'Revisione valore' : ('N/c ' + caller.vessel);
                      }
                    },
                    {
                      name: 'signer',
                      label: 'vesselProtocol.signer',
                      type: 'string'
                    }
                  ]
            }, 
            { 
                code: 54, 
                category: "Lettere ai Committenti", 
                description: "Consegna bolletta import SLOP",
                fields: [
                    {
                      name: 'consigneeTrader',
                      label: 'vesselProtocol.consigneeTrader',
                      type: 'textarea',
                      initialValue: Constants.vesselProtocolReports.consigneeTrader53
                    },
                    {
                      name: 'date',
                      label: 'vesselProtocol.date',
                      type: 'date',
                      initialValue: new Date()
                    },
                    {
                      name: 'place',
                      label: 'vesselProtocol.place',
                      type: 'string',
                      initialValue: Constants.vesselProtocolReports.comunicationPlace
                    },
                    {
                      name: 'subject',
                      label: 'vesselProtocol.subject',
                      type: 'string',
                      initialValue: (caller) => {
                          return caller.voyageNumber;
                      }
                    },
                    {
                      name: 'protocolNumber',
                      label: 'vesselProtocol.protocolNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        return caller.protocolNumber || "0";
                      }
                    },
                    {
                      name: 'registryType',
                      label: 'vesselProtocol.registryType',
                      type: 'string',
                      initialValue: (caller) => {
                        return caller.registryType
                      }
                    },
                    {
                      name: 'registryNumber',
                      label: 'vesselProtocol.registryNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        return String(caller.registryNumber ?? "");
                      }
                    },
                    {
                      name: 'billDate',
                      label: 'vesselProtocol.billDate',
                      type: 'date',
                      initialValue: new Date()
                    },
                    {
                      name: 'notes',
                      label: 'vesselProtocol.notes',
                      type: 'textarea'
                    }
                  ]
            }, 
            { 
                code: 71, 
                category: "Prospetti - Istanze", 
                description: "Prospetto di rettifica peso",
                fields: [
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'textarea',
                        initialValue: ''
                    },
                    {
                      name: 'description',
                      label: 'vesselProtocol.description',
                      type: 'textarea',
                      initialValue: 'La revisione è richiesta per rettificare la quantità comunicata nella dichiarazione digitale(dati di polizza) con la quantità accertata in raffineria a fine introduzione'
                    },
                    {
                      name: 'protocolDate',
                      label: 'vesselProtocol.protocolDate',
                      type: 'date',
                      initialValue: (caller) => {
                        return caller.protocolDate || new Date()
                      }
                    },
                    {
                      name: 'registryType',
                      label: 'vesselProtocol.registryType',
                      type: 'string',
                      initialValue: (caller) => {
                        return caller.registryType
                      }
                    },
                    {
                      name: 'registryNumber',
                      label: 'vesselProtocol.registryNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        return String(caller.registryNumber ?? "");
                      }
                    },
                    {
                      name: 'registryDate',
                      label: 'vesselProtocol.registryDate',
                      type: 'date',
                      initialValue: (caller) => {
                        return caller.registryDate || new Date()
                      }
                    },
                    {
                      name: 'companyName',
                      label: 'vesselProtocol.company',
                      type: 'string',
                      initialValue: (caller) => {
                        if(caller.voyageNumber){
                            if(caller.voyageNumber.startsWith('TF')) {
                                return Constants.vesselProtocolReports.trafiguraName;
                            } else if(caller.voyageNumber.startsWith('IB')) {
                                return Constants.vesselProtocolReports.isabName;
                            } else {
                                return null;
                            }
                        }
                      }
                    },
                    {
                      name: 'companyAddress',
                      label: 'vesselProtocol.companyAddress',
                      type: 'string',
                      initialValue: (caller) => {
                        if(caller.voyageNumber){
                            if(caller.voyageNumber.startsWith('TF')) {
                                return Constants.vesselProtocolReports.trafiguraAddress;
                            } else if(caller.voyageNumber.startsWith('IB')) {
                                return Constants.vesselProtocolReports.isabAddress;
                            } else {
                                return null;
                            }
                        }
                      }
                    },
                    {
                      name: 'companyPiva',
                      label: 'vesselProtocol.eoriNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        if(caller.voyageNumber){
                            if(caller.voyageNumber.startsWith('IB')) {
                                return Constants.vesselProtocolReports.isabVatNumber;
                            } else {
                                return null;
                            }
                        }
                      }
                    },
                    {
                        name: 'field35',
                        label: 'vesselProtocol.field35',
                        type: 'field',
                        initialValue: 'Data element 6/5 (ex campo 35)',
                        initialValueFrom: (caller) => {
                            return String(caller.quantity) || "";
                        },
                        initialValueTo: (caller) => {
                            return String(caller.netQuantity) || "";
                        },
                        
                    },
                    {
                        name: 'field38',
                        label: 'vesselProtocol.field38',
                        type: 'field',
                        initialValue: 'Data element 6/1 (ex campo 38)',
                        initialValueFrom: (caller) => {
                            return String(caller.quantity) || "";
                        },
                        initialValueTo: (caller) => {
                            return String(caller.netQuantity) || "";
                        }
                    },
                    {
                        name: 'field479AJ',
                        label: 'vesselProtocol.field479AJ',
                        type: 'field',
                        initialValue: 'Data element 4/6 (ex campo 47-9AJ)',
                    },
                    {
                        name: 'field47B00',
                        label: 'vesselProtocol.field47B00',
                        type: 'field',
                        initialValue: 'Data element 4/4 (ex campo 47-B00)',
                    },
                    /*{
                        name: 'field41',
                        label: 'vesselProtocol.field41',
                        type: 'field'
                    },
                    {
                        name: 'field42',
                        label: 'vesselProtocol.field42',
                        type: 'field'
                    },
                    {
                        name: 'field43',
                        label: 'vesselProtocol.field43',
                        type: 'field'
                    },
                    {
                        name: 'field44',
                        label: 'vesselProtocol.field44',
                        type: 'field',
                        initialValue: 'Campo 44',
                    },
                    {
                        name: 'field45',
                        label: 'vesselProtocol.field45',
                        type: 'field'
                    },
                    {
                        name: 'field46',
                        label: 'vesselProtocol.field46',
                        type: 'field',
                        initialValue: 'Campo 46',
                    },*/
                    {
                        name: 'fields',
                        label: '',
                        type: 'addFields'
                    },
                    {
                      name: 'evidenceDocumentation1',
                      label: 'vesselProtocol.evidenceDocumentation1',
                      type: 'textarea',
                      initialValue: 'D.A.U. con i dati del dichiarato'
                    },
                    {
                      name: 'evidenceDocumentation2',
                      label: 'vesselProtocol.evidenceDocumentation2',
                      type: 'textarea',
                      initialValue: 'D.A.U. di rettifica'
                    },
                    {
                        name: 'evidenceDocumentation3',
                        label: 'vesselProtocol.evidenceDocumentation3',
                        type: 'objectArray',
                        inputNumber: 1,
                        collapsible: false,
                        children: [
                            {
                                name: 'value',
                                label: 'vesselProtocol.description',
                                type: 'textarea',
                                initialValue: 'Verbale di misurazione serbatoi'
                            },
                            {
                                name: 'documentNumber',
                                label: 'vesselProtocol.documentNumber',
                                type: 'string',
                            },
                            {
                                name: 'date',
                                label: 'vesselProtocol.date',
                                type: 'date',
                            }
                        ]
                    },
                    {
                      name: 'evidenceDocumentation4',
                      label: 'vesselProtocol.evidenceDocumentation4',
                      type: 'textarea',
                      initialValue: 'M2 di rettifica'
                    },
                    {
                      name: 'evidenceDocumentation5',
                      label: 'vesselProtocol.evidenceDocumentation5',
                      type: 'textarea'
                    },
                    {
                      name: 'evidenceDocumentation6',
                      label: 'vesselProtocol.evidenceDocumentation6',
                      type: 'textarea'
                    },
                    {
                      name: 'protocolNumber',
                      label: 'vesselProtocol.protocolNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        return (caller.protocolNumber || "");
                      }
                    },
                    {
                      name: 'notes',
                      label: 'vesselProtocol.notes',
                      type: 'string'
                    },
                    {
                      name: 'signer',
                      label: 'vesselProtocol.signer',
                      type: 'string'
                    }
                  ]
            }, 
            { 
                code: 72, 
                category: "Prospetti - Istanze", 
                description: "Rettifica peso e valore riproporzionato",
                fields: [
                    {
                        name: 'consigneeTrader',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'textarea',
                        initialValue: ''
                    },
                    {
                      name: 'description',
                      label: 'vesselProtocol.description',
                      type: 'textarea',
                      initialValue: 'La revisione è richiesta per rettificare la quantità ed il valore comunicati nella dichiarazione digitale(dati di polizza) con la quantità ed il valore della merce introdotta in raffineria.'
                    },
                    {
                      name: 'protocolDate',
                      label: 'vesselProtocol.protocolDate',
                      type: 'date',
                      initialValue: (caller) => {
                        return caller.protocolDate || new Date()
                      }
                    },
                    {
                      name: 'registryType',
                      label: 'vesselProtocol.registryType',
                      type: 'string',
                      initialValue: (caller) => {
                        return caller.registryType
                      }
                    },
                    {
                      name: 'registryNumber',
                      label: 'vesselProtocol.registryNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        return String(caller.registryNumber ?? "");
                      }
                    },
                    {
                      name: 'registryDate',
                      label: 'vesselProtocol.registryDate',
                      type: 'date',
                      initialValue: (caller) => {
                        return caller.registryDate || new Date()
                      }
                    },
                    {
                      name: 'companyName',
                      label: 'vesselProtocol.commitment',
                      type: 'string',
                      initialValue: (caller) => {
                        return caller.customer
                      }
                    },
                    {
                      name: 'companyAddress',
                      label: 'vesselProtocol.commitmentAddress',
                      type: 'string'
                    },
                    {
                      name: 'companyPiva',
                      label: 'vesselProtocol.commitmentPIva',
                      type: 'string'
                    },
                    {
                        name: 'field2',
                        label: 'vesselProtocol.field2',
                        type: 'field',
                        initialValue: 'Data element 3/1 (ex campo 2)'
                    },
                    {
                        name: 'field12',
                        label: 'vesselProtocol.field12',
                        type: 'field',
                        initialValue: 'Data element 4/9 (ex campo 12)'
                    },
                    {
                        name: 'field20D',
                        label: 'vesselProtocol.field20',
                        type: 'field',
                        initialValue: 'Data element 4/1 (ex campo 20)'
                    },
                    {
                        name: 'field22D',
                        label: 'vesselProtocol.field22',
                        type: 'field',
                        initialValue: 'Data element 4/11 (ex campo 22)'
                    },
                    {
                        name: 'field35',
                        label: 'vesselProtocol.field35',
                        type: 'field',
                        initialValue: 'Data element 6/5 (ex campo 35)'
                    },
                    {
                        name: 'field38',
                        label: 'vesselProtocol.field38',
                        type: 'field',
                        initialValue: 'Data element 6/1 (ex campo 38)'
                    },
                    {
                        name: 'field42',
                        label: 'vesselProtocol.field42',
                        type: 'field',
                        initialValue: 'Data element 4/14 (ex campo 42)'
                    },
                    {
                        name: 'field44D',
                        label: 'vesselProtocol.field44',
                        type: 'field',
                        initialValue: 'Data element 2/3 (ex campo 44)'
                    },
                    {
                        name: 'field45',
                        label: 'vesselProtocol.field45',
                        type: 'field',
                        initialValue: 'Data element 4/9 (ex campo 45)'
                    },
                    {
                        name: 'field46D',
                        label: 'vesselProtocol.field46',
                        type: 'field',
                        initialValue: 'Data element 8/6 (ex campo 46)'
                    },
                    {
                        name: 'field47A00',
                        label: 'vesselProtocol.field47A00',
                        type: 'field',
                        initialValue: 'Data element 4/4 (ex campo 47-A00)'
                    },
                    {
                        name: 'field47B00',
                        label: 'vesselProtocol.field47B00',
                        type: 'field',
                        initialValue: 'Data element 4/4 (ex campo 47-B00)'
                    },
                    {
                        name: 'fields',
                        label: '',
                        type: 'addFields'
                    },
                    {
                      name: 'evidenceDocumentation1',
                      label: 'vesselProtocol.evidenceDocumentation1',
                      type: 'textarea',
                      initialValue: 'D.A.U. con i dati del dichiarato'
                    },
                    {
                      name: 'evidenceDocumentation2',
                      label: 'vesselProtocol.evidenceDocumentation2',
                      type: 'textarea',
                      initialValue: 'D.A.U. di rettifica'
                    },
                    {
                      name: 'evidenceDocumentation3',
                      label: 'vesselProtocol.evidenceDocumentation3',
                      type: 'textarea',
                      initialValue: 'Verbale di misurazione serbatoi'
                    },
                    {
                      name: 'evidenceDocumentation4',
                      label: 'vesselProtocol.evidenceDocumentation4',
                      type: 'textarea',
                      initialValue: 'Fattura commerciale(valore riliquidato)'
                    },
                    {
                      name: 'evidenceDocumentation5',
                      label: 'vesselProtocol.evidenceDocumentation5',
                      type: 'textarea',
                    },
                    {
                      name: 'evidenceDocumentation6',
                      label: 'vesselProtocol.evidenceDocumentation6',
                      type: 'textarea',
                    },
                    {
                      name: 'protocolNumber',
                      label: 'vesselProtocol.protocolNumber',
                      type: 'string',
                      initialValue: (caller) => {
                        return (caller.protocolNumber || "");
                      }
                    },
                    {
                      name: 'notes',
                      label: 'vesselProtocol.notes',
                      type: 'textarea',
                    },
                    {
                      name: 'signer',
                      label: 'vesselProtocol.signer',
                      type: 'string'
                    }
                  ]
            }
        ]
    },
    VesselProtocolPaymentReport: {
        reports: [
            {
                code: 2,
                category: "Pagamenti",
                description: "3 Prospetto per pagamento cabotaggi nazionali e comunitari",
                fields: [
                    {
                        name: 'company',
                        label: 'vesselProtocol.company',
                        type: 'select',
                        selectApi: 'masterdata/company-combo',
                        format: { label: 'label', value: 'value' },
                        queryField: {isOwner: true},
                    },
                    {
                        name: 'plantId',
                        label: 'vesselProtocol.plant',
                        type: 'select',
                        selectApi: 'masterdata/registry-plant-combo',
                        format: { label: 'label', value: 'value' },
                    },
                    {
                        name: 'dueDate',
                        label: 'vesselProtocol.paymentDuedate',
                        type: 'date',
                        initialValue: new Date()
                    },
                ]
            },
            {
                code: 4,
                category: "Pagamenti",
                description: "Prospetto per pagamenti import/export",
                fields: [
                    {
                        name: 'idCompany',
                        label: 'vesselProtocol.company',
                        type: 'select',
                        selectApi: 'masterdata/company-combo',
                        format: { label: 'label', value: 'value' },
                        queryField: {isOwner: true},
                    },
                    {
                        name: 'idPlant',
                        label: 'vesselProtocol.plant',
                        type: 'select',
                        selectApi: 'masterdata/registry-plant-combo',
                        format: { label: 'label', value: 'value' },
                    },
                    {
                        name: 'paymentDate',
                        label: 'vesselProtocol.paymentDuedate',
                        type: 'date',
                        initialValue: new Date()
                    },
                ]
            },
            { 
                code: 57, 
                category: "Pagamenti", 
                description: "Richiesta fondi per i pagamenti periodici e differiti",
                fields: [
                    {
                        name: 'idCompany',
                        label: 'vesselProtocol.company',
                        type: 'select',
                        selectApi: 'masterdata/company-combo',
                        format: { label: 'label', value: 'value' },
                        queryField: {isOwner: true},
                    },
                    {
                        name: 'administration',
                        label: 'vesselProtocol.administration',
                        type: 'string',
                        initialValue: Constants.vesselProtocolReports.consignorTrader
                    },
                    {
                        name: 'protocolSPEDNumber',
                        label: 'vesselProtocol.protocolNumber',
                        type: 'string'
                    },
                    {
                        name: 'idPlant',
                        label: 'vesselProtocol.plant',
                        type: 'select',
                        selectApi: 'masterdata/registry-plant-combo',
                        format: { label: 'label', value: 'value' },
                    },
                    {
                        name: 'duedate',
                        label: 'vesselProtocol.paymentDuedate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'documentDate',
                        label: 'vesselProtocol.documentDate',
                        type: 'date',
                        initialValue: new Date()
                    },
                    {
                        name: 'receiver',
                        label: 'vesselProtocol.consigneeTrader',
                        type: 'string'
                    },
                    {
                        name: 'signer',
                        label: 'vesselProtocol.signer',
                        type: 'string'
                    },
                ]
            }, 
        ]
    },
    Timesheet: {
        title: '',
        api: 'jetty/vessel-timesheet-detail',
        updateApi: 'jetty/vessel-timesheet-detail',
        enableDelete: true,
        deleteButtonName: 'timesheetDeleteButton',
        enableInsert: false,
        idColumn: "id",
        preventAutoLoad: true,
        preventFilters: true,
        hideRefresh: true,
        queryParamFilters: false,
        editMode: 'row',
        inLineEditPermission: 'timesheetEditButton',
        editButtonName: 'timesheetEditButton',
        defaultSortField: 'startDate',
        enableUpdate: false,
        // customActions: [
        //     {
        //         name: 'timesheetEditButton',
        //         title: 'btn.edit',
        //         visible: false,
        //         cssClass: 'p-button-rounded p-button-success mr-2',
        //         icon: 'pi pi-pencil',
        //         data: {},
        //         fields: [],
        //         action: function(data, caller){
        //             caller.$emit('onTimesheetEditClick', data);
        //         }
        //     }
        // ],
        columns: [
            { label: 'timesheet.eventDescription', name: 'registryTimesheetOperationDescription', type: 'string', readOnly: true},
            { label: 'timesheet.dateFrom', name: 'startDate', type: 'dateortime', datetime: true, inLineEdit: true, enableInlineEdit:true, 
                hideTimeField: 'startDateHideTime',
                renderColumn: (elem) => {
                    if(elem.startDate){
                        if(elem.startDateHideTime){
                            return Utils.formatDate(Utils.dateToCurrentTimeZone(elem.startDate))
                        }else{
                            return Utils.formatDateTime(Utils.dateToCurrentTimeZone(elem.startDate))
                        }
                    }
                    return "";
                },
            },
            { label: 'timesheet.dateTo', name: 'endDate', type: 'dateortime', datetime: true, inLineEdit: true, editEnabled: (data) => !data.isPunctualDate,
            hideTimeField: 'endDateHideTime',
                renderColumn: (elem) => {
                    if(elem.endDate){
                        if(elem.endDateHideTime){
                            return Utils.formatDate(Utils.dateToCurrentTimeZone(elem.endDate))
                        }else{
                            return Utils.formatDateTime(Utils.dateToCurrentTimeZone(elem.endDate))
                        }
                    }
                    return "";
                } 
            },
            { label: 'timesheet.note', name: 'note', type: 'string', inLineEdit: true, enableInlineEdit:true },
        ]
    },

    DailyPlanManagement: {
        title: '',
        api: 'operative-plan/daily-plan-last-published',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        strechColumn: true,
        idColumn: "id",
        preventAutoLoad: true,
        preventFilters: false,
        hideRefresh: true,
        queryParamFilters: true,
        selection: true,
        filterLocal: true,
        selectionMode: 'single',
        preventPagination: true,
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true, gridHide: true, defaultSort: 'asc' },
            // { label:'dailyPlanValidation.isValidated',name:'isValidated', type:'boolean', inLineEdit: true},
            { label: 'operativePlan.plant', name: 'registryPlant', type: 'string' },
            { label: 'movementInstructions.voyageNumber', name: 'voyageNumber', type: 'string' },
            { label: 'dailyPlanValidation.status', name: 'status', type: 'string' },
            // STATO COM?
            // COM DA MODIF
            {
                label: 'operationType.fk',
                name: 'registryOperationType',
                type: 'string',
                selectApi: 'masterdata/operation-type-combo',
                // gridFilterType: 'string',
                // gridFilterSelectApi: 'masterdata/operation-type-combo',
                // gridFilterSelectFormat: { 
                //     label: 'label',
                //     value: 'label' 
                // },
                // gridFilterName: 'registryOperationType'
            },
            { label: 'operativePlan.vessel', name: 'vessel', type: 'string' },
            {
                label: 'product.fk',
                name: 'registryProduct',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridFilterName: 'registryProduct'
            },
            { label: 'tankOperations.tank', name: 'tank', type: 'string' },
            { label: 'operativePlan.tankListString', name: 'tanksList', type: 'string' },
            { label: 'operativePlan.aspiratorsListString', name: 'aspiratorsList', type: 'string' },
            { label: 'operativePlan.pipilineListString', name: 'pipelinesList', type: 'string' },
            { label: 'operativePlan.pumpsListString', name: 'pumpsList', type: 'string' },
            { label: 'operativePlan.measurersListString', name: 'measurersList', type: 'string' },
            {
                label: 'operativePlan.mooringPlatform',
                name: 'mooringPlatform',
                type: 'select',
                selectApi: 'masterdata/equipment-combo?type=PTF',
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=PTF',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridFilterName: 'mooringPlatform'
            },
            { label: 'operativePlan.quantity', name: 'qty', type: 'number' },
            { label: 'operativePlan.minimumQuantity', name: 'qtyMin', type: 'number' },
            { label: 'operativePlan.maximumQuantity', name: 'qtyMax', type: 'number' },
            { label: 'operativePlan.utifProgramCode', name: 'utifProgramCode', type: 'string' },
            { label: 'operativePlan.fiscalNotificationCode', name: 'fiscalNotificationCode', type: 'string' },
            { label: 'operativePlan.fiscalNotificationDate', name: 'fiscalNotificationDate', type: 'date' },
            { label: 'operativePlan.fiscalNotificationInfoilCode', name: 'fiscalNotificationInfoilCode', type: 'string' },
            { label: 'operativePlan.fiscalNotificationInfoilDate', name: 'fiscalNotificationInfoilDate', type: 'date' },
            { label: 'operativePlan.company', name: 'ownerCompany', type: 'string' },
            { label: 'operativePlan.scheduledOperationDate', name: 'scheduledOperationDate', type: 'date', datetime: true },
            { label: 'operativePlan.etaDate', name: 'etaDate', type: 'date', datetime: true },
            { label: 'operativePlan.laycanStartDate', name: 'laycanStartDate', type: 'date', datetime: true },
            { label: 'operativePlan.laycanEndDate', name: 'laycanEndDate', type: 'date', datetime: true },
            {
                label: 'operativePlan.movementInstructionCode', name: 'movementInstructionCode', type: 'string', style: 'text-decoration: underline;', action: function (caller, record) {
                    let params = {
                        movementInstructionCode: record.movementInstructionCode
                    };
                    caller.$store.dispatch("generic/setLoading", true);
                    MovementInstructionService.printMovementInstruction(params, response => {

                        caller.$store.dispatch("generic/setLoading", false);
                        if (response.success) {
                            var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            var fileLink = document.createElement('a');

                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', record.voyageNumber + '-' + record.movementInstructionCode + '.pdf');
                            document.body.appendChild(fileLink);

                            fileLink.click();
                        } else {
                            caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })

                }
            },
            { label: 'operativePlan.notes', name: 'notes', type: 'string' },
        ]

    },
    biodieselMixingReport: {
        fieldset: ['generalData', 'finalMixtureData', 'dataTransferPureBiodiesel', 'biodieselDataOutOfCount', 'mixtureTransferData', 'productionVerificationData', 'azzPdData'],
        fields: [
            {
                name: 'documentNumber',
                label: 'documentNumber',
                type: 'number',
                category: 'generalData'
            },
            {
                name: 'startDateValidity',
                label: 'startDateValidity',
                type: 'date',
                category: 'generalData'
            },
            {
                name: 'idRegistryCompany',
                label: 'company',
                type: 'comboKey',
                api: 'masterdata/company-combo',
                category: 'generalData'
            },
            {
                name: 'biodieselTank',
                label: 'biodieselTank',
                type: 'comboKey',
                api: 'equipment-operation/combo-biodiesel-tank?isPure=true',
                category: 'generalData'
            },
            {
                name: 'mixTank',
                label: 'mixTank',
                type: 'comboKey',
                api: 'equipment-operation/combo-biodiesel-tank?isPure=false',
                category: 'generalData'
            },
            {
                name: 'sendTankTrvMix',
                label: 'sendTankTrvMix',
                type: 'comboKey',
                api: 'equipment-operation/combo-biodiesel-tank?isPure=false',
                category: 'generalData'
            },
            {
                name: 'totalBiodieselWeightKg',
                label: 'totalBiodieselWeightKg',
                type: 'calculated',
                category: 'finalMixtureData'
            },
            {
                name: 'totalBiodieselVolumeLt',
                label: 'totalBiodieselVolumeLt',
                type: 'calculated',
                category: 'finalMixtureData'
            },
            {
                name: 'percTotalBiodieselWeightKg',
                label: 'percTotalBiodieselWeightKg',
                type: 'calculated',
                category: 'finalMixtureData'
            },
            {
                name: 'percTotalBiodieselVolumeLt',
                label: 'percTotalBiodieselVolumeLt',
                type: 'calculated',
                category: 'finalMixtureData'
            },
            {
                name: 'qtyTrvPureBiodieselKg',
                label: 'qtyTrvPureBiodieselKg',
                type: 'number',
                category: 'dataTransferPureBiodiesel'
            },
            {
                name: 'volTrvPureBiodieselLt',
                label: 'volTrvPureBiodieselLt',
                type: 'number',
                category: 'dataTransferPureBiodiesel'
            },
            {
                name: 'qtyBiodieselOutOfCountKg',
                label: 'qtyBiodieselOutOfCountKg',
                type: 'number',
                category: 'biodieselDataOutOfCount'
            },
            {
                name: 'volBiodieselOutOfCountLt',
                label: 'volBiodieselOutOfCountLt',
                type: 'number',
                category: 'biodieselDataOutOfCount'
            },
            {
                name: 'qtyIntrTrvMixKg',
                label: 'qtyIntrTrvMixKg',
                type: 'number',
                category: 'mixtureTransferData'
            },
            {
                name: 'volIntrtrvMixLt',
                label: 'volIntrtrvMixLt',
                type: 'number',
                category: 'mixtureTransferData'
            },
            {
                name: 'percWeightSenderTrvMix',
                label: 'percWeightSenderTrvMix',
                type: 'decimal',
                digits: 4,
                category: 'mixtureTransferData'
            },
            {
                name: 'percVolSenderTrvMix',
                label: 'percVolSenderTrvMix',
                type: 'decimal',
                digits: 4,
                category: 'mixtureTransferData'
            },
            {
                name: 'qtyBiodieselIntrTrvMixKg',
                label: 'qtyBiodieselIntrTrvMixKg',
                type: 'calculated',
                category: 'mixtureTransferData'
            },
            {
                name: 'volBiodieselIntrTrvMixLt',
                label: 'volBiodieselIntrTrvMixLt',
                type: 'calculated',
                category: 'mixtureTransferData'
            },
            {
                name: 'stockWeightKg',
                label: 'stockWeightKg',
                type: 'number',
                category: 'productionVerificationData'
            },
            {
                name: 'stockVolumeLt',
                label: 'stockVolumeLt',
                type: 'number',
                category: 'productionVerificationData'
            },
            {
                name: 'fiscalMovedWeightAzzPdKg',
                label: 'fiscalMovedWeightAzzPdKg',
                type: 'number',
                category: 'azzPdData'
            },
            {
                name: 'fiscalMovedVolumeAzzPdLt',
                label: 'fiscalMovedVolumeAzzPdLt',
                type: 'number',
                category: 'azzPdData'
            },
            {
                name: 'qtyBiodieselPrevKg',
                label: 'qtyBiodieselPrevKg',
                type: 'number',
                category: 'azzPdData'
            },
            {
                name: 'volBiodieselPrevLt',
                label: 'volBiodieselPrevLt',
                type: 'number',
                category: 'azzPdData'
            },
            {
                name: 'percQtyBiodieselPrevKg',
                label: 'percQtyBiodieselPrevKg',
                type: 'decimal',
                digits: 4,
                category: 'azzPdData'
            },
            {
                name: 'percVolBiodieselPrevLt',
                label: 'percVolBiodieselPrevLt',
                type: 'decimal',
                digits: 4,
                category: 'azzPdData'
            },
        ]
    },

    OperationTypeClass: {
        title: 'operationTypeClass.page_title',
        api: 'masterdata/operation-type-class',
        enableUpdate: true,
        editButtonName: 'operationTypeClassEditButton',
        enableDelete: true,
        deleteButtonName: 'operationTypeClassDeleteButton',
        enableInsert: true,
        addButtonName: 'operationTypeClassAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 4
                }
            },
            {
                name: 'shortDescription',
                label: 'shortDescription',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 30
                }
            },
            {
                name: 'longDescription',
                label: 'longDescription',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 50
                }
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                formHide: true
            }
        ]

    },
    RecurringOperationTemplate: {
        title: 'recurringOperationTemplate.page_title',
        api: 'operative-plan/recurring-operation-template',
        enableUpdate: true,
        editButtonName: 'recurringOperationTemplateEditButton',
        enableDelete: true,
        deleteButtonName: 'recurringOperationTemplateDeleteButton',
        enableInsert: true,
        addButtonName: 'recurringOperationTemplateAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryPlant',
                label: 'registryPlant.fk',
                type: 'select',
                selectApi: 'masterdata/registry-plant',
                selectFormat: {
                    label: 'code',
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryPlant',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/registry-plant',
                gridFilterSelectFormat: {
                    label: 'code',
                    value: 'id'
                },
                gridFilterName: 'idRegistryPlant'
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product',
                selectFormat: {
                    label: function (el) {
                        return el.code + '-' + el.description
                    },
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product',
                gridFilterSelectFormat: {
                    label: function (el) {
                        return el.code + '-' + el.description
                    },
                    value: 'id'
                },
                gridFilterName: 'idRegistryProduct'
            },

            {
                name: 'idRegistryOperationType',
                label: 'operationType.fk',
                type: 'select',
                selectApi: 'masterdata/operation-type',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridHide: true
            },
            {
                name: 'registryOperationType',
                label: 'operationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/operation-type',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'idRegistryOperationType'
            },


            {
                name: 'idRegistryCompanyOwner',
                label: 'company.fk',
                type: 'select',
                selectApi: 'masterdata/company',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                sortField: 'Description',
                gridHide: true
            },
            {
                name: 'registryCompanyOwner',
                label: 'company.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/company',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'idRegistryCompanyOwner'
            },

            {
                name: 'notes',
                label: 'note',
                type: 'string',
                validations: {
                    maxLength: 150
                }
            }
        ]

    },
    TimesheetOperation: {
        title: 'timesheetOperation.page_title',
        api: 'masterdata/timesheet-operation',
        enableUpdate: true,
        editButtonName: 'timesheetOperationEditButton',
        enableDelete: true,
        deleteButtonName: 'timesheetOperationDeleteButton',
        enableInsert: true,
        addButtonName: 'timesheetOperationAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryTimesheetOperationType',
                label: 'timesheetOperationType.fk',
                type: 'select',
                selectApi: 'masterdata/timesheet-operation-type',
                selectFormat: {
                    label: 'description',
                    value: 'id'
                },
                sortField: 'Description',
                gridHide: true
            },
            {
                name: 'registryTimesheetOperationType',
                label: 'timesheetOperationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/timesheet-operation-type',
                gridFilterSelectFormat: {
                    label: 'description',
                    value: 'id'
                },
                gridFilterName: 'idRegistryTimesheetOperationType'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 200
                }
            },
            {
                name: 'englishDescription',
                label: 'englishDescription',
                type: 'string',
                validations: {
                    maxLength: 200
                }
            },
            {
                name: 'sequenceNumber',
                label: 'timesheetOperation.sequenceNumber',
                type: 'number',
                validations: {
                    maxLength: 4,
                    required: true
                }
            },
            {
                name: 'reportSection',
                label: 'timesheetOperation.reportSection',
                type: 'string',
                validations: {
                    maxLength: 20
                }
            },
            {
                name: 'chargeDischargeFlag',
                label: 'timesheetOperation.chargeDischargeFlag',
                type: 'string',
                validations: {
                    maxLength: 1,
                    required: true
                }
            },
            {
                name: 'isPrivate',
                label: 'timesheetOperation.isPrivate',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isMandatory',
                label: 'timesheetOperation.isMandatory',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isPunctualDate',
                label: 'timesheetOperation.isPunctualDate',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isCancelled',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                }
            }
        ]
    },
    TimesheetOperationType: {
        title: 'timesheetOperationType.page_title',
        api: 'masterdata/timesheet-operation-type',
        enableUpdate: true,
        editButtonName: 'timesheetOperationTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'timesheetOperationTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'timesheetOperationTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 3
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 200,
                    required: true
                }
            },
            {
                name: 'sequenceNumber',
                label: 'timesheetOperation.sequenceNumber',
                type: 'number',
                validations: {
                    maxLength: 4
                }
            },
            {
                name: 'isCancelled',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                }
            }
        ]
    },
    ExciseRate: {
        title: 'exciseRate.page_title',
        api: 'masterdata/excise-rate',
        enableUpdate: true,
        editButtonName: 'exciseRateEditButton',
        enableDelete: true,
        deleteButtonName: 'exciseRateDeleteButton',
        enableInsert: true,
        addButtonName: 'exciseRateAddButton',
        idColumn: "id",
        dataTables: [
            {
                label: 'exciseRateSpecialization.page_title',
                dataTableUnderForm: 'ExciseRateSpecialization',
                dataTableDefaultKey: 'idRegistryExciseRate',
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },

            {
                name: 'idRegistryEnergyProduct',
                label: 'energyProduct.fk',
                type: 'select',
                selectApi: 'masterdata/energy-product',
                selectFormat: {
                    label: function (el) {
                        return el.nc
                    }, value: 'id'
                },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryEnergyProductNCAndDescription',
                label: 'energyProduct.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/energy-product',
                gridFilterSelectFormat: {
                    label: function (el) {
                        return el.nc
                    }, value: 'id'
                },
                gridFilterName: 'idRegistryEnergyProduct'
            },
            {
                name: 'idRegistryFiscalPosition',
                label: 'fiscalPosition.fk',
                type: 'select',
                selectApi: 'masterdata/fiscal-position',
                selectFormat: {
                    label: function (el) {
                        return el.shortDescription + ' - ' + el.description
                    }, value: 'id'
                },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryFiscalPositionShortAndLongDescription',
                label: 'fiscalPosition.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-position',
                gridFilterSelectFormat: {
                    label: function (el) {
                        return el.shortDescription + ' - ' + el.description
                    }, value: 'id'
                },
                gridFilterName: 'idRegistryFiscalPosition'
            },
            {
                name: 'idRegistryFiscalUse',
                label: 'fiscalUse.fk',
                type: 'select',
                selectApi: 'masterdata/fiscal-use',
                selectFormat: { label: 'description', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryFiscalUseDescription',
                label: 'fiscalUse.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-use',
                gridFilterSelectFormat: { label: 'description', value: 'id' },
                gridFilterName: 'idRegistryFiscalUse'
            },
            {
                name: 'idRegistryFiscalDestination',
                label: 'fiscalDestination.fk',
                type: 'select',
                selectApi: 'masterdata/fiscal-destination',
                selectFormat: { label: 'description', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryFiscalDestinationDescription',
                label: 'fiscalDestination.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-destination',
                gridFilterSelectFormat: { label: 'description', value: 'id' },
                gridFilterName: 'idRegistryFiscalDestination'
            },
            {
                name: 'idRegistryDocumentType',
                label: 'documentType.fk',
                type: 'select',
                selectApi: 'masterdata/document-type',
                selectFormat: { label: 'code', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryDocumentTypeCode',
                label: 'documentType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/document-type',
                gridFilterSelectFormat: { label: 'code', value: 'id' },
                gridFilterName: 'idRegistryDocumentType'
            },
            {
                name: 'exciseRate',
                label: 'exciseRate.exciseRate',
                type: 'decimal',
                digits: 8
            },
            {
                name: 'exciseDelta',
                label: 'exciseRate.exciseDelta',
                type: 'decimal',
                digits: 8
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'endDate',
                label: 'endDate',
                type: 'date',
                format: "dd/mm/yy"
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'isSpecialized',
                label: 'exciseRate.isSpecialized',
                type: 'boolean',
                readOnly: true
            }
        ]
    },
    ExciseRateSpecialization: {
        title: 'exciseRateSpecialization.page_title',
        api: 'masterdata/excise-rate-specialization',
        enableUpdate: false,
        editButtonName: 'exciseRateEditButton',
        enableDelete: true,
        deleteButtonName: 'exciseRateDeleteButton',
        enableInsert: false,
        addButtonName: 'exciseRateAddButton',
        idColumn: "id",
        disableBack: true,
        skipCache: true,
        disableSave: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryExciseRate',
                label: 'exciseRate.fk',
                type: 'select',
                selectApi: 'masterdata/excise-rate-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryExciseRate',
                label: 'exciseRate.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/excise-rate-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryExciseRate'
            },
            {
                name: 'idRegistryEnergyProductDetail',
                label: 'energyProductDetail.fk',
                type: 'select',
                selectApi: 'masterdata/energy-product-detail-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'registryEnergyProductDetail',
                label: 'energyProductDetail.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/energy-product-detail-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryEnergyProductDetail'
            },
            {
                name: 'exciseRate',
                label: 'exciseRateSpecialization.exciseRate',
                type: 'decimal',
                digits: 8
            },
            {
                name: 'exciseDelta',
                label: 'exciseRateSpecialization.exciseDelta',
                type: 'decimal',
                digits: 8
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                validations: {
                    required: true
                },
                formHide: true
            }
        ],
        customActions: [
            {
                title: 'btn.edit',
                visible: false,
                name: 'exciseRateEditButton',
                icon: 'pi pi-pencil',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/masterData/ExciseRateSpecialization/${data.id}?idRegistryExciseRate=${data.idRegistryExciseRate}`;
                    caller.$router.push(uri);
                }
            },
        ],
        formCustomActions: [
            {
                name: 'btn_back',
                title: 'btn.back',
                icon: 'pi pi-arrow-left',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/masterData/ExciseRate/${data.idRegistryExciseRate}`;
                    caller.$router.push(uri);
                }
            },
            {
                name: 'btn_save',
                title: 'btn.save',
                icon: 'pi pi-save',
                data: {},
                fields: [],
                action: function (data, caller) {
 
                    if(data.id){
                        // UPDATE
                        CommonService.updateItem('masterdata/excise-rate-specialization', data, response => {
                            caller.disabled = false;
                            caller.$store.dispatch("generic/setLoading", false);
                            if(response.success){
                                caller.$toast.add({severity:'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000});
                            }else{
                                caller.$toast.add({detail: response.message && response.message != '' ? response.message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        }, 'id')
                    }else{
                        // SAVE
                        CommonService.createItem('masterdata/excise-rate-specialization', caller.data, response => {
                            caller.disabled = false;
                            caller.$store.dispatch("generic/setLoading", false);
                            if(response.success){
                                let uri = `/masterData/ExciseRate/${data.idRegistryExciseRate}`;
                                caller.$router.push(uri);
                            }else{
                                caller.$toast.add({detail: response.message && response.message != '' ? response.message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        })
                    }
                }
            }
        ],
        toolbarActions: [
            {
                type: 'button',
                visible: true,
                name: 'exciseRateAddButton',
                icon: 'pi pi-plus',
                title: 'btn.add',
                action: function (data, caller) {
                    let uri = `/masterData/ExciseRateSpecialization/0?idRegistryExciseRate=${caller.defaultId}`;
                    caller.$router.push(uri);
                }
            }
        ]
    },
    ClearanceDocument: {
        DAA: {
            id: {
                readonly: true,
                hidden: true,
                type: 'number',
            },
            idMovementInstructionLot: {
                readonly: true,
                hidden: true,
                type: 'number',
            },
            idRegistrySubmissionType: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/submission-type-combo',
                category: 'header',
                label: 'submissionType'
            },
            deferredSubmissionFlag: {
                readonly: false,
                hidden: false,
                type: 'boolean',
                category: 'header',
            },
            idGuarantorTypeCode: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/guarantor-type-code-combo',
                category: 'header',
                label: 'guarantorTypeCode'
            },
            idRegistryAuthorizationType: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/authorization-type-combo',
                category: 'header',
                label: 'authorizationTypeDescription'
            },
            journeyTime: {
                readonly: false,
                hidden: false,
                type: 'number',
                category: 'header',
            },
            idMesurementJourneyType: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/mesurement-journey-type-combo',
                category: 'header',
                label: 'journeyType'
            },
            issueDate: {
                readonly: false,
                hidden: false,
                type: 'date',
                format: 'dd/MM/yyyy',
                category: 'header',
            },
            arc: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'header',
            },
            dateOfValidationeEad: {
                readonly: false,
                hidden: false,
                type: 'date',
                format: 'dd/MM/yyyy HH:mm',
                category: 'header',
            },
            documentNumber: {
                readonly: false,
                hidden: false,
                type: 'number',
                category: 'header',
            },
            dateOfUpdateValidation: {
                readonly: false,
                hidden: false,
                type: 'date',
                format: 'dd/MM/yyyy HH:mm',
                category: 'header',
            },
            idEaadState: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/daa-state-combo',
                category: 'header',
                label: 'eaadStateDescription'
            },
            consignorTraderExciseNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consignorTrader',
            },
            consignorTraderName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consignorTrader',
            },
            consignorTraderStreetName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consignorTrader',
            },
            consignorTraderStreetNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consignorTrader',
            },
            consignorTraderPostcode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consignorTrader',
            },
            consignorTraderCity: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consignorTrader',
            },
            consignorTraderLanguage: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/language-combo',
                category: 'consignorTrader',
                label: 'consignorTraderLanguage'
            },
            placeOfDispatchTraderReferenceOfTaxWarehouse: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'placeOfDispatch',
            },
            placeOfDispatchTraderName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'placeOfDispatch',
            },
            placeOfDispatchTraderStreetName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'placeOfDispatch',
            },
            placeOfDispatchTraderStreetNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'placeOfDispatch',
            },
            placeOfDispatchTraderPostcode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'placeOfDispatch',
            },
            placeOfDispatchTraderCity: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'placeOfDispatch',
            },
            placeOfDispatchTraderLanguage: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/language-combo',
                category: 'placeOfDispatch',
                label: 'placeOfDispatchTraderLanguage'
            },
            consigneeTraderExciseNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consigneeTrader',
            },
            consigneeTraderName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consigneeTrader',
            },
            consigneeTraderStreetName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consigneeTrader',
            },
            consigneeTraderStreetNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consigneeTrader',
            },
            consigneeTraderPostcode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consigneeTrader',
            },
            consigneeTraderCity: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consigneeTrader',
            },
            consigneeTraderLanguage: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/language-combo',
                category: 'consigneeTrader',
                label: 'consigneeTraderLanguage'
            },
            consigneeTraderEoriNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'consigneeTrader',
            },
            complementConsigneeTraderMemberStateCode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'complementConsigneeTrader',
            },
            complementConsigneeTraderSerialNumberOfCertificateOfExemption: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'complementConsigneeTrader',
            },
            deliveryPlaceTraderExciseNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'deliveryPlace',
            },
            deliveryPlaceTraderName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'deliveryPlace',
            },
            deliveryPlaceTraderStreetName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'deliveryPlace',
            },
            deliveryPlaceTraderStreetNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'deliveryPlace',
            },
            deliveryPlaceTraderPostcode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'deliveryPlace',
            },
            deliveryPlaceTraderCity: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'deliveryPlace',
            },
            deliveryPlaceTraderLanguage: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/language-combo',
                category: 'deliveryPlace',
                label: 'deliveryPlaceTraderLanguage'
            },
            deliveryPlaceCustomsOfficeReferenceNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'deliveryPlace',
            },
            eaadLocalReferenceNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'dispatchDetails',
            },
            eaadInvoiceNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'dispatchDetails',
            },
            eaadInvoiceDate: {
                readonly: false,
                hidden: false,
                type: 'date',
                format: 'dd/MM/yyyy HH:mm',
                category: 'dispatchDetails',
            },
            idEaadOriginTypeCode: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/origin-type-code-combo',
                category: 'dispatchDetails',
                label: 'eaadOriginTypeCode'
            },
            eaadDateAndTimeOfDispatch: {
                readonly: false,
                hidden: false,
                type: 'date',
                format: 'dd/MM/yyyy HH:mm',
                category: 'dispatchDetails',
            },
            dispatchImportOfficeReferenceNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'dispatchDetails',
            },
            competentAuthorityDispatchOfficeReferenceNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'dispatchDetails',
            },
            idTransportArrangement: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/guarantor-type-code-combo',
                category: 'dispatchDetails',
                label: 'transportArrangement'
            },

            transportArrangerTraderVatNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'transportArrangerTrader',
            },
            transportArrangerTraderName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'transportArrangerTrader',
            },
            transportArrangerTraderStreetName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'transportArrangerTrader',
            },
            transportArrangerTraderStreetNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'transportArrangerTrader',
            },
            transportArrangerTraderPostcode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'transportArrangerTrader',
            },
            transportArrangerTraderCity: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'transportArrangerTrader',
            },
            transportArrangerTraderLanguage: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/language-combo',
                category: 'transportArrangerTrader',
                label: 'transportArrangerTraderLanguage'
            },
            firstTransporterTraderVatNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'firstTransporterTrader',
            },
            firstTransporterName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'firstTransporterTrader',
            },
            firstTransporterStreetName: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'firstTransporterTrader',
            },
            firstTransporterStreetNumber: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'firstTransporterTrader',
            },
            firstTransporterPostcode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'firstTransporterTrader',
            },
            firstTransporterCity: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'firstTransporterTrader',
            },
            firstTransporterLanguage: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/language-combo',
                category: 'firstTransporterTrader',
                label: 'firstTransporterLanguage'
            },
            idTransportModeCode: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/transport-mode-code-combo',
                category: 'transportDetails',
                label: 'transportModeCode'
            },
            idTransportUnitCode: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/transport-unit-code-combo',
                category: 'transportDetails',
                label: 'transportUnitCode'
            },
            identityOfTransportUnits: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'transportDetails',
            },
            transportComplementaryInformationLanguage: {
                readonly: false,
                hidden: false,
                type: 'comboKey',
                api: 'masterdata/language-combo',
                category: 'transportDetails',
                label: 'transportComplementaryInformationLanguage'
            },
            transportComplementaryInformation: {
                readonly: false,
                hidden: false,
                type: 'textarea',
                category: 'transportDetails',
            },
            bodyRecordUniqueReference: {
                readonly: false,
                hidden: false,
                type: 'number',
                category: 'product',
            },
            cpaproductCategory: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'product',
            },
            nc: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'product',
            },
            taricCode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'product',
            },
            caddCode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'product',
            },
            quantity: {
                readonly: false,
                hidden: false,
                type: 'number',
                category: 'product',
            },
            grossWeight: {
                readonly: false,
                hidden: false,
                type: 'number',
                category: 'product',
            },
            netWeight: {
                readonly: false,
                hidden: false,
                type: 'number',
                category: 'product',
            },
            density: {
                readonly: false,
                hidden: false,
                type: 'decimal',
                decimals: 4,
                category: 'product',
            },
            kindOfPackagesTypeCode: {
                readonly: false,
                hidden: false,
                type: 'text',
                category: 'packages',
            },
            numberOfPackages: {
                readonly: false,
                hidden: false,
                type: 'number',
                category: 'packages',
            },
            insertDate: {
                readonly: false,
                hidden: false,
                type: 'date',
                format: 'dd/MM/yyyy HH:mm'
            },
            createdBy: {
                readonly: false,
                hidden: false,
                type: 'text',
            },
            lastChangedDate: {
                readonly: false,
                hidden: false,
                type: 'date',
                format: 'dd/MM/yyyy HH:mm'
            },
            changedBy: {
                readonly: false,
                hidden: false,
                type: 'text',
            },
            isDeleted: {
                readonly: true,
                hidden: true,
                type: 'boolean',
            },
            cancelDate: {
                readonly: false,
                hidden: false,
                type: 'date',
                format: 'dd/MM/yyyy HH:mm'
            },
        },
        documentFields: ["typeDescription", "code", "issueDate", "product", "productCode", "owner", "receiver", "lot", "telexNotes", "shipperNotes"],
        documentType: {
            'PLZ': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                documentNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                shipper: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 200
                    }
                },
                voyageNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                externalVoyageNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                quantity: {
                    readonly: true,
                    hidden: false,
                    type: 'number'
                },
                minimumQuantity: {
                    readonly: true,
                    hidden: false,
                    type: 'number'
                },
                maximumQuantity: {
                    readonly: true,
                    hidden: false,
                    type: 'number'
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                placeOfIssue: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 100
                    }
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                documentHolder: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 200
                    }
                },
                notifyAddress: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 200
                    }
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                destinationPortCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                    validations: {
                        maxLength: 9
                    }
                },
                loadingPortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                dischargePortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                idRegistryProduct: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                productCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 150
                    }
                },
                weightInMtAir: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                volumeInM3At15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                densityAt15CDeg: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                movementInstructionNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                customCertificateDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                customCertificateNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                customCertificateDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                freightAsPer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 100
                    }
                },
                charterPartyDated: {
                    readonly: false,
                    hidden: false,
                    type: 'dateOnly'
                },
                freightAdvance: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 100
                    }
                },
                accountFreight: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 100
                    }
                },
                freigthAt: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 100
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                masterOfVessel: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                url: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
            'CM': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                voyageNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                quantity: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                minimumQuantity: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                maximumQuantity: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy'
                },
                idRegistryVessel: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                vesselFlag: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                masterOfVessel: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                loadingPortCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                loadingPortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                dischargePortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                departureDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy'
                },
                shipper: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                documentHolder: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                notifyAddress: {
                    readonly: true,
                    hidden: true,
                    type: 'textarea'
                },
                movementInstructionNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                packageandMarks: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                idRegistryProduct: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                productCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 150
                    }
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
            'CQTA': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                voyageNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                departureDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                idRegistryProduct: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                productCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 150
                    }
                },
                loadingPortCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                loadingPortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                dischargePortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                documentHolder: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                densityAt15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 5
                },
                weightInMtAir: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                firstProductDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                firstProductWeightInMt: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                secondProductDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                secondProductWeightInMt: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                volumeInM3At15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                volumeInLtAt15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                },
                movementInstructionNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                inspectorName: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                masterOfVessel: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
            'NP': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                isFip: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                    validations: {
                        required: true
                    }
                },
                documentNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                departurePortAndLocationDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                counterpartyDescription: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                deliveryNoteHolder: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 400
                    }
                },
                destinationDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 600
                    }
                },
                movementInstructionNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                commercialReason: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                transportTypeDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                carrierDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                locationOfLoadingAndIncoterm: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                contractNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                voyageNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                idRegistryProduct: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                productCode: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                weightMtAir: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                weightInMtAirCoded: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                volumeInM3At15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                volumeInM3At15CDegCoded: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                tit: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 1
                },
                externalVoyageNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                nC: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                },
                densityAt15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 5
                },
                tankList: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                url: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
            'BEM': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                isFip: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                    validations: {
                        required: true
                    }
                },
                documentNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                companyDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                voyageNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                externalVoyageNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                tankList: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                confirmation: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                contractNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                idRegistryProduct: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                productCode: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                weightInMtAir: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                volumeInM3At15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                densityAt15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 5
                },
                movementInstructionNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                url: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
            'RCD': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                voyageNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                idRegistryProduct: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                productCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 150
                    }
                },
                shipper: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 200
                    }
                },
                documentHolder: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 200
                    }
                },
                movementInstructionNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                departureDate: {
                    readonly: false,
                    hidden: true,
                    type: 'date',
                },
                dischargePortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                },
                masterOfVessel: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
            'RCC': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                voyageNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                idRegistryProduct: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                productCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 150
                    }
                },
                loadingPortCode: {
                    readonly: false,
                    hidden: true,
                    type: 'text'
                },
                dischargePortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 200
                    }
                },
                movementInstructionNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                masterOfVessel: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
            'SHT': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                productCode: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 150
                    }
                },
                dischargePortDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 200
                    }
                },
                movementInstructionNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                masterOfVessel: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
            'XAB': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentVersion: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLot: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                documentNumber: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                issueDate: {
                    readonly: false,
                    hidden: false,
                    type: 'date',
                    format: 'dd/MM/yyyy HH:mm'
                },
                languageCode: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                numberOfOriginals: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                numberOfCopies: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true
                    }
                },
                departurePortAndLocationDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                counterpartyDescription: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                deliveryNoteHolder: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 400
                    }
                },
                destinationDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 600
                    }
                },
                shippingNote: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                commercialReason: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                transportTypeDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                carrierDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 200
                    }
                },
                locationOfLoadingAndIncoterm: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                vesselName: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                contractNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                voyageNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 20
                    }
                },
                idRegistryProduct: {
                    readonly: true,
                    hidden: true,
                    type: 'number'
                },
                productCode: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                },
                productDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'textarea',
                    validations: {
                        maxLength: 300
                    }
                },
                weightMtAir: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    // decimals: 3
                },
                weightInMtAirCoded: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                volumeInM3At15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                volumeInM3At15CDegCoded: {
                    readonly: true,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                tareWeight: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                grossWeight: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 3
                },
                tit: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 1
                },
                externalVoyageNumber: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                nc: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                },
                densityAt15CDeg: {
                    readonly: false,
                    hidden: false,
                    type: 'decimal',
                    decimals: 5
                },
                tankList: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                signer: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date'
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text'
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                },
            },
        },
        documentItems: {
            'PLZ': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentBillOfLading: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                uoMCode: {
                    readonly: false,
                    hidden: false,
                    type: 'comboKey',
                    refLabel: 'uoMDescription',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=MovementInstruction'
                },
                uoMDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'comboValue',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=MovementInstruction'
                },
                movementInstructionLotUoMDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        required: true,
                        maxLength: 50
                    }
                },
                quantity: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true,
                        minValue: 0.00001,
                    },
                },
                formatQuantity: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                }
            },
            'CQTA': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentCertificateOfQuantity: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                uoMCode: {
                    readonly: false,
                    hidden: false,
                    type: 'comboKey',
                    refLabel: 'uoMDescription',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=MovementInstruction'
                },
                uoMDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'comboValue',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=MovementInstruction'
                },
                movementInstructionLotUoMDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        required: true,
                        maxLength: 50
                    }
                },
                conversionFactor: {
                    readonly: true,
                    hidden: false,
                    type: 'number',
                },
                quantity: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true,
                        minValue: 0.00001,
                    },
                },
                formatQuantity: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                    validations: {
                        maxLength: 10
                    }
                }
            },
            'RCC': {
                id: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idClearanceDocumentMasterReceiptSamples: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                },
                idMovementInstructionLotAnalysisSample: {
                    readonly: true,
                    hidden: true,
                    type: 'number',
                    // validations: {
                    //     required: true
                    // }
                },
                receiverDescription: {
                    readonly: false,
                    hidden: false,
                    type: 'text',
                    validations: {
                        maxLength: 50
                    }
                },
                idRegistrySamplingPoint: {
                    readonly: true,
                    hidden: false,
                    type: 'comboKey',
                    validations: {
                        required: true
                    },
                    refLabel: 'samplingPointDescription',
                    api: 'masterdata/sampling-point-combo?getOnlyValid=true',
                },
                samplingPointDescription: {
                    readonly: true,
                    hidden: false,
                    type: 'comboValue',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/sampling-point-combo?getOnlyValid=true',
                },
                samplingPointDescriptionEN: {
                    hidden: true,
                },
                idRegistryAnalysisSampleReceiver: {
                    readonly: true,
                    hidden: false,
                    type: 'comboKey',
                    refLabel: 'sampleReceiverDescription',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/analysis-sample-receiver-combo?getOnlyValid=true',
                },
                sampleReceiverDescription: {
                    readonly: true,
                    hidden: false,
                    type: 'comboValue',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/analysis-sample-receiver-combo?getOnlyValid=true',
                },
                numberOfSamples: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                },
                uoMCode: {
                    readonly: true,
                    hidden: false,
                    type: 'comboKey',
                    refLabel: 'uoMDescription',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=MovementInstruction'
                },
                uoMDescription: {
                    readonly: true,
                    hidden: false,
                    type: 'comboValue',
                    validations: {
                        required: true
                    },
                    api: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=MovementInstruction'
                },
                quantity: {
                    readonly: false,
                    hidden: false,
                    type: 'number',
                    validations: {
                        required: true,
                        minValue: 0.00001,
                    },
                },
                insertDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                cancelDate: {
                    readonly: true,
                    hidden: true,
                    type: 'date',
                },
                createdBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                    // validations: {
                    //     required: true
                    // }
                },
                lastChangedBy: {
                    readonly: true,
                    hidden: true,
                    type: 'text',
                },
                isCancelled: {
                    readonly: true,
                    hidden: true,
                    type: 'boolean',
                    // validations: {
                    //     required: true
                    // }
                }
            },
        }
    },
    EquipmentSenderTankDecantingOpSwitch: {
        title: 'equipmentSenderTankDecantingOpSwitch.page_title',
        api: 'masterdata/equipment-sender-tank-decanting-op-switch',
        enableUpdate: true,
        editButtonName: 'equipmentSenderTankDecantingOpSwitchEditButton',
        enableDelete: true,
        deleteButtonName: 'equipmentSenderTankDecantingOpSwitchDeleteButton',
        enableInsert: true,
        addButtonName: 'equipmentSenderTankDecantingOpSwitchAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name:'idEquipmentTank',
                label: 'equipment.fk',
                type: 'select',
                selectApi: 'masterdata/equipment-combo?type=SERB',
                selectFormat: { 
                    label: 'label', 
                    value: 'value' 
                },
                sortField: 'label',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'equipmentTank',
                label: 'equipment.fk',
                type: 'string',
                formHide: true,
                readOnly: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=SERB',
                gridFilterSelectFormat: { 
                    label: 'label',
                    value: 'value' 
                },
                gridFilterName: 'idEquipmentTank'
            },
            {
                name: 'validityStartDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'validityEndDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            },
        ]
    },
    GeneralSettings: {
        title: 'generalSettings.page_title',
        api: 'system/general-settings',
        enableUpdate: true,
        editButtonName: 'generalSettingsEditButton',
        enableDelete: true,
        deleteButtonName: 'generalSettingsDeleteButton',
        enableInsert: true,
        addButtonName: 'generalSettingsAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'settingKey',
                label: 'generalSettings.settingsKey',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'settingValue',
                label: 'generalSettings.settingValue',
                type: 'string',
                validations: {
                    maxLength: 500,
                    required: true
                }
            },
            {
                name: 'settingType',
                label: 'generalSettings.settingType',
                type: 'select',
                gridFilterType: 'select',
                selectFormat: { label: 'value', value: 'value' },
                staticOptions: [{value: 'decimal'}, {value: 'bool'}, {value: 'string'}, {value: 'int'}, {value: 'datetime'}],
                gridFilterStaticOptions: [{value: 'decimal'}, {value: 'bool'}, {value: 'string'}, {value: 'int'}, {value: 'datetime'}],
                gridFilterSelectFormat: { label: 'value', value: 'value' },
                gridFilterName: 'settingType',
                validations: {
                    required: true
                }
            }
        ]
    },

    StockManagementFixedTankCalibration: {
        title: 'StockManagementFixedTankCalibration.page_title',
        api: 'stock-management/fixed-tank-calibration',
        enableUpdate: true,
        editButtonName: 'stockManagementFixedTankCalibrationEditButton',
        enableDelete: true,
        deleteButtonName: 'stockManagementFixedTankCalibrationDeleteButton',
        enableInsert: true,
        addButtonName: 'stockManagementFixedTankCalibrationAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'tankCode',
                label: 'StockManagementFixedTankCalibration.tankCode',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'kLevel',
                label: 'StockManagementFixedTankCalibration.kLevel',
                type: 'decimal',
                validations: {
                    required: true
                }
            },
            {
                name: 'defaultDensity',
                label: 'StockManagementFixedTankCalibration.defaultDensity',
                type: 'decimal',
                digits: 5,
                validations: {
                    required: true
                }
            }
        ]
    },

    StockManagementIgccTank: {
        title: 'StockManagementIgccTank.page_title',
        api: 'stock-management/igcc-tank',
        enableUpdate: true,
        editButtonName: 'stockManagementIgccTankEditButton',
        enableDelete: true,
        deleteButtonName: 'stockManagementIgccTankDeleteButton',
        enableInsert: true,
        addButtonName: 'stockManagementIgccTankAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'tankCode',
                label: 'StockManagementIgccTank.tankCode',
                type: 'string',
                validations: {
                    maxLength: 50,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 255,
                    required: true
                }
            },
            {
                name: 'plantCode',
                label: 'StockManagementIgccTank.plantCode',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            }
        ]
    },

    StockManagementLockedAsset: {
        title: 'StockManagementLockedAsset.page_title',
        api: 'stock-management/locked-asset',
        enableUpdate: false,
        enableDelete: true,
        deleteButtonName: 'stockManagementLockedAssetDeleteButton',
        enableInsert: true,
        addButtonName: 'stockManagementLockedAssetAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'referenceDate',
                label: 'StockManagementLockedAsset.referenceDate',
                type: 'date'
            },
            {
                name: 'assetCode',
                label: 'StockManagementLockedAsset.assetCode',
                type: 'string'
            }
        ]
    },

    StockManagementLockedDaily: {
        title: 'StockManagementLockedDaily.page_title',
        api: 'stock-management-daily-locked',
        updateApi: 'stock-management-daily-locked',
        editMode: 'dialog',
        strechColumn: true,
        preventPagination: true,
        preventFilters: true,
        hideRefresh: true,
        editButtonName: 'StockManagementLockedDailyEditButton',
        enableUpdate: true,
        deleteButtonName: 'StockManagementLockedDailyDeleteButton',
        enableDelete: true,
        enableInsert: false,
        queryParamFilters: true,
        preventAutoLoad: true,

        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'referenceDate',
                label: 'StockManagementLockedDaily.referenceDate',
                type: 'date',
                inLineEdit: true,
            },
            {
                name: 'tankcode',
                label: 'StockManagementLockedDaily.tank',
                type: 'string',
                inLineEdit: true,
            },
            {
                name: 'type',
                label: 'StockManagementLockedDaily.type',
                type: 'string',
                inLineEdit: true,
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                inLineEdit: true,

            },
            {
                name: 'productDescription',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct',
            },
            {
                name: 'productLevel',
                label: 'StockManagementLockedDaily.productLevel',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'waterLevel',
                label: 'StockManagementLockedDaily.waterLevel',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'density',
                label: 'StockManagementLockedDaily.density',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'temperature',
                label: 'StockManagementLockedDaily.temperature',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'pressure',
                label: 'StockManagementLockedDaily.pressure',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'weightInKg',
                label: 'StockManagementLockedDaily.weightInKg',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'volumeInLt',
                label: 'StockManagementLockedDaily.volumeInLt',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'totalVolumeInCubicMeters',
                label: 'StockManagementLockedDaily.totalVolumeInCubicMeters',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'liquidWeightInTons',
                label: 'StockManagementLockedDaily.liquidWeightInTons',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'netGasWeightInTons',
                label: 'StockManagementLockedDaily.netGasWeightInTons',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'gasVolumeInCubicMeters',
                label: 'StockManagementLockedDaily.gasVolumeInCubicMeters',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'molecularWeight',
                label: 'StockManagementLockedDaily.molecularWeight',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'gasTemperature',
                label: 'StockManagementLockedDaily.gasTemperature',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'waterLevelCubicMeters',
                label: 'StockManagementLockedDaily.waterLevelCubicMeters',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'environmentTemperature',
                label: 'StockManagementLockedDaily.environmentTemperature',
                type: 'number',
                inLineEdit: true,
            },
            {
                name: 'note',
                label: 'StockManagementLockedDaily.note',
                type: 'string',
                inLineEdit: true,
            },
        ]
    },

    DeltaStockReportExcludedTanksConfig: {
        title: 'DeltaStockReportExcludedTanksConfig.page_title',
        api: 'stock-management-delta-stock-report-excluded-tanks-config',
        enableUpdate: false,
        enableDelete: true,
        deleteButtonName: 'deltaStockReportExcludedTanksConfigDeleteButton',
        enableInsert: true,
        addButtonName: 'deltaStockReportExcludedTanksConfigAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'tankCode',
                label: 'tankOperations.tank',
                type: 'string'
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'endDate',
                label: 'endDate',
                type: 'date',
                validations: {
                    required: true
                }
            }
        ]
    },

    DeltaStockReportProductConfig: {
        title: 'DeltaStockReportProductConfig.page_title',
        api: 'stock-management-delta-stock-report-product-config',
        enableUpdate: false,
        enableDelete: true,
        deleteButtonName: 'deltaStockReportProductConfigDeleteButton',
        enableInsert: true,
        addButtonName: 'deltaStockReportProductConfigAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'category',
                label: 'DeltaStockReportProductConfig.category',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 50
                }
            },
            {
                name: 'subCategory',
                label: 'DeltaStockReportProductConfig.subCategory',
                type: 'string',
                validations: {
                    required: true,
                    maxLength: 50
                }
            }
        ]
    },

    TanksMovementsListA18: {
        title: 'TanksMovementsListA18.page_title',
        api: 'fiscal-reports/a18',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        strechColumn: true,
        preventAutoLoad: true,
        hideRefresh: true,
        idColumn: "id",
        queryParamFilters: true,
        preventPagination: true,
        filterLocal: true,
        preventFilters: true,
        columns: [
            {
                 name: 'measurementReportNumber',
                 label: 'TanksMovementsListA18.measurementReportNumber',
                 type: 'string'
            },
            {
                 name: 'tankCode',
                 label: 'TanksMovementsListA18.tankCode',
                 type: 'string'
            },
            {
                 name: 'tankFiscalCircuitCode',
                 label: 'TanksMovementsListA18.tankFiscalCircuitCode',
                 type: 'string'
            },
            {
                 name: 'operationDate',
                 label: 'TanksMovementsListA18.operationDate',
                 type: 'date',
                 datetime: true
            },
            {
                 name: 'operationTypeShortDesc',
                 label: 'TanksMovementsListA18.operationTypeShortDesc',
                 type: 'string'
            },
            {
                 name: 'operationTypeCode',
                 label: 'TanksMovementsListA18.operationTypeCode',
                 type: 'string'
            },
            {
                 name: 'productCode',
                 label: 'TanksMovementsListA18.productCode',
                 type: 'string'
            },
            {
                 name: 'otherTankCode',
                 label: 'TanksMovementsListA18.otherTankCode',
                 type: 'string'
            },
            {
                 name: 'otherTankFiscalCircuitCode',
                 label: 'TanksMovementsListA18.otherTankFiscalCircuitCode',
                 type: 'string'
            },
            {
                 name: 'movedWeight',
                 label: 'TanksMovementsListA18.movedWeight',
                 type: 'number'
            },
            {
                 name: 'movedVolume',
                 label: 'TanksMovementsListA18.movedVolume',
                 type: 'number'
            },
            {
                 name: 'company',
                 label: 'TanksMovementsListA18.company',
                 type: 'string'
            },
            {
                 name: 'xyz',
                 label: 'TanksMovementsListA18.xyz',
                 type: 'string'
            },
            {
                 name: 'fiscalPositionShortDesc',
                 label: 'TanksMovementsListA18.fiscalPositionShortDesc',
                 type: 'string'
            },
            {
                 name: 'fiscalDestinationShortDesc',
                 label: 'TanksMovementsListA18.fiscalDestinationShortDesc',
                 type: 'string'
            },
            {
                 name: 'fiscalUseDesc',
                 label: 'TanksMovementsListA18.fiscalUseDesc',
                 type: 'string'
            }
        ]
    },

    StockSummary: {
        title: 'StockSummary.page_title',
        api: 'fiscal-reports/stock-summary',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        preventAutoLoad: true,
        strechColumn: true,
        hideRefresh: true,
        idColumn: "id",
        queryParamFilters: true,
        preventPagination: true,
        preventFilters: true,
        filterLocal: true,
        columns: [
            {
                name: 'productCode',
                label: 'StockSummary.productCode',
                type: 'string'
           },
           {
                name: 'productDescription',
                label: 'StockSummary.productDescription',
                type: 'string'
            },
           {
                name: 'initialStockWeight',
                label: 'StockSummary.initialStockWeight',
                type: 'number'
           },
           {
                name: 'initialStockVolume',
                label: 'StockSummary.initialStockVolume',
                type: 'number'
           },
           {
                name: 'introducedWeight',
                label: 'StockSummary.introducedWeight',
                type: 'number'
           },
           {
                name: 'introducedVolume',
                label: 'StockSummary.introducedVolume',
                type: 'number'
           },
           {
                name: 'processedWeight',
                label: 'StockSummary.processedWeight',
                type: 'number'
           },
           {
                name: 'processedVolume',
                label: 'StockSummary.processedVolume',
                type: 'number'
           },
           {
                name: 'fiscalStockWeight',
                label: 'StockSummary.fiscalStockWeight',
                type: 'number'
           },
           {
                name: 'fiscalStockVolume',
                label: 'StockSummary.fiscalStockVolume',
                type: 'number'
           },
        ]
    },

    AdditiveReport: {
        title: 'AdditiveReport.page_title',
        api: 'fiscal-reports/additive',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        preventAutoLoad: true,
        strechColumn: true,
        hideRefresh: true,
        idColumn: "id",
        queryParamFilters: true,
        preventPagination: true,
        filterLocal: true,
        preventFilters: true,
        columns: [
            {
                name: 'tankCode',
                label: 'AdditiveReport.tankCode',
                type: 'string'
           },
           {
               name: 'operationDate',
               label: 'AdditiveReport.operationDate',
               type: 'date',
               datetime: true
            },
           {
                name: 'productDescription',
                label: 'StockSummary.productDescription',
                type: 'string'
            },
           {
                name: 'initialFiscalStockWeight',
                label: 'StockSummary.initialStockWeight',
                type: 'number'
           },
           {
                name: 'initialFiscalStockVolume',
                label: 'StockSummary.initialStockVolume',
                type: 'number'
           },
           {
                name: 'introductionInWeight',
                label: 'StockSummary.introducedWeight',
                type: 'number'
           },
           {
                name: 'introductionInVolume',
                label: 'StockSummary.introducedVolume',
                type: 'number'
           },
           {
                name: 'extractionInWeight',
                label: 'StockSummary.processedWeight',
                type: 'number'
           },
           {
                name: 'extractionInVolume',
                label: 'StockSummary.processedVolume',
                type: 'number'
           },
           {
                name: 'finalFiscalStockWeight',
                label: 'StockSummary.fiscalStockWeight',
                type: 'number'
           },
           {
                name: 'finalFiscalStockVolume',
                label: 'StockSummary.fiscalStockVolume',
                type: 'number'
           },
        ]
    },

    VettingSafety: {
        title: 'VettingSafety.page_title',
        api: 'vetting-safety',
        updateApi: 'vetting-safety',
        enableUpdate: true,
        editMode: 'dialog',
        strechColumn: true,
        editButtonName: 'vettingSafetyEditButton',
        enableDelete: false,
        enableInsert: false,
        preventAutoLoad: true,
        hideRefresh: true,
        idColumn: "safetyVettingId",
        queryParamFilters: true,
        preventPagination: true,
        preventFilters: true,
        columns: [
            // {
            //     name: 'status',
            //     label: 'VettingSafety.status',
            //     type: 'string'
            // },
            {
                name: 'plantCode',
                label: 'VettingSafety.plant',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'voyageNumber',
                label: 'VettingSafety.voyageNumber',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'vesselName',
                label: 'VettingSafety.vessel',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'vesselImoCode',
                label: 'VettingSafety.imoCode',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'chargeDischargeFlag',
                label: 'VettingSafety.chargeDischarge',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'product',
                label: 'VettingSafety.product',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'voyageType',
                label: 'VettingSafety.voyageType',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'agency',
                label: 'VettingSafety.agency',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'laycanStartDate',
                label: 'VettingSafety.laycan',
                type: 'date',
                onlyDate: true,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'etaDate',
                label: 'VettingSafety.eta',
                type: 'date',
                datetime: true,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'norDate',
                label: 'VettingSafety.etb',
                type: 'date',
                datetime: true,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'ticket',
                label: 'VettingSafety.ticket',
                type: 'string',
                inLineEdit: true
            },
            {
                name: 'notes',
                label: 'VettingSafety.note',
                type: 'string',
                inLineEdit: true
            },
            {
                name: 'isPreArrivalOk',
                label: 'VettingSafety.isPreArrivalOk',
                type: 'boolean',
                inLineEdit: true
            },
            {
                name: 'mooringDate',
                label: 'VettingSafety.mooringDate',
                type: 'date',
                datetime: true,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'inspectionDate',
                label: 'VettingSafety.inspectionDate',
                type: 'date',
                onlyDate: true,
                inLineEdit: true
            },
            { 
                label: 'VettingSafety.evaluation',
                name: 'evaluation', 
                type: 'select', 
                inLineEdit: true, 
                selectApi: 'vetting-safety-evaluation-combo', 
                updateField: 'evaluationCode'
            },
            {
                name: 'doubleMooring',
                label: 'VettingSafety.doubleMooring',
                type: 'boolean',
                inLineEdit: true
            },
            {
                name: 'vesselNomination',
                label: 'VettingSafety.vesselNamed',
                type: 'boolean',
                inLineEdit: true
            },
            {
                name: 'invoice',
                label: 'VettingSafety.invoice',
                type: 'boolean',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'amount',
                label: 'VettingSafety.amount',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            }
        ]
    },

    StockManagementMovementType: {
        title: 'StockManagementMovementType.page_title',
        api: 'stock-management/movement-type',
        enableUpdate: true,
        editButtonName: 'stockManagementMovementTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'stockManagementMovementTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'stockManagementMovementTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 2
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 30
                }
            },
            {
                name: 'plantCode',
                label: 'StockManagementMovementType.isCalculationEnabled',
                type: 'boolean',
            }
        ]
    },

    StockManagementPipe: {
        title: 'StockManagementPipe.page_title',
        api: 'stock-management/pipe',
        enableUpdate: true,
        editButtonName: 'stockManagementPipeEditButton',
        enableDelete: true,
        deleteButtonName: 'stockManagementPipeDeleteButton',
        enableInsert: true,
        addButtonName: 'stockManagementPipeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'pipeCode',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 20
                }
            }
        ]
    },

    StockManagementTankCorrectionFactor: {
        title: 'StockManagementTankCorrectionFactor.page_title',
        api: 'stock-management/tank-correction-factor',
        enableUpdate: true,
        editButtonName: 'stockManagementTankCorrectionFactorEditButton',
        enableDelete: true,
        deleteButtonName: 'stockManagementTankCorrectionFactorDeleteButton',
        enableInsert: true,
        addButtonName: 'stockManagementTankCorrectionFactorAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'tankCode',
                label: 'StockManagementTankCorrectionFactor.tankCode',
                type: 'string',
                validations: {
                    maxLength: 20,
                }
            },
            {
                name: 'correctionFactor',
                label: 'StockManagementTankCorrectionFactor.correctionFactor',
                type: 'number',
            }
        ]
    },

    StockManagementDailyDeadStock: {
        title: 'StockManagementDailyDeadStock.page_title',
        api: 'stock-management/deadstock',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        strechColumn: true,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: true,
        preventAutoLoad: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
            },
            {
                name: 'referenceDate',
                label: 'StockManagementDailyDeadStock.referenceDate',
                type: 'date',
                onlyDate: true,
            },
            {
                name: 'assetCode', //
                label: 'StockManagementDailyDeadStock.assetCode',
                type: 'string',
            },
            {
                name: 'movementType',
                label: 'StockManagementDailyDeadStock.movementType',
                type: 'decimal',
                digits: 3,
            },
            
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'weight', //
                label: 'StockManagementDailyDeadStock.weight',
                type: 'number'
            },
            {
                name: 'volume', //
                label: 'StockManagementDailyDeadStock.volume',
                type: 'number'
            },
            {
                name: 'density', //
                label: 'StockManagementDailyDeadStock.density',
                type: 'decimal',
                digits: 4,
            },
            {
                name: 'level', //
                label: 'StockManagementDailyDeadStock.level',
                type: 'decimal',
                digits: 3,
            },
            {
                name: 'minLevel', //
                label: 'StockManagementDailyDeadStock.minLevel',
                type: 'decimal',
                digits: 3,
            },
            {
                name: 'realLevel', //
                label: 'StockManagementDailyDeadStock.realLevel',
                type: 'decimal',
                digits: 3,
            },
            {
                name: 'waterLevel', //
                label: 'StockManagementDailyDeadStock.waterLevel',
                type: 'decimal',
                digits: 3,
            },
            {
                name: 'environmentTemperature',//
                label: 'StockManagementDailyDeadStock.environmentTemperature',
                type: 'decimal',
                digits: 2,
            },
            {
                name: 'temperature', //
                label: 'StockManagementDailyDeadStock.temperature',
                type: 'decimal',
                digits: 2,
            },
            {
                name: 'gasTemperature',//
                label: 'StockManagementDailyDeadStock.gasTemperature',
                type: 'decimal',
                digits: 2,
            },
            {
                name: 'molecularWeight', //
                label: 'StockManagementDailyDeadStock.molecularWeight',
                type: 'decimal',
                digits: 5,
            },
            {
                name: 'pressure', //
                label: 'StockManagementDailyDeadStock.pressure',
                type: 'decimal',
                digits: 3,
            }
        ]
    },

    StockManagementDailyPipe: {
        title: 'StockManagementDailyPipe.page_title',
        api: 'stock-management/daily-pipe',
        enableUpdate: true,
        editButtonName: 'stockManagementDailyPipeEditButton',
        enableDelete: false,
        deleteButtonName: 'stockManagementDailyPipeDeleteButton',
        enableInsert: false,
        strechColumn: true,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: true,
        preventAutoLoad: true,
        editMode: 'dialog',
        customActions: [
            {
                name: 'stockManagementDailyPipeDeleteButton',
                title: 'btn.delete',
                visible: false,
                cssClass: 'p-button-sm p-button-rounded p-button-warning mr-1 mb-1',
                icon: 'pi pi-trash',
                data: {},
                fields: [],
                action: function (data, caller) {
                    caller.$emit('onStockManagementPipeDelete', data);
                }
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
            },
            {
                name: 'referenceDate',
                label: 'StockManagementDailyPipe.referenceDate',
                type: 'date',
                inLineEdit: true,
                onlyDate: true,
                editEnabled: () => false,
            },
            {
                name: 'pipeCode',
                label: 'StockManagementDailyPipe.pipeCode',
                type: 'string',
                inLineEdit: true,
                editEnabled: () => true,
            },
            {
                name: 'capability',
                label: 'StockManagementDailyPipe.capability',
                type: 'decimal',
                digits: 3,
                inLineEdit: true
            },
            

            {
                name: 'movementType',  //movementTypeSource
                label: 'StockManagementDailyTank.movementType',
                type: 'select-simple',
                selectApi: 'stock-management/movement-type?start=0&limit=250',
                selectFormat: { label: 'description', value: 'id' },
                gridHide: true,
                inLineEdit: true,
                updateField: 'movementType'
            },
            {
                name: 'movementType',
                label: 'StockManagementDailyTank.movementType',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'stock-management/movement-type?start=0&limit=250',
                gridFilterSelectFormat: { label: 'description', value: 'id' },
                gridFilterName: 'movementType',
                cls: ({movementTypeSource}) =>{
                    switch(movementTypeSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'vesselName',
                label: 'StockManagementDailyPipe.vesselName',
                type: 'string',
                inLineEdit: true
            },
            
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                inLineEdit: true
            },
            {
                name: 'productDescription',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'density',
                label: 'StockManagementDailyPipe.density',
                type: 'decimal',
                digits: 4,
                inLineEdit: true
            },
            {
                name: 'temperature',
                label: 'StockManagementDailyPipe.temperature',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            },
            {
                name: 'fX54',
                label: 'StockManagementDailyPipe.fX54',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            },
            {
                name: 'fX56',
                label: 'StockManagementDailyPipe.fX56',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            },
            {
                name: 'weightInKg',
                label: 'StockManagementDailyPipe.weightInKg',
                type: 'decimal',
                digits: 3,
                inLineEdit: true
            },
            {
                name: 'notes',
                label: 'note',
                type: 'decimal',
                digits: 3,
                inLineEdit: true
            },
        ]
    },

    StockManagementDailyTank: {
        title: 'StockManagementDailyTank.page_title',
        api: 'stock-management/daily-tank',
        enableUpdate: true,
        editButtonName: 'stockManagementDailyTankEditButton',
        enableDelete: false,
        deleteButtonName: 'stockManagementDailyTankDeleteButton',
        enableInsert: false,
        insertButtonName: 'stockManagementDailyTankInsertButton',
        strechColumn: true,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: true,
        preventAutoLoad: true,
        editMode: 'dialog',
        queryParamFilters: true,

        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
                formHide: true
            },
            {
                name: 'referenceDate',
                label: 'StockManagementDailyTank.referenceDate',
                type: 'date',
                inLineEdit: true,
                gridHide: true,
                onlyDate: true,
                editEnabled: () => false,
            },

            {
                name: 'tankcode',  //
                label: 'StockManagementDailyTank.tankcode',
                type: 'select-simple',
                selectApi: 'masterdata/equipment-combo?type=SERB',
                selectFormat: { label: 'label', value: 'label' },
                gridHide: true,
                inLineEdit: true,
                editEnabled: (item) => {return !item.id},
                updateField: 'tankcode'
            },
            {
                name: 'tankcode',
                label: 'StockManagementDailyTank.tankcode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=SERB',
                gridFilterSelectFormat: { label: 'label', value: 'label' },
                gridFilterName: 'tankcode'
            },

            {
                name: 'movementType',  //movementTypeSource
                label: 'StockManagementDailyTank.movementType',
                type: 'select-simple',
                selectApi: 'stock-management/movement-type?start=0&limit=250',
                selectFormat: { label: 'description', value: 'description' },
                gridHide: true,
                inLineEdit: true,
                updateField: 'movementType'
            },
            {
                name: 'movementType',
                label: 'StockManagementDailyTank.movementType',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'stock-management/movement-type?start=0&limit=250',
                gridFilterSelectFormat: { label: 'description', value: 'description' },
                gridFilterName: 'movementType',
                cls: ({movementTypeSource}) =>{
                    switch(movementTypeSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'idRegistryProduct',  //idProductSource, productSource
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                inLineEdit: true
            },
            {
                name: 'productDescription',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct',
                cls: ({idProductSource}) =>{
                    switch(idProductSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'productLevel',  //productLevelSource
                label: 'StockManagementDailyTank.productLevel',
                type: 'number',
                inLineEdit: true,
                cls: ({productLevelSource}) =>{
                    switch(productLevelSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            
            // SOLO PER MANUALE + CONG -> Lvl Cong (mm)


            {
                name: 'waterLevel',  //waterLevelSource
                label: 'StockManagementDailyTank.waterLevel',
                type: 'number',
                inLineEdit: true,
                cls: ({waterLevelSource}) =>{
                    switch(waterLevelSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'yesterdayWaterLevel',  //waterLevelSource
                label: 'StockManagementDailyTank.yesterdayWaterLevel',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaWaterLevel',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaWaterLevel',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'sludgeWeight',  //sludgeWeightSource
                label: 'StockManagementDailyTank.sludgeWeight',
                type: 'number',
                inLineEdit: true,
                cls: ({sludgeWeightSource}) =>{
                    switch(sludgeWeightSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'sludgeVolume',  //sludgeWeightSource
                label: 'StockManagementDailyTank.sludgeVolume',
                type: 'number',
                inLineEdit: true,
                cls: ({sludgeVolumeSource}) =>{
                    switch(sludgeVolumeSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'density',  //densitySource
                label: 'StockManagementDailyTank.density',
                type: 'decimal',
                digits: 4,
                inLineEdit: true,
                cls: ({densitySource}) =>{
                    switch(densitySource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'yesterdayDensity',  //waterLevelSource
                label: 'StockManagementDailyTank.yesterdayDensity',
                type: 'decimal',
                digits: 4,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaDensity',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaDensity',
                type: 'decimal',
                digits: 4,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'temperature', // temperatureSource
                label: 'StockManagementDailyTank.temperature',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
                cls: ({temperatureSource}) =>{
                    switch(temperatureSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },

            {
                name: 'weightInKg', //weightInKgSource
                label: 'StockManagementDailyTank.weightInKg',
                type: 'number',
                inLineEdit: true,
                cls: ({weightInKgSource}) =>{
                    switch(weightInKgSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'volumeInLt', //volumeInLtSource
                label: 'StockManagementDailyTank.volumeInLt',
                type: 'number',
                inLineEdit: true,
                cls: ({volumeInLtSource}) =>{
                    switch(volumeInLtSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'waterLevelCubicMeters',  //
                label: 'StockManagementDailyTank.waterLevelCubicMeters',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'yesterdayWaterLevelCubicMeters',  //waterLevelSource
                label: 'StockManagementDailyTank.yesterdayWaterLevelCubicMeters',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaWaterLevelCubicMeters',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaWaterLevelCubicMeters',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'waterLevelFromMTS',  // waterLevelFromMTSource
                label: 'StockManagementDailyTank.waterLevelFromMTS',
                type: 'decimal',
                digits: 0,
                inLineEdit: true,
                cls: ({waterLevelFromMTSource}) =>{
                    switch(waterLevelFromMTSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'deltaWaterLevelFromMTS',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaWaterLevelFromMTS',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'waterVolumeCubicMetersMTS',  // 
                label: 'StockManagementDailyTank.waterVolumeCubicMetersMTS',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaWaterVolumeCubicMetersMTS',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaWaterVolumeCubicMetersMTS',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'volumeMTS',  // 
                label: 'StockManagementDailyTank.volumeMTS',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'weightMTS',  // 
                label: 'StockManagementDailyTank.weightMTS',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaWeightMTS',  // 
                label: 'StockManagementDailyTank.deltaWeightMTS',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'pressure',  //pressureSource
                label: 'StockManagementDailyTank.pressure',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                cls: ({pressureSource}) =>{
                    switch(pressureSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'molecularWeight',  // molecularWeightSource
                label: 'StockManagementDailyTank.molecularWeight',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
                cls: ({molecularWeightSource}) =>{
                    switch(molecularWeightSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'gasTemperature',  // gasTemperatureSource
                label: 'StockManagementDailyTank.gasTemperature',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
                cls: ({gasTemperatureSource}) =>{
                    switch(gasTemperatureSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'environmentTemperature',  // environmentTemperatureSource
                label: 'StockManagementDailyTank.environmentTemperature',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
                cls: ({environmentTemperatureSource}) =>{
                    switch(environmentTemperatureSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'note',  //
                label: 'note',
                type: 'string',
                inLineEdit: true
            },

            // {
            //     name: 'totalVolumeInCubicMeters',  //
            //     label: 'StockManagementDailyTank.totalVolumeInCubicMeters',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'liquidWeightInTons',  //
            //     label: 'StockManagementDailyTank.liquidWeightInTons',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'netGasWeightInTons',  //
            //     label: 'StockManagementDailyTank.netGasWeightInTons',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'gasVolumeInCubicMeters',  //
            //     label: 'StockManagementDailyTank.gasVolumeInCubicMeters',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'environmentSensor',  //
            //     label: 'StockManagementDailyTank.environmentSensor',
            //     type: 'string',
            //     inLineEdit: true
            // },
            // {
            //     name: 'spVolumeTotal',  //
            //     label: 'StockManagementDailyTank.spVolumeTotal',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spVolMax',  //
            //     label: 'StockManagementDailyTank.spVolMax',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spVolUnextractable',  //
            //     label: 'StockManagementDailyTank.spVolUnextractable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spVolAvailable',  //
            //     label: 'StockManagementDailyTank.spVolAvailable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightTotal',  //
            //     label: 'StockManagementDailyTank.spWeightTotal',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightMax',  //
            //     label: 'StockManagementDailyTank.spWeightMax',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightUnextractable',  //
            //     label: 'StockManagementDailyTank.spWeightUnextractable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightAvailable',  //
            //     label: 'StockManagementDailyTank.spWeightAvailable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightExtractable',  //
            //     label: 'StockManagementDailyTank.spWeightExtractable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
        ]
    },

    StockManagementDailyTankManualLocked: {
        title: 'StockManagementDailyTank.page_title',
        api: 'stock-management/daily-tank',
        enableUpdate: true,
        editButtonName: 'stockManagementDailyTankEditButton',
        enableDelete: false,
        deleteButtonName: 'stockManagementDailyTankDeleteButton',
        enableInsert: false,
        insertButtonName: 'stockManagementDailyTankInsertButton',
        strechColumn: true,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: true,
        preventAutoLoad: true,
        editMode: 'dialog',
        queryParamFilters: true,
        highlightConditions: [
            {
                field: 'type',
                fn: (el) => {return el === 'Locked'},
                class: 'row-yellow'
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
                formHide: true
            },
            {
                name: 'referenceDate',
                label: 'StockManagementDailyTank.referenceDate',
                type: 'date',
                inLineEdit: true,
                gridHide: true,
                onlyDate: true,
                editEnabled: () => false,
            },

            {
                name: 'tankcode',  //
                label: 'StockManagementDailyTank.tankcode',
                type: 'select-simple',
                selectApi: 'masterdata/equipment-combo?type=SERB',
                selectFormat: { label: 'label', value: 'label' },
                gridHide: true,
                inLineEdit: true,
                editEnabled: (item) => {return !item.id},
                updateField: 'tankcode'
            },
            {
                name: 'tankcode',
                label: 'StockManagementDailyTank.tankcode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=SERB',
                gridFilterSelectFormat: { label: 'label', value: 'label' },
                gridFilterName: 'tankcode'
            },

            {
                name: 'movementType',  //movementTypeSource
                label: 'StockManagementDailyTank.movementType',
                type: 'select-simple',
                selectApi: 'stock-management/movement-type?start=0&limit=250',
                selectFormat: { label: 'description', value: 'description' },
                gridHide: true,
                inLineEdit: true,
                updateField: 'movementType'
            },
            {
                name: 'movementType',
                label: 'StockManagementDailyTank.movementType',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'stock-management/movement-type?start=0&limit=250',
                gridFilterSelectFormat: { label: 'description', value: 'description' },
                gridFilterName: 'movementType',
                cls: ({movementType}) =>{
                    switch(movementType){
                        case 'FS':
                            return "fs-col-bg";
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'idRegistryProduct',  //idProductSource, productSource
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                inLineEdit: true
            },
            {
                name: 'productDescription',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct',
                cls: ({idProductSource}) =>{
                    switch(idProductSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'productLevel',  //productLevelSource
                label: 'StockManagementDailyTank.productLevel',
                type: 'number',
                inLineEdit: true,
                cls: ({productLevelSource}) =>{
                    switch(productLevelSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'lockedProductLevel',
                label: 'StockManagementDailyTank.lockedProductLevel',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false
            },
            // SOLO PER MANUALE + CONG -> Lvl Cong (mm)
            {
                name: 'lockedForcedProductLevel',
                label: 'StockManagementDailyTank.lockedForcedProductLevel',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
                cls: ({lockedForcedProductLevel}) =>{
                    if(lockedForcedProductLevel){
                        return "manual-col-bg"
                    }
                    return "";
                }
            },
            // SOLO PER MANUALE + CONG -> Lvl Cong (mm) Forzato Manualmente

            {
                name: 'waterLevel',  //waterLevelSource
                label: 'StockManagementDailyTank.waterLevel',
                type: 'number',
                inLineEdit: true,
                cls: ({waterLevelSource}) =>{
                    switch(waterLevelSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'yesterdayWaterLevel',  //waterLevelSource
                label: 'StockManagementDailyTank.yesterdayWaterLevel',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaWaterLevel',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaWaterLevel',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            
            {
                name: 'sludgeWeight',  //sludgeWeightSource
                label: 'StockManagementDailyTank.sludgeWeight',
                type: 'number',
                inLineEdit: true,
                cls: ({sludgeWeightSource}) =>{
                    switch(sludgeWeightSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'sludgeVolume',  //sludgeWeightSource
                label: 'StockManagementDailyTank.sludgeVolume',
                type: 'number',
                inLineEdit: true,
                cls: ({sludgeVolumeSource}) =>{
                    switch(sludgeVolumeSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'density',  //densitySource
                label: 'StockManagementDailyTank.density',
                type: 'decimal',
                digits: 4,
                inLineEdit: true,
                cls: ({densitySource}) =>{
                    switch(densitySource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'yesterdayDensity',  //waterLevelSource
                label: 'StockManagementDailyTank.yesterdayDensity',
                type: 'decimal',
                digits: 4,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaDensity',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaDensity',
                type: 'decimal',
                digits: 4,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'temperature', // temperatureSource
                label: 'StockManagementDailyTank.temperature',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
                cls: ({temperatureSource}) =>{
                    switch(temperatureSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },

            {
                name: 'weightInKg', //weightInKgSource
                label: 'StockManagementDailyTank.weightInKg',
                type: 'number',
                inLineEdit: true,
                cls: ({weightInKgSource}) =>{
                    switch(weightInKgSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'volumeInLt', //volumeInLtSource
                label: 'StockManagementDailyTank.volumeInLt',
                type: 'number',
                inLineEdit: true,
                cls: ({volumeInLtSource}) =>{
                    switch(volumeInLtSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'waterLevelCubicMeters',  //
                label: 'StockManagementDailyTank.waterLevelCubicMeters',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'yesterdayWaterLevelCubicMeters',  //waterLevelSource
                label: 'StockManagementDailyTank.yesterdayWaterLevelCubicMeters',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaWaterLevelCubicMeters',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaWaterLevelCubicMeters',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'waterLevelFromMTS',  // waterLevelFromMTSource
                label: 'StockManagementDailyTank.waterLevelFromMTS',
                type: 'decimal',
                digits: 0,
                inLineEdit: true,
                cls: ({waterLevelFromMTSource}) =>{
                    switch(waterLevelFromMTSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'deltaWaterLevelFromMTS',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaWaterLevelFromMTS',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'waterVolumeCubicMetersMTS',  // 
                label: 'StockManagementDailyTank.waterVolumeCubicMetersMTS',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaWaterVolumeCubicMetersMTS',  //waterLevelSource
                label: 'StockManagementDailyTank.deltaWaterVolumeCubicMetersMTS',
                type: 'number',
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'volumeMTS',  // 
                label: 'StockManagementDailyTank.volumeMTS',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'weightMTS',  // 
                label: 'StockManagementDailyTank.weightMTS',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'deltaWeightMTS',  // 
                label: 'StockManagementDailyTank.deltaWeightMTS',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                editEnabled: () => false,
            },
            {
                name: 'pressure',  //pressureSource
                label: 'StockManagementDailyTank.pressure',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                cls: ({pressureSource}) =>{
                    switch(pressureSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'molecularWeight',  // molecularWeightSource
                label: 'StockManagementDailyTank.molecularWeight',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
                cls: ({molecularWeightSource}) =>{
                    switch(molecularWeightSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'gasTemperature',  // gasTemperatureSource
                label: 'StockManagementDailyTank.gasTemperature',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
                cls: ({gasTemperatureSource}) =>{
                    switch(gasTemperatureSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'environmentTemperature',  // environmentTemperatureSource
                label: 'StockManagementDailyTank.environmentTemperature',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
                cls: ({environmentTemperatureSource}) =>{
                    switch(environmentTemperatureSource){
                        case 'PI':
                            return "pi-col-bg"
                        case 'SIM':
                            return "sim-col-bg"
                        case 'LIMS':
                            return "lims-col-bg"
                        case 'Manual':
                            return "manual-col-bg"
                        case 'WEB':
                            return "web-col-bg"
                        case 'MTS':
                            return "mts-col-bg"
                        case 'ERROR':
                            return "error-col-bg"
                    }
                    return "";
                }
            },
            {
                name: 'note',  //
                label: 'note',
                type: 'string',
                inLineEdit: true
            },

            // {
            //     name: 'totalVolumeInCubicMeters',  //
            //     label: 'StockManagementDailyTank.totalVolumeInCubicMeters',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'liquidWeightInTons',  //
            //     label: 'StockManagementDailyTank.liquidWeightInTons',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'netGasWeightInTons',  //
            //     label: 'StockManagementDailyTank.netGasWeightInTons',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'gasVolumeInCubicMeters',  //
            //     label: 'StockManagementDailyTank.gasVolumeInCubicMeters',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'environmentSensor',  //
            //     label: 'StockManagementDailyTank.environmentSensor',
            //     type: 'string',
            //     inLineEdit: true
            // },
            // {
            //     name: 'spVolumeTotal',  //
            //     label: 'StockManagementDailyTank.spVolumeTotal',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spVolMax',  //
            //     label: 'StockManagementDailyTank.spVolMax',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spVolUnextractable',  //
            //     label: 'StockManagementDailyTank.spVolUnextractable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spVolAvailable',  //
            //     label: 'StockManagementDailyTank.spVolAvailable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightTotal',  //
            //     label: 'StockManagementDailyTank.spWeightTotal',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightMax',  //
            //     label: 'StockManagementDailyTank.spWeightMax',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightUnextractable',  //
            //     label: 'StockManagementDailyTank.spWeightUnextractable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightAvailable',  //
            //     label: 'StockManagementDailyTank.spWeightAvailable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
            // {
            //     name: 'spWeightExtractable',  //
            //     label: 'StockManagementDailyTank.spWeightExtractable',
            //     type: 'decimal',
            //     digits: 3,
            //     inLineEdit: true
            // },
        ]
    },

    StockManagementExcludedTanks: {
        title: 'StockManagementDailyTank.page_title',
        api: 'stock-management/excluded-tanks',
        enableUpdate: false,
        enableDelete: true,
        deleteButtonName: 'stockManagementExcludedTankButtons',
        enableInsert: true,
        insertButtonName: 'stockManagementDailyTankInsertButton',
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: true,
        preventAutoLoad: false,
        editMode: 'dialog',
        queryParamFilters: true,

        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
                formHide: true
            },
            {
                name: 'tankcode',
                label: 'StockManagementDailyTank.tankcode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=SERB',
                gridFilterSelectFormat: { label: 'label', value: 'label' },
                gridFilterName: 'tankcode'
            },
        ]
    },

    StockManagementManualEditLog: {
        title: 'StockManagementManualEditLog.page_title',
        api: 'stock-management-manual-edit-log',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: true,
        preventAutoLoad: false,

        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
                formHide: true
            },
            {
                name: 'referenceDate',
                label: 'StockManagementManualEditLog.referenceDate',
                type: 'date',
                onlyDate: true
            },
            {
                name: 'tankCode',
                label: 'StockManagementDailyTank.tankcode',
                type: 'string',
                onlyDate: true
            },
            {
                name: 'modificationDate',
                label: 'StockManagementManualEditLog.modificationDate',
                type: 'date',
                datetime: true
            },
            {
                name: 'modificationUser',
                label: 'StockManagementManualEditLog.modificationUser',
                type: 'string'
            },
            {
                name: 'modificationField',
                label: 'StockManagementManualEditLog.modificationField',
                type: 'string'
            },
            {
                name: 'oldValue',
                label: 'StockManagementManualEditLog.oldValue',
                type: 'string'
            },
            {
                name: 'newValue',
                label: 'StockManagementManualEditLog.newValue',
                type: 'string'
            }
        ]
    },
    StockManagementMts: {
        title: 'StockManagementMts.page_title',
        api: 'stock-management/mts',
        enableDelete: true,
        deleteButtonName: 'stockManagementMtsDeleteButton',
        idColumn: "id",
        defaultSortField: 'dailyStockReferenceDate',
        defaultSortOrder: 1,
        preventAutoLoad: true,
        preventFilters: false,
        preventPagination: true,
        hideRefresh: true,
        filterLocal: true,
        queryParamFilters: true,
        editMode: 'dialog',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                gridHide: true
            },
            {
                name: 'dailyStockReferenceDate',
                label: 'StockManagementMts.referenceDate',
                type: 'date',
                onlyDate: true,
                inLineEdit: true, 
                editEnabled: (item) => {return !item.id},
                defaultSort: 'asc',
                gridHide: true
            },
            {
                name: 'idRegistryPlant',
                label: 'registryPlant.fk',
                type: 'select',
                selectApi: 'masterdata/registry-plant-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true,
                inLineEdit: true, 
                validations: {
                    required: true
                }
            },
            {
                name: 'registryPlant',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true
            },
            {
                name: 'measureDate',
                label: 'StockManagementMts.measureDate',
                type: 'date', 
                datetime: true,
                inLineEdit: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'tankCodeMts',
                label: 'StockManagementMts.tankCodeMts',
                type: 'string',
                readonly: true,
                editEnabled: () => false,
                inLineEdit: true,
                cls: ({tankCodeMts}) => {
                    if(!tankCodeMts){
                        return "error-col-bg-red"
                    } 
                    return "";
                }
            },
            {
                name: 'tankCode',
                label: 'StockManagementMts.tankCode',
                type: 'select-simple',
                selectApi: 'masterdata/equipment-combo?type=SERB',
                selectFormat: { label: 'label', value: 'label' },
                gridHide: false,
                inLineEdit: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=SERB',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridFilterName: 'registryPlant',
                validations: {
                    required: true
                },
                cls: ({tankCode}) => {
                    if(!tankCode){
                        return "error-col-bg-red"
                    } 
                    return "";
                }
            },
            {
                name: 'waterLevelMts',
                label: 'StockManagementMts.waterLevelMts',
                type: 'string',
                readonly: true,
                editEnabled: () => false,
                inLineEdit: true,
                cls: ({waterLevelMts}) => {
                    if(!waterLevelMts){
                        return "error-col-bg-red"
                    } 
                    return "";
                }
            },
            {
                name: 'waterLevel',
                label: 'StockManagementMts.waterLevel',
                type: 'number',
                inLineEdit: true ,
                cls: ({waterLevel}) => {
                    if(!waterLevel){
                        return "error-col-bg-red"
                    } 
                    return "";
                }
            },
            {
                name: 'notes',
                label: 'StockManagementMts.notes',
                type: 'string',
                readonly: true,
                editEnabled: () => false,
                inLineEdit: true
            },
            {
                name: 'productLevelMts',
                label: 'StockManagementMts.productLevelMts',
                type: 'string',
                readonly: true,
                editEnabled: () => false,
                inLineEdit: true
            },
            {
                name: 'recordStatus',
                label: 'StockManagementMts.recordStatus',
                type: 'string',
                readOnly: true,
                gridHide: true
            }
        ],
        customActions:[
            {
                title: 'StockManagementMts.btn.update',
                cssClass: 'p-button-rounded p-button mr-2',
                name: 'stockManagementMtsEditButton',
                icon: 'pi pi-pencil',
                data: {},
                fields: [],
                action: function (data, caller) {
                    caller.$emit('onEditMtsDataItemClick', data);
                }
            }
        ]
    },
    VesselProtocolAlerts: {
        title: 'VesselProtocolAlerts.page_title',
        api: 'vessel-protocol-alert-count',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: false,
        columns: [
            {
                name: 'alertType',
                label: 'VesselProtocolAlerts.alertType',
                type: 'string',
                defaultSort: 'asc'
            },
            {
                name: 'alertCount',
                label: 'VesselProtocolAlerts.alertCount',
                type: 'number'
            }
        ],
        customActions: [
            {
                name: 'vesselProtocolAlertsDetailsButton',
                title: 'btn.viewDetails',
                visible: false,
                cssClass: 'p-button-rounded p-button-success mr-2',
                icon: 'pi pi-info',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/vessel-protocol-alerts-details?code=${data.alertCode}`;
                        caller.$router.push(uri);
                }
            }
        ]
    },
    VesselProtocolAlertDetails: {
        title: 'VesselProtocolAlerts.page_title',
        api: 'vessel-protocol-alert',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        preventFilters: true,
        preventAutoLoad: true,
        queryParamFilters: true,

        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
                formHide: true
            },
            {
                name: 'protocolNumber',
                label: 'vesselProtocol.protocolNumber',
                type: 'string',
            },
            {
                name: 'voyageNumber',
                label: 'vesselProtocol.voyageNumber',
                type: 'string',
            },
            {
                name: 'productDescription',
                label: 'vesselProtocol.productDescription',
                type: 'string',
            },
            {
                name: 'operationType',
                label: 'vesselProtocol.operationType',
                type: 'string',
            },
            {
                name: 'vessel',
                label: 'vesselProtocol.vessel',
                type: 'string',
            },
            {
                name: 'originCountry',
                label: 'vesselProtocol.originCountry',
                type: 'string',
            },
            {
                name: 'destinationCountry',
                label: 'vesselProtocol.destinationCountry',
                type: 'string',
            }
        ],
        customActions: [
            {
                title: 'movementInstructions.btn.vesselProtocol',
                cssClass: 'p-button-rounded p-button-success mr-2',
                name: 'movementInstructionVesselProtocolFromLotButton',
                icon: 'pi pi-directions',
                action: function (data, caller) {
                    let uri = `/vessel-protocol?id=${data.vesselProtocolLotId}`;
                    caller.$router.push(uri);
                }
            },
            {
                title: 'VesselProtocolAlerts.hiddenButton',
                cssClass: 'p-button-rounded p-button mr-2',
                name: 'vesselProtocolAlertsHiddenButton',
                icon: 'pi pi-eye-slash',
                action: function (data, caller) {
                    caller.$confirm.require({
                        message: caller.$t('VesselProtocolAlerts.hiddenMessage'),
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: caller.$t('yes'),
                        rejectLabel: caller.$t('no'),
                        accept: () => {                  
                            let body = {
                                idVesselProtocol: data.id,
                                alertType: caller.filters.code.value
                            };

                            caller.$store.dispatch("generic/setLoading", true);           
                            genericService.post(`vessel-protocol-alert-hidden`, body).then(response => {
                                caller.$store.dispatch("generic/setLoading", false); 
                                if(response.success){
                                    caller.onRefresh();
                                }                               
                            })
                        },
                        reject: () => {
                            caller.$confirm.close();
                        }
                    });
                }
            }
        ],
        toolbarActions: [
            {
                type:'button',
                visible: true,
                name: 'alertProtocolloNaviExport',
                icon: 'pi pi-file-excel',
                title: 'VesselProtocolAlerts.exportExcel',
                cssClass: 'p-button-outlined p-button',
                action: function (data, caller) {
                    caller.$store.dispatch("generic/setLoading", true);             
                    genericService.dowloadFile(`vessel-protocol-alert-export?alertType=${caller.filters.code.value}`).then(response => {
                        caller.$store.dispatch("generic/setLoading", false); 

                        if(response.success){
                            let fileNameWithExtension = `Alert_${caller.filters.code.value}.xlsx`;
                            var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            var fileLink = document.createElement('a');
                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', fileNameWithExtension);
                            document.body.appendChild(fileLink);
                        
                            fileLink.click();
                        }else{
                            caller.$toast.add({ detail: response.message || caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
            }
        ]
    },
    VesselProtocolReportLoading: {
        title: 'ProtocolReport.loading',
        api: 'vessel-protocol-loading-print-grid-list-sim-web',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        strechColumn: true,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: true,
        preventAutoLoad: true,
        queryParamFilters: true,
        columnsGroups: [
            {
                header: 'ProtocolReport.generalInfo',
                colSpan: 7
            },
            {
                header: 'ProtocolReport.asset',
                colSpan: 3
            },
            {
                header: 'ProtocolReport.customsInfo',
                colSpan: 4
            },
            {
                header: 'ProtocolReport.refineryAscertainmentData',
                colSpan: 5
            },
            {
                header: 'ProtocolReport.boardAscertainmentData',
                colSpan: 3
            },
            {
                header: 'ProtocolReport.receiverAscertainmentData',
                colSpan: 2
            },
            {
                header: 'ProtocolReport.diffKg',
                colSpan: 1
            },
            {
                header: 'ProtocolReport.diffPerc',
                colSpan: 1
            },
            {
                header: 'ProtocolReport.diffKg',
                colSpan: 1
            },
            {
                header: 'ProtocolReport.diffPerc',
                colSpan: 1
            },
            {
                header: 'ProtocolReport.diffLt',
                colSpan: 1
            },
            {
                header: 'ProtocolReport.diffPerc',
                colSpan: 1
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
                formHide: true
            },
            { name: 'protocolNumber', label: 'ProtocolReport.protocolNumber', type: 'string'},
            { name: 'allocation', label: 'ProtocolReport.allocation', type: 'string'},
            { name: 'shipsName', label: 'ProtocolReport.shipsName', type: 'string'},
            { name: 'cargoName', label: 'ProtocolReport.cargoName', type: 'string'},
            { name: 'cargoDestination', label: 'ProtocolReport.cargoDestination', type: 'string'},
            { name: 'finalDestination', label: 'ProtocolReport.finalDestination', type: 'string'},
            { name: 'installation', label: 'ProtocolReport.installation', type: 'string'},
            { name: 'berth', label: 'ProtocolReport.berth', type: 'string'},
            { name: 'shorePipeline', label: 'ProtocolReport.shorePipeline', type: 'string'},
            { name: 'shoreTank', label: 'ProtocolReport.shoreTank', type: 'string'},
            { name: 'customsDeclaration', label: 'ProtocolReport.customsDeclaration', type: 'string'},
            { name: 'documentsNumber', label: 'ProtocolReport.documentsNumber', type: 'string'},
            { name: 'documentsDate', label: 'ProtocolReport.documentsDate', type: 'date'},
            { name: 'inf3AtrEur1Coo', label: 'ProtocolReport.inf3AtrEur1Coo', type: 'string'},
            { name: 'averageDensity', label: 'ProtocolReport.averageDensity',  type: 'decimal', digits: 4, showZero: true},
            { name: 'zolfoPerc', label: 'ProtocolReport.zolfoPerc',  type: 'decimal', digits: 2, showZero: true},
            { name: 'billOfLadingDate', label: 'ProtocolReport.billOfLadingDate', type: 'date'},
            { name: 'billOfLadingQttyLitri', label: 'ProtocolReport.billOfLadingQttyLitri',  type: 'decimal', digits: 3, showZero: true},
            { name: 'billOfLadingQttyKg', label: 'ProtocolReport.billOfLadingQttyKg',  type: 'decimal', digits: 3, showZero: true},

            { name: 'pesoBordo', label: 'ProtocolReport.pesoBordo',  type: 'decimal', digits: 0, showZero: true},
            { name: 'vef', label: 'ProtocolReport.vef',  type: 'decimal', digits: 4, showZero: true},
            { name: 'pesoBordoVEF', label: 'ProtocolReport.pesoBordoVEF',  type: 'decimal', digits: 0, showZero: true},
            { name: 'qtaAccertataDepRiceventeKg', label: 'ProtocolReport.qtaAccertataDepRiceventeKg',  type: 'decimal', digits: 0, showZero: true}, 
            { name: 'qtaAccertataDepRiceventeLitri', label: 'ProtocolReport.qtaAccertataDepRiceventeLitri',  type: 'decimal', digits: 0, showZero: true},
            { name: 'pesoBordoVEF_Meno_QtaAccertataBillOfLadingKg', label: 'ProtocolReport.pesoBordoVEF_Meno_QtaAccertataBillOfLadingKg',  type: 'decimal', digits: 0, showZero: true},
            { name: 'pesoBordoVEF_Meno_QtaAccertataBillOfLadingPerc', label: 'ProtocolReport.pesoBordoVEF_Meno_QtaAccertataBillOfLadingPerc',  type: 'decimal', digits: 2, showZero: true},

            { name: 'qtaAccDepRicevente_Meno_QtaAccBillOfLadingKg', label: 'ProtocolReport.qtaAccDepRicevente_Meno_QtaAccBillOfLadingKg',  type: 'decimal', digits: 3, showZero: true},
            { name: 'qtaAccDepRicevente_Meno_QtaAccBillOfLadingKgPerc', label: 'ProtocolReport.qtaAccDepRicevente_Meno_QtaAccBillOfLadingKgPerc',  type: 'decimal', digits: 2, showZero: true},
            { name: 'qtaAccDepRicevente_Meno_QtaAccBillOfLadingLitri', label: 'ProtocolReport.qtaAccDepRicevente_Meno_QtaAccBillOfLadingLitri',  type: 'decimal', digits: 3, showZero: true},
            { name: 'qtaAccDepRicevente_Meno_QtaAccBillOfLadingLitriPerc', label: 'ProtocolReport.qtaAccDepRicevente_Meno_QtaAccBillOfLadingLitriPerc',  type: 'decimal', digits: 2, showZero: true}
        ]
    },
    VesselProtocolReportDischarge: {
        title: 'ProtocolReport.discharge',
        api: 'vessel-protocol-discharge-print-grid-list-sim-web',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        strechColumn: true,
        idColumn: "id",
        hideRefresh: true,
        preventPagination: true,
        defaultLimit: 100000,
        filterLocal: true,
        preventAutoLoad: true,
        queryParamFilters: true,
        columnsGroups: [
            {
                header: 'ProtocolReport.generalInfo',
                colSpan: 8
            },
            {
                header: 'ProtocolReport.asset',
                colSpan: 3
            },
            {
                header: 'ProtocolReport.customsInfo',
                colSpan: 3
            },
            {
                header: 'ProtocolReport.commercialInfo',
                colSpan: 3
            },
            {
                header: 'ProtocolReport.billOfLadingData',
                colSpan: 4
            },
            {
                header: 'ProtocolReport.ascertainmentData',
                colSpan: 10
            },
            {
                header: 'ProtocolReport.volumeDiff',
                colSpan: 6
            },
            {
                header: 'ProtocolReport.percDiff',
                colSpan: 6
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
                formHide: true
            },
            { name: 'protocolNumber', label: 'ProtocolReport.protocolNumber', type: 'string'},
            { name: 'allocation', label: 'ProtocolReport.allocation', type: 'string'},
            { name: 'shipsName', label: 'ProtocolReport.shipsName', type: 'string'},
            { name: 'cargoName', label: 'ProtocolReport.cargoName', type: 'string'},
            { name: 'origineOfTheCargo', label: 'ProtocolReport.origineOfTheCargo', type: 'string'},
            { name: 'comingOfTheCargo', label: 'ProtocolReport.comingOfTheCargo', type: 'string'},
            { name: 'shipToShip', label: 'ProtocolReport.shipToShip', type: 'string'},
            { name: 'installation', label: 'ProtocolReport.installation', type: 'string'},
            { name: 'berth', label: 'ProtocolReport.berth', type: 'string'},
            { name: 'shorePipeline', label: 'ProtocolReport.shorePipeline', type: 'string'},
            { name: 'shoreTank', label: 'ProtocolReport.shoreTank', type: 'string'},
            { name: 'customsDeclaration', label: 'ProtocolReport.customsDeclaration', type: 'string'},
            { name: 'documentsNumber', label: 'ProtocolReport.documentsNumber', type: 'string'},
            { name: 'documentsDate', label: 'ProtocolReport.documentsDate', type: 'date'},
            { name: 'incoterms', label: 'ProtocolReport.incoterms', type: 'string'},
            { name: 'totaleFatture', label: 'ProtocolReport.totaleFatture',  type: 'decimal', digits: 2},
            { name: 'valoreUnitario', label: 'ProtocolReport.valoreUnitario', type: 'decimal', digits: 2},
            { name: 'dataPolizza', label: 'ProtocolReport.dataPolizza', type: 'string', datetime: true},
            { name: 'qtaBillOfLading', label: 'ProtocolReport.qtaBillOfLading', type: 'decimal', digits: 0},
            { name: 'vol15BillOfLading_A', label: 'ProtocolReport.vol15BillOfLading_A', type: 'decimal', digits: 0},
            { name: 'densitaDiPolizza', label: 'ProtocolReport.densitaDiPolizza', type: 'decimal', digits: 4},
            { name: 'densitaBarilotto', label: 'ProtocolReport.densitaBarilotto', type: 'decimal', digits: 4},
            { name: 'obq', label: 'ProtocolReport.obq', type: 'decimal', digits: 4, showZero: true},
            { name: 'rob', label: 'ProtocolReport.rob', type: 'decimal', digits: 4, showZero: true},
            { name: 'vef', label: 'ProtocolReport.vef', type: 'decimal', digits: 4, showZero: true},
            { name: 'volumeBordoPartenzaLordoAcquaObq', label: 'ProtocolReport.volumeBordoPartenzaLordoAcquaObq', type: 'decimal', digits: 3},
            { name: 'volumeBordoPartenzaLordoAcquaObqVEF', label: 'ProtocolReport.volumeBordoPartenzaLordoAcquaObqVEF', type: 'decimal', digits: 3},
            { name: 'volumeBordoArrivoLordoAcquaObq', label: 'ProtocolReport.volumeBordoArrivoLordoAcquaObq', type: 'decimal', digits: 3},
            { name: 'volumeBordoArrivoLordoAcquaObqVEF', label: 'ProtocolReport.volumeBordoArrivoLordoAcquaObqVEF', type: 'decimal', digits: 3},
            { name: 'volumeLordoSbarcatoVEFApplicato', label: 'ProtocolReport.volumeLordoSbarcatoVEFApplicato', type: 'decimal', digits: 3},
            { name: 'accertamentoSerbatoioNettoH2O', label: 'ProtocolReport.accertamentoSerbatoioNettoH2O', type: 'decimal', digits: 3},
            { name: 'bordoArrivo_Meno_BordoPartenzaInLitri', label: 'ProtocolReport.bordoArrivo_Meno_BordoPartenzaInLitri', type: 'decimal', digits: 3},
            { name: 'bordoArrivoVEF_Meno_BordoPartenzaVEFInLitri', label: 'ProtocolReport.bordoArrivoVEF_Meno_BordoPartenzaVEFInLitri', type: 'decimal', digits: 3},
            { name: 'volumeBordoArrivoLordo_Meno_BOLLitriA15', label: 'ProtocolReport.volumeBordoArrivoLordo_Meno_BOLLitriA15', type: 'decimal', digits: 3},
            { name: 'volumeLordoSbarcatoVEF_Meno_BOLInLitri', label: 'ProtocolReport.volumeLordoSbarcatoVEF_Meno_BOLInLitri', type: 'string'},
            { name: 'accSerbatoioLordoAcqua_Meno_VolLordoSbarcatoVEFInLitri', label: 'ProtocolReport.accSerbatoioLordoAcqua_Meno_VolLordoSbarcatoVEFInLitri', type: 'decimal', digits: 3},
            { name: 'accSerbatoioLordoAcqua_Meno_VBillOfLadingQuantityLiters15', label: 'ProtocolReport.accSerbatoioLordoAcqua_Meno_VBillOfLadingQuantityLiters15', type: 'decimal', digits: 3},
            { name: 'bordoArrivo_Meno_BordoPartenzaPerc', label: 'ProtocolReport.bordoArrivo_Meno_BordoPartenzaPerc', type: 'decimal', digits: 2},
            { name: 'bordoArrivoVEF_Meno_BordoPartenzaVEFPerc', label: 'ProtocolReport.bordoArrivoVEF_Meno_BordoPartenzaVEFPerc', type: 'decimal', digits: 2},
            { name: 'volumeBordoArrivoLordo_Meno_BOLLitriA15Perc', label: 'ProtocolReport.volumeBordoArrivoLordo_Meno_BOLLitriA15Perc', type: 'decimal', digits: 2},
            { name: 'volumeLordoSbarcatoVEF_Meno_BOLPerc', label: 'ProtocolReport.volumeLordoSbarcatoVEF_Meno_BOLPerc', type: 'decimal', digits: 2},
            { name: 'accSerbatoioLordoAcqua_Meno_VolLordoSbarcatoVEFPerc', label: 'ProtocolReport.accSerbatoioLordoAcqua_Meno_VolLordoSbarcatoVEFPerc', type: 'decimal', digits: 2},
            { name: 'accSerbatoioLordoAcqua_Meno_VBillOfLadingQuantityPerc', label: 'ProtocolReport.accSerbatoioLordoAcqua_Meno_VBillOfLadingQuantityPerc', type: 'decimal', digits: 2},

        ]
    },
    DischargeTrackingReport: {
        title: 'DischargeTrackingReport.page_title',
        api: 'vessel-protocol-discharge-tracking',
        updateApi: 'vetting-safety',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        preventAutoLoad: true,
        hideRefresh: true,
        idColumn: "id",
        queryParamFilters: true,
        preventPagination: true,
        preventFilters: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
            },
            { name: 'referenceDate', label: 'vesselProtocol.referenceDate', type: 'date'},
            { name: 'voyageNumber', label: 'movementInstructions.voyageNumber', type: 'string'},
            // { name: 'lotCode', label: 'lotCode', type: 'string'},
            // { name: 'product', label: 'product.fk', type: 'string'},
            // { name: 'origin', label: 'movementInstructions.origin', type: 'string'},
            // { name: 'vesselName', label: 'vessel.fk', type: 'string'},
            // { name: 'laycanStartDate', label: 'movementInstructions.laycanFrom', type: 'date'},
            // { name: 'laycanEndDate', label: 'movementInstructions.laycanTo', type: 'date'},
            { name: 'certificateOfOriginStatus', label: 'DischargeTrackingReport.certificateOfOriginStatus', type: 'string'},
            { name: 'suppliersDeclarationStatus', label: 'DischargeTrackingReport.suppliersDeclarationStatus', type: 'string'},
            { name: 'exportDeclarationStatus', label: 'DischargeTrackingReport.exportDeclarationStatus', type: 'string'},
            { name: 'nonManipulationDeclarationStatus', label: 'DischargeTrackingReport.nonManipulationDeclarationStatus', type: 'string'},
            { name: 'voyageReportStatus', label: 'DischargeTrackingReport.voyageReportStatus', type: 'string'},
            { name: 'receiptOfDocumentsAndSamplesStatus', label: 'DischargeTrackingReport.receiptOfDocumentsAndSamplesStatus', type: 'string'},
            { name: 'isOriginSamplePresent', label: 'DischargeTrackingReport.isOriginSamplePresent', type: 'boolean'},
            { name: 'isCompleted', label: 'DischargeTrackingReport.isCompleted', type: 'boolean'},
            { name: 'isDeleted', label: 'isDeleted', type: 'boolean'}
        ]
    },
    StocchinoPlanAggregationType: {
        title: 'StocchinoPlanAggregationType.page_title',
        api: 'stocchino-plan-registry/aggregation-type',
        enableUpdate: true,
        editButtonName: 'stocchinoPlanAggregationTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'stocchinoPlanAggregationTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'stocchinoPlanAggregationTypeAddButton',
        idColumn: "id",
        getItemWithList: true,
        updateWithPost: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
            },
            { name: 'nome', label: 'StocchinoPlanAggregationType.nome', type: 'string'},
            { name: 'descrizione', label: 'StocchinoPlanAggregationType.descrizione', type: 'string'},
            { name: 'validityStartDate', label: 'StocchinoPlanAggregationType.validityStartDate', type: 'date'},
            { name: 'validityEndDate', label: 'StocchinoPlanAggregationType.validityEndDate', type: 'date'},
            { name: 'versione', label: 'StocchinoPlanAggregationType.versione', type: 'number'},
            { name: 'isAggregazioneProdotti', label: 'StocchinoPlanAggregationType.isAggregazioneProdotti', type: 'boolean'}
        ],
    },
    StocchinoPlanProductAggregation: {
        title: 'StocchinoPlanProductAggregation.page_title',
        api: 'stocchino-plan-registry/product-aggregation',
        expanderKey: 'codice',
        parentIdColumn: 'idParent',
        parentLabelColumn: 'codice',
        filterApi: 'stocchino-plan-registry/aggregation-type-combo?getAggregazioneProdotti=true',
        filterField: 'IdAggregazione',
        filterLabel: 'StocchinoPlanAggregationType.fk',
        headerField: 'idAggregazione',
        enableInsert: true,
        addButtonName: 'StocchinoPlanProductAggregationAddButton',
        enableDelete: true,
        addButtonName: 'StocchinoPlanProductAggregationDeleteButton',
        enableNewVersion: true,
        toolbarActions: [
            {
                type:'button',
                visible: true,
                icon: 'pi pi-clone',
                title: 'StocchinoPlanProductAggregation.newVersion',
                name: 'StocchinoPlanProductAggregationNewVersion',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    let cloneApi = `stocchino-plan-registry/aggregation-type-clone`;
                    let body = {
                        tipologiaAggregazioneFromID: caller.globalFilter,
                        nome: "",
                        descrizione: ""
                      }
                    CommonService.createItem(cloneApi, body, response => {
                        console.log(response)
                    })
                }
            },
            {
                type:'button',
                visible: true,
                icon: 'pi pi-clone',
                title: 'btn.clone',
                name: 'StocchinoPlanProductAggregationClone',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    caller.$emit('onOpenCloneAggregationClick', caller.globalFilter);
                }
            }
        ],
        columns: [
            { name: 'codice', label: 'StocchinoPlanProductAggregation.codice', type: 'string'},
            { name: 'descrizione', label: 'StocchinoPlanProductAggregation.descrizione', type: 'string'},
            { name: 'site', label: 'StocchinoPlanProductAggregation.site', type: 'string'},
            { name: 'ordineLivello', label: 'StocchinoPlanProductAggregation.ordineLivello', type: 'number'},
            { name: 'isFamigliaProdottoDefault', label: 'StocchinoPlanProductAggregation.isFamigliaProdottoDefault', type: 'boolean'},
            { name: 'isSemiFinished', label: 'StocchinoPlanProductAggregation.isSemiFinished', type: 'boolean'},
            { name: 'isRawMaterial', label: 'StocchinoPlanProductAggregation.isRawMaterial', type: 'boolean'},
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
        ]
    },
    StocchinoPlanFamilyAggregation: {
        title: 'StocchinoPlanFamilyAggregation.page_title',
        api: 'stocchino-plan-registry/family-aggregation',
        expanderKey: 'codice',
        parentIdColumn: 'idParent',
        parentLabelColumn: 'codice',
        filterApi: 'stocchino-plan-registry/aggregation-type-combo?getAggregazioneProdotti=false',
        filterField: 'IdAggregazione',
        filterLabel: 'StocchinoPlanAggregationType.fk',
        headerField: 'idAggregazione',
        enableInsert: true,
        addButtonName: 'StocchinoPlanFamilyAggregationAddButton',
        enableDelete: true,
        addButtonName: 'StocchinoPlanFamilyAggregationDeleteButton',
        enableNewVersion: true,
        toolbarActions: [
            {
                type:'button',
                visible: true,
                icon: 'pi pi-clone',
                title: 'StocchinoPlanFamilyAggregation.newVersion',
                name: 'StocchinoPlanFamilyAggregationNewVersion',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    let cloneApi = `stocchino-plan-registry/aggregation-type-clone`;
                    let body = {
                        tipologiaAggregazioneFromID: caller.globalFilter,
                        nome: "",
                        descrizione: ""
                      }
                    CommonService.createItem(cloneApi, body, response => {
                        console.log(response)
                    })
                }
            },
            {
                type:'button',
                visible: true,
                icon: 'pi pi-clone',
                title: 'btn.clone',
                name: 'StocchinoPlanFamilyAggregationClone',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    caller.$emit('onOpenCloneAggregationClick', caller.globalFilter);
                }
            }
        ],
        columns: [
            { name: 'codice', label: 'StocchinoPlanFamilyAggregation.codice', type: 'string'},
            { name: 'descrizione', label: 'StocchinoPlanFamilyAggregation.descrizione', type: 'string'},
            { name: 'site', label: 'StocchinoPlanFamilyAggregation.site', type: 'string'},
            { name: 'ordine', label: 'StocchinoPlanFamilyAggregation.ordineLivello', type: 'number'},
            { name: 'isSemiFinished', label: 'StocchinoPlanFamilyAggregation.isSemiFinished', type: 'boolean'},
            { name: 'isRawMaterial', label: 'StocchinoPlanFamilyAggregation.isRawMaterial', type: 'boolean'},
            { name: 'calcolaMinMaxWS', label: 'StocchinoPlanFamilyAggregation.calcolaMinMaxWS', type: 'boolean'},
            { name: 'invertiSegnoDeltaTargetSummary', label: 'StocchinoPlanFamilyAggregation.invertiSegnoDeltaTargetSummary', type: 'boolean'},
            

        ]
    },
    StocchinoPlanFamilyAssociation: {
        title: 'StocchinoPlanFamilyAssociation.page_title',
        api: 'stocchino-plan-registry/family-aggregation',
        expanderKey: 'codice',
        parentIdColumn: 'idParent',
        parentLabelColumn: 'codice',
        filterApi: 'stocchino-plan-registry/aggregation-type-combo?getAggregazioneProdotti=false',
        filterField: 'IdAggregazione',
        filterLabel: 'StocchinoPlanAggregationType.fk',
        headerField: 'idAggregazione',
        enableInsert: false,
        addButtonName: 'StocchinoPlanFamilyAggregationAddButton',
        enableDelete: false,
        addButtonName: 'StocchinoPlanFamilyAggregationDeleteButton',
        enableNewVersion: false,
        columns: [
            { name: 'codice', label: 'StocchinoPlanFamilyAggregation.codice', type: 'string'},
            { name: 'descrizione', label: 'StocchinoPlanFamilyAggregation.descrizione', type: 'string'},
            { name: 'site', label: 'StocchinoPlanFamilyAggregation.site', type: 'string'},
            { name: 'ordine', label: 'StocchinoPlanFamilyAggregation.ordineLivello', type: 'number'},
            { name: 'isSemiFinished', label: 'StocchinoPlanFamilyAggregation.isSemiFinished', type: 'boolean'},
            { name: 'isRawMaterial', label: 'StocchinoPlanFamilyAggregation.isRawMaterial', type: 'boolean'},
            { name: 'calcolaMinMaxWS', label: 'StocchinoPlanFamilyAggregation.calcolaMinMaxWS', type: 'boolean'},
            { name: 'invertiSegnoDeltaTargetSummary', label: 'StocchinoPlanFamilyAggregation.invertiSegnoDeltaTargetSummary', type: 'boolean'},
            

        ]
    },
    OperativeLevels: {
        title: 'operativeLevels.page_title',
        api: 'tank-management/tank-operative-level',
        enableUpdate: true,
        editButtonName: 'operativeLevelsEditButton',
        enableDelete: true,
        deleteButtonName: 'operativeLevelsDeleteButton',
        enableInsert: true,
        addButtonName: 'operativeLevelsAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
            },
            { name: 'plant', label: 'registryPlant.fk', type: 'string'},
            { name: 'tankCode', label: 'StockManagementDailyTank.tankcode', type: 'string'},
            { name: 'lastUpdateDate', label: 'operativeLevels.lastUpdateDate', type: 'date'},
            { name: 'referenceLevel', label: 'operativeLevels.referenceLevel', type: 'number'},
            { name: 'maxOperativeLevel', label: 'operativeLevels.maxOperativeLevel', type: 'number'},
            { name: 'minOperativeLevel', label: 'operativeLevels.minOperativeLevel', type: 'number'},
            { name: 'conversionFactor', label: 'operativeLevels.conversionFactor', type: 'decimal', digits: 1}
        ],
        toolbarActions: [
            {
                type:'button',
                visible: true,
                icon: 'pi pi-download',
                title: 'operativeLevels.downloadTemplateExcel',
                name: 'operativeLevelsDownloadTemplateExcel',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    caller.$emit('onOperativeLevelsDownloadExcelTemplate');
                }
            },
            {
                type:'button',
                visible: true,
                name: 'operativeLevelsImportExcel',
                icon: 'pi pi-upload',
                title: 'operativeLevels.importExcel',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    caller.$emit('onOperativeLevelsImportExcel');
                }
            }
        ]
    },
    StocchinoPlanTankExceptions: {
        title: 'StocchinoPlanTankExceptions.page_title',
        api: 'stocchino-plan-registry/tank-exceptions',
        enableUpdate: true,
        editButtonName: 'tankExceptionsEditButton',
        enableDelete: true,
        deleteButtonName: 'tankExceptionsDeleteButton',
        enableInsert: true,
        addButtonName: 'tankExceptionsAddButton',
        idColumn: "id",
        getItemWithList: true,
        updateWithPost: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc',
                gridHide: true,
            },
            { name: 'idAttr', label: 'StocchinoPlanTankExceptions.idAttr', type: 'string'},
            { name: 'isImportEscluso', label: 'StocchinoPlanTankExceptions.isImportEscluso', type: 'boolean'},
            { name: 'isVolumeFromPI', label: 'StocchinoPlanTankExceptions.isVolumeFromPI', type: 'boolean'},
            { name: 'isDaRielaborare', label: 'StocchinoPlanTankExceptions.isDaRielaborare', type: 'boolean'},
            { name: 'startDate', label: 'StocchinoPlanTankExceptions.startDate', type: 'date'},
            { name: 'endDate', label: 'StocchinoPlanTankExceptions.endDate', type: 'date'}
        ]
    },

    StocchinoPlanAuditMasterData: {
        title: 'StocchinoPlanAuditMasterData.page_title',
        api: 'stocchino-plan-audit/registry-sp-audit',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        showPrevSuccDialog: true,
        prevRecordName: 'valorePrecedente',
        succRecordName: 'valoreSuccessivo',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { name: 'entity', label: 'StocchinoPlanAudit.entity', type: 'string'},
            { name: 'idRecord', label: 'StocchinoPlanAudit.idRecord', type: 'string'},
            { name: 'username', label: 'StocchinoPlanAudit.username', type: 'string'},
            { name: 'dataOperazione', label: 'StocchinoPlanAudit.dataOperazione', type: 'date'},
            { name: 'tipoOperazione', label: 'StocchinoPlanAudit.tipoOperazione', type: 'string'},

        ]
    },

    StocchinoPlanAuditStocchino: {
        title: 'StocchinoPlanAuditStocchino.page_title',
        api: 'stocchino-plan-audit/stocchino-plan-audit',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        showPrevSuccDialog: true,
        prevRecordName: 'valorePrecedente',
        succRecordName: 'valoreSuccessivo',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { name: 'entity', label: 'StocchinoPlanAudit.entity', type: 'string'},
            { name: 'idRecord', label: 'StocchinoPlanAudit.idRecord', type: 'string'},
            { name: 'username', label: 'StocchinoPlanAudit.username', type: 'string'},
            { name: 'dataOperazione', label: 'StocchinoPlanAudit.dataOperazione', type: 'date'},
            { name: 'tipoOperazione', label: 'StocchinoPlanAudit.tipoOperazione', type: 'string'},

        ]
    },

    StocchinoPlanAuditOperativeLevels: {
        title: 'StocchinoPlanAuditOperativeLevels.page_title',
        api: 'tank-management/tank-operative-level-audit',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        showPrevSuccDialog: true,
        prevRecordName: 'oldValue',
        succRecordName: 'newValue',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { name: 'entity', label: 'StocchinoPlanAudit.entity', type: 'string'},
            { name: 'recordId', label: 'StocchinoPlanAudit.idRecord', type: 'string'},
            { name: 'username', label: 'StocchinoPlanAudit.username', type: 'string'},
            { name: 'operationDate', label: 'StocchinoPlanAudit.dataOperazione', type: 'date'},
            { name: 'operationType', label: 'StocchinoPlanAudit.tipoOperazione', type: 'string'},

        ]
    },
    FamilyPoolConfiguration: {
        title: 'FamilyPoolConfiguration.page_title',
        api: 'true-up/family-pool-configuration',
        enableUpdate: true,
        editButtonName: 'familyPoolConfigurationEditButton',
        enableDelete: true,
        deleteButtonName: 'familyPoolConfigurationDeleteButton',
        enableInsert: true,
        addButtonName: 'familyPoolConfigurationAddButton',
        idColumn: "id",
        getItemWithList: true,
        defaultSortField: 'monthStartDate',
        defaultSortOrder: -1,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { name: 'monthStartDate', label: 'FamilyPoolConfiguration.monthStartDate', type: 'date'},
            { name: 'poolName', label: 'FamilyPoolConfiguration.poolName', type: 'string'},
            { name: 'familyGroup', label: 'FamilyPoolConfiguration.familyGroup', type: 'string'},
            { name: 'isCrude', label: 'FamilyPoolConfiguration.isCrude', type: 'boolean'},
            { name: 'isProduct', label: 'FamilyPoolConfiguration.isProduct', type: 'boolean'},
            { name: 'isTrueUpEqualsToVessel', label: 'FamilyPoolConfiguration.isTrueUpEqualsToVessel', type: 'boolean'},
            { name: 'planningPriceFormula', label: 'FamilyPoolConfiguration.planningPriceFormula', type: 'string'},
            { name: 'referenceDensity', label: 'FamilyPoolConfiguration.referenceDensity', digits: 4,type: 'decimal'},
            { name: 'plannedDensity', label: 'FamilyPoolConfiguration.plannedDensity', digits: 4, type: 'decimal'},
            { name: 'isDeleted', label: 'FamilyPoolConfiguration.isDeleted', type: 'boolean', formHide: true }
        ],
        toolbarActions: [
            {
                type:'button',
                visible: true,
                name: 'cloneFamilyPoolConfigurationButton',
                icon: 'pi pi-copy',
                title: 'btn.clone',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    caller.$emit('onCloneFamilyPoolConfiguration');
                }
            },
        ]
    },
    ProvisionalInvoice: {
        title: 'provisionalInvoice.page_title',
        api: 'true-up/provisional-invoice',
        enableUpdate: true,
        editButtonName: 'provisionalInvoiceEditButton',
        enableDelete: true,
        deleteButtonName: 'provisionalInvoiceDeleteButton',
        //enableInsert: true,
        //addButtonName: 'provisionalInvoiceAddButton',
        idColumn: "id",
        preventAutoLoad: true,
        hideRefresh: true,
        getItemWithList: true,
        filterLocal: true,
        editMode: 'dialog',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { name: 'initialInventoryDate', label: 'provisionalInvoice.initialInventoryDate', type: 'date', inLineEdit: true},
            { name: 'endingInventoryDate', label: 'provisionalInvoice.endingInventoryDate', type: 'date', inLineEdit: true},
            { name: 'weeklyDescription', label: 'provisionalInvoice.weeklyDescription', type: 'string', inLineEdit: true},
            { name: 'planningTrueUpFamilyPoolConfiguration', label: 'provisionalInvoice.planningTrueUpFamilyPoolConfiguration', type: 'string'},
            { name: 'familyGroup', label: 'provisionalInvoice.familyGroup', type: 'string', inLineEdit: true},
            { name: 'familyPool', label: 'provisionalInvoice.familyPool', type: 'string', inLineEdit: true},
            { name: 'cubicMeters', label: 'provisionalInvoice.cubicMeters', type: 'decimal', digits: 3, inLineEdit: true},
            { name: 'density', label: 'provisionalInvoice.density', type: 'decimal', digits: 4, inLineEdit: true},
            { name: 'conversionFactor', label: 'provisionalInvoice.conversionFactor', type: 'decimal', digits: 3, inLineEdit: true},
            { name: 'barrels', label: 'provisionalInvoice.barrels', type: 'decimal', digits: 3, inLineEdit: true},
            { name: 'metricTonnes', label: 'provisionalInvoice.metricTonnes', type: 'decimal', digits: 3, inLineEdit: true},
            { name: 'settlementBase', label: 'provisionalInvoice.settlementBase', type: 'string', inLineEdit: true},
            { name: 'settlementDate', label: 'provisionalInvoice.settlementDate', type: 'date', inLineEdit: true},
            { name: 'settle', label: 'provisionalInvoice.settle', type: 'decimal', digits: 3, inLineEdit: true},
            { name: 'premiumOrDiscount', label: 'provisionalInvoice.premiumOrDiscount', type: 'decimal', digits: 3, inLineEdit: true},
            { name: 'price', label: 'provisionalInvoice.price', type: 'decimal', digits: 3, inLineEdit: true},
            { name: 'escalatedPrice', label: 'provisionalInvoice.escalatedPrice', type: 'decimal', digits: 3, inLineEdit: true},
            { name: 'invoiceAmount', label: 'provisionalInvoice.invoiceAmount', type: 'decimal', inLineEdit: true},
            { name: 'trafiguraTrade', label: 'provisionalInvoice.trafiguraTrade', type: 'string', inLineEdit: true},
            { name: 'isValid', label: 'provisionalInvoice.isValid', type: 'boolean', inLineEdit: true}
        ]
    },
    ProductsWeightedAverageValue: {
        api: 'true-up/products-weight-average-value',
        idColumn: "id",
        preventAutoLoad: true,
        hideRefresh: true,
        getItemWithList: true,
        filterLocal: true,
        selection: true,
        selectionMode: 'single',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { name: 'planningTrueUpFamilyPoolConfiguration', label: 'ProductsWeightedAverageValue.planningTrueUpFamilyPoolConfiguration', type: 'string' }
        ]
    },
    ProductsWtdDetail: {
        api: 'true-up/products-wtd-detail',
        idColumn: "id",
        preventAutoLoad: true,
        hideRefresh: true,
        getItemWithList: true,
        filterLocal: true,
        enableUpdate: true,
        editButtonName: 'productsWtdDetailEditButton',
        enableDelete: true,
        deleteButtonName: 'productsWtdDetailDeleteButton',
        editMode: 'dialog',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { name: 'transactionTypeDescription', label: 'ProductsWtdDetail.transactionTypeDescription', type: 'string', inLineEdit: true },
            { name: 'dealId', label: 'ProductsWtdDetail.dealId', type: 'string', inLineEdit: true },
            { name: 'vesselDescription', label: 'ProductsWtdDetail.vesselDescription', type: 'string', inLineEdit: true},
            { name: 'quantity', label: 'ProductsWtdDetail.quantity', type: 'decimal', digits: 3, inLineEdit: true },
            { name: 'density', label: 'ProductsWtdDetail.density', type: 'decimal', digits: 4, inLineEdit: true },
            { name: 'premiumOrDiscount', label: 'ProductsWtdDetail.premiumOrDiscount', type: 'decimal', digits: 3, inLineEdit: true }
        ]
    },
    InvoicePrices: {
        api: 'true-up/invoice-prices-get-prices',
        idColumn: "id",
        preventAutoLoad: true,
        hideRefresh: true,
        getItemWithList: true,
        queryParamFilters: true,
        filterLocal: true,
        editMode: 'dialog',
        columns: [
            { name: 'settlementDate', label: 'InvoicePrices.settlementDate', type: 'date' },
            { name: 'isFinal', label: 'InvoicePrices.isFinal', type: 'boolean' },
            { name: 'productFamily', label: 'InvoicePrices.productFamily', type: 'string' },
            { name: 'settlementUnits', label: 'InvoicePrices.settlementUnits', type: 'string' },
            { name: 'consumptionProduction', label: 'InvoicePrices.consumptionProduction', type: 'string' },
            { name: 'baseQuote', label: 'InvoicePrices.baseQuote', type: 'string' },
            { name: 'settle', label: 'InvoicePrices.settle', type: 'decimal', digits: 3 },
            { name: 'premiumDiscount', label: 'InvoicePrices.premiumDiscount', type: 'decimal', digits: 3 },
            { name: 'price', label: 'InvoicePrices.price', type: 'decimal', digits: 3  },
            { name: 'density', label: 'InvoicePrices.density', type: 'decimal', digits: 4 },
            { name: 'escalatedPrice', label: 'InvoicePrices.escalatedPrice', type: 'decimal', digits: 3  }
        ]
    },
    FeedstocksWeightedAverage: {
        api: 'true-up/feedstock-weighted-average-value',
        idColumn: "id",
        preventAutoLoad: true,
        hideRefresh: true,
        getItemWithList: true,
        filterLocal: true,
        selection: true,
        selectionMode: 'single',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { name: 'planningTrueUpFamilyPoolConfiguration', label: 'ProductsWeightedAverageValue.planningTrueUpFamilyPoolConfiguration', type: 'string' }
        ]
    },
    FeedstocksWtdDetail: {
        api: 'true-up/feedstock-wtd-detail',
        idColumn: "id",
        preventAutoLoad: true,
        hideRefresh: true,
        getItemWithList: true,
        filterLocal: true,
        enableUpdate: true,
        editButtonName: 'feedstocksWtdDetailEditButton',
        enableDelete: true,
        deleteButtonName: 'feedstocksWtdDetailDeleteButton',
        editMode: 'dialog',
        columns: [
            { name: 'gradeDescription', label: 'FeedstocksWtdDetail.gradeDescription', type: 'string', inLineEdit: true },
            { name: 'vesselDescription', label: 'FeedstocksWtdDetail.vesselDescription', type: 'string', inLineEdit: true},
            { name: 'laycanFrom', label: 'FeedstocksWtdDetail.laycanFrom', type: 'date', inLineEdit: true},
            { name: 'laycanTo', label: 'FeedstocksWtdDetail.laycanTo', type: 'date', inLineEdit: true},
            { name: 'dealId', label: 'FeedstocksWtdDetail.dealId', type: 'string', inLineEdit: true },
            { name: 'tradeId', label: 'FeedstocksWtdDetail.tradeId', type: 'string', inLineEdit: true },
            { name: 'volume', label: 'FeedstocksWtdDetail.volume', type: 'decimal', digits: 3, inLineEdit: true },
            { 
                name: 'premiumOrDiscount', 
                label: 'FeedstocksWtdDetail.premiumOrDiscount', 
                type: 'decimal', 
                digits: 3, 
                inLineEdit: true,
                cls: ({premiumOrDiscount}) =>{ 
                    if(premiumOrDiscount < 0){
                        return 'sim-col-red';
                    } 
                }
            }
        ]
    },
    PiTag: {
        title: 'pitag.page_title',
        api: 'pi-tag',
        enableUpdate: true,
        editButtonName: 'pitagEditButton',
        enableDelete: true,
        deleteButtonName: 'pitagDeleteButton',
        enableInsert: true,
        addButtonName: 'pitagAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'equipmentCode',
                label: 'pitag.equipmentCode',
                type: 'string'
            },
            {
                name: 'tagCode',
                label: 'pitag.tagCode',
                type: 'string'
            },
            {
                name: 'tagType',
                label: 'pitag.tagType',
                type: 'string'
            },
            {
                name: 'isMain',
                label: 'pitag.isMain',
                type: 'boolean'
            }
        ],
        customActions:[
            {
                title: 'btn.readPi',
                name: 'read_pi_tag',
                cssClass: 'p-button-rounded p-button-success mr-2',
                icon: 'pi pi-info-circle',
                action: function (data, caller) {
                    
                    let params = {
                        date: new Date(),
                        tag: data.tagCode
                    };

                    caller.$store.dispatch("generic/setLoading", true);
                    
                    EquipmentOperationService.readFromPiByTagAndDate(params, ({success, data, message}) =>{
                        caller.$store.dispatch("generic/setLoading", false);
                        if(success && data && data.items){
                            console.log(data);

                            let res = data.items[0];

                            let timestamp = Utils.formatDateTime(Utils.dateToCurrentTimeZone(res.timestamp));
                            let description = res.description;
                            let uom = res.uoM;
                            let value = res.value;

                            let tagInfo = `[${timestamp} - ${description}: ${value} ${uom}`;

                            caller.$toast.add({detail: tagInfo && tagInfo != '' ? tagInfo :  caller.$t('messages.generic_error_message'), life:6000, severity:'success'});
                        } else {
                            caller.$toast.add({detail: message && message != '' ? message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                        }
                    });
                }
            }
        ]
    },
    DailyProfileException: {
        title: 'DailyProfileException.page_title',
        api: 'daily-profiles-exceptions',
        enableUpdate: true,
        editButtonName: 'DailyProfileExceptionEditButton',
        enableDelete: true,
        deleteButtonName: 'DailyProfileExceptionDeleteButton',
        enableInsert: true,
        addButtonName: 'DailyProfileExceptionAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'endDate',
                label: 'endDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'externalProductCode',
                label: 'DailyProfileException.externalProductCode',
                type: 'string',
                validations: {
                    maxLength: 20,
                    required: false
                }
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'idPlanningDailyProfileFamily',
                label: 'DailyProfiles.family',
                type: 'select',
                selectApi: 'daily-profiles/families-complete-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
                name: 'planningDailyProfileFamily',
                label: 'DailyProfiles.family',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'daily-profiles/families-complete-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idPlanningDailyProfileFamily'
            },
            {
                name: 'isTruck',
                label: 'DailyProfileException.isTruck',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isVersalis',
                label: 'DailyProfileException.isVersalis',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'isAirLiquide',
                label: 'DailyProfileException.isAirLiquide',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'stkManagement',
                label: 'DailyProfileException.stkManagement',
                type: 'boolean',
                validations: {
                    required: true
                }
            }
        ]
    },
    CrudeConversionFactor: {
        title: 'crudeConversionFactor.page_title',
        api: 'masterdata/crude-conversion-factor',
        enableUpdate: true,
        editButtonName: 'crudeConversionFactorEditButton',
        enableDelete: true,
        deleteButtonName: 'crudeConversionFactorDeleteButton',
        enableInsert: true,
        addButtonName: 'crudeConversionFactorAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryProduct',
                label: 'crudeConversionFactor.registryProduct',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'registryProduct',
                label: 'crudeConversionFactor.registryProduct',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'conversionFactor',
                label: 'crudeConversionFactor.conversionFactor',
                type: 'decimal', 
                digits: 6
            }
        ]
    },
    PeriodicMovementBySeaReport: {
        title: 'periodicMovementBySeaReport.page_title',
        api: 'material-balance-periodic-movement-by-sea-report',
        hideRefresh: true,
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        queryParamFilters: true,
        filterLocal: true,
        defaultSortField: 'finalDate',
        defaultLimit: 100000,
        preventPagination: true,
        columns: [
            {
                name: 'voyageNumber',
                label: 'periodicMovementBySeaReport.voyageNumber',
                type: 'string'
            },
            {
                name: 'vesselName',
                label: 'periodicMovementBySeaReport.vesselName',
                type: 'string'
            },
            {
                name: 'productCode',
                label: 'periodicMovementBySeaReport.productCode',
                type: 'string'
            },
            {
                name: 'productDescription',
                label: 'periodicMovementBySeaReport.productDescription',
                type: 'string'
            },
            {
                name: 'incoterms',
                label: 'periodicMovementBySeaReport.incoterms',
                type: 'string'
            },
            {
                name: 'loadingDischargePort',
                label: 'periodicMovementBySeaReport.loadingDischargePort',
                type: 'string'
            },
            {
                name: 'finalDate',
                label: 'periodicMovementBySeaReport.finalDate',
                type: 'date',
                defaultSort: 'asc'
            },
            {
                name: 'finalQuantityMT',
                label: 'periodicMovementBySeaReport.finalQuantityMT',
                type: 'decimal', 
                digits: 3
            },
            {
                name: 'finalQuantityM3',
                label: 'periodicMovementBySeaReport.finalQuantityM3',
                type: 'decimal', 
                digits: 3
            },
            {
                name: 'finalQuantityBBL',
                label: 'periodicMovementBySeaReport.finalQuantityBBL',
                type: 'decimal', 
                digits: 3
            },
            {
                name: 'density15',
                label: 'periodicMovementBySeaReport.density15',
                type: 'decimal', 
                digits: 4
            },
            {
                name: 'mooringPlatform',
                label: 'periodicMovementBySeaReport.mooringPlatform',
                type: 'string'
            }
        ]
    },
    DealNotePriceWindow: {
        title: 'dealNotePriceWindow.page_title',
        api: 'planning/deal-note-price-window',
        enableUpdate: true,
        editButtonName: 'dealNotePriceWindowEditButton',
        enableDelete: true,
        deleteButtonName: 'dealNotePriceWindowDeleteButton',
        hideRefresh: true,
        idColumn: "id",
        editMode: 'dialog',
        columns: [
            { name: 'description', label: 'dealNotePriceWindow.description', type: 'string', inLineEdit: true, validations: {required: true}},
            {
                name: 'idRegistryPriceWindowType',
                label: 'dealNotePriceWindow.priceWindowType',
                type: 'select',
                selectApi: 'masterdata/price-window-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                inLineEdit: true,
                validations: {
                    required: true
                }
            },
            { name: 'priceWindowType', label: 'dealNotePriceWindow.priceWindowType', type: 'string', formHide: true},
            { name: 'monthDate', label: 'dealNotePriceWindow.monthDate', type: 'date', onlyDate: true, inLineEdit: true},
            { name: 'dateFrom', label: 'dealNotePriceWindow.dateFrom', type: 'date', onlyDate: true, inLineEdit: true},
            { name: 'dateTo', label: 'dealNotePriceWindow.dateTo', type: 'date', onlyDate: true, inLineEdit: true},
            {
                name: 'idPriceWindowDateType',
                label: 'dealNotePriceWindow.priceWindowDateType',
                type: 'select',
                selectApi: 'masterdata/price-window-date-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                inLineEdit: true
            },
            { name: 'priceWindowDateType', label: 'dealNotePriceWindow.priceWindowDateType', type: 'string', formHide: true},
            { name: 'dayToAdd', label: 'dealNotePriceWindow.dayToAdd', type: 'number', inLineEdit: true},
            { name: 'isReferenceDateIncluded', label: 'dealNotePriceWindow.isReferenceDateIncluded', type: 'boolean', inLineEdit: true}
        ]
    },
    DensityEstimation: {
        title: 'DensityEstimation.page_title',
        api: 'planning/density-estimation',
        enableUpdate: true,
        editButtonName: 'DensityEstimationEditButton',
        enableDelete: true,
        deleteButtonName: 'DensityEstimationDeleteButton',
        preventAutoLoad: true,
        hideRefresh: true,
        editMode: 'dialog',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                gridHide: true
            },
            {
                name: 'voyageNumber',
                label: 'VoyageNumber',
                type: 'string',
                readonly: true,
            },
            {
                name: 'vesselName',
                label: 'VesselName',
                type: 'string',
                readonly: true,
            },
            {
                name: 'laycanStartDate',
                label: 'LaycanStartDate',
                type: 'date',
                inLineEdit: true
            },
            {
                name: 'laycanEndDate',
                label: 'LaycanEndDate',
                type: 'date',
                inLineEdit: true
            },
            {
                name: 'deal',
                label: 'Deal',
                type: 'string',
                inLineEdit: true
            },
            {
                name: 'productCode',
                label: 'Deal',
                type: 'string',
                readonly: true
            },
            {
                name: 'previsionalQuantityMt',
                label: 'PrevisionalQuantityMt',
                type: 'decimal',
                digits: 3,
                inLineEdit: true
            },
            {
                name: 'finalQuantityMt',
                label: 'FinalQuantityMt',
                type: 'decimal',
                digits: 3,
                inLineEdit: true
            },
            {
                name: 'isFinal',
                label: 'IsFinal',
                type: 'bool',
                inLineEdit: true
            },
            {
                name: 'density',
                label: 'Density',
                type: 'decimal',
                digits: 4,
                inLineEdit: true
            },
            {
                name: 'premium',
                label: 'Premium',
                type: 'decimal',
                digits: 4,
                inLineEdit: true
            },
            {
                name: 'isRevised',
                label: 'IsRevised',
                type: 'bool',
                inLineEdit: true
            }
        ]
    },
    PremiumDiscountCalculationDetails: {
        title: 'PremiumDiscountCalculationDetails.page_title',
        api: 'planning/premium-discount-calculation-details',
        enableUpdate: true,
        editButtonName: 'PremiumDiscountCalculationDetailsEditButton',
        enableDelete: true,
        deleteButtonName: 'PremiumDiscountCalculationDetailsDeleteButton',
        preventAutoLoad: true,
        hideRefresh: true,
        editMode: 'dialog',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                gridHide: true
            },
            {
                name: 'referenceDate',
                label: 'ReferenceDate',
                type: 'date'
            },
            {
                name: 'voyageNumber',
                label: 'VoyageNumber',
                type: 'string'
            },
            {
                name: 'vesselName',
                label: 'VesselName',
                type: 'string'
            },
            {
                name: 'laycanStartDate',
                label: 'LaycanStartDate',
                type: 'date',
                inLineEdit: true
            },
            {
                name: 'laycanEndDate',
                label: 'LaycanEndDate',
                type: 'date',
                inLineEdit: true
            },
            {
                name: 'deal',
                label: 'Deal',
                type: 'string',
                inLineEdit: true
            },
            {
                name: 'productPool',
                label: 'ProductPool',
                type: 'string',
                inLineEdit: true
            },
            {
                name: 'productCode',
                label: 'ProductCode',
                type: 'string',
                inLineEdit: true
            },
            {
                name: 'productName',
                label: 'ProductName',
                type: 'string',
                inLineEdit: true
            },
            {
                name: 'quantityMt',
                label: 'QuantityMt',
                type: 'decimal',
                digits: 3,
                inLineEdit: true
            },
            {
                name: 'previsionalQuantity',
                label: 'PrevisionalQuantityMt',
                type: 'decimal',
                digits: 3,
                inLineEdit: true
            },
            {
                name: 'finalQuantity',
                label: 'FinalQuantityMt',
                type: 'decimal',
                digits: 3,
                inLineEdit: true
            },
            {
                name: 'isFinal',
                label: 'IsFinal',
                type: 'bool',
                inLineEdit: true
            },
            {
                name: 'discount',
                label: 'Discount',
                type: 'decimal',
                digits: 4,
                inLineEdit: true
            },
            {
                name: 'isRevised',
                label: 'IsRevised',
                type: 'bool',
                inLineEdit: true
            }
        ]
    },
    EquipmentTrackingAudit: {
        title: 'EquipmentTrackingAudit.page_title',
        api: 'equipment-tracking-audit',
        enableUpdate: false,
        enableDelete: false,
        preventAutoLoad: false,
        idColumn: "id",
        defaultSortField: 'operationDate',
        defaultSortOrder: 1,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                gridHide: true
            },
            {
                name: 'username',
                label: 'users.username',
                type: 'string'
            },
            {
                name: 'equipmentTrackingRecordId',
                label: 'EquipmentTrackingAudit.idRecord',
                type: 'number'
            },
            {
                name: 'referenceDate',
                label: 'TankTracking.referenceDate',
                type: 'date'
            },
            {
                name: 'operationDate',
                label: 'operationDate',
                type: 'date',
                datetime: true, 
                defaultSort: 'asc'
            },
            {
                name: 'equipmentCode',
                label: 'cargoByLandEquipment.registryEquipment',
                type: 'string'
            },
            {
                name: 'newViewedFlagStatus',
                label: 'TankTracking.viewed',
                type: 'boolean'
            }
        ]
    },
    DenaturantBulkCapacity: {
        title: 'denaturantBulkCapacity.page_title',
        api: 'masterdata/denaturant-bulk-capacity',
        enableUpdate: true,
        editButtonName: 'DenaturantBulkCapacityEditButton',
        enableDelete: true,
        deleteButtonName: 'DenaturantBulkCapacityDeleteButton',
        enableInsert: true,
        addButtonName: 'DenaturantBulkCapacityAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryPlant',
                label: 'registryPlant.fk',
                type: 'select',
                selectApi: 'masterdata/registry-plant',
                selectFormat: {
                    label: 'code',
                    value: 'id'
                },
                sortField: 'Code',
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryPlant',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/registry-plant',
                gridFilterSelectFormat: {
                    label: 'code',
                    value: 'id'
                },
                gridFilterName: 'idRegistryPlant'
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {
                name: 'capacity',
                label: 'denaturantBulkCapacity.capacity',
                type: 'number',
                validations: {
                    required: true,
                    minValue: 1,
                    maxValue: 2147483647
                }
            },
            {
                name: 'bulkColor',
                label: 'color',
                type: 'string',
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                formHide: true
            }
        ]
    },
    DenaturantBulkRegistry: {
        title: 'denaturantBulkRegistry.page_title',
        api: 'equipment-operation/denaturant-registry-bulk',
        enableUpdate: true,
        editButtonName: 'denaturantBulkRegistryUpdateButton',
        editMode: 'dialog',
        enableDelete: true,
        deleteButtonName: 'denaturantBulkRegistryDeleteButton',
        enableInsert: false,
        idColumn: "id",
        defaultSortField: 'operationDate',
        defaultSortOrder: 1,
        preventAutoLoad: true,
        preventFilters: true,
        hideRefresh: true,
        filterLocal: true,
        defaultLimit: 100000,
        preventPagination: true,
        queryParamFilters: true,
        columns: [
            { 
                name: 'id', 
                label: 'id', 
                type: 'number', 
                readOnly: true, 
                editEnabled: () => false,
                inLineEdit: true, 
                gridHide: true
            },
            { 
                label: 'denaturantBulkRegistry.operationDate', 
                name: 'operationDate', 
                type: 'date', 
                datetime: true,
                inLineEdit: true, 
                editEnabled: (item) => {return !item.id},
                defaultSort: 'asc'
            },
            { 
                label: 'denaturantBulkRegistry.operationType', 
                name: 'operationType', 
                type: 'string', 
                editEnabled: () => false,
                inLineEdit: true,
                localized: true,
                renderColumn: (el)=>{
                    return "denaturantBulkRegistry.operationType" + el.operationType;
                }
            },
            { 
                label: 'denaturantBulkRegistry.movedQuantity', 
                name: 'movedQuantity', 
                type: 'number',
                editEnabled: () => true,
                inLineEdit: true
            },
            { 
                label: 'denaturantBulkRegistry.stockQuantity', 
                name: 'stockQuantity', 
                type: 'number',
                editEnabled: () => false, 
                inLineEdit: false
            }
        ]
    },
    DenaturantBulkThreshold: {
        title: 'denaturantBulkRegistry.thresholdSettings.gridName',
        api: 'equipment-operation/denaturant-bulk-threshold',
        enableUpdate: true,
        editButtonName: 'denaturantBulkThresholdEditButton',
        editMode: 'dialog',
        enableDelete: false,
        enableInsert: false,
        idColumn: "id",
        preventFilters: true,
        hideRefresh: false,
        defaultLimit: 100000,
        preventPagination: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                gridHide:true,
                readOnly: true,
                editEnabled: () => false, 
                inLineEdit: false
            },
            {
                name: 'settingKey',
                label: 'generalSettings.settingsKey',
                type: 'string',
                localized: true,
                renderColumn: (el)=>{
                    return "denaturantBulkRegistry.thresholdSettings." + el.settingKey;
                },
                editEnabled: () => false, 
                inLineEdit: true
            },
            {
                name: 'settingValue',
                label: 'generalSettings.settingValue',
                type: 'number',
                editEnabled: () => true, 
                inLineEdit: true
            },
            // {
            //     name: 'settingType',
            //     label: 'generalSettings.settingType',
            //     type: 'select',
            //     gridFilterType: 'select',
            //     selectFormat: { label: 'value', value: 'value' },
            //     staticOptions: [{value: 'decimal'}, {value: 'bool'}, {value: 'string'}, {value: 'int'}, {value: 'datetime'}],
            //     gridFilterStaticOptions: [{value: 'decimal'}, {value: 'bool'}, {value: 'string'}, {value: 'int'}, {value: 'datetime'}],
            //     gridFilterSelectFormat: { label: 'value', value: 'value' },
            //     gridFilterName: 'settingType',
            //     validations: {
            //         required: true
            //     },
            //     editEnabled: () => false, 
            //     inLineEdit: false
            // }
        ]
    },
    // /system/timespan-configuration
    TimeSpanConfiguration: {
        title: 'TimeSpanConfiguration.page_title',
        api: 'system/timespan-configuration',
        enableUpdate: true,
        editButtonName: 'TimeSpanConfigurationEditButton',
        enableDelete: true,
        deleteButtonName: 'TimeSpanConfigurationDeleteButton',
        enableInsert: true,
        addButtonName: 'TimeSpanConfigurationAddButton',
        idColumn: "id",
        defaultLimit: 100000,
        preventPagination: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'module',
                label: 'Modulo',
                type: 'string',
            },
            {
                name: 'configurationKey',
                label: 'key',
                type: 'string',
            },
            {
                name: 'year',
                label: 'year',
                type: 'number',
                validations: {
                    minValue: 2023,
                    maxValue: 2999
                }
            },
            {
                name: 'month',
                label: 'month',
                type: 'select',
                gridFilterType: 'select',
                selectFormat: { label: 'label', value: 'value' },
                staticOptions: [
                    { value: '1', label: 'Gennaio' },
                    { value: '2', label: 'Febbraio' },
                    { value: '3', label: 'Marzo' },
                    { value: '4', label: 'Aprile' },
                    { value: '5', label: 'Maggio' },
                    { value: '6', label: 'Giugno' },
                    { value: '7', label: 'Luglio' },
                    { value: '8', label: 'Agosto' },
                    { value: '9', label: 'Settembre' },
                    { value: '10', label: 'Ottobre' },
                    { value: '11', label: 'Novembre' },
                    { value: '12', label: 'Dicembre' }
                  ],
                gridFilterStaticOptions:[
                    { value: '1', label: 'Gennaio' },
                    { value: '2', label: 'Febbraio' },
                    { value: '3', label: 'Marzo' },
                    { value: '4', label: 'Aprile' },
                    { value: '5', label: 'Maggio' },
                    { value: '6', label: 'Giugno' },
                    { value: '7', label: 'Luglio' },
                    { value: '8', label: 'Agosto' },
                    { value: '9', label: 'Settembre' },
                    { value: '10', label: 'Ottobre' },
                    { value: '11', label: 'Novembre' },
                    { value: '12', label: 'Dicembre' }
                  ],
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'dayOfWeek'
            },
            {
                name: 'day',
                label: 'day',
                type: 'number',
                validations: {
                    minValue: 1,
                    maxValue: 31
                }
            },
            {
                name: 'dayOfWeek',
                label: 'Giorno della Settimana',
                type: 'select',
                gridFilterType: 'select',
                selectFormat: { label: 'label', value: 'value' },
                staticOptions: [{value: '0', label: 'Domenica'}, {value: '1', label: 'Lunedì'}, {value: '2', label: 'Martedì'}, {value: '3', label: 'Mercoledì'}, {value: '4', label: 'Giovedì'}, {value: '5', label: 'Venerdì'}, {value: '6', label: 'Sabato'}],
                gridFilterStaticOptions:[{value: '0', label: 'Domenica'}, {value: '1', label: 'Lunedì'}, {value: '2', label: 'Martedì'}, {value: '3', label: 'Mercoledì'}, {value: '4', label: 'Giovedì'}, {value: '5', label: 'Venerdì'}, {value: '6', label: 'Sabato'}],
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'dayOfWeek'
            },
            {
                name: 'hourFrom',
                label: 'Ora da (HH:MM:SS)',
                type: 'string',
            },
            {
                name: 'hourTo',
                label: 'Ora a (HH:MM:SS)',
                type: 'string',
            }
        ]
    },
    StockAdjustment:{
        title: 'StockAdjustment.page_title',
        api: 'planning-stock-adjustment',
        getItemWithList: true,
        enableUpdate: true,
        editButtonName: 'stockAdjustmentEditButton',
        enableDelete: true,
        deleteButtonName: 'stockAdjustmentDeleteButton',
        editMode: 'dialog',
        hideRefresh: true,
        preventAutoLoad: true,
        selection: true,
        selectionMode: 'multiple',
        defaultSortField: 'date',
        idColumn: "id",
        columns: [
            {
                name: 'voyageNumber',
                label: 'StockAdjustment.voyageNumber',
                type: 'string',
                formHide: true
            },
            {               
                name: 'initialStockDate',
                label: 'StockAdjustment.initialStockDate',
                type: 'date',
                onlyDate: true,
                formHide: true,
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {               
                name: 'date',
                label: 'StockAdjustment.date',
                type: 'date',
                onlyDate: true,
                inLineEdit: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'tankCode',
                label: 'StockAdjustment.tankCode',
                type: 'select',
                gridHide: true,
                selectApi: 'tank-management/tank-combo',
                selectFormat: { label: 'label', value: 'value' },
                inLineEdit: true,
                validations: {
                    required: true
                }                
            },
            {
                name: 'tankCode',
                label: 'StockAdjustment.tankCode',
                type: 'string',
                formHide: true
            },
            {
                name: 'adjustmentType',
                label: 'StockAdjustment.adjustmentType',
                type: 'select',
                selectApi: 'masterdata/adjustment-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                inLineEdit: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'productCode',
                label: 'StockAdjustment.productCode',
                type: 'string',
                inLineEdit: true,
                validations: {
                    required: true,
                    maxLength: 4
                }
            },
            {
                name: 'poolName',
                label: 'StockAdjustment.poolName',
                type: 'string',
                inLineEdit: true,
                validations: {
                    required: true,
                    maxLength: 50
                }
            },            
            {
                name: 'dischargeOrCharge',
                label: 'StockAdjustment.dischargeOrCharge',
                type: 'string',
                inLineEdit: true,
                validations: {
                    required: true,
                    maxLength: 5
                }
            },
            {
                name: 'description',
                label: 'StockAdjustment.description',
                type: 'string',
                inLineEdit: true,
                validations: {
                    required: true,
                    maxLength: 100
                }
            },
            {
                name: 'cubicMeters',
                label: 'StockAdjustment.cubicMeters',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                validations: {
                    required: true,
                }
            },
            {
                name: 'density',
                label: 'StockAdjustment.density',
                type: 'decimal',
                digits: 4,
                inLineEdit: true,
                validations: {
                    required: false,
                }
            },
            {               
                name: 'referenceDate',
                label: 'StockAdjustment.referenceDate',
                type: 'string',
                formHide: true,
                inLineEdit: false
            },
            {               
                name: 'referenceDate',
                label: 'StockAdjustment.referenceDate',
                type: 'date',
                inLineEdit: false
            },
            {
                name: 'conversionFactor',
                label: 'StockAdjustment.conversionFactor',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                validations: {
                    required: false,
                }
            },
            {
                name: 'barrels',
                label: 'StockAdjustment.barrels',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                validations: {
                    required: false,
                }
            },
            {
                name: 'metricTonnes',
                label: 'StockAdjustment.metricTonnes',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
                validations: {
                    required: false,
                }
            }
        ]
    },
    AdjustmentType:{
        title: 'AdjustmentType.page_title',
        api: 'masterdata/adjustment-type',
        enableUpdate: true,
        editButtonName: 'adjustmentTypeEditButton',
        enableDelete: true,
        deleteButtonName: 'adjustmentTypeDeleteButton',
        enableInsert: true,
        addButtonName: 'adjustmentTypeAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
               
                name: 'description',
                label: 'AdjustmentType.description',
                type: 'string',
                validations:{
                    required: true,
                    maxLength: 50
                }
            }
        ]
    },
    TmTankVolumetricData: {
        title: 'TmTankVolumetricData.page_title',
        api: 'tank-management/tank-volumetric-data',
        enableUpdate: true,
        editButtonName: 'tmTankVolumetricDataEditButton',
        enableDelete: true,
        deleteButtonName: 'tmTankVolumetricDataDeleteButton',
        enableInsert: true,
        addButtonName: 'tmTankVolumetricDataAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
               
                name: 'tankCode',
                label: 'tankConfiguration.tankCode',
                type: 'select',
                selectApi: 'tank-management/tank-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            {
               
                name: 'tankCode',
                label: 'tankConfiguration.tankCode',
                type: 'string',
                formHide: true
            },
            {
                name: 'grossShellCapacity',
                label: 'TmTankVolumetricData.grossShellCapacity',
                type: 'number',
            },
            {
                name: 'maxOperatingCapacity',
                label: 'TmTankVolumetricData.maxOperatingCapacity',
                type: 'number',
            },
            {
                name: 'workingVolume',
                label: 'TmTankVolumetricData.workingVolume',
                type: 'number',
            },
            {
                name: 'deadStockVolume',
                label: 'TmTankVolumetricData.deadStockVolume',
                type: 'decimal',
                digits: 3,
            }
        ],
        toolbarActions: [
            {
                type:'button',
                visible: true,
                name: 'tmTankVolumetricImportData',
                icon: 'pi pi-upload',
                title: 'TmTankVolumetricData.importTemplate',
                cssClass: 'p-button-success p-button-sm',
                action: function (body, caller) {

                    caller.$router.push("/tankManagement/tm-tank-volumetric-data")
                }
            },
            {
                type:'button',
                visible: true,
                name: 'tmTankVolumetricDataGetTEmplate',
                icon: 'pi pi-download',
                title: 'TmTankVolumetricData.downloadTemplate',
                cssClass: 'p-button-sm',
                action: function (body, caller) {
                    caller.$store.dispatch("generic/setLoading", true);             
                    genericService.dowloadFile('tank-management/tank-volumetric-data-excel-template').then(response => {
                        caller.$store.dispatch("generic/setLoading", false); 
                        
                        if(response.success){
                            var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                            var fileLink = document.createElement('a');
                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', 'VolumetricDataTemplate.xlsx');
                            document.body.appendChild(fileLink);          
                            fileLink.click();
                        }else{
                            caller.$toast.add({ detail: response.message || caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                        }
                    })
                }
            }
        ]
    },
    ExciseFiscalRegister: {
        title: 'ExciseFiscalRegister.page_title',
        api: 'excise-fiscal-register',
        enableUpdate: true,
        editButtonName: 'ExciseFiscalRegisterEditButton',
        enableDelete: true,
        deleteButtonName: 'ExciseFiscalRegisterDeleteButton',
        editMode: 'dialog',
        hideRefresh: true,
        preventAutoLoad: true,
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                inLineEdit: true,
                editEnabled: () => false
            },
            {
                name: 'notificationCode',
                label: 'ExciseFiscalRegister.notificationCode',
                type: 'string',
                style: 'text-decoration: underline;',
                inLineEdit: true,
                editEnabled: () => false,
                action: function (caller, record) {
                    if(record.idFiscalNotificationHeader != null){
                        FiscalNotificationService.getFiscalNotificationHeader(record.idFiscalNotificationHeader, response => {
                            if (response.success && response.data != null) {
                                let uri = `/last-fiscal-notification?idScheduledOperation=${response.data.idOperativePlanScheduledOperation}`;
                                caller.$router.push(uri);
                            } else {
                                caller.$toast.add({ detail: response.message && response.message != '' ? response.message : caller.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                            }
                        })
                    }
                }
            },
            {               
                name: 'notificationDate',
                label: 'ExciseFiscalRegister.notificationDate',
                type: 'date',
                datetime: true,
                inLineEdit: true,
                defaultSort: 'asc',
                editEnabled: () => false,
            },
            {
                name: 'idRegistryPlant',
                label: 'registryPlant.fk',
                type: 'select',
                selectApi: 'masterdata/registry-plant-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                sortField: 'Description',
                gridHide: true,
                inLineEdit: true
            },
            {
                name: 'plantDescription',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/registry-plant-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryPlant'
            },
            {
                name: 'idRegistryOperationType',
                label: 'operationType.fk',
                type: 'select',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                sortField: 'Code',
                gridHide: true,
                inLineEdit: true
            },
            {
                name: 'operationTypeDescription',
                label: 'operationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/operation-type-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryOperationType'
            },
            {
                name: 'idRegistryEquipment',
                label: 'ExciseFiscalRegister.tankCode',
                type: 'select',
                selectApi: 'masterdata/equipment-combo?type=SERB',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                },
                inLineEdit: true
            },
            {
                name: 'tankCode',
                label: 'ExciseFiscalRegister.tankCode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=SERB',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryEquipment'
            },
            {
                name: 'idRegistryProduct',
                label: 'ExciseFiscalRegister.idRegistryProduct',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true,
                inLineEdit: true
            },
            {
                name: 'productCode',
                label: 'ExciseFiscalRegister.productCode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct'
            },
            {               
                name: 'productDescription',
                label: 'ExciseFiscalRegister.productDescription',
                type: 'string',
                inLineEdit: true,
            },
            {               
                name: 'productFamily',
                label: 'ExciseFiscalRegister.productFamily',
                type: 'string'
            },
            {               
                name: 'isGas',
                label: 'ExciseFiscalRegister.isGas',
                type: 'boolean',
                formHide: true
            },
            {               
                name: 'nc',
                label: 'ExciseFiscalRegister.nc',
                type: 'string',
                inLineEdit: true,
            },
            {               
                name: 'fiscalMovedWeight',
                label: 'ExciseFiscalRegister.fiscalMovedWeight',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
            },
            {               
                name: 'fiscalMovedVolume',
                label: 'ExciseFiscalRegister.fiscalMovedVolume',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
            },
            {               
                name: 'denaturantQuantity',
                label: 'ExciseFiscalRegister.denaturantQuantity',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
            },
            {               
                name: 'denaturantVolume',
                label: 'ExciseFiscalRegister.denaturantVolume',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
            },
            {               
                name: 'fiscalStockWeight',
                label: 'ExciseFiscalRegister.fiscalStockWeight',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
            },
            {               
                name: 'fiscalStockVolume',
                label: 'ExciseFiscalRegister.fiscalStockVolume',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
            },
            {               
                name: 'placeOfDeliveryName',
                label: 'ExciseFiscalRegister.placeOfDeliveryName',
                type: 'string',
                inLineEdit: true,
            },
            {
                name: 'idRegistryFiscalDestination',
                label: 'ExciseFiscalRegister.fiscalDestination',
                type: 'select',
                selectApi: 'masterdata/fiscal-destination-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                },
                inLineEdit: true
            },
            {
                name: 'fiscalDestination',
                label: 'ExciseFiscalRegister.fiscalDestination',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-destination',
                gridFilterSelectFormat: { label: 'shortDescription', value: 'id' },
                gridFilterName: 'idRegistryFiscalDestination'
            },
            {
                name: 'idRegistryFiscalPosition',
                label: 'ExciseFiscalRegister.fiscalPosition',
                type: 'select',
                selectApi: 'masterdata/fiscal-position-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                },
                inLineEdit: true
            },
            {
                name: 'fiscalPosition',
                label: 'ExciseFiscalRegister.fiscalPosition',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-position',
                gridFilterSelectFormat: { label: 'description', value: 'id' },
                gridFilterName: 'idRegistryFiscalPosition'
            },
            {
                name: 'idRegistryFiscalUse',
                label: 'fiscalUse.fk',
                type: 'select',
                selectApi: 'masterdata/fiscal-use-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                },
                inLineEdit: true
            },
            {
                name: 'fiscalUse',
                label: 'fiscalUse.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-use',
                gridFilterSelectFormat: { label: 'description', value: 'id' },
                gridFilterName: 'idRegistryFiscalUse'
            },
            {               
                name: 'fiscalDocumentNumber',
                label: 'ExciseFiscalRegister.fiscalDocumentNumber',
                type: 'string',
                inLineEdit: true,
            },
            {               
                name: 'exciseDuty',
                label: 'ExciseFiscalRegister.exciseDuty',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
            },
            {               
                name: 'exciseDue',
                label: 'ExciseFiscalRegister.exciseDue',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
            },
            {               
                name: 'exciseDutySuspended',
                label: 'ExciseFiscalRegister.exciseDutySuspended',
                type: 'decimal',
                digits: 2,
                inLineEdit: true,
            },
            {               
                name: 'taxableQuantity',
                label: 'ExciseFiscalRegister.taxableQuantity',
                type: 'decimal',
                digits: 3,
                inLineEdit: true,
            },
            {               
                name: 'exciseRate',
                label: 'ExciseFiscalRegister.exciseRate',
                type: 'decimal',
                digits: 8,
                inLineEdit: true,
            },
            {               
                name: 'portOrLocation',
                label: 'ExciseFiscalRegister.portOrLocation',
                type: 'string',
                inLineEdit: true,
            },
            {               
                name: 'infoilCvtOperationKey',
                label: 'ExciseFiscalRegister.infoilCvtOperationKey',
                type: 'string',
                gridHide: true,
            }
        ],
        customActions:[
            {
                title: 'tankOperations.exportInCalculator',
                cssClass: 'p-button-rounded p-button mr-2',
                name: 'exciseFiscalRegisterExportToCalculator',
                icon: 'pi pi-table',
                data: {},
                fields: [],
                action: function (data, caller) {
                    caller.$emit('onExportToCalculator', data);
                }
            }
        ]
    },
    DeductionDefault : {
        title: 'DeductionDefault.page_title',
        api: 'demurrage-deduction',
        enableUpdate: true,
        editButtonName: 'DeductionDescriptionEditButton',
        enableDelete: true,
        deleteButtonName: 'DeductionDescriptionDeleteButton',
        enableInsert: true,
        addButtonName: 'DeductionDescriptionAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
            },
            {
                name: 'deductionDescription',
                label: 'DeductionDefault.DeductionDescription',
                type: 'string',
            },
            {
                name: 'idRegistryTimesheetOperationStartDate',
                label: 'DeductionDefault.timesheetOperationStartDate',
                type: 'select',
                selectApi: 'masterdata/timesheet-operation-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
            },
            {
                name: 'registryTimesheetOperationStartDate',
                label: 'DeductionDefault.timesheetOperationStartDate',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/timesheet-operation-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryTimesheetOperationStartDate'
            },
            {
                name: 'idRegistryTimesheetOperationEndDate',
                label: 'DeductionDefault.timesheetOperationEndDate',
                type: 'select',
                selectApi: 'masterdata/timesheet-operation-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
            },
            {
                name: 'registryTimesheetOperationEndDate',
                label: 'DeductionDefault.timesheetOperationEndDate',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/timesheet-operation-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryTimesheetOperationEndDate'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
            }
        ]
    },
    StockManagementInventory: {
        title: 'StockManagementInventory.page_title',
        api: 'stock-management-inventory/registry',
        enableUpdate: true,
        editButtonName: 'stockManagementInventoryRegistryUpdateButton',
        editMode: 'dialog',
        enableDelete: true,
        deleteButtonName: 'stockManagementInventoryRegistryDeleteButton',
        idColumn: "id",
        defaultSortField: 'tankCode',
        defaultSortOrder: 1,
        preventAutoLoad: true,
        preventFilters: false,
        preventPagination: true,
        hideRefresh: true,
        filterLocal: true,
        queryParamFilters: true,
        //editMode: 'dialog',
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                gridHide: true
            },
            {
                name: 'referenceDate',
                label: 'StockManagementInventory.referenceDate',
                type: 'date',
                onlyDate: true,
                gridHide: true,
                formHide: true
            },
            {
                name: 'isValidated',
                label: 'StockManagementInventory.isValidated',
                type: 'boolean',
                readOnly: true
            },
            {
                name: 'idRegistryPlant',
                label: 'registryPlant.fk',
                type: 'select',
                selectApi: 'masterdata/registry-plant-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true,
                formHide: true
            },
            {
                name: 'registryPlant',
                label: 'registryPlant.fk',
                type: 'string',
                formHide: true
            },
            {
                name: 'tankCode',
                label: 'StockManagementMts.tankCode',
                type: 'select-simple',
                selectApi: 'masterdata/equipment-combo?type=SERB',
                selectFormat: { label: 'label', value: 'label' },
                gridHide: false,
                inLineEdit: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo?type=SERB',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'label'
                },
                gridFilterName: 'registryPlant',
                validations: {
                    required: true
                }
            },
            {
                name: 'idRegistryProduct',
                label: 'product.fk',
                type: 'select',
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                formHide: true
            },
            {
                name: 'registryProduct',
                label: 'product.fk',
                type: 'string',
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/product-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryProduct',
                readonly: true,
                enableEdit: () => false
            },
            {
                name: 'isOutOfService',
                label: 'StockManagementInventory.isOutOfService',
                type: 'boolean',
                readOnly: true
            },
            {
                name: 'isHidden',
                label: 'StockManagementInventory.isHidden',
                type: 'boolean',
                inLineEdit: 'true'
            },
            {
                name: 'measureDate',
                label: 'StockManagementInventory.measureDate',
                type: 'date', 
                datetime: true,
                inLineEdit: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'productLevelManual',
                label: 'StockManagementInventory.productLevelManual',
                type: 'number',
                inLineEdit: true 
            },
            {
                name: 'productLevelAuto',
                label: 'StockManagementInventory.productLevelAuto',
                type: 'number',
                inLineEdit: true 
            },
            {
                name: 'productLevelDailyStock',
                label: 'StockManagementInventory.productLevelDailyStock',
                type: 'number',
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'productLevelManualVsAutoDelta',
                label: 'StockManagementInventory.productLevelManualVsAutoDelta',
                type: 'number',
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'density',
                label: 'StockManagementInventory.density',
                type: 'decimal',
                digits: 5,
                inLineEdit: true
            },
            {
                name: 'densityDailyStock',
                label: 'StockManagementInventory.densityDailyStock',
                type: 'decimal',
                digits: 5,
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'temperatureManual',
                label: 'StockManagementInventory.temperatureManual',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            },
            {
                name: 'temperatureAuto',
                label: 'StockManagementInventory.temperatureAuto',
                type: 'decimal',
                digits: 2,
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'temperatureDailyStock',
                label: 'StockManagementInventory.temperatureDailyStock',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            },
            {
                name: 'temperatureManualVsAutoDelta',
                label: 'StockManagementInventory.temperatureManualVsAutoDelta',
                type: 'decimal',
                digits: 2,
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'waterLevel',
                label: 'StockManagementInventory.waterLevel',
                type: 'number',
                inLineEdit: true 
            },
            {
                name: 'waterLevelDailyStock',
                label: 'StockManagementInventory.waterLevelDailyStock',
                type: 'number',
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'sludgeLevel',
                label: 'StockManagementInventory.sludgeLevel',
                type: 'number',
                inLineEdit: true 
            },
            {
                name: 'gasTemperature',
                label: 'StockManagementInventory.gasTemperature',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            },
            {
                name: 'gasTemperatureDailyStock',
                label: 'StockManagementInventory.gasTemperatureDailyStock',
                type: 'decimal',
                digits: 2,
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'pressure',
                label: 'StockManagementInventory.pressure',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            },
            {
                name: 'pressureDailyStock',
                label: 'StockManagementInventory.pressureDailyStock',
                type: 'decimal',
                digits: 2,
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'molecularWeight',
                label: 'StockManagementInventory.molecularWeight',
                type: 'decimal',
                digits: 2,
                inLineEdit: true
            },
            {
                name: 'molecularWeightDailyStock',
                label: 'StockManagementInventory.molecularWeightDailyStock',
                type: 'decimal',
                digits: 2,
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'weightInKg',
                label: 'StockManagementInventory.weightInKg',
                type: 'number',
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'weightInKgDailyStock',
                label: 'StockManagementInventory.weightInKgDailyStock',
                type: 'number',
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'volumeInLt',
                label: 'StockManagementInventory.volumeInLt',
                type: 'number',
                inLineEdit: true 
            },
            {
                name: 'volumeInLtDailyStock',
                label: 'StockManagementInventory.volumeInLtDailyStock',
                type: 'number',
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'waterVolumeInLt',
                label: 'StockManagementInventory.waterVolumeInLt',
                type: 'number',
                inLineEdit: true 
            },
            {
                name: 'waterVolumeInLtDailyStock',
                label: 'StockManagementInventory.waterVolumeInLtDailyStock',
                type: 'number',
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'theoreticalVolumeInLt',
                label: 'StockManagementInventory.theoreticalVolumeInLt',
                type: 'number',
                inLineEdit: true 
            },
            {
                name: 'technicalVolumeInLt',
                label: 'StockManagementInventory.technicalVolumeInLt',
                type: 'number',
                inLineEdit: true 
            },
            {
                name: 'lastChangedBy',
                label: 'StockManagementInventory.lastChangedBy',
                type: 'string',
                readonly: true,
                enableEdit: () => false 
            },
            {
                name: 'audit',
                label: 'StockManagementInventory.audit',
                type: 'string',
                inLineEdit: true 
            },
            {
                name: 'schedulingNotes',
                label: 'StockManagementInventory.schedulingNotes',
                type: 'string',
                inLineEdit: true 
            },
            {
                name: 'notes',
                label: 'StockManagementInventory.notes',
                type: 'string',
                inLineEdit: true 
            }
        ]
    },
    RegistryMaterialBalanceIgccEquipment: {
        title: 'RegistryMaterialBalanceIgccEquipment.page_title',
        api: 'masterdata/material-balance-igcc-equipment',
        enableUpdate: true,
        editButtonName: 'materialBalanceTemplateLotEditButton',
        enableDelete: true,
        deleteButtonName: 'materialBalanceTemplateLotDeleteButton',
        enableInsert: true,
        addButtonName: 'materialBalanceTemplateLotAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryEquipment',
                label: 'equipment.fk',
                type: 'select',
                selectApi: 'masterdata/equipment-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'equipment',
                label: 'equipment.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/equipment-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryEquipment'
            },
            {
                name: 'isEnabled',
                label: 'isEnabled',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'validityStartDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'validityEndDate',
                label: 'endDate',
                type: 'date',
                validations: {
                    required: true
                }
            }

        ]
    },
    RegistryTankOperationRules: {
        title: 'registryTankOperationRules.page_title',
        api: 'masterdata/tank-operation-rules',
        enableUpdate: true,
        editButtonName: 'tankOperationRulesEditButton',
        enableDelete: true,
        deleteButtonName: 'tankOperationRulesDeleteButton',
        enableInsert: true,
        addButtonName: 'tankOperationRulesAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'currentOperationDescription',
                label: 'registryTankOperationRules.currentOperationDescription',
                type: 'select',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: { label: 'label', value: 'label' },
                gridHide: true,
            },
            {
                name: 'currentOperationCode',
                label: 'registryTankOperationRules.currentOperationCode',
                type: 'string',
                validations: {
                    required: true
                },
                formHide: true,
            },
            {
                name: 'currentOperationDescription',
                label: 'registryTankOperationRules.currentOperationDescription',
                type: 'string',
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'previousOperationDescription',
                label: 'registryTankOperationRules.previousOperationDescription',
                type: 'select',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: { label: 'label', value: 'label' },
                gridHide: true,
            },
            {
                name: 'previousOperationCode',
                label: 'registryTankOperationRules.previousOperationCode',
                type: 'string',
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'previousOperationDescription',
                label: 'registryTankOperationRules.previousOperationDescription',
                type: 'string',
                validations: {
                    required: true
                },
                formHide: true
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                formHide: true
            }
        ]
    },
    VersalisRegister: {
        title: 'versalisRegister.page_title',
        api: 'masterdata/versalis-register',
        enableUpdate: true,
        editButtonName: 'versalisRegisterEditButton',
        enableDelete: true,
        deleteButtonName: 'versalisRegisterDeleteButton',
        enableInsert: true,
        addButtonName: 'versalisRegisterAddButton',
        idColumn: "id",
        dataTables: [
            {
                label: 'versalisRegister.detail_page_title',
                dataTableUnderForm: 'VersalisRegisterDetail',
                dataTableDefaultKey: 'idRegistryVersalisRegister',
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'code',
                label: 'code',
                type: 'string',
                validations: {
                    maxLength: 10,
                    required: true
                }
            },
            {
                name: 'description',
                label: 'description',
                type: 'string',
                validations: {
                    maxLength: 100,
                    required: true
                }
            }
        ]
    },
    VersalisRegisterDetail: {
        title: 'versalisRegister.page_title',
        api: 'masterdata/versalis-register-detail',
        enableUpdate: false,
        editButtonName: 'versalisRegisterDetailEditButton',
        enableDelete: true,
        deleteButtonName: 'versalisRegisterDetailDeleteButton',
        enableInsert: false,
        addButtonName: 'versalisRegisterDetailAddButton',
        idColumn: "id",
        disableBack: true,
        skipCache: true,
        disableSave: true,
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'idRegistryVersalisRegister',
                label: 'versalisRegister.fk',
                type: 'select',
                selectApi: 'masterdata/versalis-register',
                selectFormat: { label: 'description', value: 'id' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryVersalisRegister',
                label: 'versalisRegister.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/versalis-register',
                gridFilterSelectFormat: { label: 'description', value: 'id' },
                gridFilterName: 'idRegistryVersalisRegister'
            },
            {
                name: 'nc',
                label: 'NC',
                type: 'string',
                validations: {
                    maxLength: 8,
                    required: true
                }
            },
            {
                name: 'idRegistryFiscalDestination',
                label: 'fiscalDestination.fk',
                type: 'select',
                selectApi: 'masterdata/fiscal-destination-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true,
                validations: {
                    required: true
                }
            },
            {
                name: 'registryFiscalDestination',
                label: 'fiscalDestination.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/fiscal-destination-combo',
                gridFilterSelectFormat: { label: 'label', value: 'value' },
                gridFilterName: 'idRegistryFiscalDestination'
            },
            {
                name: 'validityStartDate',
                label: 'startDate',
                type: 'date',
                validations: {
                    required: true
                }
            },
            {
                name: 'validityEndDate',
                label: 'endDate',
                type: 'date'
            },
            {
                name: 'isDeleted',
                label: 'isDeleted',
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false
            }
        ],
        customActions: [
            {
                title: 'btn.edit',
                visible: false,
                name: 'versalisRegisterDetailEditButton',
                icon: 'pi pi-pencil',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/masterdata/VersalisRegisterDetail/${data.id}?idRegistryVersalisRegister=${data.idRegistryVersalisRegister}`;
                    caller.$router.push(uri);
                }
            },
        ],
        formCustomActions: [
            {
                name: 'btn_back',
                title: 'btn.back',
                icon: 'pi pi-arrow-left',
                data: {},
                fields: [],
                action: function (data, caller) {
                    let uri = `/masterdata/VersalisRegister/${data.idRegistryVersalisRegister}`;
                    caller.$router.push(uri);
                }
            },
            {
                name: 'btn_save',
                title: 'btn.save',
                icon: 'pi pi-save',
                data: {},
                fields: [],
                action: function (data, caller) {
                    caller.submitted = true;

                    let valid = !caller.v$.$invalid;

                    if (!valid){
                        return;
                    }

                    if(data.id){
                        // UPDATE
                        CommonService.updateItem('masterdata/versalis-register-detail', data, response => {
                            caller.disabled = false;
                            caller.$store.dispatch("generic/setLoading", false);
                            if(response.success){
                                let uri = `/masterdata/VersalisRegister/${data.idRegistryVersalisRegister}`;
                                caller.$router.push(uri);
                            }else{
                                caller.$toast.add({detail: response.message && response.message != '' ? response.message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        }, 'id')
                    }else{
                        // SAVE
                        CommonService.createItem('masterdata/versalis-register-detail', caller.data, response => {
                            caller.disabled = false;
                            caller.$store.dispatch("generic/setLoading", false);
                            if(response.success){
                                let uri = `/masterdata/VersalisRegister/${data.idRegistryVersalisRegister}`;
                                caller.$router.push(uri);
                            }else{
                                caller.$toast.add({detail: response.message && response.message != '' ? response.message :  caller.$t('messages.generic_error_message'), life:3000, severity:'error'});
                            }
                        })
                    }
                }
            }
        ],
        toolbarActions: [
            {
                type: 'button',
                visible: true,
                name: 'versalisRegisterDetailAddButton',
                icon: 'pi pi-plus',
                title: 'btn.add',
                action: function (data, caller) {
                    let uri = `/masterdata/VersalisRegisterDetail/0?idRegistryVersalisRegister=${caller.defaultId}`;
                    caller.$router.push(uri);
                }
            }
        ]
    },
    VersalisMovementsGas: {
        title: 'versalisRegister.product_movements_page_title',
        api: 'versalis-movements-gas',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "KY",
        preventAutoLoad: true,
        hideRefresh: true,
        preventFilters: true,
        defaultSortField: 'datA_DI_RIFERIMENTO',
        defaultSortOrder: 1,
        columns: [
            {
                name: 'ky',
                label: 'KY',
                type: 'string',
                readOnly: true,
            },
            {
                name: 'idreG_ANNO_EMISSIONE_DOC',
                label: 'VersalisMovements.IDREG_ANNO_EMISSIONE_DOC',
                type: 'string'
            },
            {
                name: 'datA_DI_RIFERIMENTO',
                label: 'VersalisMovements.DATA_DI_RIFERIMENTO',
                type: 'date'
            },
            {
                name: 'codicE_PRODOTTO_NOM_COMB',
                label: 'VersalisMovements.CODICE_PRODOTTO_NOM_COMB',
                type: 'string'
            },
            {
                name: 'documentO_TIPO',
                label: 'VersalisMovements.DOCUMENTO_TIPO',
                type: 'string'
            },
            {
                name: 'documentO_NUMERO',
                label: 'VersalisMovements.DOCUMENTO_NUMERO',
                type: 'string'
            },
            {
                name: 'documentO_DATA_EMISSIONE',
                label: 'VersalisMovements.DOCUMENTO_DATA_EMISSIONE',
                type: 'date'
            },
            {
                name: 'proV_DEST_MERCE',
                label: 'VersalisMovements.PROV_DEST_MERCE',
                type: 'string'
            },
            {
                name: 'qtA_PESO_IN',
                label: 'VersalisMovements.QTA_PESO_IN',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_LITRI_A15C_IN',
                label: 'VersalisMovements.QTA_LITRI_A15C_IN',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_PESO_OUT',
                label: 'VersalisMovements.QTA_PESO_OUT',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_LITRI_A15C_OUT',
                label: 'VersalisMovements.QTA_LITRI_A15C_OUT',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'densitA_A15C',
                label: 'VersalisMovements.DENSITA_A15C',
                type: 'decimal',
                digits: 4
            },
            {
                name: 'accisA_DOVUTA',
                label: 'VersalisMovements.ACCISA_DOVUTA',
                type: 'decimal',
                digits: 2
            },
            {
                name: 'reC_UPDDT',
                label: 'VersalisMovements.REC_UPDDT',
                type: 'date',
                datetime: true
            }
        ]
    },
    VersalisMovementsBTZ: {
        title: 'versalisRegister.product_movements_page_title',
        api: 'versalis-movements-btz',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "KY",
        preventAutoLoad: true,
        hideRefresh: true,
        preventFilters: true,
        defaultSortField: 'datA_DI_RIFERIMENTO',
        defaultSortOrder: 1,
        columns: [
            {
                name: 'ky',
                label: 'KY',
                type: 'string',
                readOnly: true,
            },
            {
                name: 'idreG_ANNO_EMISSIONE_DOC',
                label: 'VersalisMovements.IDREG_ANNO_EMISSIONE_DOC',
                type: 'string'
            },
            {
                name: 'datA_DI_RIFERIMENTO',
                label: 'VersalisMovements.DATA_DI_RIFERIMENTO',
                type: 'date'
            },
            {
                name: 'codicE_PRODOTTO_NOM_COMB',
                label: 'VersalisMovements.CODICE_PRODOTTO_NOM_COMB',
                type: 'string'
            },
            {
                name: 'documentO_TIPO',
                label: 'VersalisMovements.DOCUMENTO_TIPO',
                type: 'string'
            },
            {
                name: 'documentO_NUMERO',
                label: 'VersalisMovements.DOCUMENTO_NUMERO',
                type: 'string'
            },
            {
                name: 'documentO_DATA_EMISSIONE',
                label: 'VersalisMovements.DOCUMENTO_DATA_EMISSIONE',
                type: 'date'
            },
            {
                name: 'proV_DEST_MERCE',
                label: 'VersalisMovements.PROV_DEST_MERCE',
                type: 'string'
            },
            
            {
                name: 'qtA_PESO_IN',
                label: 'VersalisMovements.QTA_PESO_IN',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_LITRI_A15C_IN',
                label: 'VersalisMovements.QTA_LITRI_A15C_IN',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_PESO_OUT',
                label: 'VersalisMovements.QTA_PESO_OUT',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_LITRI_A15C_OUT',
                label: 'VersalisMovements.QTA_LITRI_A15C_OUT',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'densitA_A15C',
                label: 'VersalisMovements.DENSITA_A15C',
                type: 'decimal',
                digits: 4
            },
            {
                name: 'accisA_DOVUTA',
                label: 'VersalisMovements.ACCISA_DOVUTA',
                type: 'decimal',
                digits: 2
            },
            {
                name: 'reC_UPDDT',
                label: 'VersalisMovements.REC_UPDDT',
                type: 'date',
                datetime: true
            }
        ]
    },
    VersalisMovementsATZ: {
        title: 'versalisRegister.product_movements_page_title',
        api: 'versalis-movements-atz',
        enableUpdate: false,
        enableDelete: false,
        enableInsert: false,
        idColumn: "KY",
        preventAutoLoad: true,
        hideRefresh: true,
        preventFilters: true,
        defaultSortField: 'datA_DI_RIFERIMENTO',
        defaultSortOrder: 1,
        columns: [
            {
                name: 'ky',
                label: 'KY',
                type: 'string',
                readOnly: true,
            },
            {
                name: 'idreG_ANNO_EMISSIONE_DOC',
                label: 'VersalisMovements.IDREG_ANNO_EMISSIONE_DOC',
                type: 'string'
            },
            {
                name: 'datA_DI_RIFERIMENTO',
                label: 'VersalisMovements.DATA_DI_RIFERIMENTO',
                type: 'date'
            },
            {
                name: 'codicE_PRODOTTO_NOM_COMB',
                label: 'VersalisMovements.CODICE_PRODOTTO_NOM_COMB',
                type: 'string'
            },
            {
                name: 'documentO_TIPO',
                label: 'VersalisMovements.DOCUMENTO_TIPO',
                type: 'string'
            },
            {
                name: 'documentO_NUMERO',
                label: 'VersalisMovements.DOCUMENTO_NUMERO',
                type: 'string'
            },
            {
                name: 'documentO_DATA_EMISSIONE',
                label: 'VersalisMovements.DOCUMENTO_DATA_EMISSIONE',
                type: 'date'
            },
            {
                name: 'proV_DEST_MERCE',
                label: 'VersalisMovements.PROV_DEST_MERCE',
                type: 'string'
            },
           
            {
                name: 'qtA_PESO_IN',
                label: 'VersalisMovements.QTA_PESO_IN',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_LITRI_A15C_IN',
                label: 'VersalisMovements.QTA_LITRI_A15C_IN',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_PESO_OUT',
                label: 'VersalisMovements.QTA_PESO_OUT',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'qtA_LITRI_A15C_OUT',
                label: 'VersalisMovements.QTA_LITRI_A15C_OUT',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'densitA_A15C',
                label: 'VersalisMovements.DENSITA_A15C',
                type: 'decimal',
                digits: 4
            },
            {
                name: 'accisA_DOVUTA',
                label: 'VersalisMovements.ACCISA_DOVUTA',
                type: 'decimal',
                digits: 2
            },
            {
                name: 'reC_UPDDT',
                label: 'VersalisMovements.REC_UPDDT',
                type: 'date',
                datetime: true
            }
        ]
    },
    VersalisMovementsRawMaterial: {
        title: 'versalisRegister.raw_materials_movements_page_title',
        api: 'versalis-movements-raw-material',
        enableUpdate: true,
        editButtonName: 'VersalisMovementsRawMaterialEditButton',
        enableDelete: true,
        deleteButtonName: 'VersalisMovementsRawMaterialDeleteButton',
        enableInsert: true,
        addButtonName: 'VersalisMovementsRawMaterialInsertButton',
        idColumn: "id",
        defaultSortField: 'movementDate',
        defaultSortOrder: 1,
        preventAutoLoad: true,
        preventFilters: false,
        hideRefresh: true,
        showDetailDialog: true,
        editMode: 'dialog',
        addMode: 'dialog',
        columns: [
            { name: 'id', label: 'id', type: 'number', readOnly: true,  defaultSort: 'asc' },
            { name: 'forspedXediId', label: 'KY', type: 'string', gridHide: true, readOnly: true },
            { label: 'RecordId', name: 'recordId', type: 'string', gridHide: true, readOnly: true },
            { label: 'VersalisMovements.IDREG_PROTOCOL_NUM', name: 'protocolNumber', type: 'string', readOnly: true },
            { label: 'VersalisMovements.IDREG_PROTOCOL_NUM', name: 'protocolNumber', type: 'select', selectApi: 'masterdata/versalis-register-combo', format: '', gridHide: true, inLineEdit: true, editEnabled: (item) => !item.id },
            { label: 'VersalisMovements.IDREG_ANNO_EMISSIONE_DOC', name: 'registerYear', type: 'string', readOnly: false, inLineEdit: true },
            { label: 'VersalisMovements.MOVIMENTO_TIPO', name: 'movementType', type: 'string', readOnly: false, inLineEdit: false, localized: true, renderColumn: (item) => 'VersalisMovements.movementType' + item.movementType },
            {
                name: 'movementType',
                label: 'VersalisMovements.MOVIMENTO_TIPO',
                type: 'static-select',
                staticOptions: [{value: 'G', label: 'Giacenza'}, {value: 'C', label: 'Carico'}, {value: 'S', label: 'Scarico'}],
                format: { label: 'label', value: 'value' },
                gridHide: true,
                inLineEdit: true
            },
            { label: 'VersalisMovements.DATA_DI_RIFERIMENTO', name: 'movementDate', type: 'date', readOnly: false, inLineEdit: true, onlyDate: true },
            { label: 'VersalisMovements.DOCUMENTO_TIPO', name: 'documentType', type: 'string', readOnly: false, inLineEdit: true },
            { label: 'VersalisMovements.DOCUMENTO_NUMERO', name: 'documentNumber', type: 'string', readOnly: false, inLineEdit: true },
            { label: 'VersalisMovements.DOCUMENTO_DATA_EMISSIONE', name: 'documentDate', type: 'date', readOnly: false, inLineEdit: true, onlyDate: true },
            { label: 'VersalisMovements.PROV_DEST_MERCE', name: 'country', type: 'string', readOnly: false, inLineEdit: true },
            { label: 'VersalisMovements.QTA_PESO', name: 'movedWeight', type: 'decimal', digits: 3, readOnly: false, inLineEdit: true, showZero: true, gridHide: true },
            { label: 'VersalisMovements.QTA_LITRI_A15C', name: 'movedVolume', type: 'decimal', digits: 3, readOnly: false, inLineEdit: true, showZero: true, gridHide: true },
            { label: 'VersalisMovements.QTA_PESO_IN', name: 'movedWeightIn', type: 'decimal', digits: 3, readOnly: false, inLineEdit: false, formHide: true },
            { label: 'VersalisMovements.QTA_LITRI_A15C_IN', name: 'movedVolumeIn', type: 'decimal', digits: 3, readOnly: false, inLineEdit: false, formHide: true },
            { label: 'VersalisMovements.QTA_PESO_OUT', name: 'movedWeightOut', type: 'decimal', digits: 3, readOnly: false, inLineEdit: false, formHide: true },
            { label: 'VersalisMovements.QTA_LITRI_A15C_OUT', name: 'movedVolumeOut', type: 'decimal', digits: 3, readOnly: false, inLineEdit: false, formHide: true },
            { label: 'VersalisMovements.DENSITA_A15C', name: 'density15', type: 'decimal', digits: 4, readOnly: false, inLineEdit: true, showZero: true },
            { label: 'VersalisMovements.ACCISA_DOVUTA', name: 'exciseDue', type: 'decimal', digits: 2, readOnly: false, inLineEdit: true, showZero: true },
            { label: 'VersalisMovements.GIACENZA_PESO', name: 'stockWeight', type: 'decimal', digits: 3, readOnly: true, showZero: true },
            { label: 'VersalisMovements.GIACENZA_LITRI', name: 'stockVolume', type: 'decimal', digits: 3, readOnly: true, showZero: true },
            { label: 'VersalisMovements.REC_UPDDT', name: 'xediTrackBLastUpdateDate', type: 'date', readOnly: false, inLineEdit: false, onlyDate: false,
                datetime: true },

        ]
    },
    SiteContractOperations: {
        title: 'SiteContractOperations.page_title',
        api: 'movement-instruction/site-contract-operations',
        enableUpdate: true,
        editButtonName: 'siteContractOperationsEditButton',
        enableDelete: true,
        deleteButtonName: 'siteContractOperationsDeleteButton',
        enableInsert: true,
        addButtonName: 'siteContractOperationsAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { 
                name: 'idCounterParty', 
                label: 'SiteContractOperations.counterParty', 
                type: 'select', 
                selectApi: 'masterdata/company-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            { 
                name: 'counterParty', 
                label: 'SiteContractOperations.counterParty', 
                type: 'string', 
                formHide: true
            },
            { 
                name: 'idRegistryProduct', 
                label: 'SiteContractOperations.registryProduct', 
                type: 'select', 
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            { 
                name: 'registryProduct', 
                label: 'SiteContractOperations.registryProduct', 
                type: 'string', 
                formHide: true
            },
            { 
                name: 'idRegistryOperationType', 
                label: 'SiteContractOperations.registryOperation', 
                type: 'select', 
                selectApi: 'movement-instruction/user-role-operation-type-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            { 
                name: 'registryOperationType', 
                label: 'SiteContractOperations.registryOperation', 
                type: 'string', 
                formHide: true
            },
            { 
                name: 'idRegistryPlant', 
                label: 'SiteContractOperations.registryPlant', 
                type: 'select', 
                selectApi: 'masterdata/registry-plant-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            { 
                name: 'registryPlant', 
                label: 'SiteContractOperations.registryPlant', 
                type: 'string', 
                formHide: true
            },
            { 
                name: 'isDeleted', 
                label: 'isDeleted', 
                type: 'boolean',
                defaultFilter: true,
                defaultFilterValue: false,
                formHide: true
            }
        ]
    },
    ProductTollingMapping: {
        title: 'ProductTollingMapping.page_title',
        api: 'masterdata/product-tolling-mapping',
        enableUpdate: true,
        editButtonName: 'ProductTollingMappingEditButton',
        enableDelete: true,
        deleteButtonName: 'ProductTollingMappingDeleteButton',
        enableInsert: true,
        addButtonName: 'ProductTollingMappingAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { 
                name: 'tollingRegistryProductId', 
                label: 'ProductTollingMapping.registryProductTolling', 
                type: 'select', 
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            { 
                name: 'tollingRegistryProduct', 
                label: 'ProductTollingMapping.registryProductTolling', 
                type: 'string', 
                formHide: true
            },
            { 
                name: 'referenceRegistryProductId', 
                label: 'ProductTollingMapping.registryProductReference', 
                type: 'select', 
                selectApi: 'masterdata/product-combo',
                selectFormat: { label: 'label', value: 'value' },
                gridHide: true
            },
            { 
                name: 'referenceRegistryProduct', 
                label: 'ProductTollingMapping.registryProductReference', 
                type: 'string', 
                formHide: true
            },
            { 
                name: 'tollingFamilyGroup', 
                label: 'ProductTollingMapping.tollingFamilyGroup', 
                type: 'string', 
            },
            { 
                name: 'familyPool', 
                label: 'ProductTollingMapping.familyPool', 
                type: 'string', 
            },
            {
                name: 'startDate',
                label: 'startDate',
                type: 'date'
            },
            {
                name: 'endDate',
                label: 'expireDate',
                type: 'date',
                format: "dd/mm/yy"
            }
        ]
    },
    SysGatewayServer: {
        title: 'SysGatewayServer.page_title',
        api: 'system/gateway-server',
        enableUpdate: true,
        editButtonName: 'SysGatewayServerEditButton',
        enableDelete: true,
        deleteButtonName: 'SysGatewayServerDeleteButton',
        enableInsert: true,
        addButtonName: 'SysGatewayServerAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'name',
                label: 'name',
                type: 'string',
                validations: {
                    required: true
                }
            },
            {
                name: 'url',
                label: 'SysGatewayServer.url',
                type: 'string',
                validations: {
                    required: true
                }
            }
        ]
    },
    SysGatewayRoute: {
        title: 'SysGatewayRoute.page_title',
        api: 'system/gateway-route',
        enableUpdate: true,
        editButtonName: 'SysGatewayGatewayEditButton',
        enableDelete: true,
        deleteButtonName: 'SysGatewayGatewayDeleteButton',
        enableInsert: true,
        addButtonName: 'SysGatewayGatewayAddButton',
        idColumn: "id",
        toolbarActions: [
            {
                type:'button',
                visible: true,
                name: 'refreshGatewayRoutes',
                icon: 'pi pi-refresh',
                title: 'SysGatewayServer.refreshBtn',
                cssClass: 'p-button-success p-button-sm',
                action: function(data, caller){
                    caller.$confirm.require({
                        message: caller.$t('SysGatewayServer.refreshMessage'),
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: caller.$t('yes'),
                        rejectLabel: caller.$t('no'),
                        accept: () => {
                            caller.$store.dispatch("generic/setLoading", true);
                            CommonService.createItem('system/gateway-server-refresh', {}, resp => {
                                caller.$store.dispatch("generic/setLoading", false);
                                caller.$toast.add({ severity: 'success', summary: 'Success Message', detail: caller.$t('messages.update_message_success'), life: 3000 });
                            })
                        },
                        reject: () => {
                            //callback to execute when user rejects the action
                            caller.$confirm.close();
                        }
                    });
                }
            }
        ],
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            {
                name: 'fromUrl',
                label: 'SysGatewayRoute.fromUrl',
                type: 'string',
                validations: {
                    required: true
                }
            },
            {
                name: 'toUrl',
                label: 'SysGatewayRoute.toUrl',
                type: 'string',
                validations: {
                    required: true
                }
            },
            {
                name: 'routeOrder',
                label: 'SysGatewayRoute.routeOrder',
                type: 'number',
                validations: {
                    required: true
                }
            },
            {
                name: 'idSysGatewayServer',
                label: 'SysGatewayServer.fk',
                type: 'select',
                selectApi: 'system/gateway-server-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'sysGatewayServer',
                label: 'SysGatewayServer.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'system/gateway-server-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idSysGatewayServer'
            },
            {
                name: 'authenticated',
                label: 'SysGatewayRoute.authenticated',
                type: 'boolean',
                validations: {
                    required: true
                }
            },
            {
                name: 'prefix',
                label: 'SysGatewayRoute.prefix',
                type: 'string'
            }
        ]
    },
    InfoilDeviationCheckConfig: {
        title: 'InfoilDeviationCheckConfig.page_title',
        api: 'masterdata/infoil-deviation-check-config',
        enableUpdate: true,
        editButtonName: 'InfoilDeviationCheckConfigEditButton',
        enableDelete: true,
        deleteButtonName: 'InfoilDeviationCheckConfigDeleteButton',
        enableInsert: true,
        addButtonName: 'InfoilDeviationCheckConfigAddButton',
        idColumn: "id",
        columns: [
            {
                name: 'id',
                label: 'id',
                type: 'number',
                readOnly: true,
                defaultSort: 'asc'
            },
            { 
                name: 'combinedNomenclature', 
                label: 'InfoilDeviationCheckConfig.combinedNomenclature', 
                type: 'string'
            },
            {
                name: 'tankCode',
                label: 'tankConfiguration.tankCode',
                type: 'select',
                gridHide: true,
                selectApi: 'tank-management/tank-combo',
                selectFormat: { label: 'label', value: 'value' },
                inLineEdit: true,
                validations: {
                    required: true
                }                
            },
            {
                name: 'tankCode',
                label: 'tankConfiguration.tankCode',
                type: 'string',
                formHide: true
            },
            {
                name: 'idRegistryOperationType',
                label: 'operationType.fk',
                type: 'select',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'registryOperationType',
                label: 'operationType.fk',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/operation-type-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'idRegistryOperationType'
            },
            {
                name: 'previousIdRegistryOperationType',
                label: 'InfoilDeviationCheckConfig.previousIdRegistryOperationType',
                type: 'select',
                selectApi: 'masterdata/operation-type-combo',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true
            },
            {
                name: 'previousRegistryOperationType',
                label: 'InfoilDeviationCheckConfig.previousIdRegistryOperationType',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/operation-type-combo',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'previousIdRegistryOperationType'
            },
            {
                name: 'measureType',
                label: 'InfoilDeviationCheckConfig.measureType',
                type: 'string'
            },

            {
                name: 'uomCode',
                label: 'uoMCode',
                type: 'select',
                selectApi: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=EquipmentOperationTank',
                selectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridHide: true,
            },
            {
                name: 'uomCode',
                label: 'uoMCode',
                type: 'string',
                formHide: true,
                gridFilterType: 'select',
                gridFilterSelectApi: 'masterdata/unit-of-measure-combo?getOnlyValid=true&scope=EquipmentOperationTank',
                gridFilterSelectFormat: {
                    label: 'label',
                    value: 'value'
                },
                gridFilterName: 'uomCode'
            },

            // {
            //     name: 'uomCode',
            //     label: 'InfoilDeviationCheckConfig.uomCode',
            //     type: 'string'
            // },
            {
                name: 'alertThreshold',
                label: 'InfoilDeviationCheckConfig.alertThreshold',
                type: 'decimal',
                digits: 3
            },
            {
                name: 'isPiEnabled',
                label: 'InfoilDeviationCheckConfig.isPiEnabled',
                type: 'boolean'
            },
            {
                name: 'isThresholdCheckEnabled',
                label: 'InfoilDeviationCheckConfig.isThresholdCheckEnabled',
                type: 'boolean'
            },
            {
                name: 'isSendToInfoilEnabled',
                label: 'InfoilDeviationCheckConfig.isSendToInfoilEnabled',
                type: 'boolean'
            }
        ]
    },
}

export default {
    getConfiguration(name) {
        return configurations[name];
    }
}

/**
**EXAMPLES

GENERIC
    $NAME: {
        title: 'title',
        api: 'api/url',
        enableUpdate: true/false,
        enableDelete: true/false,
        enableInsert: true/false,
        idColumn: "id",
        columns: [],
        toolbarActions: [],
        customActions: []

    }


COLUMN

    {
        name: 'columnName',
        label: 'label',
        type: 'string', //number, decimal, select, boolean
        validations: {
            maxLength: N,
            required: true
        }
    }


SELECT / STRING

    {
        name:'idField',
        label: 'label',
        type: 'select',
        selectApi: 'api',
        selectFormat: { label: 'labelName', value: 'valueName' },
        gridHide: true
    },
    {
        name: 'field',
        label: 'label',
        type: 'string',
        formHide: true,
        gridFilterType: 'select',
        gridFilterSelectApi: 'api',
        gridFilterSelectFormat: { label: 'labelName', value: 'valueName' },
        gridFilterName: 'idField
    }


CUSTOM ACTION
    
    {
        visible: false,
        name: 'action_name',
        icon: 'pi pi-icon',
        title: 'actionTitle',
        message: 'actionMessage',
        confirmBtnText: 'btn.text',
        data: {},
        fields: [],
        action: function(data, caller){}
    }

* 
 */